import React, { useRef, useState, useEffect, useCallback } from "react";
import { storage } from "../lib/storage.js";
import { FACTIONS, FAC_KEYS, GRAND_FACTION_KEYS, GRAND_SUMMARY_FACS, FOREIGN_FACTION_KEYS, FAC_HOTKEY } from "./data/factions.js";
import { GENERAL_SKILLS, ADDITIONAL_SKILLS } from "./data/skills.js";
import GeneralAvatar, { avatarHash } from "./components/GeneralAvatar.jsx";
import GrandHudGenerals from "./components/GrandHudGenerals.jsx";

// 3D 视图按需加载，保持默认 2D 模式的首屏体积和启动速度。
const Battle3DView = React.lazy(() => import("./Battle3DView.jsx"));

/* ============================================================
   三国·沙盘 v5
   框选群令 · 关隘 · 城池攻防(易帜/休养/募兵) · 六幅地图 · 可调图幅
   老兵金星与将旗晋阶 · 设置面板 · 快捷键标注
   ============================================================ */

/* ---- world size (mutable) ---- */
const TILE = 12;
const SIZES = { s: { name: "小", w: 720, h: 480 }, m: { name: "中", w: 960, h: 600 }, l: { name: "大", w: 1440, h: 900 } };
let W = 960, H = 600, COLS = Math.ceil(W / TILE), ROWS = Math.ceil(H / TILE);
function setWorldSize(key) {
  W = SIZES[key].w; H = SIZES[key].h;
  COLS = Math.ceil(W / TILE); ROWS = Math.ceil(H / TILE);
}

const MEN_PER_DOT = 10;
const SPEEDS = [0.25, 0.5, 1, 2, 4];
const SWORD_CURSOR = `url("data:image/svg+xml,${encodeURIComponent('<svg xmlns="http://www.w3.org/2000/svg" width="32" height="32" viewBox="0 0 32 32"><path d="M5 27 8 18 22 4l6 6-14 14-9 3z" fill="#f5e7b2" stroke="#241b12" stroke-width="2"/><path d="m18 8 6 6M8 18l6 6" stroke="#9b6d35" stroke-width="2"/><path d="M22 4 28 10" stroke="#fff7d1" stroke-width="2"/></svg>')}") 8 8, crosshair`;
const SAVE_KEY = "sanguo:save-v5";
const SETTINGS_KEY = "sanguo:settings-v5";

const GENERALS = {
  wei: ["曹操","张辽","夏侯惇","司马懿","许褚","徐晃","张郃","曹仁"],
  shu: ["刘备","关羽","张飞","赵云","诸葛亮","黄忠","马超","魏延"],
  wu:  ["孙权","周瑜","吕蒙","陆逊","甘宁","太史慈","黄盖","程普"],
  lvbu: ["吕布","陈宫","高顺","张辽","貂蝉","侯成","魏续","宋宪"],
  yuan: ["袁绍","颜良","文丑","审配","沮授","田丰","高览","张郃"],
  dong: ["董卓","李儒","华雄","吕布","李傕","郭汜","徐荣","樊稠"],
  liubiao: ["刘表","蒯越","蒯良","蔡瑁","黄祖","文聘","刘琦","刘琮"],
  kongrong: ["孔融","太史慈","武安国","宗宝","王修","郑玄","张俭","陈纪"],
  yuanshu: ["袁术","纪灵","桥蕤","雷薄","陈兰","张勋","袁胤","韩胤"],
  gongsun: ["公孙瓒","赵云","公孙越","严纲","田楷","单经","公孙范","关靖"],
  mateng: ["马腾","韩遂","马超","马岱","庞德","成公英","梁兴","杨秋"],
  zhanglu: ["张鲁","张卫","张琪瑛","杨任","杨昂","阎圃","杜灏","李休"],
  liuzhang: ["刘璋","法正","张松","严颜","吴懿","吴兰","雷铜","李严"],
  taoqian: ["陶谦","陈登","陈珪","糜竺","曹豹","笮融","闵纯","王朗"],
  zhangxiu: ["张绣","贾诩","胡车儿","雷叙","张先","邹氏","张济","胡封"],
};
/* Legacy inline skill data moved to ./data/skills.js.
const LEGACY_GENERAL_SKILLS = {
  "关羽": [{ id: "assassinate", name: "🗡 青龙刺杀", note: "锁定最近敌方武将，重创其生命并打击军心" }],
  "张飞": [{ id: "assassinate", name: "🗡 猛将刺杀", note: "突袭最近敌方武将，重创其生命并打击军心" }],
  "赵云": [{ id: "assassinate", name: "🗡 七进七出", note: "锁定最近敌方武将，重创其生命并打击军心" }],
  "张辽": [{ id: "assassinate", name: "🗡 威震逍遥津", note: "锁定最近敌方武将，重创其生命并打击军心" }],
  "许褚": [{ id: "assassinate", name: "🗡 虎痴突袭", note: "锁定最近敌方武将，重创其生命并打击军心" }],
  "马超": [{ id: "assassinate", name: "🗡 铁骑突杀", note: "锁定最近敌方武将，重创其生命并打击军心" }],
  "甘宁": [{ id: "assassinate", name: "🗡 锦帆夜袭", note: "锁定最近敌方武将，重创其生命并打击军心" }],
  "太史慈": [{ id: "assassinate", name: "🗡 神亭突击", note: "锁定最近敌方武将，重创其生命并打击军心" }],
  "诸葛亮": [{ id: "wideAttack", name: "🔥 八阵火攻", note: "对指定军团周围敌军造成范围伤害并降低士气" }],
  "周瑜": [{ id: "wideAttack", name: "🔥 赤壁火海", note: "对指定军团周围敌军造成范围伤害并降低士气" }],
  "司马懿": [{ id: "wideAttack", name: "🔥 玄谋天火", note: "对指定军团周围敌军造成范围伤害并降低士气" }],
  "陆逊": [{ id: "wideAttack", name: "🔥 火烧连营", note: "对指定军团周围敌军造成范围伤害并降低士气" }],
  "黄月英": [{ id: "wideAttack", name: "🔥 连弩轰击", note: "对指定军团周围敌军造成范围伤害并降低士气" }],
};
// 可在武将府中分配的通用技能。它们先作为武将档案与经营/战斗贡献说明使用，
// 不会替换武将专属技的释放逻辑，避免随意配技破坏实时战斗平衡。
const ADDITIONAL_SKILLS = [
  ["march", "🏃 急行军", "军略", "行军速度提高，适合快速支援和追击。"],
  ["fortify", "🛡 坚壁", "军略", "驻守城池时降低受到的常规伤害。"],
  ["formation", "⚔ 雁行阵", "军略", "统军时提升远程部队的齐射稳定性。"],
  ["charge", "🐎 铁骑冲阵", "军略", "骑兵冲锋造成更高的首次冲击伤害。"],
  ["logistics", "🌾 粮道整饬", "军略", "降低出征粮草消耗，长途作战更持久。"],
  ["scout", "👁 斥候先行", "军略", "扩大视野并更早发现附近敌军。"],
  ["duel", "🗡 单骑挑战", "武勇", "近战时更容易压制敌方将领的军心。"],
  ["spearhead", "⚡ 破阵突击", "武勇", "对敌方坚守阵型的第一轮攻击更有效。"],
  ["archery", "🏹 百步穿杨", "武勇", "弓兵远程攻击的命中与伤害略有提升。"],
  ["guardian", "🦁 护军死士", "武勇", "所属军团溃散阈值提高，较不易崩溃。"],
  ["fire", "🔥 火计", "谋略", "火攻和营寨破坏效果增强。"],
  ["stratagem", "🎭 奇谋", "谋略", "战术成功率和对敌士气打击小幅提高。"],
  ["water", "🌊 水战", "谋略", "在河流和水乡地形作战时获得优势。"],
  ["heal", "🧪 医护", "谋略", "战后伤兵恢复与军团士气恢复更快。"],
  ["counterintelligence", "🕯 反间", "谋略", "降低敌方煽动和离间造成的影响。"],
  ["diplomacy", "🤝 外交", "政务", "外交、劝降和招揽行动更容易成功。"],
  ["governance", "🏮 治理", "政务", "任太守时提升城池收入与民心恢复。"],
  ["recruitment", "📣 招募", "政务", "城池募兵效率提高，补充兵力更快。"],
  ["inspiration", "🎺 鼓舞", "家眷", "提升所属军团的初始士气和士气恢复。"],
  ["charm", "🌸 倾城", "家眷", "婚配、安抚和家眷相关互动的魅力效果增强。"],
].map(([id, name, category, contribution]) => ({ id: `extra:${id}`, name, category, contribution, note: "可在技能管理页手动分配。" })); */
function generalSkills(name) {
  return GENERAL_SKILLS[name] || [];
}
/* Legacy inline avatar moved to ./components/GeneralAvatar.jsx.
function avatarHash(name) {
  return [...name].reduce((sum, ch, i) => sum + ch.charCodeAt(0) * (i + 3), 0);
}
function GeneralAvatar({ name, gender = "m", faction = "neutral", size = 38 }) {
  const hash = avatarHash(name);
  const F = FACTIONS[faction] || FACTIONS.neutral;
  const hairColors = ["#17130f", "#34251b", "#5b3825", "#8a6a46"];
  const skinColors = ["#f0c6a4", "#d99f7b", "#e7b58f", "#c98768"];
  const hair = hairColors[hash % hairColors.length];
  const skin = skinColors[Math.floor(hash / 7) % skinColors.length];
  const female = gender === "f";
  const uid = `avatar-${hash}-${name.length}-${female ? "f" : "m"}`;
  return (
    <svg width={size} height={Math.round(size * 1.25)} viewBox="0 0 48 60" role="img" aria-label={`${name}头像`} style={{ flex: "0 0 auto", borderRadius: 4, border: `1px solid ${F.color}99`, background: F.dark, boxShadow: "0 2px 5px rgba(0,0,0,0.35)" }}>
      <defs><linearGradient id={uid} x1="0" y1="0" x2="0" y2="1"><stop offset="0" stopColor={F.light} stopOpacity="0.7" /><stop offset="1" stopColor={F.dark} /></linearGradient></defs>
      <rect width="48" height="60" rx="4" fill={`url(#${uid})`} />
      <path d={female ? "M7 58 Q8 37 17 34 Q24 31 31 34 Q40 37 41 58Z" : "M5 58 Q7 36 16 34 Q24 31 32 34 Q41 36 43 58Z"} fill={F.color} />
      <path d="M15 34 Q24 29 33 34 L36 48 Q24 55 12 48Z" fill={skin} />
      {female ? <path d="M11 31 Q9 13 24 9 Q39 13 37 33 L33 29 L31 18 Q24 22 16 18 L15 31Z" fill={hair} /> : <path d="M12 29 Q9 12 24 8 Q39 12 36 29 L31 20 Q24 23 16 20Z" fill={hair} />}
      <path d="M18 31 Q24 35 30 31" fill="none" stroke="#7b4939" strokeWidth="1" />
      <circle cx="19" cy="27" r="1.2" fill="#2a2118" /><circle cx="29" cy="27" r="1.2" fill="#2a2118" />
      {female ? <path d="M8 14 Q24 2 40 14 L36 18 Q24 11 12 18Z" fill={F.color} opacity="0.9" /> : <path d="M10 15 Q24 2 38 15 L34 18 Q24 11 14 18Z" fill={F.color} opacity="0.9" />}
      <text x="24" y="56" textAnchor="middle" fontSize="7" fill="#fff8e5" fontFamily="serif">{name.slice(-1)}</text>
    </svg>
  );
}
*/
function generalSkillContribution(name, profile = null) {
  const special = generalSkills(name);
  if (special.length) return special.map(skill => `${skill.name}：${skill.note}`).join("\n");
  const gender = profile?.gender || (isFemaleName(name) ? "f" : "m");
  return gender === "f"
    ? "内助安民：婚配后提升配偶忠诚度；魅力越高，家眷带来的城池民心收益越明显。"
    : "统军作战：负责所属军团的统率与战斗指挥，统率越高，守城和出征表现越稳定。";
}
const UNIT_TYPES = {
  inf:   { name: "步兵", glyph: "步", key: "Q", hp: 30, atk: 6.0, spd: 26, note: "均衡耐战" },
  spear: { name: "枪兵", glyph: "枪", key: "W", hp: 32, atk: 5.0, spd: 22, note: "克骑 ×2.2" },
  arch:  { name: "弓兵", glyph: "弓", key: "E", hp: 18, atk: 3.0, spd: 24, note: "70步齐射 · 据丘+25%" },
  cav:   { name: "骑兵", glyph: "骑", key: "R", hp: 34, atk: 6.5, spd: 46, note: "冲锋 ×3 · 山林失速" },
};
const ORDERS = {
  attack:  { name: "进攻", icon: "⚔", key: "A", note: "主动寻敌歼灭" },
  hold:    { name: "坚守", icon: "🛡", key: "H", note: "据守原地，敌近则战" },
  feint:   { name: "佯攻", icon: "🎭", key: "Y", note: "三进两退，诱敌离阵" },
  retreat: { name: "撤退", icon: "🏳", key: "T", note: "有序退场，不算折损" },
};
const PURSUIT_STANCES = {
  ruthless: { name: "穷追猛打", icon: "🗡" },
  normal: { name: "常规追击", icon: "⚖" },
  merciful: { name: "网开一面", icon: "🕊" },
};
const FORMS = {
  fang: { name: "方阵", atk: 1.0,  def: 1.0,  spd: 1.0,  range: 1.0,  note: "中正平和" },
  feng: { name: "锋矢", atk: 1.18, def: 0.88, spd: 1.05, range: 1.0,  note: "攻+18% 防−12%" },
  yuan: { name: "圆阵", atk: 0.9,  def: 1.22, spd: 0.8,  range: 1.0,  note: "防+22% 攻速−" },
  yan:  { name: "雁行", atk: 1.0,  def: 0.94, spd: 1.05, range: 1.18, note: "弓射程+18%" },
};
const B_TYPES = {
  city: { name: "城池", key: "C", hp: 1200, aura: 78, note: "防御×1.5 · 补给 · 休养 · 募兵 · 可被围而易帜" },
  camp: { name: "兵营", key: "V", hp: 400,  aura: 0,  note: "募兵50/6秒(上限600) · 补给 · 可拆毁" },
  pass: { name: "关隘", key: "G", hp: 1600, aura: 55, note: "扼守要道 · 圈内防御×1.4 · 可强攻拆毁" },
};
const SUPPLY_R = 115;
const CAPTURE_TIME = 8;
const BROKEN_JOIN_R = 72;
const BROKEN_FORM_MIN = 500;

/* ---------- rng / noise ---------- */
function mulberry32(a) {
  return function () {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
function makeValueNoise(seed) {
  const rnd = mulberry32(seed);
  const g = {};
  const val = (ix, iy) => {
    const k = ix + "," + iy;
    if (g[k] === undefined) g[k] = rnd();
    return g[k];
  };
  const smooth = (t) => t * t * (3 - 2 * t);
  return function (x, y) {
    const ix = Math.floor(x), iy = Math.floor(y);
    const fx = smooth(x - ix), fy = smooth(y - iy);
    const a = val(ix, iy), b = val(ix + 1, iy), c = val(ix, iy + 1), d = val(ix + 1, iy + 1);
    return a + (b - a) * fx + (c - a) * fy + (a - b - c + d) * fx * fy;
  };
}
function fbm(n, x, y) {
  return n(x, y) * 0.55 + n(x * 2.1, y * 2.1) * 0.28 + n(x * 4.3, y * 4.3) * 0.17;
}

const T_NAME = ["平原", "林地", "丘陵", "山地", "水域"];
const T_ICON = ["◻", "🌲", "⛰", "🗻", "〰"];
const T_SWATCH = ["#cdbd8f", "#5c8a4e", "#a68a5a", "#6b5a48", "#6f95a8"];
const T_INFO = [
  "速度100% · 防御基准 · 骑步皆宜",
  "速度72%（骑42%）· 防御+25% · 箭矢伤害减半",
  "速度58%（骑45%）· 防御+35% · 弓兵射程+25%",
  "速度34%（骑22%）· 防御+55% · 极利据守",
  "速度40% · 防御−35%（半渡而击）",
];

/* ---------- terrain ---------- */
const T_SPEED = [1.0, 0.72, 0.58, 0.34, 0.4];
const T_SPEED_CAV = [1.0, 0.42, 0.45, 0.22, 0.3];
const T_DEF = [1.0, 1.25, 1.35, 1.55, 0.65];
// 仅用于渲染的视觉高度，不参与战斗数值；战斗高度仍由 ELEV 控制。
const T_VIS_HEIGHT = [0, 0.35, 1.1, 2.4, -0.7];

function genTerrain(seed, opts = {}) {
  const { river = "v", mountainBias = 0, forestBias = 0, clearRects = [], ridges = [] } = opts;
  const n1 = makeValueNoise(seed), n2 = makeValueNoise(seed + 999);
  const rnd = mulberry32(seed + 7);
  const map = new Uint8Array(COLS * ROWS);
  // 连续高程原料：保存分类前的高程噪声，供 3D 高度场构建平滑坡面（仅渲染用，不参与战斗数值）。
  const elev = new Float32Array(COLS * ROWS);
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      const e = fbm(n1, x * 0.055, y * 0.055) + mountainBias;
      const f = fbm(n2, x * 0.09, y * 0.09) + forestBias;
      elev[y * COLS + x] = e;
      let t = 0;
      if (e > 0.72) t = 3;
      else if (e > 0.62) t = 2;
      else if (f > 0.62 && e > 0.35) t = 1;
      map[y * COLS + x] = t;
    }
  }
  let riverX0 = 0, amp = 0, freq = 0, phase = 0;
  const fords = [];
  if (river === "v") {
    riverX0 = COLS * (0.35 + rnd() * 0.3);
    amp = 6 + rnd() * 8; freq = 0.06 + rnd() * 0.05; phase = rnd() * 10;
    for (let y = 0; y < ROWS; y++) {
      const cx = riverX0 + Math.sin(y * freq + phase) * amp;
      for (let dx = -1; dx <= 1; dx++) {
        const x = Math.round(cx) + dx;
        if (x >= 0 && x < COLS) map[y * COLS + x] = 4;
      }
    }
    for (let i = 0; i < 2; i++) fords.push({ axis: "v", at: Math.floor(ROWS * (0.25 + 0.5 * rnd())) });
  } else if (river === "wide-h") {
    const cy = ROWS * 0.52; amp = 2.5; freq = 0.05; phase = 1.7; riverX0 = cy;
    for (let x = 0; x < COLS; x++) {
      const c = cy + Math.sin(x * freq + phase) * amp;
      for (let dy = -3; dy <= 3; dy++) {
        const y = Math.round(c) + dy;
        if (y >= 0 && y < ROWS) map[y * COLS + x] = 4;
      }
    }
    fords.push({ axis: "h", at: Math.floor(COLS * 0.48) });
  }
  // 山岭 (关隘所倚): vertical mountain wall with a gap
  for (const rg of ridges) {
    const col = Math.round(rg.colFrac * COLS);
    const gapY = Math.round(rg.gapFrac * ROWS);
    const gapH = rg.gapH || 5;
    for (let y = 0; y < ROWS; y++) {
      if (Math.abs(y - gapY) <= gapH / 2) continue;
      for (let dx = -1; dx <= 1; dx++) {
        const x = col + dx;
        if (x >= 0 && x < COLS && map[y * COLS + x] !== 4) map[y * COLS + x] = 3;
      }
    }
    // 谷口两侧丘陵
    for (let y = gapY - gapH; y <= gapY + gapH; y++) {
      for (const x of [col - 2, col + 2]) {
        if (x >= 0 && x < COLS && y >= 0 && y < ROWS && map[y * COLS + x] === 0) map[y * COLS + x] = 2;
      }
    }
  }
  for (const r of clearRects) {
    const x0 = Math.round(r.xf * COLS), y0 = Math.round(r.yf * ROWS);
    for (let y = y0; y < y0 + r.h; y++)
      for (let x = x0; x < x0 + r.w; x++)
        if (x >= 0 && x < COLS && y >= 0 && y < ROWS && map[y * COLS + x] !== 4)
          map[y * COLS + x] = 0;
  }
  return { map, elev, fords, riverX0, amp, freq, phase, seed, river };
}
function terrainAt(terr, px, py) {
  const x = Math.max(0, Math.min(COLS - 1, Math.floor(px / TILE)));
  const y = Math.max(0, Math.min(ROWS - 1, Math.floor(py / TILE)));
  return terr.map[y * COLS + x];
}

/* ---------- terrain painting ---------- */
function paintTerrain(ctx, terr) {
  const rnd = mulberry32(terr.seed + 3);
  ctx.fillStyle = "#e7dcc1";
  ctx.fillRect(0, 0, W, H);
  for (let i = 0; i < W * H / 640; i++) {
    ctx.fillStyle = `rgba(120,100,60,${0.02 + rnd() * 0.03})`;
    ctx.beginPath();
    ctx.arc(rnd() * W, rnd() * H, 4 + rnd() * 26, 0, Math.PI * 2);
    ctx.fill();
  }
  // 2.5D 基底：先给每类地形铺一层稳定色块，再叠加水纹、林木和山石细节。
  const baseColors = ["rgba(205,189,143,0.18)", "rgba(84,122,72,0.35)", "rgba(166,138,90,0.34)", "rgba(102,83,70,0.38)", "rgba(102,141,161,0.62)"];
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      const t = terr.map[y * COLS + x];
      ctx.fillStyle = baseColors[t];
      ctx.fillRect(x * TILE, y * TILE, TILE, TILE);
    }
  }
  // 高地向右下投出短阴影；地形边界因此不再只是颜色变化，而有凸起/落差感。
  for (let y = ROWS - 1; y >= 0; y--) {
    for (let x = COLS - 1; x >= 0; x--) {
      const t = terr.map[y * COLS + x];
      const h = T_VIS_HEIGHT[t];
      if (h <= 0) continue;
      const px = x * TILE, py = y * TILE, depth = Math.max(2, Math.round(h * 2.5));
      ctx.fillStyle = `rgba(34,27,18,${Math.min(0.24, 0.06 + h * 0.045)})`;
      ctx.beginPath();
      ctx.moveTo(px, py + TILE - 1);
      ctx.lineTo(px + TILE, py + TILE - 1);
      ctx.lineTo(px + TILE, py + TILE + depth);
      ctx.lineTo(px, py + TILE + depth);
      ctx.closePath();
      ctx.fill();
      ctx.fillStyle = `rgba(255,247,220,${Math.min(0.15, 0.035 + h * 0.025)})`;
      ctx.fillRect(px, py, TILE, 1.5);
    }
  }
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++)
      if (terr.map[y * COLS + x] === 4) {
        ctx.fillStyle = "#8fa8b5";
        ctx.fillRect(x * TILE, y * TILE, TILE, TILE);
        // 伪3D：顶边内阴影，暗示水面是"洼下去"的
        ctx.fillStyle = "rgba(20,30,40,0.18)";
        ctx.fillRect(x * TILE, y * TILE, TILE, 2);
      }
  ctx.strokeStyle = "rgba(70,95,110,0.5)";
  ctx.lineWidth = 1;
  for (let y = 0; y < ROWS; y++)
    for (let x = 0; x < COLS; x++)
      if (terr.map[y * COLS + x] === 4 && rnd() < 0.4) {
        const px = x * TILE + rnd() * 4, py = y * TILE + rnd() * TILE;
        ctx.beginPath();
        ctx.moveTo(px, py);
        ctx.quadraticCurveTo(px + 4, py - 2, px + 8, py);
        ctx.stroke();
      }
  for (const f of terr.fords) {
    if (f.axis === "v") {
      const cx = terr.riverX0 + Math.sin(f.at * terr.freq + terr.phase) * terr.amp;
      const bx = (cx - 2.2) * TILE, by = f.at * TILE;
      ctx.fillStyle = "#a5814f";
      ctx.fillRect(bx, by, TILE * 4.4, TILE * 0.9);
      ctx.strokeStyle = "#6e5433";
      for (let i = 0; i < 5; i++) {
        ctx.beginPath(); ctx.moveTo(bx + i * TILE, by); ctx.lineTo(bx + i * TILE, by + TILE * 0.9); ctx.stroke();
      }
    } else {
      const cy = terr.riverX0 + Math.sin(f.at * terr.freq + terr.phase) * terr.amp;
      const bx = f.at * TILE, by = (cy - 3.4) * TILE;
      ctx.fillStyle = "#a5814f";
      ctx.fillRect(bx, by, TILE * 1.1, TILE * 6.8);
      ctx.strokeStyle = "#6e5433";
      for (let i = 0; i < 7; i++) {
        ctx.beginPath(); ctx.moveTo(bx, by + i * TILE); ctx.lineTo(bx + TILE * 1.1, by + i * TILE); ctx.stroke();
      }
    }
  }
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      const t = terr.map[y * COLS + x];
      const px = x * TILE, py = y * TILE;
      if (t === 1) {
        ctx.fillStyle = "rgba(60,90,55,0.16)";
        ctx.fillRect(px, py, TILE, TILE);
        if (rnd() < 0.6) {
          const tx = px + 2 + rnd() * 8, ty = py + 3 + rnd() * 7;
          ctx.strokeStyle = "rgba(45,70,40,0.85)";
          ctx.beginPath(); ctx.moveTo(tx, ty + 4); ctx.lineTo(tx, ty); ctx.stroke();
          ctx.fillStyle = "rgba(52,84,48,0.9)";
          ctx.beginPath(); ctx.arc(tx, ty - 1.5, 2.4, 0, Math.PI * 2); ctx.fill();
        }
      } else if (t === 2) {
        ctx.fillStyle = "rgba(130,110,70,0.18)";
        ctx.fillRect(px, py, TILE, TILE);
        // 伪3D：底边浅阴影，暗示丘陵略微凸起
        ctx.fillStyle = "rgba(40,32,20,0.14)";
        ctx.fillRect(px, py + TILE - 3, TILE, 3);
        if (rnd() < 0.45) {
          const hx = px + 2 + rnd() * 6, hy = py + 4 + rnd() * 5;
          ctx.strokeStyle = "rgba(90,75,45,0.8)"; ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(hx, hy + 3); ctx.lineTo(hx + 3.5, hy - 2); ctx.lineTo(hx + 7, hy + 3);
          ctx.stroke();
        }
      } else if (t === 3) {
        ctx.fillStyle = "rgba(80,70,55,0.3)";
        ctx.fillRect(px, py, TILE, TILE);
        // 伪3D：底边深阴影+顶边淡高光，比丘陵更明显地"凸出"
        ctx.fillStyle = "rgba(30,24,16,0.22)";
        ctx.fillRect(px, py + TILE - 5, TILE, 5);
        ctx.fillStyle = "rgba(255,255,255,0.05)";
        ctx.fillRect(px, py, TILE, 2);
        if (rnd() < 0.55) {
          const hx = px + 1 + rnd() * 4, hy = py + 6 + rnd() * 4;
          ctx.strokeStyle = "rgba(50,42,32,0.95)"; ctx.lineWidth = 1.6;
          ctx.beginPath();
          ctx.moveTo(hx, hy + 4); ctx.lineTo(hx + 4.5, hy - 5); ctx.lineTo(hx + 9, hy + 4);
          ctx.stroke();
          ctx.strokeStyle = "rgba(240,238,230,0.9)"; ctx.lineWidth = 1.2;
          ctx.beginPath();
          ctx.moveTo(hx + 3, hy - 2); ctx.lineTo(hx + 4.5, hy - 5); ctx.lineTo(hx + 6, hy - 2);
          ctx.stroke();
        }
      } else if (t === 0 && rnd() < 0.25) {
        // 平原草丛小簇：呼应耕地/原野感，避免相对其余四类地形显得"未绘制"
        const gx = px + 2 + rnd() * 8, gy = py + 3 + rnd() * 7;
        ctx.strokeStyle = "rgba(120,100,60,0.4)";
        ctx.lineWidth = 1;
        ctx.beginPath();
        ctx.moveTo(gx, gy + 3); ctx.lineTo(gx + 1.5, gy);
        ctx.moveTo(gx + 3, gy + 3); ctx.lineTo(gx + 4, gy + 0.7);
        ctx.stroke();
      }
    }
  }
  // 地形分区边界：相邻不同类型地块之间描一道手绘感淡墨线（曲线+抖动，避免像素网格感），让区域轮廓更清晰
  ctx.strokeStyle = "rgba(58,46,30,0.28)";
  ctx.lineWidth = 1;
  for (let y = 0; y < ROWS; y++) {
    for (let x = 0; x < COLS; x++) {
      const t = terr.map[y * COLS + x];
      const px = x * TILE, py = y * TILE;
      if (x + 1 < COLS && terr.map[y * COLS + x + 1] !== t) {
        const jig = (rnd() - 0.5) * 1.2;
        ctx.beginPath();
        ctx.moveTo(px + TILE, py);
        ctx.quadraticCurveTo(px + TILE + jig, py + TILE / 2, px + TILE, py + TILE);
        ctx.stroke();
      }
      if (y + 1 < ROWS && terr.map[(y + 1) * COLS + x] !== t) {
        const jig = (rnd() - 0.5) * 1.2;
        ctx.beginPath();
        ctx.moveTo(px, py + TILE);
        ctx.quadraticCurveTo(px + TILE / 2, py + TILE + jig, px + TILE, py + TILE);
        ctx.stroke();
      }
    }
  }
  ctx.strokeStyle = "rgba(60,45,25,0.6)";
  ctx.lineWidth = 3;
  ctx.strokeRect(4, 4, W - 8, H - 8);
}

/* ---------- alliances ---------- */
function pairKey(a, b) { return [a, b].sort().join("|"); }
function isAllied(sim, f1, f2) { return f1 === f2 || sim.allies.includes(pairKey(f1, f2)); }
function enemySetsOf(sim) {
  const out = {};
  const activeFacs = [...new Set([...FAC_KEYS, ...sim.armies.map(a => a.fac), ...sim.units.map(u => u.fac)])];
  for (const f of activeFacs)
    out[f] = new Set(activeFacs.filter(g => !isAllied(sim, f, g)));
  return out;
}
function teamsOf(sim) {
  const activeFacs = [...new Set([...FAC_KEYS, ...sim.armies.map(a => a.fac), ...sim.units.map(u => u.fac)])];
  const parent = {}; activeFacs.forEach(f => parent[f] = f);
  const find = f => parent[f] === f ? f : (parent[f] = find(parent[f]));
  for (const p of sim.allies) { const [a, b] = p.split("|"); parent[find(a)] = find(b); }
  const t = {}; activeFacs.forEach(f => t[f] = find(f));
  return t;
}

/* ---------- 天下外交 ---------- */
function grandRelationKey(f1, f2) { return pairKey(f1, f2); }
function createGrandRelations() {
  const relations = {};
  for (let i = 0; i < GRAND_FACTION_KEYS.length; i++) {
    for (let j = i + 1; j < GRAND_FACTION_KEYS.length; j++) relations[grandRelationKey(GRAND_FACTION_KEYS[i], GRAND_FACTION_KEYS[j])] = 0;
  }
  return relations;
}
function grandRelation(g, f1, f2) {
  if (f1 === f2) return 100;
  return Math.max(-100, Math.min(100, g?.relations?.[grandRelationKey(f1, f2)] ?? 0));
}
function grandAreAllied(g, f1, f2) {
  return f1 === f2 || !!g?.alliances?.includes(grandRelationKey(f1, f2));
}

/* ---------- log ---------- */
let LOG_LIMIT = 8;   // 战报条数（设置可调）
let FX_LITE = false; // 特效精简模式（设置可调）
let BATTLE_AI = true; // 敌军战场AI（设置可调）
const ELEV = [0, 0, 1, 2, -1]; // 地形假高程：平原/林地/丘陵/山地/水域，供高地压制判定
function log(sim, msg) {
  sim.log.unshift(msg);
  sim.log = sim.log.slice(0, LOG_LIMIT);
}
function advanceHistoricalNarrative(sim) {
  const history = sim.historical;
  if (!sim.running || !history?.events?.length) return;
  history.fired = history.fired || [];
  const elapsed = sim.battleTime || 0;
  history.events.forEach((event, index) => {
    if (history.fired[index] || elapsed < event.at) return;
    history.fired[index] = true;
    log(sim, `📜 ${history.title}：${event.text}`);
  });
}

/* ---------- buildings ---------- */
let BID = 1;
function addBuilding(sim, fac, type, x, y, name) {
  const b = {
    id: BID++, fac, type, x, y,
    hp: B_TYPES[type].hp, maxHp: B_TYPES[type].hp,
    name: name || (type === "city" ? "坞堡" : type === "pass" ? "关" : "营"),
    spawned: 0, spawnTimer: 0, armyId: null,
    capProg: 0, capFac: null,
  };
  sim.buildings.push(b);
  const verbs = { city: "筑城", camp: "扎营", pass: "立关" };
  log(sim, `🏯 ${FACTIONS[fac].name}军${verbs[type]}「${b.name}」`);
  return b;
}
function defAuraDivisor(sim, unit) {
  let d = 1;
  for (const b of sim.buildings) {
    if (b.hp <= 0) continue;
    const aura = B_TYPES[b.type].aura;
    if (!aura || !isAllied(sim, b.fac, unit.fac)) continue;
    if ((b.x - unit.x) ** 2 + (b.y - unit.y) ** 2 < aura * aura) {
      d = Math.max(d, b.type === "city" ? 1.5 : 1.4);
    }
  }
  return d;
}

/* ---------- armies / units ---------- */
let UID = 1;
function makeArmy(sim, fac, utype, men, x, y, opts = {}) {
  const dots = Math.max(3, Math.round(men / MEN_PER_DOT));
  const pool = GENERALS[fac];
  const used = sim.armies.filter(a => a.fac === fac).map(a => a.general);
  const avail = pool.filter(g => !used.includes(g));
  const list = avail.length ? avail : pool;
  const general = opts.general || list[Math.floor(Math.random() * list.length)];
  const generalInfo = generalProfile(general);
  const army = {
    id: UID++, fac, utype, general, total: dots, alive: dots,
    generalForce: opts.force ?? generalInfo.force, generalWisdom: opts.wisdom ?? generalInfo.wisdom,
    broken: false, kills: 0, order: "attack", form: opts.form || "fang",
    anchor: { x, y }, feintT: 0, feintPhase: "advance",
    withdrawn: 0, retreatDir: x < W / 2 ? -1 : 1, militia: !!opts.militia,
    morale: opts.morale ?? 90, food: 100, level: opts.level || 0, captives: 0, capturedGens: [],
    genDead: false, starving: false, elite: !!opts.elite,
    lockRetreatDir: false, genEscaped: false,
    moveQueue: [], patrol: false, patrolRoute: null,
    rallyCd: 0, pursuitStance: "normal", skillCooldowns: {},
    aiCd: Math.random() * 1.5, aiFocus: null, encircled: false,
  };
  sim.armies.push(army);
  const cols = Math.ceil(Math.sqrt(dots * 1.9));
  const sp = utype === "cav" ? 6 : 5;
  for (let i = 0; i < dots; i++) {
    spawnUnit(sim, army,
      x + ((i % cols) - cols / 2) * sp + (Math.random() - 0.5) * 2,
      y + (Math.floor(i / cols) - Math.floor(dots / cols) / 2) * sp + (Math.random() - 0.5) * 2,
      i === Math.floor(cols / 2) && !opts.militia);
  }
  if (!opts.silent) log(sim, `⚔ ${FACTIONS[fac].name}·${general} 率${UNIT_TYPES[utype].name} ${men.toLocaleString()} 人列阵`);
  return army;
}
function spawnUnit(sim, army, ux, uy, isGen) {
  const stats = UNIT_TYPES[army.utype];
  const em = army.elite ? 1.35 : 1;
  sim.units.push({
    x: Math.max(8, Math.min(W - 8, ux)),
    y: Math.max(8, Math.min(H - 8, uy)),
    fac: army.fac, army: army.id, type: army.utype,
    hp: isGen ? (army.elite ? 900 : 340) : stats.hp * em * (0.85 + Math.random() * 0.3),
    maxHp: isGen ? (army.elite ? 900 : 340) : stats.hp * em * 1.15,
    atk: isGen ? (army.elite ? 48 : 26) : stats.atk * em * (0.85 + Math.random() * 0.3),
    cd: Math.random() * 0.6,
    gen: !!isGen, flee: false, dead: false, kills: 0,
    charge: army.utype === "cav" ? 1 : 0,
    hx: 0, hy: 0, tgt: null, tgtT: 0,
  });
}
function removeArmy(sim, armyId) {
  const a = sim.armies.find(x => x.id === armyId);
  if (!a) return;
  sim.units = sim.units.filter(u => u.army !== armyId);
  sim.armies = sim.armies.filter(x => x.id !== armyId);
  for (const b of sim.buildings) if (b.armyId === armyId) b.armyId = null;
  log(sim, `↩ ${FACTIONS[a.fac].name}·${a.general} 所部移出沙盘`);
}
function defectArmy(sim, army, newFac) {
  const oldFac = army.fac;
  army.fac = newFac;
  army.broken = false;
  army.morale = 60;
  army.order = "hold";
  let cx = 0, cy = 0, n = 0;
  for (const u of sim.units) {
    if (u.dead || u.army !== army.id) continue;
    u.fac = newFac; u.flee = false;
    cx += u.x; cy += u.y; n++;
  }
  if (n) army.anchor = { x: cx / n, y: cy / n };
  log(sim, `🕊 ${FACTIONS[oldFac].name}·${army.general} 临阵倒戈，改旗易帜归${FACTIONS[newFac].name}！`);
}

function buildGrid(units) {
  const cell = 26, gw = Math.ceil(W / cell), gh = Math.ceil(H / cell);
  const grid = new Array(gw * gh);
  for (const u of units) {
    if (u.dead) continue;
    const gx = Math.max(0, Math.min(gw - 1, (u.x / cell) | 0));
    const gy = Math.max(0, Math.min(gh - 1, (u.y / cell) | 0));
    const idx = gy * gw + gx;
    (grid[idx] || (grid[idx] = [])).push(u);
  }
  return { grid, cell, gw, gh };
}
function findEnemy(u, G, radius, enemySets) {
  const { grid, cell, gw, gh } = G;
  const foes = enemySets[u.fac];
  const r = Math.ceil(radius / cell);
  const gx = (u.x / cell) | 0, gy = (u.y / cell) | 0;
  let best = null, bestScore = -Infinity;
  let bestFlee = null, bfd = radius * radius;
  const cavHunt = u.type === "cav" && !u.gen; // 骑兵优先冲击弓兵/主将
  for (let dy = -r; dy <= r; dy++) {
    for (let dx = -r; dx <= r; dx++) {
      const X = gx + dx, Y = gy + dy;
      if (X < 0 || Y < 0 || X >= gw || Y >= gh) continue;
      const cellArr = grid[Y * gw + X];
      if (!cellArr) continue;
      for (const v of cellArr) {
        if (v.dead || !foes.has(v.fac)) continue;
        const d2 = (v.x - u.x) ** 2 + (v.y - u.y) ** 2;
        if (v.flee) { if (d2 < bfd) { bfd = d2; bestFlee = v; } continue; }
        if (d2 > radius * radius) continue;
        // 评分：越近越好，残血目标优先补刀，骑兵额外偏好弓兵/主将
        let score = -d2;
        const hpFrac = v.hp / (v.maxHp || v.hp || 1);
        if (hpFrac < 0.35) score += 900; // 补刀
        if (cavHunt && (v.type === "arch" || v.gen)) score += 700; // 冲阵首选软目标
        if (v.gen) score += 200;
        if (score > bestScore) { bestScore = score; best = v; }
      }
    }
  }
  return best || bestFlee;
}
function findEnemyBuilding(sim, u, radius, enemySets) {
  let best = null, bd = radius * radius;
  const foes = enemySets[u.fac];
  for (const b of sim.buildings) {
    if (!foes.has(b.fac) || b.hp <= 0) continue;
    const d = (b.x - u.x) ** 2 + (b.y - u.y) ** 2;
    if (d < bd) { bd = d; best = b; }
  }
  return best;
}
// 解析右键落点意图：优先命中就近敌/友单位，其次命中城池/关隘的光环范围，否则视为空地
function resolveMoveTarget(sim, x, y) {
  let nearUnit = null, nud = 16 * 16;
  for (const u of sim.units) {
    if (u.dead) continue;
    const d = (u.x - x) ** 2 + (u.y - y) ** 2;
    if (d < nud) { nud = d; nearUnit = u; }
  }
  if (nearUnit) return { kind: "unit", fac: nearUnit.fac, armyId: nearUnit.army };
  let nearB = null, nbd = Infinity;
  for (const b of sim.buildings) {
    if (b.hp <= 0) continue;
    const pickR = b.type === "camp" ? 40 : B_TYPES[b.type].aura;
    const d = (b.x - x) ** 2 + (b.y - y) ** 2;
    if (d <= pickR * pickR && d < nbd) { nbd = d; nearB = b; }
  }
  if (nearB) return { kind: "building", fac: nearB.fac, building: nearB };
  return { kind: "ground", fac: null, building: null };
}

// 溃军被手动带到目标点后，按「城池 → 友军 → 空地」顺序处理归宿。
function reformBrokenArmy(sim, army, x, y, reason) {
  const aliveUnits = sim.units.filter(u => u.army === army.id && !u.dead);
  if (!aliveUnits.length) return;
  army.broken = false;
  army.manualMove = false;
  army.morale = reason === "city" ? 58 : 42;
  army.food = 100;
  army.order = "hold";
  army.form = reason === "city" ? "yuan" : "fang";
  army.moveQueue = [];
  army.patrol = false;
  army.patrolRoute = null;
  army.anchor = { x, y };
  for (const u of aliveUnits) u.flee = false;
  const label = reason === "city" ? "入城重整" : "重编生力军";
  log(sim, `🚩 ${FACTIONS[army.fac].name}·${army.general} ${label}（${(aliveUnits.length * MEN_PER_DOT).toLocaleString()}人）`);
}

function mergeBrokenArmy(sim, broken, target, x, y) {
  const sourceUnits = sim.units.filter(u => u.army === broken.id && !u.dead);
  if (!sourceUnits.length || !target || target.id === broken.id) return;
  target.total += broken.total;
  target.alive += sourceUnits.length;
  target.morale = Math.max(target.morale, 42);
  target.food = Math.max(target.food, 70);
  target.anchor = { x, y };
  for (const u of sourceUnits) {
    u.army = target.id;
    u.fac = target.fac;
    u.flee = false;
  }
  for (const b of sim.buildings) if (b.armyId === broken.id) b.armyId = null;
  sim.armies = sim.armies.filter(a => a.id !== broken.id);
  log(sim, `🤝 ${FACTIONS[broken.fac].name}·${broken.general} 溃军并入${FACTIONS[target.fac].name}·${target.general}（${(sourceUnits.length * MEN_PER_DOT).toLocaleString()}人）`);
}

function resolveBrokenArrival(sim, army, x, y) {
  if (!army.broken || !army.manualMove) return;
  const city = sim.buildings.find(b =>
    b.hp > 0 && b.type === "city" && isAllied(sim, army.fac, b.fac) &&
    (b.x - x) ** 2 + (b.y - y) ** 2 <= B_TYPES.city.aura ** 2);
  if (city) {
    reformBrokenArmy(sim, army, city.x, city.y, "city");
    return;
  }
  const target = sim.armies
    .filter(a => a.id !== army.id && !a.broken && a.alive > 0 && isAllied(sim, army.fac, a.fac))
    .map(a => ({ a, d2: ((a.cx ?? a.anchor.x) - x) ** 2 + ((a.cy ?? a.anchor.y) - y) ** 2 }))
    .sort((a, b) => a.d2 - b.d2)[0];
  if (target && target.d2 <= BROKEN_JOIN_R ** 2) {
    mergeBrokenArmy(sim, army, target.a, x, y);
    return;
  }
  if (army.alive * MEN_PER_DOT > BROKEN_FORM_MIN) {
    reformBrokenArmy(sim, army, x, y, "ground");
  } else {
    army.manualMove = false;
    for (const u of sim.units) if (u.army === army.id && !u.dead) u.flee = true;
    log(sim, `🏳 ${FACTIONS[army.fac].name}·${army.general} 溃军不足${BROKEN_FORM_MIN}人，仍未能重编`);
  }
}
function typeMultiplier(a, d) {
  if (a.type === "spear" && d.type === "cav") return 2.2;
  if (a.type === "cav" && d.type === "spear") return 0.6;
  return 1;
}
// 侧翼/背袭 + 高地压制的近战伤害倍率（首版系数，未来可能微调）
function tacticalDmgMultiplier(sim, attacker, victim) {
  let mult = 1;
  if (victim.hx || victim.hy) {
    const dx = attacker.x - victim.x, dy = attacker.y - victim.y;
    const dist = Math.hypot(dx, dy) || 1;
    const facingDot = (victim.hx * dx + victim.hy * dy) / dist; // 受击者朝向 与 "受击者→攻击者" 方向的点积
    if (facingDot < -0.3) mult *= 1.4;      // 背袭
    else if (facingDot <= 0.45) mult *= 1.15; // 侧翼
  }
  const atkElev = ELEV[terrainAt(sim.terrain, attacker.x, attacker.y)];
  const defElev = ELEV[terrainAt(sim.terrain, victim.x, victim.y)];
  if (atkElev > defElev) mult *= 1.2; // 高地压制
  return mult;
}
function atkOf(sim, u, army) {
  let a = u.atk;
  a *= 1 + Math.min(u.kills, 3) * 0.08;
  if (army) {
    // 武力决定将领对全军近战、冲锋和攻城的直接带动；控制在小幅区间，避免单将完全替代兵力。
    a *= 0.8 + (army.generalForce ?? generalCombatStats(army.general).force) / 250;
    a *= 1 + army.level * 0.05;
    a *= FORMS[army.form].atk;
    if (army.morale > 75) a *= 1.06;
    if (army.starving) a *= 0.8;
  }
  return a;
}
function defenseDivisor(sim, victim, victimArmy) {
  const t = terrainAt(sim.terrain, victim.x, victim.y);
  let d = T_DEF[t];
  d *= defAuraDivisor(sim, victim);
  if (victimArmy) {
    // 智慧体现为临战判断、阵地利用与战术规避，降低受到的常规伤害。
    d *= 0.88 + (victimArmy.generalWisdom ?? generalCombatStats(victimArmy.general).wisdom) / 500;
    d *= FORMS[victimArmy.form].def;
    if (victimArmy.encircled) d *= 0.9; // 合围：阵脚已乱，防御打折（首版系数）
  }
  return d;
}
function onKill(sim, killer, killerArmy) {
  if (!killer) return;
  killer.kills++;
  killer.hp = Math.min(killer.maxHp, killer.hp + 6);
  if (killerArmy) {
    killerArmy.kills++;
    const lvl = Math.min(5, Math.floor(killerArmy.kills / 25));
    if (lvl > killerArmy.level) {
      killerArmy.level = lvl;
      log(sim, `⭐ ${FACTIONS[killerArmy.fac].name}·${killerArmy.general} 战功累累，晋升${["","一","二","三","四","五"][lvl]}级！`);
    }
    killerArmy.morale = Math.min(100, killerArmy.morale + 0.35);
  }
}
function killOrCapture(sim, victim, killer, killerArmy, corpseCtx) {
  const va = sim.armies.find(a => a.id === victim.army);
  const stance = killerArmy ? killerArmy.pursuitStance : "normal";
  const captureChance = stance === "ruthless" ? 0.2 : stance === "merciful" ? 0.75 : 0.5;
  if (victim.flee && killerArmy && Math.random() < captureChance && !(victim.gen && va && va.protectRegen)) {
    victim.dead = true; victim.captured = true;
    if (va) va.alive--;
    killerArmy.captives++;
    if (victim.gen) {
      killerArmy.capturedGens.push(va ? va.general : "敌将");
      log(sim, `🔗 ${FACTIONS[killerArmy.fac].name}·${killerArmy.general} 生擒 ${FACTIONS[victim.fac].name}·${va ? va.general : "敌将"}！`);
      if (va) va.genDead = true;
      // 辕门献俘：触发对话事件
      if (va) {
        sim.pendingDialogs = sim.pendingDialogs || [];
        sim.pendingDialogs.push({
          type: "captive",
          captorArmyId: killerArmy.id,
          victimGeneral: va.general,
          victimFac: victim.fac,
        });
      }
    }
    onKill(sim, killer, killerArmy);
    sim.fx.push({ x: victim.x, y: victim.y, t: 0.45, type: 3 });
    return;
  }
  victim.dead = true;
  if (va) { va.alive--; va.morale = Math.max(0, va.morale - 90 / va.total * 1.1); }
  onKill(sim, killer, killerArmy);
  sim.fx.push({ x: victim.x, y: victim.y, t: 0.5, type: 1 });
  corpseCtx.fillStyle = victim.gen ? "rgba(90,20,15,0.8)" : "rgba(70,40,30,0.35)";
  corpseCtx.beginPath();
  corpseCtx.arc(victim.x, victim.y, victim.gen ? 3.5 : 1.6, 0, Math.PI * 2);
  corpseCtx.fill();
  if (victim.gen && va && !va.genDead) {
    va.genDead = true;
    va.morale = Math.max(0, va.morale - 32);
    log(sim, `☠ ${FACTIONS[victim.fac].name}·${va.general} 阵亡！`);
  }
}

/* ---------- simulation ---------- */
function stepSim(sim, dt, corpseCtx) {
  if (sim.running) sim.battleTime = (sim.battleTime || 0) + dt;
  advanceHistoricalNarrative(sim);
  const units = sim.units;
  const G = buildGrid(units);
  const enemySets = enemySetsOf(sim);
  const armyById = new Map(sim.armies.map(a => [a.id, a]));
  const cents = {};
  for (const u of units) {
    if (u.dead || u.flee) continue;
    const a = armyById.get(u.army);
    if (a && a.order === "retreat") continue;
    const c = cents[u.fac] || (cents[u.fac] = { x: 0, y: 0, n: 0 });
    c.x += u.x; c.y += u.y; c.n++;
  }
  for (const k in cents) { cents[k].x /= cents[k].n; cents[k].y /= cents[k].n; }

  // upkeep: food, morale, feint
  if (sim.running) {
    for (const a of sim.armies) {
      if (a.alive <= 0) continue;
      for (const skillId of Object.keys(a.skillCooldowns || {})) {
        a.skillCooldowns[skillId] = Math.max(0, a.skillCooldowns[skillId] - dt);
      }
      let cx = 0, cy = 0, n = 0, genU = null;
      for (const u of units) if (!u.dead && u.army === a.id) { cx += u.x; cy += u.y; n++; if (u.gen) genU = u; }
      if (n) { cx /= n; cy /= n; } else { cx = a.anchor.x; cy = a.anchor.y; }
      a.cx = cx; a.cy = cy; // 供下方军团级 pass（合围/传导/AI）复用，避免重复扫描
      if (a.broken) continue; // 崩溃军仍需质心供合围/传导判定，但不再走补给与军令逻辑
      if (a.protectRegen && genU) genU.hp = Math.min(genU.maxHp, genU.hp + 6 * dt); // 主角光环：缓慢回血
      let supplied = false;
      for (const b of sim.buildings) {
        if (b.hp <= 0 || !isAllied(sim, b.fac, a.fac)) continue;
        if ((b.x - cx) ** 2 + (b.y - cy) ** 2 < SUPPLY_R * SUPPLY_R) { supplied = true; break; }
      }
      if (supplied) a.food = Math.min(100, a.food + 5 * dt);
      else a.food = Math.max(0, a.food - 0.9 * dt);
      const wasStarving = a.starving;
      a.starving = a.food <= 0;
      if (a.starving && !wasStarving) log(sim, `🌾 ${FACTIONS[a.fac].name}·${a.general} 粮草断绝，军心浮动！`);
      const wisdomFactor = 0.82 + (a.generalWisdom ?? generalCombatStats(a.general).wisdom) / 250;
      if (a.starving) a.morale = Math.max(0, a.morale - (2.6 / wisdomFactor) * dt);
      else a.morale = Math.min(100, a.morale + (supplied ? 2.2 : 0.9) * wisdomFactor * dt);
      if (a.order === "feint") {
        a.feintT += dt;
        const dur = a.feintPhase === "advance" ? 3 : 2.6;
        if (a.feintT > dur) {
          a.feintT = 0;
          a.feintPhase = a.feintPhase === "advance" ? "withdraw" : "advance";
        }
      }
    }

    // 军团级 pass：士气传导 + 合围（围三阙一）+ 敌方战场AI 决策
    // O(军队数²)，军队数通常很小（几到十几支），不进单位级热路径
    {
      const teamOfFac = teamsOf(sim);
      const playerTeam = sim.tacticsFac ? teamOfFac[sim.tacticsFac] : null;
      const tactical = sim.armies.filter(a => !a.militia && a.alive > 0);
      for (const a of tactical) {
        let nearbyEnemy = 0, nearbyFriendly = 0, brokenNeighbor = false;
        const quadrants = new Set();
        for (const b of tactical) {
          if (b === a) continue;
          const dx = b.cx - a.cx, dy = b.cy - a.cy;
          const d2 = dx * dx + dy * dy;
          if (teamOfFac[b.fac] !== teamOfFac[a.fac]) {
            if (d2 < 160 * 160) { nearbyEnemy++; quadrants.add((dx >= 0 ? 1 : 0) | (dy >= 0 ? 2 : 0)); }
          } else if (d2 < 120 * 120) {
            nearbyFriendly++;
            if (b.broken) brokenNeighbor = true;
          }
        }
        if (a.broken) continue; // 崩溃军自身不再参与合围/AI 判定，但已计入邻军的统计
        a.encircled = quadrants.size >= 3; // 三面及以上被围（留一面即"围三阙一"，不触发）
        if (a.encircled) a.morale = Math.max(0, a.morale - 6 * dt); // 首版系数：合围士气流失
        if (brokenNeighbor) a.morale = Math.max(0, a.morale - 4 * dt); // 首版系数：友邻崩溃的恐慌传导
        else if (!a.genDead && nearbyEnemy < nearbyFriendly) a.morale = Math.min(100, a.morale + 1.2 * dt); // 主将在阵+局部占优：小幅鼓舞

        // 敌方战场AI：只接管非玩家队伍的军队；沙盘模式 sim.tacticsFac 为空则整体不生效
        if (BATTLE_AI && playerTeam != null && teamOfFac[a.fac] !== playerTeam) {
          a.aiCd -= dt;
          if (a.aiCd <= 0) {
            a.aiCd = 1.6 + Math.random() * 0.8;
            if (a.morale < 25 && nearbyEnemy > nearbyFriendly * 1.3) {
              let refuge = null, rd = 200 * 200;
              for (const bld of sim.buildings) {
                if (bld.hp <= 0 || (bld.type !== "city" && bld.type !== "pass") || !isAllied(sim, bld.fac, a.fac)) continue;
                const d2 = (bld.x - a.cx) ** 2 + (bld.y - a.cy) ** 2;
                if (d2 < rd) { rd = d2; refuge = bld; }
              }
              if (refuge) { a.order = "hold"; a.form = "yuan"; a.anchor = { x: refuge.x, y: refuge.y }; }
              else if (a.order !== "retreat") { a.order = "retreat"; a.retreatDir = a.cx < W / 2 ? -1 : 1; }
            } else if (nearbyEnemy * 1.3 < nearbyFriendly && (a.order === "hold" || a.order === "feint")) {
              a.order = "attack";
            }
            if (a.utype === "cav" && a.order === "attack") {
              a.form = "feng";
              let focus = null, fd = Infinity;
              for (const b of tactical) {
                if (teamOfFac[b.fac] === teamOfFac[a.fac] || b.broken || b.utype !== "arch") continue;
                const d2 = (b.cx - a.cx) ** 2 + (b.cy - a.cy) ** 2;
                if (d2 < fd) { fd = d2; focus = b; }
              }
              a.aiFocus = focus ? { x: focus.cx, y: focus.cy } : null;
            } else {
              a.aiFocus = null;
            }
            if (!a.broken && a.order !== "attack" && a.order !== "retreat" && a.utype !== "cav") {
              a.form = "yuan"; // 步/枪据点防守收尾
            }
          }
        }
      }
    }

    // city functions: heal + capture + recruit
    for (const b of sim.buildings) {
      if (b.hp <= 0) continue;
      if (b.type === "city") {
        const aura2 = B_TYPES.city.aura ** 2;
        let friends = 0;
        const foeCount = {};
        for (const u of units) {
          if (u.dead || u.flee) continue;
          if ((b.x - u.x) ** 2 + (b.y - u.y) ** 2 > aura2) continue;
          if (isAllied(sim, u.fac, b.fac)) {
            friends++;
            if (!u.gen) u.hp = Math.min(u.maxHp, u.hp + 1.3 * dt); // 伤兵休养
          } else {
            foeCount[u.fac] = (foeCount[u.fac] || 0) + 1;
          }
        }
        const foes = Object.values(foeCount).reduce((s, v) => s + v, 0);
        if (foes >= 5 && foes > friends * 2) {
          b.capProg += dt;
          b.capFac = Object.entries(foeCount).sort((a, c) => c[1] - a[1])[0][0];
          if (b.capProg >= CAPTURE_TIME) {
            const old = b.fac;
            b.fac = b.capFac; b.capProg = 0; b.capFac = null;
            b.hp = Math.round(b.maxHp * 0.6);
            b.spawned = 0; b.armyId = null; // 新主可再募兵
            log(sim, `🚩 「${b.name}」易帜！${FACTIONS[old].name}失之，${FACTIONS[b.fac].name}得之`);
            for (const fa of sim.armies) if (fa.fac === old) fa.morale = Math.max(0, fa.morale - 14);
          }
        } else {
          b.capProg = Math.max(0, b.capProg - dt * 1.5);
          if (b.capProg === 0) b.capFac = null;
        }
      }
      // recruit: camps fast, cities slow but deep
      const isRecruiter = b.type === "camp" || b.type === "city";
      if (isRecruiter) {
        const cap = b.type === "camp" ? 60 : 100;
        const interval = b.type === "camp" ? 6 : 10;
        if (b.spawned >= cap) continue;
        b.spawnTimer += dt;
        if (b.spawnTimer > interval) {
          b.spawnTimer = 0;
          let army = b.armyId ? sim.armies.find(a => a.id === b.armyId && a.fac === b.fac) : null;
          if (!army || army.broken) {
            army = makeArmy(sim, b.fac, "inf", 30, b.x, b.y + 18, { militia: true, general: b.type === "city" ? "守军" : "新募", silent: true });
            sim.units = sim.units.filter(u => u.army !== army.id);
            army.total = 0; army.alive = 0;
            if (b.type === "city") { army.order = "hold"; army.anchor = { x: b.x, y: b.y }; }
            b.armyId = army.id;
          }
          for (let i = 0; i < 5; i++) {
            spawnUnit(sim, army, b.x + (Math.random() - 0.5) * 24, b.y + 14 + Math.random() * 10, false);
            army.total++; army.alive++; b.spawned++;
          }
          if (b.spawned >= cap) log(sim, `🪖 ${FACTIONS[b.fac].name}军「${b.name}」募兵已尽`);
        }
      }
    }
  }

  for (const u of units) {
    if (u.dead) continue;
    const army = armyById.get(u.army);
    const order = army ? army.order : "attack";
    const form = army ? FORMS[army.form] : FORMS.fang;
    const terr = terrainAt(sim.terrain, u.x, u.y);
    const stats = UNIT_TYPES[u.type];
    const tSpd = u.type === "cav" ? T_SPEED_CAV[terr] : T_SPEED[terr];
    let spd = stats.spd * tSpd * form.spd * (u.flee ? 1.45 : 1);
    if (army && army.starving) spd *= 0.85;

    // 溃军默认自动逃散；一旦玩家手动下达移动，则暂时解除逃散并按锚点行军。
    if (army?.broken && army.manualMove) {
      u.flee = false;
      if (!sim.running) continue;
      const dx = army.anchor.x - u.x, dy = army.anchor.y - u.y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist > 24) {
        u.hx = dx / dist; u.hy = dy / dist;
        u.x += (dx / dist) * spd * 0.9 * dt;
        u.y += (dy / dist) * spd * 0.9 * dt;
        u.x = Math.max(6, Math.min(W - 6, u.x));
        u.y = Math.max(6, Math.min(H - 6, u.y));
      }
      continue;
    }
    if (u.flee) {
      const dirX = u.x < W / 2 ? -1 : 1;
      u.hx = dirX; u.hy = 0; // 溃逃方向即朝向：追兵从背后砍上就是名副其实的背袭
      u.x += dirX * spd * dt + (Math.random() - 0.5) * 0.6;
      u.y += (Math.random() - 0.5) * spd * 0.4 * dt;
      if (u.x < 2 || u.x > W - 2) {
        u.dead = true; u.escaped = true;
        if (sim.grandCtx) {
          if (u.fac === sim.grandCtx.atkFac) sim.grandCtx.fledAtk = (sim.grandCtx.fledAtk || 0) + 1;
          else if (u.fac === sim.grandCtx.defFac) sim.grandCtx.fledDef = (sim.grandCtx.fledDef || 0) + 1;
        }
      }
      continue;
    }
    if (!sim.running) continue;

    if (army && army.starving) {
      u.hp -= 0.45 * dt;
      if (u.hp <= 0) { killOrCapture(sim, u, null, null, corpseCtx); continue; }
    }

    if (order === "retreat") {
      const enemy = findEnemy(u, G, 10, enemySets);
      if (enemy) {
        const dist = Math.hypot(enemy.x - u.x, enemy.y - u.y) || 1;
        if (dist < 5.5) {
          u.cd -= dt;
          if (u.cd <= 0) {
            u.cd = 0.6 + Math.random() * 0.25;
            const ea = armyById.get(enemy.army);
            enemy.hp -= atkOf(sim, u, army) * typeMultiplier(u, enemy) * 0.8 / defenseDivisor(sim, enemy, ea) * tacticalDmgMultiplier(sim, u, enemy);
            if (!FX_LITE) sim.fx.push({ x: (u.x + enemy.x) / 2, y: (u.y + enemy.y) / 2, t: 0.18, type: 0 });
            if (enemy.hp <= 0 && !enemy.dead) killOrCapture(sim, enemy, u, army, corpseCtx);
          }
        }
      }
      u.hx = army.retreatDir; u.hy = 0;
      u.x += army.retreatDir * spd * 0.95 * dt + (Math.random() - 0.5) * 0.5;
      u.y += (Math.random() - 0.5) * spd * 0.3 * dt;
      if (u.x < 4 || u.x > W - 4) {
        u.dead = true; u.withdrew = true;
        army.alive--; army.withdrawn++;
        if (u.gen) army.genEscaped = true;
      }
      u.x = Math.max(2, Math.min(W - 2, u.x));
      u.y = Math.max(6, Math.min(H - 6, u.y));
      continue;
    }

    if (u.type === "cav" && u.charge < 1) u.charge = Math.min(1, u.charge + dt * 0.12);

    const isArcher = u.type === "arch" && !u.gen;
    const holdMode = order === "hold";
    const feintWithdraw = order === "feint" && army.feintPhase === "withdraw";
    const seekR = holdMode ? 75 : isArcher ? 130 : 90;
    // 目标粘滞：短时锁定同一敌人，避免每帧重新索敌导致战线抖动；也省下多数帧的 findEnemy 邻域扫描
    let enemy;
    if (u.tgt && !u.tgt.dead && !u.tgt.flee && u.tgtT > 0 && enemySets[u.fac].has(u.tgt.fac) &&
      (u.tgt.x - u.x) ** 2 + (u.tgt.y - u.y) ** 2 <= (seekR * 1.4) ** 2) {
      enemy = u.tgt;
    } else {
      enemy = findEnemy(u, G, seekR, enemySets);
      u.tgt = enemy || null;
      u.tgtT = 0.5 + Math.random() * 0.5;
    }
    u.tgtT -= dt;
    let mx = 0, my = 0;

    if (feintWithdraw && (!enemy || Math.hypot(enemy.x - u.x, enemy.y - u.y) > 6)) {
      const dx = army.anchor.x - u.x, dy = army.anchor.y - u.y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist > 14) { mx = (dx / dist) * spd; my = (dy / dist) * spd; }
      if (isArcher && enemy) {
        const ed = Math.hypot(enemy.x - u.x, enemy.y - u.y);
        let range = 70 * form.range; if (terr === 2 || terr === 3) range *= 1.25;
        if (ed <= range) {
          u.cd -= dt;
          if (u.cd <= 0) {
            u.cd = 1.7 + Math.random() * 0.6;
            sim.arrows.push({
              x: u.x, y: u.y,
              tx: enemy.x + (Math.random() - 0.5) * 10, ty: enemy.y + (Math.random() - 0.5) * 10,
              t: 0, dur: ed / 110, fac: u.fac, atk: atkOf(sim, u, army) * 2.6, shooterArmy: u.army, shooter: u,
            });
          }
        }
      }
      if (mx || my) { const hn = Math.hypot(mx, my) || 1; u.hx = mx / hn; u.hy = my / hn; }
      u.x += mx * dt; u.y += my * dt;
      u.x = Math.max(6, Math.min(W - 6, u.x));
      u.y = Math.max(6, Math.min(H - 6, u.y));
      continue;
    }

    if (enemy) {
      const dx = enemy.x - u.x, dy = enemy.y - u.y;
      const dist = Math.hypot(dx, dy) || 1;
      if (isArcher) {
        let range = 70 * form.range;
        if (terr === 2 || terr === 3) range *= 1.25;
        if (dist <= range && dist > 14) {
          u.cd -= dt;
          if (u.cd <= 0) {
            u.cd = 1.7 + Math.random() * 0.6;
            sim.arrows.push({
              x: u.x, y: u.y,
              tx: enemy.x + (Math.random() - 0.5) * 10, ty: enemy.y + (Math.random() - 0.5) * 10,
              t: 0, dur: dist / 110, fac: u.fac, atk: atkOf(sim, u, army) * 2.6, shooterArmy: u.army, shooter: u,
            });
          }
          u.hx = dx / dist; u.hy = dy / dist; // 站定射箭：朝向目标
          u.x += (Math.random() - 0.5) * 0.4;
          u.y += (Math.random() - 0.5) * 0.4;
          continue;
        } else if (dist <= 14) {
          mx = (-dx / dist) * spd * 0.8; my = (-dy / dist) * spd * 0.8;
          u.cd -= dt;
          if (u.cd <= 0 && dist < 6) {
            u.cd = 0.8;
            const ea = armyById.get(enemy.army);
            enemy.hp -= atkOf(sim, u, army) * 0.6 / defenseDivisor(sim, enemy, ea) * tacticalDmgMultiplier(sim, u, enemy);
            if (!FX_LITE) sim.fx.push({ x: (u.x + enemy.x) / 2, y: (u.y + enemy.y) / 2, t: 0.18, type: 0 });
            if (enemy.hp <= 0 && !enemy.dead) killOrCapture(sim, enemy, u, army, corpseCtx);
          }
        } else if (!holdMode) {
          mx = (dx / dist) * spd; my = (dy / dist) * spd;
        }
      } else if (dist < 5.5) {
        u.hx = dx / dist; u.hy = dy / dist; // 白刃战：朝向交战对象
        u.cd -= dt;
        if (u.cd <= 0) {
          u.cd = 0.55 + Math.random() * 0.25;
          const ea = armyById.get(enemy.army);
          let dmg = atkOf(sim, u, army) * typeMultiplier(u, enemy) / defenseDivisor(sim, enemy, ea) * tacticalDmgMultiplier(sim, u, enemy);
          if (u.type === "cav" && u.charge > 0.6) {
            dmg *= 3; u.charge = 0;
            sim.fx.push({ x: enemy.x, y: enemy.y, t: 0.35, type: 2 });
            const kx = (enemy.x - u.x) / dist, ky = (enemy.y - u.y) / dist;
            enemy.x = Math.max(6, Math.min(W - 6, enemy.x + kx * 7));
            enemy.y = Math.max(6, Math.min(H - 6, enemy.y + ky * 7));
          }
          enemy.hp -= dmg;
          if (!FX_LITE) sim.fx.push({ x: (u.x + enemy.x) / 2, y: (u.y + enemy.y) / 2, t: 0.18, type: 0 });
          if (enemy.hp <= 0 && !enemy.dead) killOrCapture(sim, enemy, u, army, corpseCtx);
        }
      } else {
        mx = (dx / dist) * spd; my = (dy / dist) * spd;
      }
    } else if (holdMode) {
      const dx = army.anchor.x - u.x, dy = army.anchor.y - u.y;
      const dist = Math.hypot(dx, dy) || 1;
      if (dist > 30) { mx = (dx / dist) * spd * 0.7; my = (dy / dist) * spd * 0.7; }
      else if (u.gen && army.moveQueue && army.moveQueue.length) {
        // 主将抵达则接续下一路径点；巡逻模式下队列耗尽即从头补满，循环往返
        army.anchor = army.moveQueue.shift();
        if (army.patrol && army.moveQueue.length === 0 && army.patrolRoute) {
          army.moveQueue = army.patrolRoute.map(p => ({ x: p.x, y: p.y }));
        }
      }
    } else {
      const b = findEnemyBuilding(sim, u, 150, enemySets);
      if (b) {
        const dx = b.x - u.x, dy = b.y - u.y, dist = Math.hypot(dx, dy) || 1;
        if (b.type === "city") {
          // 围城：入圈驻扎，静待易帜
          const ring = B_TYPES.city.aura * 0.65;
          if (dist > ring) { mx = (dx / dist) * spd; my = (dy / dist) * spd; }
          else { u.x += (Math.random() - 0.5) * 0.5; u.y += (Math.random() - 0.5) * 0.5; }
        } else if (dist < 22) {
          u.cd -= dt;
          if (u.cd <= 0) {
            u.cd = 0.7;
            b.hp -= atkOf(sim, u, army) * 1.4;
            sim.fx.push({ x: b.x + (Math.random() - 0.5) * 20, y: b.y + (Math.random() - 0.5) * 16, t: 0.2, type: 0 });
            if (b.hp <= 0) {
              log(sim, `🔥 ${FACTIONS[b.fac].name}军「${b.name}」${b.type === "pass" ? "关破" : "营寨被毁"}！`);
              for (const fa of sim.armies) if (fa.fac === b.fac) fa.morale = Math.max(0, fa.morale - (b.type === "pass" ? 15 : 8));
              corpseCtx.fillStyle = "rgba(40,30,20,0.5)";
              corpseCtx.fillRect(b.x - 15, b.y - 12, 30, 24);
              corpseCtx.strokeStyle = "rgba(20,15,10,0.7)";
              corpseCtx.beginPath();
              corpseCtx.moveTo(b.x - 14, b.y - 11); corpseCtx.lineTo(b.x + 14, b.y + 11);
              corpseCtx.moveTo(b.x + 14, b.y - 11); corpseCtx.lineTo(b.x - 14, b.y + 11);
              corpseCtx.stroke();
            }
          }
        } else {
          mx = (dx / dist) * spd; my = (dy / dist) * spd;
        }
      } else {
        let tgt = army && army.aiFocus ? army.aiFocus : null; // 敌方AI：骑兵优先绕向锁定的弓队/软目标
        if (!tgt) {
          let td = 1e9;
          for (const f of FAC_KEYS) {
            if (!enemySets[u.fac].has(f) || !cents[f]) continue;
            const d = (cents[f].x - u.x) ** 2 + (cents[f].y - u.y) ** 2;
            if (d < td) { td = d; tgt = cents[f]; }
          }
        }
        if (tgt) {
          const dx = tgt.x - u.x, dy = tgt.y - u.y, dist = Math.hypot(dx, dy) || 1;
          mx = (dx / dist) * spd; my = (dy / dist) * spd;
        }
      }
    }
    if (mx || my) { const hn = Math.hypot(mx, my) || 1; u.hx = mx / hn; u.hy = my / hn; } // 移动方向即朝向（未被上面各分支显式覆盖时的兜底）
    u.x += mx * dt + (Math.random() - 0.5) * 0.5;
    u.y += my * dt + (Math.random() - 0.5) * 0.5;
    u.x = Math.max(6, Math.min(W - 6, u.x));
    u.y = Math.max(6, Math.min(H - 6, u.y));
  }

  // 统一在单位移动完成后检查抵达，避免同一支溃军的多个单位重复触发重整。
  for (const a of [...sim.armies]) {
    if (!a.broken || !a.manualMove || a.alive <= 0) continue;
    let cx = 0, cy = 0, n = 0;
    for (const u of units) {
      if (u.dead || u.army !== a.id) continue;
      cx += u.x; cy += u.y; n++;
    }
    if (!n) continue;
    cx /= n; cy /= n;
    if ((a.anchor.x - cx) ** 2 + (a.anchor.y - cy) ** 2 <= 30 * 30) {
      resolveBrokenArrival(sim, a, cx, cy);
    }
  }

  // arrows
  if (sim.running) {
    for (const a of sim.arrows) {
      a.t += dt;
      if (a.t >= a.dur && !a.hit) {
        a.hit = true;
        const probe = { x: a.tx, y: a.ty, fac: a.fac };
        const victim = findEnemy(probe, G, 8, enemySets);
        if (victim) {
          const va = armyById.get(victim.army);
          let dmg = a.atk / defenseDivisor(sim, victim, va);
          if (terrainAt(sim.terrain, victim.x, victim.y) === 1) dmg *= 0.55;
          victim.hp -= dmg;
          if (victim.hp <= 0 && !victim.dead)
            killOrCapture(sim, victim, a.shooter, armyById.get(a.shooterArmy), corpseCtx);
          if (!FX_LITE) sim.fx.push({ x: a.tx, y: a.ty, t: 0.15, type: 0 });
        }
      }
    }
    sim.arrows = sim.arrows.filter(a => a.t < a.dur + 0.05);
    if (sim.arrows.length > 900) sim.arrows.splice(0, sim.arrows.length - 900);
  }

  // rout
  for (const a of sim.armies) {
    if (a.rallyCd > 0) a.rallyCd = Math.max(0, a.rallyCd - dt);
    if (a.broken || a.alive <= 0 || a.order === "retreat" || a.militia) continue;
    if (a.morale < 18) {
      a.broken = true;
      log(sim, `🏳 ${FACTIONS[a.fac].name}·${a.general} 军心崩溃，全军奔逃！`);
      for (const u of sim.units) if (u.army === a.id && !u.dead) u.flee = true;
    }
  }

  for (const f of sim.fx) f.t -= dt;
  sim.fx = sim.fx.filter(f => f.t > 0);

  if (sim.running && !sim.done) {
    const team = teamsOf(sim);
    const aliveTeams = new Set();
    for (const u of sim.units) {
      if (u.dead || u.flee) continue;
      const a = armyById.get(u.army);
      if (!a || a.order !== "retreat") aliveTeams.add(team[u.fac]);
    }
    if (aliveTeams.size <= 1 && sim.armies.length > 1 && sim.started) {
      sim.done = true;
      if (aliveTeams.size === 1) {
        const t = [...aliveTeams][0];
        const winFacs = FAC_KEYS.filter(f => team[f] === t && sim.units.some(u => !u.dead && u.fac === f));
        log(sim, `🏆 ${winFacs.map(f => FACTIONS[f].name).join("")}军大获全胜！`);
      } else log(sim, `🕊 战场归于沉寂`);
    }
  }
}

/* ---------- drawing ---------- */
function drawBuildings(ctx, sim, time) {
  for (const b of sim.buildings) {
    if (b.hp <= 0) continue;
    const F = FACTIONS[b.fac];
    // 伪3D：地面阴影，让建筑读起来像"立在地上"而非贴图
    ctx.fillStyle = "rgba(20,16,10,0.22)";
    ctx.beginPath();
    ctx.ellipse(b.x + 3, b.y + (b.type === "pass" ? 22 : 15), b.type === "pass" ? 9 : 15, b.type === "pass" ? 4 : 5, 0, 0, Math.PI * 2);
    ctx.fill();
    if (b.type === "city") {
      ctx.strokeStyle = F.color + "33";
      ctx.setLineDash([4, 5]);
      ctx.beginPath(); ctx.arc(b.x, b.y, B_TYPES.city.aura, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);
      ctx.fillStyle = "#c9bda0";
      ctx.strokeStyle = "#4a3b28"; ctx.lineWidth = 2;
      ctx.fillRect(b.x - 16, b.y - 13, 32, 26);
      ctx.strokeRect(b.x - 16, b.y - 13, 32, 26);
      ctx.fillStyle = "#4a3b28";
      for (let i = -16; i < 16; i += 5) ctx.fillRect(b.x + i, b.y - 15, 3, 3);
      ctx.fillStyle = "#6e5433";
      ctx.fillRect(b.x - 3.5, b.y + 6, 7, 7);
      ctx.fillStyle = "#b0a184";
      ctx.fillRect(b.x - 6, b.y - 8, 12, 10);
      ctx.strokeStyle = "#4a3b28"; ctx.lineWidth = 1;
      ctx.strokeRect(b.x - 6, b.y - 8, 12, 10);
      ctx.beginPath(); ctx.moveTo(b.x, b.y - 8); ctx.lineTo(b.x, b.y - 22); ctx.stroke();
      const wave = Math.sin(time * 5 + b.x) * 1.6;
      ctx.fillStyle = F.color;
      ctx.beginPath();
      ctx.moveTo(b.x, b.y - 22); ctx.lineTo(b.x + 13, b.y - 19 + wave); ctx.lineTo(b.x, b.y - 15);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#2a2118";
      ctx.font = "bold 10px 'Songti SC',serif";
      ctx.textAlign = "center";
      ctx.fillText(b.name, b.x, b.y + 24);
      ctx.textAlign = "left";
      // capture progress ring
      if (b.capProg > 0 && b.capFac) {
        ctx.strokeStyle = FACTIONS[b.capFac].color;
        ctx.lineWidth = 3;
        ctx.beginPath();
        ctx.arc(b.x, b.y, 22, -Math.PI / 2, -Math.PI / 2 + (b.capProg / CAPTURE_TIME) * Math.PI * 2);
        ctx.stroke();
      }
    } else if (b.type === "pass") {
      // 关墙
      ctx.fillStyle = "#9b8d72";
      ctx.strokeStyle = "#3c3020"; ctx.lineWidth = 2;
      ctx.fillRect(b.x - 8, b.y - 26, 16, 52);
      ctx.strokeRect(b.x - 8, b.y - 26, 16, 52);
      ctx.fillStyle = "#3c3020";
      for (let i = -26; i < 26; i += 7) ctx.fillRect(b.x - 9.5, b.y + i, 3, 3.5);
      for (let i = -26; i < 26; i += 7) ctx.fillRect(b.x + 6.5, b.y + i, 3, 3.5);
      // 门洞
      ctx.fillStyle = "#4e4030";
      ctx.beginPath();
      ctx.arc(b.x, b.y + 4, 5.5, Math.PI, 0);
      ctx.rect(b.x - 5.5, b.y + 4, 11, 8);
      ctx.fill();
      // 城楼与旗
      ctx.fillStyle = "#b0a184";
      ctx.fillRect(b.x - 6.5, b.y - 34, 13, 8);
      ctx.strokeRect(b.x - 6.5, b.y - 34, 13, 8);
      ctx.strokeStyle = "#2a2118"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(b.x, b.y - 34); ctx.lineTo(b.x, b.y - 46); ctx.stroke();
      const wave = Math.sin(time * 5 + b.x) * 1.5;
      ctx.fillStyle = F.color;
      ctx.beginPath();
      ctx.moveTo(b.x, b.y - 46); ctx.lineTo(b.x + 12, b.y - 43 + wave); ctx.lineTo(b.x, b.y - 40);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#2a2118";
      ctx.font = "bold 10px 'Songti SC',serif";
      ctx.textAlign = "center";
      ctx.fillText(b.name, b.x, b.y + 36);
      ctx.textAlign = "left";
    } else {
      ctx.strokeStyle = "#4a3b28"; ctx.lineWidth = 1;
      for (const [dx, dy] of [[-8, 2], [0, -4], [8, 3]]) {
        ctx.fillStyle = "#d6c7a3";
        ctx.beginPath();
        ctx.moveTo(b.x + dx - 6, b.y + dy + 5);
        ctx.lineTo(b.x + dx, b.y + dy - 5);
        ctx.lineTo(b.x + dx + 6, b.y + dy + 5);
        ctx.closePath(); ctx.fill(); ctx.stroke();
      }
      ctx.beginPath(); ctx.moveTo(b.x, b.y - 5); ctx.lineTo(b.x, b.y - 16); ctx.stroke();
      const wave = Math.sin(time * 5 + b.x) * 1.4;
      ctx.fillStyle = F.color;
      ctx.beginPath();
      ctx.moveTo(b.x, b.y - 16); ctx.lineTo(b.x + 10, b.y - 13.5 + wave); ctx.lineTo(b.x, b.y - 10);
      ctx.closePath(); ctx.fill();
    }
    if (b.hp < b.maxHp) {
      const w = 30, frac = b.hp / b.maxHp;
      ctx.fillStyle = "rgba(0,0,0,0.4)";
      ctx.fillRect(b.x - w / 2, b.y - (b.type === "pass" ? 52 : 28), w, 3.5);
      ctx.fillStyle = frac > 0.4 ? "#c9a227" : "#b03a2e";
      ctx.fillRect(b.x - w / 2, b.y - (b.type === "pass" ? 52 : 28), w * frac, 3.5);
    }
  }
}

function drawUnits(ctx, sim, time, selectedIds, settings, selRect) {
  const selSet = new Set(selectedIds || []);
  const armyById = new Map(sim.armies.map(a => [a.id, a]));
  // selection rings
  for (const id of selSet) {
    const a = armyById.get(id);
    if (!a || a.alive <= 0) continue;
    let cx = 0, cy = 0, n = 0;
    for (const u of sim.units) if (!u.dead && u.army === id) { cx += u.x; cy += u.y; n++; }
    if (n) {
      cx /= n; cy /= n;
      const ringR = Math.max(22, Math.sqrt(n) * 4.5);
      ctx.setLineDash([6, 5]);
      // 深色描边打底 + 更亮的金色描边：避免与浅色/深色地形都撞色看不清
      ctx.strokeStyle = "rgba(20,16,10,0.55)";
      ctx.lineWidth = 3.2;
      ctx.beginPath(); ctx.arc(cx, cy, ringR, 0, Math.PI * 2); ctx.stroke();
      ctx.strokeStyle = "rgba(255,221,120,0.95)";
      ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.arc(cx, cy, ringR, 0, Math.PI * 2); ctx.stroke();
      ctx.setLineDash([]);
    }
    // 移防目的地标记：军队尚未抵达锚点时闪烁提示落点，抵达后自动消失
    if (n && a.order === "hold" && a.anchor) {
      const ddx = a.anchor.x - cx, ddy = a.anchor.y - cy;
      if (ddx * ddx + ddy * ddy > 30 * 30) {
        const ax = a.anchor.x, ay = a.anchor.y;
        const pulse = 0.45 + Math.sin(time * 4) * 0.25;
        ctx.save();
        ctx.globalAlpha = pulse;
        ctx.strokeStyle = FACTIONS[a.fac].color;
        ctx.lineWidth = 1.6;
        ctx.beginPath();
        ctx.moveTo(ax, ay - 7); ctx.lineTo(ax + 5, ay); ctx.lineTo(ax, ay + 7); ctx.lineTo(ax - 5, ay);
        ctx.closePath(); ctx.stroke();
        ctx.beginPath(); ctx.moveTo(ax, ay + 7); ctx.lineTo(ax, ay + 13); ctx.stroke();
        ctx.restore();
      }
    }
    // 已排路径点：从部队当前位置经锚点连线到各续点，巡逻军以金色标注
    if (n && a.order === "hold" && a.moveQueue && a.moveQueue.length) {
      ctx.strokeStyle = a.patrol ? "rgba(232,195,74,0.85)" : "rgba(212,178,76,0.55)";
      ctx.setLineDash([4, 4]);
      ctx.lineWidth = 1.2;
      ctx.beginPath();
      ctx.moveTo(cx, cy);
      ctx.lineTo(a.anchor.x, a.anchor.y);
      for (const p of a.moveQueue) ctx.lineTo(p.x, p.y);
      ctx.stroke();
      ctx.setLineDash([]);
      for (const p of a.moveQueue) {
        ctx.beginPath(); ctx.arc(p.x, p.y, 2.5, 0, Math.PI * 2);
        ctx.fillStyle = "rgba(232,195,74,0.85)"; ctx.fill();
      }
    }
  }
  for (const u of sim.units) {
    if (u.dead) continue;
    const F = FACTIONS[u.fac];
    if (u.gen) {
      const army = armyById.get(u.army);
      const lvl = army ? army.level : 0;
      const highlighted = army && selSet.has(army.id);
      // 武将拥有独立光环与脉动标记，选中其军团时再加金色外圈，便于在密集战场中辨认。
      ctx.save();
      ctx.globalAlpha = highlighted ? 0.9 : 0.48 + Math.sin(time * 4 + u.x) * 0.12;
      ctx.strokeStyle = highlighted ? "#ffe18a" : F.color;
      ctx.lineWidth = highlighted ? 1.8 : 1.1;
      ctx.beginPath(); ctx.arc(u.x, u.y, highlighted ? 9.5 : 7.2, 0, Math.PI * 2); ctx.stroke();
      if (highlighted) {
        ctx.fillStyle = "rgba(255,218,116,0.18)";
        ctx.beginPath(); ctx.arc(u.x, u.y, 7.5, 0, Math.PI * 2); ctx.fill();
      }
      ctx.restore();
      const flagLen = 11 + lvl * 1.6;               // 将旗随功勋加长
      const poleH = 13 + lvl * 1.5;
      ctx.fillStyle = F.dark;
      ctx.beginPath(); ctx.arc(u.x, u.y, 4.2 + lvl * 0.3, 0, Math.PI * 2); ctx.fill();
      if (lvl >= 3) {                                // 名将金边
        ctx.strokeStyle = "#e8c34a"; ctx.lineWidth = 0.9;
        ctx.beginPath(); ctx.arc(u.x, u.y, 4.6 + lvl * 0.25, 0, Math.PI * 2); ctx.stroke();
      }
      ctx.strokeStyle = "#2a2118"; ctx.lineWidth = 1;
      ctx.beginPath(); ctx.moveTo(u.x, u.y); ctx.lineTo(u.x, u.y - poleH); ctx.stroke();
      const wave = Math.sin(time * 6 + u.x) * 1.5;
      ctx.fillStyle = F.color;
      ctx.beginPath();
      ctx.moveTo(u.x, u.y - poleH);
      ctx.lineTo(u.x + flagLen, u.y - poleH + 2 + wave);
      ctx.lineTo(u.x, u.y - poleH + 6);
      ctx.closePath(); ctx.fill();
      ctx.fillStyle = "#f2e9d5";
      ctx.font = "bold 6px sans-serif";
      ctx.fillText(F.name, u.x + 2.5, u.y - poleH + 4.5);
      // 星级
      if (lvl > 0) {
        ctx.fillStyle = "#e8c34a";
        for (let i = 0; i < lvl; i++) ctx.fillRect(u.x + 1 + i * 2.4, u.y - poleH + 8, 1.6, 1.6);
      }
      if (settings.showNames && army) {
        ctx.fillStyle = "rgba(30,22,14,0.85)";
        ctx.font = "bold 8px 'Songti SC',serif";
        ctx.textAlign = "center";
        ctx.fillText(army.general, u.x, u.y - poleH - 3);
        ctx.textAlign = "left";
      }
      continue;
    }
    const col = u.flee ? F.light : F.color;
    const vet = Math.min(u.kills, 3);
    const grow = vet >= 3 ? 0.5 : 0;                 // 三级老兵身形微壮
    // 伪3D：极小的地面阴影，让单位读起来略微"立起"；高兵力场次随特效精简自动跳过
    if (!FX_LITE) {
      ctx.fillStyle = "rgba(15,12,8,0.25)";
      ctx.beginPath();
      ctx.ellipse(u.x + 0.6, u.y + 1.6, 1.6 + grow / 2, 0.7, 0, 0, Math.PI * 2);
      ctx.fill();
    }
    ctx.fillStyle = col;
    switch (u.type) {
      case "spear": {
        // 菱形（枪头轮廓）+ 竖向枪尖线，与步兵的方块区分开
        const r = 1.9 + grow / 2;
        ctx.beginPath();
        ctx.moveTo(u.x, u.y - r); ctx.lineTo(u.x + r, u.y);
        ctx.lineTo(u.x, u.y + r); ctx.lineTo(u.x - r, u.y);
        ctx.closePath(); ctx.fill();
        ctx.strokeStyle = col; ctx.lineWidth = 0.9;
        ctx.beginPath(); ctx.moveTo(u.x, u.y - r); ctx.lineTo(u.x, u.y - r - 3.3); ctx.stroke();
        break;
      }
      case "arch":
        ctx.beginPath(); ctx.arc(u.x, u.y, 1.5 + grow / 2, 0, Math.PI * 2); ctx.fill();
        break;
      case "cav": {
        // 三角形（冲锋箭头轮廓），与其余三种圆/方/菱形区分开
        const r = 2.6 + grow / 2;
        ctx.beginPath();
        ctx.moveTo(u.x, u.y - r); ctx.lineTo(u.x + r * 0.95, u.y + r * 0.75);
        ctx.lineTo(u.x - r * 0.95, u.y + r * 0.75);
        ctx.closePath(); ctx.fill();
        if (u.charge > 0.6 && !u.flee) {
          ctx.fillStyle = "rgba(255,235,160,0.7)";
          ctx.fillRect(u.x - r, u.y - r - 1.6, r * 2 * u.charge, 0.8);
        }
        break;
      }
      default:
        ctx.fillRect(u.x - 1.4 - grow / 2, u.y - 1.4 - grow / 2, 2.8 + grow, 2.8 + grow);
    }
    if (vet >= 1 && !u.flee) {
      // 老兵星徽：一级暗金，二级金，三级亮金加大
      const cols3 = ["", "#b99a3a", "#e8c34a", "#ffdf70"];
      ctx.fillStyle = cols3[vet];
      const s = vet >= 3 ? 1.7 : 1.2;
      ctx.fillRect(u.x - s / 2, u.y - 3.6 - grow, s, s);
    }
  }
  for (const a of sim.arrows) {
    const p = Math.min(1, a.t / a.dur);
    const x = a.x + (a.tx - a.x) * p;
    const y = a.y + (a.ty - a.y) * p - Math.sin(p * Math.PI) * 14;
    const p2 = Math.min(1, p + 0.06);
    const nx = a.x + (a.tx - a.x) * p2;
    const ny = a.y + (a.ty - a.y) * p2 - Math.sin(p2 * Math.PI) * 14;
    ctx.strokeStyle = "rgba(40,30,20,0.85)";
    ctx.lineWidth = 1;
    ctx.beginPath(); ctx.moveTo(x, y); ctx.lineTo(nx, ny); ctx.stroke();
  }
  for (const f of sim.fx) {
    if (f.type === 0) {
      ctx.strokeStyle = `rgba(255,220,120,${f.t / 0.18})`;
      ctx.lineWidth = 1.4;
      ctx.beginPath();
      ctx.moveTo(f.x - 3, f.y + 3); ctx.lineTo(f.x + 3, f.y - 3);
      ctx.stroke();
    } else if (f.type === 1) {
      const a = f.t / 0.5;
      ctx.strokeStyle = `rgba(150,30,20,${a})`;
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(f.x, f.y, (1 - a) * 8 + 2, 0, Math.PI * 2); ctx.stroke();
    } else if (f.type === 2) {
      const a = f.t / 0.35;
      ctx.strokeStyle = `rgba(255,200,80,${a})`;
      ctx.lineWidth = 2;
      ctx.beginPath(); ctx.arc(f.x, f.y, (1 - a) * 14 + 3, 0, Math.PI * 2); ctx.stroke();
    } else {
      const a = f.t / 0.45;
      ctx.strokeStyle = `rgba(90,90,110,${a})`;
      ctx.lineWidth = 1.6;
      ctx.beginPath(); ctx.arc(f.x, f.y, a * 7 + 1.5, 0, Math.PI * 2); ctx.stroke();
    }
  }
  // 框选矩形
  if (selRect) {
    ctx.strokeStyle = "rgba(212,178,76,0.9)";
    ctx.fillStyle = "rgba(212,178,76,0.08)";
    ctx.lineWidth = 1.2;
    ctx.setLineDash([5, 4]);
    const x = Math.min(selRect.x0, selRect.x1), y = Math.min(selRect.y0, selRect.y1);
    const w = Math.abs(selRect.x1 - selRect.x0), h = Math.abs(selRect.y1 - selRect.y0);
    ctx.fillRect(x, y, w, h);
    ctx.strokeRect(x, y, w, h);
    ctx.setLineDash([]);
  }
}

/* ---------- scenarios ---------- */
function presetScenario(name, desc, seed, opts, setup, allies = []) {
  return {
    name, desc,
    make() {
      return {
        terrain: genTerrain(seed, opts), meta: { seed, opts }, allies,
        setup,
      };
    },
  };
}
function campaignFactionScenario(name, desc, seed, opts, objective, enemyIndex = 0) {
  return {
    name, desc,
    make(context = {}) {
      const fac = context.fac || "lvbu";
      const enemyPool = {
        lvbu: ["wei", "shu", "wu"], yuan: ["wei", "wu", "shu"], dong: ["wei", "shu", "wu"],
      }[fac] || ["wei", "shu", "wu"];
      const enemy = enemyPool[enemyIndex % enemyPool.length];
      return {
        terrain: genTerrain(seed, opts), meta: { seed, opts },
        setup(sim) {
          const px = W * 0.2, ex = W * 0.8, cy = H * 0.5;
          if (objective.type === "capture") addBuilding(sim, enemy, "city", ex, cy, objective.building);
          if (objective.type === "survive") addBuilding(sim, fac, "city", px, cy, objective.building || "本阵");
          const p = makeArmy(sim, fac, "inf", 6500, px, cy, { general: GENERALS[fac][0], form: "fang" });
          if (objective.type === "survive") { p.order = "hold"; p.anchor = { x: px, y: cy }; }
          makeArmy(sim, fac, "cav", 3000, px + 20, cy + 100, { general: GENERALS[fac][1], form: "feng" });
          makeArmy(sim, enemy, "inf", 8500, ex, cy - 25, { general: GENERALS[enemy][0] });
          makeArmy(sim, enemy, "arch", 4000, ex - 20, cy + 100, { general: GENERALS[enemy][1], form: "yan" });
          if (objective.type === "slay") log(sim, `🎯 本关目标：阵斩${objective.general}`);
        },
      };
    },
  };
}
const SCENARIOS = {
  random: {
    name: "随机沙盘", desc: "随机山川河流，自由布阵",
    make() {
      const sd = Math.floor(Math.random() * 99999);
      const opts = { river: "v" };
      return { terrain: genTerrain(sd, opts), meta: { seed: sd, opts }, setup() {} };
    },
  },
  campaign_extra_1: campaignFactionScenario("虎牢再战", "吕布等群雄争雄 · 决战雄关", 15001, { river: "none", mountainBias: 0.08 }, { type: "slay", general: "华雄" }, 0),
  campaign_extra_2: campaignFactionScenario("下邳困城", "孤城突围 · 逆风求生", 15002, { river: "v", forestBias: 0.04 }, { type: "survive", t: 110, building: "下邳" }, 1),
  campaign_extra_3: campaignFactionScenario("濮阳争夺", "骑兵鏖战 · 夺取城门", 15003, { river: "none", forestBias: 0.08 }, { type: "capture", building: "濮阳" }, 2),
  campaign_extra_4: campaignFactionScenario("白门楼", "背水一战 · 阵斩敌将", 15004, { river: "none", mountainBias: 0.06 }, { type: "slay", general: "曹操" }, 0),
  campaign_extra_5: campaignFactionScenario("河北会盟", "袁绍大军 · 诸侯决裂", 15005, { river: "v", forestBias: 0.03 }, { type: "annihilate" }, 1),
  campaign_extra_6: campaignFactionScenario("官渡粮道", "少胜多 · 烧毁敌营", 15006, { river: "v", forestBias: 0.02 }, { type: "capture", building: "乌巢" }, 0),
  campaign_extra_7: campaignFactionScenario("长安乱战", "董卓西凉军 · 夺回京畿", 15007, { river: "none", mountainBias: 0.1 }, { type: "capture", building: "长安" }, 1),
  campaign_extra_8: campaignFactionScenario("西凉铁骑", "铁骑冲阵 · 护送主公", 15008, { river: "none", mountainBias: 0.12 }, { type: "escort", general: "董卓" }, 2),
  campaign_extra_9: campaignFactionScenario("中原鏖兵", "三军混战 · 破敌主阵", 15009, { river: "wide-h", forestBias: 0.06 }, { type: "annihilate" }, 0),
  campaign_extra_10: campaignFactionScenario("霸业终章", "外族未平 · 一统中原", 15010, { river: "none", mountainBias: 0.08 }, { type: "capture", building: "洛阳" }, 1),
  changan: {
    name: "长安", desc: "坚城当道 · 魏守蜀攻",
    make() {
      const opts = { river: "none", mountainBias: 0.04, clearRects: [{ xf: 0.24, yf: 0.34, w: 16, h: 14 }] };
      return {
        terrain: genTerrain(1013, opts), meta: { seed: 1013, opts },
        setup(sim) {
          const cx = W * 0.32, cy = H * 0.48;
          addBuilding(sim, "wei", "city", cx, cy, "长安");
          addBuilding(sim, "wei", "camp", cx + 100, cy - 50);
          const a1 = makeArmy(sim, "wei", "arch", 4000, cx, cy - 60, { general: "曹仁" });
          a1.order = "hold"; a1.anchor = { x: cx, y: cy - 60 };
          const a2 = makeArmy(sim, "wei", "spear", 6000, cx, cy + 70, { general: "张郃", form: "yuan" });
          a2.order = "hold"; a2.anchor = { x: cx, y: cy + 70 };
          makeArmy(sim, "shu", "inf", 9000, W - 140, H * 0.35, { general: "诸葛亮" });
          makeArmy(sim, "shu", "cav", 4000, W - 110, H * 0.65, { general: "马超", form: "feng" });
          makeArmy(sim, "shu", "arch", 4000, W - 60, H * 0.5, { general: "黄忠" });
        },
      };
    },
  },
  luoyang: {
    name: "洛阳", desc: "三家逐鹿中原孤城",
    make() {
      const opts = { river: "none", forestBias: 0.03, clearRects: [{ xf: 0.39, yf: 0.34, w: 18, h: 16 }] };
      return {
        terrain: genTerrain(2077, opts), meta: { seed: 2077, opts },
        setup(sim) {
          const cx = W / 2, cy = H / 2;
          addBuilding(sim, "wei", "city", cx, cy, "洛阳");
          const g = makeArmy(sim, "wei", "inf", 7000, cx, cy + 60, { general: "司马懿", form: "yuan" });
          g.order = "hold"; g.anchor = { x: cx, y: cy + 60 };
          addBuilding(sim, "wei", "camp", cx - 24, cy - 72);
          makeArmy(sim, "shu", "inf", 6000, 110, H * 0.5, { general: "关羽" });
          makeArmy(sim, "shu", "cav", 3000, 90, H * 0.72, { general: "张飞", form: "feng" });
          makeArmy(sim, "wu", "inf", 6000, W - 110, H * 0.5, { general: "周瑜" });
          makeArmy(sim, "wu", "arch", 3500, W - 80, H * 0.3, { general: "甘宁" });
        },
      };
    },
  },
  chibi: {
    name: "赤壁", desc: "大江横陈 · 吴蜀联盟 · 半渡而击",
    make() {
      const opts = { river: "wide-h", forestBias: 0.05 };
      return {
        terrain: genTerrain(208, opts), meta: { seed: 208, opts },
        allies: [pairKey("shu", "wu")],
        setup(sim) {
          makeArmy(sim, "wei", "inf", 14000, W * 0.42, H * 0.2, { general: "曹操" });
          makeArmy(sim, "wei", "cav", 6000, W * 0.62, H * 0.16, { general: "张辽", form: "feng" });
          makeArmy(sim, "wei", "arch", 5000, W * 0.3, H * 0.14, { general: "徐晃" });
          addBuilding(sim, "wei", "camp", W * 0.52, H * 0.1);
          const w1 = makeArmy(sim, "wu", "arch", 6000, W * 0.44, H * 0.78, { general: "周瑜", form: "yan" });
          w1.order = "hold"; w1.anchor = { x: W * 0.44, y: H * 0.78 };
          const w2 = makeArmy(sim, "wu", "inf", 5000, W * 0.52, H * 0.88, { general: "黄盖" });
          w2.order = "hold"; w2.anchor = { x: W * 0.52, y: H * 0.88 };
          const s1 = makeArmy(sim, "shu", "arch", 4000, W * 0.58, H * 0.76, { general: "诸葛亮", form: "yan" });
          s1.order = "hold"; s1.anchor = { x: W * 0.58, y: H * 0.76 };
          const s2 = makeArmy(sim, "shu", "spear", 4000, W * 0.48, H * 0.7, { general: "赵云", form: "yuan" });
          s2.order = "hold"; s2.anchor = { x: W * 0.48, y: H * 0.7 };
        },
      };
    },
  },
  hulao: {
    name: "虎牢关", desc: "一夫当关 · 董军据雄关 · 诸侯联军叩关",
    make() {
      const opts = { river: "none", mountainBias: 0.06, ridges: [{ colFrac: 0.44, gapFrac: 0.5, gapH: 6 }] };
      return {
        terrain: genTerrain(3111, opts), meta: { seed: 3111, opts },
        allies: [pairKey("shu", "wu")],
        setup(sim) {
          const px = W * 0.44, py = H * 0.5;
          addBuilding(sim, "dong", "pass", px, py, "虎牢关");
          addBuilding(sim, "dong", "city", W * 0.2, H * 0.5, "汜水");
          addBuilding(sim, "dong", "camp", W * 0.3, H * 0.35);
          const d1 = makeArmy(sim, "dong", "cav", 6000, px - 45, py, { general: "吕布", form: "feng" });
          d1.order = "hold"; d1.anchor = { x: px - 45, y: py };
          const d2 = makeArmy(sim, "dong", "spear", 5000, px - 85, py, { general: "高顺", form: "yuan" });
          d2.order = "hold"; d2.anchor = { x: px - 85, y: py };
          makeArmy(sim, "shu", "inf", 8000, W * 0.78, H * 0.34, { general: "刘备" });
          makeArmy(sim, "shu", "cav", 3500, W * 0.85, H * 0.5, { general: "关羽", form: "feng" });
          makeArmy(sim, "wu", "inf", 7000, W * 0.78, H * 0.66, { general: "孙权" });
          makeArmy(sim, "wu", "arch", 4000, W * 0.88, H * 0.62, { general: "太史慈" });
        },
      };
    },
  },
  jieting: {
    name: "街亭", desc: "据山者危 · 蜀屯高岗 · 魏断水道",
    make() {
      const opts = { river: "none", mountainBias: 0.1, forestBias: 0.04 };
      return {
        terrain: genTerrain(4225, opts), meta: { seed: 4225, opts },
        setup(sim) {
          const hx = W * 0.6, hy = H * 0.38;
          const m1 = makeArmy(sim, "shu", "arch", 5000, hx, hy, { general: "马谡... 赵云", form: "yan" });
          m1.general = "赵云";
          m1.order = "hold"; m1.anchor = { x: hx, y: hy };
          const m2 = makeArmy(sim, "shu", "spear", 5000, hx - 40, hy + 40, { general: "魏延", form: "yuan" });
          m2.order = "hold"; m2.anchor = { x: hx - 40, y: hy + 40 };
          addBuilding(sim, "shu", "camp", W * 0.85, H * 0.7);
          makeArmy(sim, "wei", "inf", 10000, W * 0.18, H * 0.6, { general: "司马懿" });
          makeArmy(sim, "wei", "cav", 5000, W * 0.14, H * 0.35, { general: "张郃", form: "feng" });
          addBuilding(sim, "wei", "camp", W * 0.1, H * 0.5);
          log(sim, "⚠ 蜀军屯于山上而营在远处 — 魏军若围山断粮，高地亦成死地");
        },
      };
    },
  },
  guandu: {
    name: "官渡", desc: "粮道为命 · 烧敌营者胜",
    make() {
      const opts = { river: "v", forestBias: 0.02 };
      return {
        terrain: genTerrain(5200, opts), meta: { seed: 5200, opts },
        setup(sim) {
          addBuilding(sim, "wei", "camp", W * 0.16, H * 0.3);
          addBuilding(sim, "wei", "camp", W * 0.14, H * 0.7);
          makeArmy(sim, "wei", "inf", 7000, W * 0.22, H * 0.5, { general: "曹操" });
          makeArmy(sim, "wei", "cav", 3000, W * 0.18, H * 0.42, { general: "许褚", form: "feng" });
          addBuilding(sim, "yuan", "camp", W * 0.86, H * 0.28);
          addBuilding(sim, "yuan", "camp", W * 0.88, H * 0.72);
          makeArmy(sim, "yuan", "inf", 12000, W * 0.78, H * 0.5, { general: "袁绍" });
          makeArmy(sim, "yuan", "arch", 6000, W * 0.84, H * 0.4, { general: "颜良" });
          log(sim, "🌾 两军皆倚营为粮 — 遣轻骑烧其营，敌不战自溃");
        },
      };
    },
  },
  xinye: presetScenario("新野", "小城据守 · 奇兵出城", 6117, { river: "v", forestBias: 0.08 }, (sim) => {
    const cx = W * 0.66, cy = H * 0.5;
    addBuilding(sim, "shu", "city", cx, cy, "新野");
    const g = makeArmy(sim, "shu", "inf", 4200, cx, cy + 50, { general: "刘备", form: "yuan" }); g.order = "hold"; g.anchor = { x: cx, y: cy + 50 };
    makeArmy(sim, "shu", "cav", 1800, cx - 80, cy + 110, { general: "赵云", form: "feng" });
    makeArmy(sim, "wei", "inf", 7500, W * 0.18, H * 0.42, { general: "曹仁" });
    makeArmy(sim, "wei", "cav", 2800, W * 0.14, H * 0.62, { general: "许褚", form: "feng" });
  }),
  fancheng: presetScenario("樊城", "襄水相隔 · 守城与援军", 6231, { river: "v", mountainBias: 0.03 }, (sim) => {
    const cx = W * 0.55, cy = H * 0.46;
    addBuilding(sim, "wei", "city", cx, cy, "樊城"); addBuilding(sim, "wei", "camp", cx - 100, cy - 80);
    makeArmy(sim, "wei", "spear", 6500, cx, cy + 60, { general: "曹仁", form: "yuan" });
    makeArmy(sim, "wei", "arch", 3000, cx - 50, cy - 40, { general: "徐晃", form: "yan" });
    makeArmy(sim, "shu", "inf", 7500, W * 0.2, H * 0.46, { general: "关羽" });
    makeArmy(sim, "shu", "cav", 2500, W * 0.16, H * 0.68, { general: "关平", form: "feng" });
  }),
  yiling: presetScenario("夷陵", "林火连营 · 蜀吴决战", 6345, { river: "wide-h", forestBias: 0.16 }, (sim) => {
    makeArmy(sim, "shu", "inf", 8000, W * 0.72, H * 0.64, { general: "刘备" });
    makeArmy(sim, "shu", "spear", 3500, W * 0.78, H * 0.78, { general: "黄忠", form: "yuan" });
    makeArmy(sim, "wu", "arch", 6500, W * 0.25, H * 0.28, { general: "陆逊", form: "yan" });
    makeArmy(sim, "wu", "cav", 3500, W * 0.18, H * 0.48, { general: "甘宁", form: "feng" });
    addBuilding(sim, "wu", "camp", W * 0.16, H * 0.18);
  }),
  dingjunshan: presetScenario("定军山", "高地争夺 · 黄忠斩将", 6459, { river: "none", mountainBias: 0.13, ridges: [{ colFrac: 0.58, gapFrac: 0.42, gapH: 5 }] }, (sim) => {
    const hx = W * 0.58, hy = H * 0.38;
    const h = makeArmy(sim, "shu", "arch", 5000, hx, hy, { general: "黄忠", form: "yan", elite: true }); h.order = "hold"; h.anchor = { x: hx, y: hy };
    makeArmy(sim, "shu", "inf", 4500, hx + 50, hy + 55, { general: "法正" });
    makeArmy(sim, "wei", "inf", 8000, W * 0.18, H * 0.46, { general: "夏侯渊" });
    makeArmy(sim, "wei", "cav", 3000, W * 0.14, H * 0.68, { general: "张郃", form: "feng" });
  }),
  wuzhangyuan: presetScenario("五丈原", "蜀军北伐 · 司马懿坚守", 6573, { river: "none", mountainBias: 0.08, forestBias: 0.04 }, (sim) => {
    const cx = W * 0.38, cy = H * 0.5;
    addBuilding(sim, "wei", "camp", cx, cy); addBuilding(sim, "wei", "pass", cx + 40, cy - 90, "五丈原关");
    const d = makeArmy(sim, "wei", "spear", 8500, cx + 50, cy + 30, { general: "司马懿", form: "yuan" }); d.order = "hold"; d.anchor = { x: cx + 50, y: cy + 30 };
    makeArmy(sim, "shu", "inf", 7500, W * 0.78, H * 0.38, { general: "诸葛亮" });
    makeArmy(sim, "shu", "arch", 4200, W * 0.84, H * 0.58, { general: "姜维", form: "yan" });
  }),
  hefei: presetScenario("合肥", "逍遥津 · 少胜多", 6687, { river: "v", mountainBias: 0.02 }, (sim) => {
    addBuilding(sim, "wei", "pass", W * 0.42, H * 0.5, "合肥关");
    makeArmy(sim, "wei", "cav", 3500, W * 0.38, H * 0.48, { general: "张辽", form: "feng", elite: true });
    makeArmy(sim, "wei", "spear", 4000, W * 0.33, H * 0.62, { general: "李典", form: "yuan" });
    makeArmy(sim, "wu", "inf", 11000, W * 0.78, H * 0.46, { general: "孙权" });
    makeArmy(sim, "wu", "cav", 3500, W * 0.84, H * 0.66, { general: "太史慈", form: "feng" });
  }),
  shouchun: presetScenario("寿春", "淮南攻防 · 三军混战", 6791, { river: "wide-h", forestBias: 0.04 }, (sim) => {
    addBuilding(sim, "wei", "city", W * 0.5, H * 0.5, "寿春");
    makeArmy(sim, "wei", "inf", 6500, W * 0.5, H * 0.62, { general: "邓艾", form: "yuan" });
    makeArmy(sim, "shu", "cav", 4200, W * 0.16, H * 0.28, { general: "姜维", form: "feng" });
    makeArmy(sim, "wu", "inf", 6500, W * 0.82, H * 0.38, { general: "吕蒙" });
    makeArmy(sim, "wu", "arch", 3000, W * 0.84, H * 0.68, { general: "陆逊", form: "yan" });
  }),
  xiangyang: presetScenario("襄阳", "荆襄重镇 · 水陆夹攻", 6813, { river: "v", mountainBias: 0.04 }, (sim) => {
    addBuilding(sim, "wei", "city", W * 0.57, H * 0.43, "襄阳");
    makeArmy(sim, "wei", "arch", 5000, W * 0.58, H * 0.35, { general: "曹真", form: "yan" });
    makeArmy(sim, "wei", "inf", 6000, W * 0.62, H * 0.55, { general: "张郃" });
    makeArmy(sim, "shu", "inf", 6500, W * 0.18, H * 0.4, { general: "关羽" });
    makeArmy(sim, "wu", "arch", 3500, W * 0.22, H * 0.72, { general: "吕蒙", form: "yan" });
  }, [pairKey("shu", "wu")]),
  jiangling: presetScenario("江陵", "江城争夺 · 吴蜀水战", 6927, { river: "wide-h", forestBias: 0.06 }, (sim) => {
    addBuilding(sim, "shu", "city", W * 0.52, H * 0.5, "江陵");
    makeArmy(sim, "shu", "inf", 6000, W * 0.52, H * 0.62, { general: "关羽", form: "yuan" });
    makeArmy(sim, "wu", "inf", 6500, W * 0.2, H * 0.38, { general: "吕蒙" });
    makeArmy(sim, "wu", "arch", 3500, W * 0.16, H * 0.62, { general: "陆逊", form: "yan" });
  }),
  nanjun: presetScenario("南郡", "周瑜围城 · 曹仁固守", 7031, { river: "v", forestBias: 0.08 }, (sim) => {
    addBuilding(sim, "wei", "city", W * 0.66, H * 0.48, "南郡");
    const d = makeArmy(sim, "wei", "inf", 6000, W * 0.66, H * 0.58, { general: "曹仁", form: "yuan" }); d.order = "hold"; d.anchor = { x: W * 0.66, y: H * 0.58 };
    makeArmy(sim, "wu", "inf", 7500, W * 0.22, H * 0.34, { general: "周瑜" });
    makeArmy(sim, "wu", "cav", 2500, W * 0.18, H * 0.68, { general: "甘宁", form: "feng" });
    makeArmy(sim, "shu", "arch", 3000, W * 0.36, H * 0.2, { general: "赵云", form: "yan" });
  }),
  chengdu: presetScenario("成都", "蜀都保卫战 · 山地与坚城", 7145, { river: "none", mountainBias: 0.15, forestBias: 0.1 }, (sim) => {
    addBuilding(sim, "shu", "city", W * 0.34, H * 0.52, "成都"); addBuilding(sim, "shu", "camp", W * 0.2, H * 0.34);
    makeArmy(sim, "shu", "inf", 7000, W * 0.36, H * 0.64, { general: "姜维", form: "yuan" });
    makeArmy(sim, "shu", "arch", 3200, W * 0.25, H * 0.45, { general: "黄忠", form: "yan" });
    makeArmy(sim, "wei", "inf", 9000, W * 0.8, H * 0.38, { general: "邓艾" });
    makeArmy(sim, "wei", "cav", 3500, W * 0.86, H * 0.62, { general: "钟会", form: "feng" });
  }),
  hanzhong: presetScenario("汉中", "秦岭关隘 · 蜀魏拉锯", 7259, { river: "none", mountainBias: 0.17, ridges: [{ colFrac: 0.5, gapFrac: 0.5, gapH: 5 }] }, (sim) => {
    addBuilding(sim, "shu", "pass", W * 0.5, H * 0.5, "汉中关");
    makeArmy(sim, "shu", "spear", 6000, W * 0.56, H * 0.57, { general: "魏延", form: "yuan" });
    makeArmy(sim, "shu", "cav", 2800, W * 0.62, H * 0.38, { general: "赵云", form: "feng" });
    makeArmy(sim, "wei", "inf", 8500, W * 0.16, H * 0.48, { general: "曹真" });
    makeArmy(sim, "wei", "arch", 3500, W * 0.12, H * 0.68, { general: "张郃", form: "yan" });
  }),
  liaodong: presetScenario("辽东", "塞外寒地 · 远征孤军", 7373, { river: "none", mountainBias: 0.12, forestBias: 0.12 }, (sim) => {
    addBuilding(sim, "wei", "pass", W * 0.72, H * 0.42, "襄平");
    makeArmy(sim, "wei", "inf", 6000, W * 0.7, H * 0.55, { general: "司马懿", form: "yuan" });
    makeArmy(sim, "wei", "cav", 2500, W * 0.78, H * 0.3, { general: "张辽", form: "feng" });
    makeArmy(sim, "wu", "inf", 4500, W * 0.16, H * 0.4, { general: "甘宁" });
    makeArmy(sim, "shu", "arch", 3000, W * 0.2, H * 0.7, { general: "魏延", form: "yan" });
  }),
  nanzhong: presetScenario("南中", "蛮王据险 · 山林难行", 7487, { river: "v", mountainBias: 0.2, forestBias: 0.2 }, (sim) => {
    addBuilding(sim, "shu", "city", W * 0.32, H * 0.52, "南中城");
    makeArmy(sim, "shu", "inf", 5000, W * 0.35, H * 0.64, { general: "诸葛亮", form: "yuan" });
    makeArmy(sim, "shu", "cav", 2000, W * 0.25, H * 0.38, { general: "赵云", form: "feng" });
    makeArmy(sim, "wu", "inf", 5500, W * 0.78, H * 0.38, { general: "陆逊" });
    makeArmy(sim, "wei", "arch", 3500, W * 0.84, H * 0.7, { general: "徐晃", form: "yan" });
  }),
};

// 预定沙盘的历史/演义档案：时间只用于推进旁白，不锁定胜负，玩家仍可改写结局。
// at 为战斗开始后的秒数；source 标明“史实”“演义”或两者的混合来源。
const HISTORICAL_BATTLE_ARCHIVES = {
  guandu: {
    year: "建安五年（200）", source: "史实骨架 · 《三国志》兼采演义", title: "官渡之战",
    background: "袁绍拥河北大军南下，曹操兵少而粮道脆弱。乌巢粮仓是决定战局的关键。",
    outcome: "史实结局：曹操奇袭乌巢，袁军主力溃散；本沙盘允许玩家改写结局。",
    events: [
      { at: 2, text: "袁曹两军隔营对峙，双方都在等待对方的粮道先出破绽。" },
      { at: 22, text: "📜 许攸来投（演义叙事）：乌巢粮仓的位置成为曹军奇袭的机会。" },
      { at: 48, text: "🔥 历史节点：曹操亲率精锐奔袭乌巢——若粮营失守，袁军士气将迅速崩解。" },
      { at: 82, text: "官渡决战进入收束阶段：正面硬撼或切断粮道，都可能改写北方归属。" },
    ],
  },
  chibi: {
    year: "建安十三年（208）", source: "史实骨架 · 《三国演义》强化火攻", title: "赤壁之战",
    background: "曹操南下荆州，孙权与刘备结盟。北军不习水战，长江与风向成为战场的一部分。",
    outcome: "史实结局：孙刘联军击退曹操；小说中以黄盖诈降、连环船与东风火攻渲染高潮。",
    events: [
      { at: 2, text: "孙刘联军据江南列阵，曹军营寨沿江铺开，双方暂以水面相隔。" },
      { at: 26, text: "📜 黄盖诈降（演义名场面）：火船接近敌营，江风和队形将决定火攻能否奏效。" },
      { at: 54, text: "🔥 赤壁火起！历史与演义都记载曹军水寨遭到重创，但火势范围由战场局势决定。" },
      { at: 92, text: "华容道方向出现溃军：曹军若能保持主将和精锐，仍有机会撤回江北。" },
    ],
  },
  hulao: {
    year: "初平三年（192）前后题材", source: "演义会战 · 取材《三国演义》",
    title: "虎牢关三英战吕布", background: "诸侯联军叩关，吕布凭雄关与骑兵拒敌；“三英战吕布”是小说名场面，非严格史实记录。",
    outcome: "演义结局：刘关张合力逼退吕布，联军声势大振；本沙盘允许联军或守军取胜。",
    events: [
      { at: 3, text: "诸侯联军抵达虎牢关外，关上弓弩与守军依险列阵。" },
      { at: 30, text: "🗡 吕布出关挑战：演义中关羽、张飞先后迎战，刘备随后加入围攻。" },
      { at: 60, text: "虎牢关前进入将领决斗阶段：不要让主将被孤军包围，骑兵可从侧翼支援。" },
    ],
  },
  jieting: {
    year: "建兴六年（228）", source: "史实骨架 · 《三国演义》戏剧化",
    title: "街亭之战", background: "诸葛亮第一次北伐期间，马谡违背据城、据水部署的军令，屯兵山上，魏军由张郃断其水道。",
    outcome: "史实结局：街亭失守，蜀军退回汉中；本沙盘可用稳守水源或主动突击改写战果。",
    events: [
      { at: 2, text: "蜀军占据高岗但远离水源，魏军正在封锁山下道路。" },
      { at: 28, text: "📜 张郃分兵断水：若蜀军长期坚守高地，粮草与士气会成为真正的敌人。" },
      { at: 70, text: "街亭成败将影响北伐退路：保住营寨与水线，比单纯追求歼敌更接近史实难题。" },
    ],
  },
  yiling: {
    year: "章武二年（222）", source: "史实骨架 · 《三国演义》强化连营火攻", title: "夷陵之战",
    background: "刘备为荆州与关羽复仇，深入吴境后连营数百里；陆逊坚守并等待蜀军疲惫。",
    outcome: "史实结局：陆逊火烧蜀军营寨，刘备败退白帝城；本沙盘可通过分散营寨避免连锁火灾。",
    events: [
      { at: 2, text: "蜀军深入山林列营，吴军暂避锋芒，双方都在寻找对方阵线的弱点。" },
      { at: 34, text: "📜 陆逊坚守不战：吴军等待蜀军疲惫，蜀军则应避免把营寨拉成一条过长的火线。" },
      { at: 68, text: "🔥 火攻窗口出现：演义中的连营大火极具戏剧性，但本模拟按实际营寨与部队位置计算。" },
    ],
  },
  dingjunshan: {
    year: "建安二十三年（218）", source: "史实骨架 · 《三国演义》强化斩将", title: "定军山之战",
    background: "汉中争夺中，蜀军与魏军争夺高地。黄忠在法正谋划下夺取定军山并击杀夏侯渊。",
    outcome: "史实结局：夏侯渊战死，魏军汉中形势转弱；本沙盘中的斩将由实时战斗决定。",
    events: [
      { at: 3, text: "蜀军占据高地，魏军必须谨慎接敌，否则会在坡地与弓矢下付出代价。" },
      { at: 35, text: "📜 法正示警：夏侯渊亲临前线可能暴露主将，黄忠正等待高地反击的时机。" },
      { at: 64, text: "定军山主将交锋：保护将旗、控制高地，比单纯冲入敌阵更符合这场战役的特点。" },
    ],
  },
  hefei: {
    year: "建安二十年（215）题材", source: "史实骨架 · 《三国志》兼采演义", title: "合肥·逍遥津",
    background: "孙权率军攻合肥，张辽以少量精锐突袭吴军，逍遥津成为吴军撤退时的险地。",
    outcome: "史实结局：张辽威震逍遥津，孙权退军；本沙盘允许吴军以协同包围改写少胜多。",
    events: [
      { at: 2, text: "吴军兵力占优而阵线较长，魏军据合肥关观察渡口和撤退路线。" },
      { at: 25, text: "🗡 张辽率精骑突袭：小股精锐若切入主将附近，可能造成远超兵力比例的震慑。" },
      { at: 58, text: "逍遥津退路成为关键：吴军应避免主将与大军被河道、关隘和敌骑分割。" },
    ],
  },
  nanjun: {
    year: "建安十四年（209）", source: "史实骨架 · 《三国演义》周瑜围城叙事", title: "南郡争夺",
    background: "赤壁后孙刘争夺荆州门户，周瑜与曹仁围绕南郡展开长期攻防。",
    outcome: "史实与演义版本对南郡归属、战斗细节有差异，本沙盘将其处理为吴攻魏守、蜀军可援的开放战局。",
    events: [
      { at: 2, text: "曹仁据南郡固守，吴军从外线施压，蜀军援军可能改变围城平衡。" },
      { at: 40, text: "📜 围城不只靠正面冲锋：切断援路、压低守军士气，才是荆襄攻防的核心。" },
      { at: 82, text: "南郡城防出现缺口：攻方需把握易帜时机，守方则应利用城内恢复与援军。" },
    ],
  },
  wuzhangyuan: {
    year: "青龙二年—三年（234）", source: "史实骨架 · 《三国演义》强化五丈原传说", title: "五丈原对峙",
    background: "诸葛亮第五次北伐与司马懿对峙，魏军坚守不出，蜀军粮道与时间压力不断增加。",
    outcome: "史实结局：诸葛亮病逝军中，蜀军退回；本沙盘只模拟一次开放战斗，不强制复刻病逝事件。",
    events: [
      { at: 3, text: "蜀军压境，司马懿依营寨与关隘坚守；双方都不愿在不利地形贸然决战。" },
      { at: 45, text: "📜 坚守与粮道：时间越久，蜀军补给压力越大，魏军则等待其自退。" },
      { at: 90, text: "五丈原进入持久对峙：若玩家主动打破僵局，战果将偏离史实但保留演义式决战可能。" },
    ],
  },
};
for (const [key, archive] of Object.entries(HISTORICAL_BATTLE_ARCHIVES)) {
  if (SCENARIOS[key]) SCENARIOS[key].history = archive;
}

/* ---------- campaign mode ---------- */
const CAMPAIGN_KEY = "sanguo:campaign-v2";
const CAMPAIGN_LEGACY_KEY = "sanguo:campaign-v1";
const CAMPAIGN_FACS = ["wei", "shu", "wu", "lvbu", "yuan", "dong"];

function createDefaultCampaignProgress() {
  return Object.fromEntries(CAMPAIGN_FACS.map(f => [f, 0]));
}

export function createDefaultCampaignProfile() {
  return { medals: 0, veteran: 0, supply: 0, officer: 0 };
}
function createDefaultCampaignProfiles() {
  return Object.fromEntries(CAMPAIGN_FACS.map(f => [f, createDefaultCampaignProfile()]));
}
export function migrateCampaignSave(raw) {
  const legacyFlat = !!raw && typeof raw === "object" && !raw.progress;
  // 保持旧版扁平存档的返回形状，避免旧存档迁移时改变已有三条路线的数据结构；
  // 新版带 progress 的存档会自动获得新增势力的进度字段。
  const progress = legacyFlat ? { wei: 0, shu: 0, wu: 0 } : createDefaultCampaignProgress();
  const profiles = createDefaultCampaignProfiles();
  if (!raw || typeof raw !== "object") return { progress, profiles };
  if (raw.progress && typeof raw.progress === "object") {
    for (const f of CAMPAIGN_FACS) progress[f] = Math.max(0, Number(raw.progress[f]) || 0);
  } else {
    // v1 was a flat { wei, shu, wu } progress object.
    for (const f of ["wei", "shu", "wu"]) progress[f] = Math.max(0, Number(raw[f]) || 0);
  }
  if (raw.profiles && typeof raw.profiles === "object") {
    for (const f of CAMPAIGN_FACS) {
      const p = raw.profiles[f];
      if (!p) continue;
      profiles[f] = {
        medals: Math.max(0, Number(p.medals) || 0),
        veteran: Math.max(0, Number(p.veteran) || 0),
        supply: Math.max(0, Number(p.supply) || 0),
        officer: Math.max(0, Number(p.officer) || 0),
      };
    }
  }
  return { progress, profiles };
}

export function getCampaignRewardOptions(facKey, stageIndex) {
  const rotation = stageIndex % 3;
  const options = [
    { id: "veteran", icon: "⭐", title: "老兵整训", desc: "下一关己方部队获得老兵战功与少量等级加成。" },
    { id: "supply", icon: "🌾", title: "军需补给", desc: "下一关锦囊 +1，己方部队开局粮草充足、士气小幅提升。" },
    { id: "officer", icon: "🏅", title: "将校封赏", desc: "下一关己方主将等级提升一级，初始士气进一步提升。" },
  ];
  // 固定轮换只用于稳定卡片顺序，不使用随机数。
  return options.map((_, i) => options[(i + rotation + (facKey === "shu" ? 1 : facKey === "wu" ? 2 : 0)) % options.length]);
}

export function applyCampaignReward(profile, rewardId) {
  const next = { ...createDefaultCampaignProfile(), ...profile, medals: (profile?.medals || 0) + 1 };
  if (rewardId === "veteran") next.veteran++;
  if (rewardId === "supply") next.supply++;
  if (rewardId === "officer") next.officer++;
  return next;
}

export function applyCampaignProfile(sim, profile, facKey) {
  if (!sim || !profile) return;
  const mine = sim.armies.filter(a => a.fac === facKey && a.alive > 0);
  for (const army of mine) {
    army.level = Math.min(5, army.level + profile.veteran + profile.officer);
    army.morale = Math.min(100, army.morale + profile.supply * 4 + profile.officer * 6);
    army.food = 100;
    if (profile.veteran > 0) {
      for (const u of sim.units) {
        if (u.army === army.id && !u.dead) u.kills = Math.min(3, (u.kills || 0) + profile.veteran);
      }
    }
  }
  sim.tactics = 3 + profile.supply;
  sim.tacticsFac = facKey;
}
// 各势力的闯关顺序：仅选用该势力出场的场景，难度大致递增
const CAMPAIGN_ORDER = {
  wei: [
    { key: "guandu",  obj: "官渡：以本部烧毁吴军粮营，先声夺人" },
    { key: "changan", obj: "长安：坚壁清野，挫蜀军兵锋" },
    { key: "luoyang", obj: "洛阳：三面受敌，力保孤城不失" },
    { key: "jieting", obj: "街亭：断蜀军水道粮道，困死山上之敌" },
    { key: "hulao",   obj: "虎牢关：凭雄关拒蜀吴联军" },
    { key: "chibi",   obj: "赤壁：逆天改命，击退联军渡江" },
    { key: "campaign_extra_7", obj: "长安：西征夺回京畿" },
    { key: "campaign_extra_8", obj: "西凉：铁骑护送主公" },
    { key: "campaign_extra_9", obj: "中原：三军鏖战破敌" },
    { key: "campaign_extra_10", obj: "洛阳：魏军霸业终章" },
  ],
  shu: [
    { key: "changan", obj: "长安：兴复汉室，克取长安" },
    { key: "jieting", obj: "街亭：据险而守，勿失水源" },
    { key: "luoyang", obj: "洛阳：三分天下，笑到最后" },
    { key: "hulao",   obj: "虎牢关：合纵孙吴，共破雄关" },
    { key: "chibi",   obj: "赤壁：联吴抗曹，火烧连营" },
    { key: "campaign_extra_1", obj: "虎牢：方天画戟震天下" },
    { key: "campaign_extra_2", obj: "下邳：孤城死守待援" },
    { key: "campaign_extra_3", obj: "濮阳：骑兵夺城" },
    { key: "campaign_extra_4", obj: "白门楼：阵斩强敌" },
    { key: "campaign_extra_5", obj: "河北：诸侯会盟" },
  ],
  wu: [
    { key: "guandu",  obj: "官渡：以众击寡，烧敌粮营速战速决" },
    { key: "luoyang", obj: "洛阳：坐山观虎斗，后发制人" },
    { key: "hulao",   obj: "虎牢关：助盟破关，共图中原" },
    { key: "chibi",   obj: "赤壁：一战成名，扬威江东" },
    { key: "campaign_extra_3", obj: "濮阳：骑兵突入敌城" },
    { key: "campaign_extra_5", obj: "河北：击破袁氏联军" },
    { key: "campaign_extra_6", obj: "官渡：烧毁敌军粮道" },
    { key: "campaign_extra_8", obj: "淮南：铁骑护主突围" },
    { key: "campaign_extra_9", obj: "中原：水陆并进" },
    { key: "campaign_extra_10", obj: "建业：江东霸业终章" },
  ],
  lvbu: Array.from({ length: 10 }, (_, i) => ({
    key: `campaign_extra_${i + 1}`,
    obj: ["虎牢关：方天画戟震天下", "下邳：孤城死守待援", "濮阳：骑兵夺城", "白门楼：阵斩强敌", "河北：诸侯会盟", "官渡：截断敌军粮道", "长安：夺回京畿", "西凉：铁骑护主", "中原：三军鏖战", "洛阳：吕布霸业终章"][i],
  })),
  yuan: Array.from({ length: 10 }, (_, i) => ({
    key: `campaign_extra_${i + 1}`,
    obj: ["河北起兵：收服诸郡", "界桥：枪骑破阵", "幽州：击退公孙军", "青州：平定黄巾余部", "官渡：正面决战", "乌巢：护住粮道", "荆州：南下争衡", "合肥：淮南争锋", "邺城：守住河北根基", "九州：袁氏霸业终章"][i],
  })),
  dong: Array.from({ length: 10 }, (_, i) => ({
    key: `campaign_extra_${i + 1}`,
    obj: ["西凉起兵：铁骑入关", "虎牢：拒诸侯联军", "洛阳：挟天子令诸侯", "长安：清扫关中", "凉州：平定叛军", "汉中：阻断蜀军", "荆襄：争夺水陆要地", "淮南：击退吴军", "中原：董军反攻", "天下：董卓霸业终章"][i],
  })),
};

/* ---------- story mode (故事模式) ---------- */
const STORY_KEY = "sanguo:story-v1";
// winText/loseText: string | (choiceId) => string —— 终章多用函数形式，按最后一次抉择分叉结局文案
function resolveStoryText(text, choiceId) {
  return typeof text === "function" ? text(choiceId) : text;
}
// objective types: annihilate | survive{t} | escort(protectGeneral 撤出指定边) | slay{general} | capture{building} | burncamps
const STORY = {
  liubei: {
    name: "桃园之志", role: "势力 · 蜀", fac: "shu",
    desc: "从涿郡草莽到兴复汉室，走完刘玄德的一生征途。",
    chapters: [
      {
        title: "第一回 · 黄巾之乱",
        narration: "中平元年，黄巾蜂起，涿郡震动。桃园结义方毕，兄弟三人自募乡勇，迎战贼将程远志、邓茂于大兴山下。此战虽小，却是漫漫征途的第一步。",
        seed: 8101, opts: { river: "none", forestBias: 0.05 },
        choices: [
          { id: "a", text: "广募乡勇", desc: "多得一支两千人义军相助" },
          { id: "b", text: "精练本部", desc: "本部兵马开局即为一级精锐" },
        ],
        objective: { type: "annihilate", desc: "歼灭黄巾贼军（黄旗以吴色代）" },
        setup(sim, ctx, choice) {
          const lv = choice === "b" ? 1 : 0;
          makeArmy(sim, "shu", "inf", 3000, W * 0.2, H * 0.5, { general: "刘备", level: lv });
          makeArmy(sim, "shu", "spear", 2000, W * 0.16, H * 0.34, { general: "关羽", level: lv });
          makeArmy(sim, "shu", "cav", 1500, W * 0.16, H * 0.66, { general: "张飞", level: lv, form: "feng" });
          if (choice === "a") makeArmy(sim, "shu", "inf", 2000, W * 0.28, H * 0.5, { general: "乡勇" });
          makeArmy(sim, "wu", "inf", 5000, W * 0.72, H * 0.42, { general: "程远志" });
          makeArmy(sim, "wu", "inf", 4000, W * 0.76, H * 0.62, { general: "邓茂" });
        },
        winText: "贼众溃散，兄弟三人名震涿郡。乱世的大幕，才刚刚拉开。",
        loseText: "义军初败，然桃园之誓犹在。收拾残部，再图后举。",
        renown: 2,
      },
      {
        title: "第二回 · 长坂坡",
        narration: "建安十三年，曹操南下，玄德携民渡江，日行十余里。虎豹骑追至长坂，情势危急——务必护送刘备突出东面重围！下达「撤退」令即可向东突围，但退得太早，追兵衔尾必致大溃；且战且走方为上策。",
        seed: 8202, opts: { river: "none", forestBias: 0.06 },
        choices: [
          { id: "a", text: "携民渡江", desc: "民心所向：全军士气+15，此关声望翻倍" },
          { id: "b", text: "轻骑先行", desc: "刘备本部改为两千轻骑，快马先走" },
        ],
        objective: { type: "escort", desc: "护送刘备（下「撤退」令）自东侧边界脱离战场" },
        protectGeneral: "刘备",
        setup(sim, ctx, choice) {
          const lb = makeArmy(sim, "shu", choice === "b" ? "cav" : "inf", choice === "b" ? 2000 : 3000,
            W * 0.55, H * 0.5, { general: "刘备", morale: choice === "a" ? 100 : 90 });
          lb.retreatDir = 1; lb.lockRetreatDir = true;
          // 首回若"精练本部"，关张本部的精锐延续到此章
          const lv = ctx.priorChoice === "b" ? 1 : 0;
          const zf = makeArmy(sim, "shu", "spear", 2000, W * 0.42, H * 0.42, { general: "张飞", form: "yuan", morale: choice === "a" ? 100 : 90, level: lv });
          zf.order = "hold"; zf.anchor = { x: W * 0.42, y: H * 0.42 };
          makeArmy(sim, "shu", "cav", 1500, W * 0.44, H * 0.62, { general: "赵云", form: "feng", elite: true, morale: choice === "a" ? 100 : 90, level: lv });
          makeArmy(sim, "wei", "cav", 4500, W * 0.14, H * 0.4, { general: "曹纯", form: "feng" });
          makeArmy(sim, "wei", "inf", 6000, W * 0.1, H * 0.62, { general: "曹操" });
          makeArmy(sim, "wei", "cav", 2500, W * 0.3, H * 0.16, { general: "张辽", form: "feng" });
        },
        winText: "「子龙一身都是胆也！」玄德得脱，江东信使已在渡口相候。",
        loseText: "长坂喋血，玄德未能脱身。历史在此改写为悲歌……再试一次。",
        renown: 3,
      },
      {
        title: "第三回 · 赤壁",
        narration: "孙刘会盟，共拒曹操八十三万大军于赤壁。大江横陈，北军不习水战——半渡而击，一战定三分！",
        seed: 208, opts: { river: "wide-h", forestBias: 0.05 },
        allies: [pairKey("shu", "wu")],
        choices: [
          { id: "a", text: "借东风", desc: "联军弓兵获二级战阵加成，箭雨更烈" },
          { id: "b", text: "苦肉计", desc: "黄盖诈降：魏军全军士气开局−25" },
        ],
        objective: { type: "annihilate", desc: "与东吴联军共歼曹军" },
        setup(sim, ctx, choice) {
          const em = choice === "b" ? 65 : 90;
          makeArmy(sim, "wei", "inf", 13000, W * 0.42, H * 0.2, { general: "曹操", morale: em });
          makeArmy(sim, "wei", "cav", 5000, W * 0.62, H * 0.16, { general: "张辽", form: "feng", morale: em });
          makeArmy(sim, "wei", "arch", 5000, W * 0.3, H * 0.14, { general: "徐晃", morale: em });
          addBuilding(sim, "wei", "camp", W * 0.52, H * 0.1);
          const lvA = choice === "a" ? 2 : 0;
          const w1 = makeArmy(sim, "wu", "arch", 6000, W * 0.44, H * 0.78, { general: "周瑜", form: "yan", level: lvA });
          w1.order = "hold"; w1.anchor = { x: W * 0.44, y: H * 0.78 };
          const s1 = makeArmy(sim, "shu", "arch", 4500, W * 0.58, H * 0.76, { general: "诸葛亮", form: "yan", level: lvA });
          s1.order = "hold"; s1.anchor = { x: W * 0.58, y: H * 0.76 };
          const s2 = makeArmy(sim, "shu", "spear", 4000, W * 0.48, H * 0.7, { general: "赵云", form: "yuan" });
          s2.order = "hold"; s2.anchor = { x: W * 0.48, y: H * 0.7 };
          makeArmy(sim, "wu", "inf", 5000, W * 0.52, H * 0.88, { general: "黄盖" });
        },
        winText: "火光映天，樯橹灰飞烟灭。天下三分，自此而始。",
        loseText: "北军竟渡江成功，江东震恐。重整旗鼓，再守大江。",
        renown: 3,
      },
      {
        title: "第四回 · 定军山",
        narration: "汉中之役，黄忠请为先锋。夏侯渊恃勇轻出——法正登高观阵，以旗为号：斩夏侯渊者，定军山也！",
        seed: 8404, opts: { river: "none", mountainBias: 0.09, forestBias: 0.03 },
        choices: [
          { id: "a", text: "法正献策·据高", desc: "全军雁行据丘列阵，弓兵射程占尽地利" },
          { id: "b", text: "正面强攻", desc: "增派三千援军，以力破巧" },
        ],
        objective: { type: "slay", general: "夏侯渊", desc: "阵斩魏将夏侯渊" },
        setup(sim, ctx, choice) {
          const form = choice === "a" ? "yan" : "fang";
          const y1 = makeArmy(sim, "shu", "arch", 4000, W * 0.32, H * 0.36, { general: "黄忠", form });
          if (choice === "a") { y1.order = "hold"; y1.anchor = { x: W * 0.32, y: H * 0.36 }; }
          makeArmy(sim, "shu", "inf", 5000, W * 0.24, H * 0.56, { general: "刘备" });
          makeArmy(sim, "shu", "cav", 2500, W * 0.2, H * 0.72, { general: "赵云", form: "feng" });
          if (choice === "b") makeArmy(sim, "shu", "inf", 3000, W * 0.3, H * 0.66, { general: "魏延" });
          makeArmy(sim, "wei", "inf", 7000, W * 0.72, H * 0.44, { general: "夏侯渊" });
          makeArmy(sim, "wei", "spear", 4000, W * 0.78, H * 0.62, { general: "张郃", form: "yuan" });
          addBuilding(sim, "wei", "camp", W * 0.86, H * 0.5);
        },
        winText: "「当得其魁！」老将刀落，夏侯授首。汉中王之业，就此奠定。",
        loseText: "定军山未克，汉中相持。养精蓄锐，来年再战。",
        renown: 3,
      },
      {
        title: "终回 · 北伐长安",
        narration: "白帝托孤，星落五丈原之前——这一次，让北伐抵达终点。围而取之，让汉旗插上长安城头！",
        seed: 1013, opts: { river: "none", mountainBias: 0.04, clearRects: [{ xf: 0.24, yf: 0.34, w: 16, h: 14 }] },
        choices: [
          { id: "a", text: "子午谷奇谋", desc: "魏延三千轻骑直插城下，兵行险着" },
          { id: "b", text: "稳出祁山", desc: "步步为营：随军携一座兵营保障粮道" },
        ],
        objective: { type: "capture", building: "长安", desc: "围困并夺取长安城（围而易帜）" },
        setup(sim, ctx, choice) {
          const cx = W * 0.32, cy = H * 0.48;
          addBuilding(sim, "wei", "city", cx, cy, "长安");
          const d1 = makeArmy(sim, "wei", "arch", 4000, cx, cy - 60, { general: "曹真" });
          d1.order = "hold"; d1.anchor = { x: cx, y: cy - 60 };
          const d2 = makeArmy(sim, "wei", "spear", 5000, cx, cy + 70, { general: "司马懿", form: "yuan" });
          d2.order = "hold"; d2.anchor = { x: cx, y: cy + 70 };
          addBuilding(sim, "wei", "camp", cx + 110, cy - 40);
          makeArmy(sim, "shu", "inf", 8000, W - 140, H * 0.4, { general: "诸葛亮" });
          makeArmy(sim, "shu", "arch", 4000, W - 70, H * 0.55, { general: "姜维", form: "yan" });
          if (choice === "a") makeArmy(sim, "shu", "cav", 3000, cx + 150, cy + 120, { general: "魏延", form: "feng" });
          else {
            makeArmy(sim, "shu", "inf", 3000, W - 110, H * 0.7, { general: "魏延" });
            addBuilding(sim, "shu", "camp", W - 90, H * 0.82);
          }
        },
        winText: (c) => c === "a"
          ? "魏延轻骑直插城下，里应外合，长安一夜易帜！子午谷之险，终成奇功。桃园一梦，二十余年，至此圆满。——桃园之志·全卷终"
          : "步步为营，粮道无忧，长安在旷日围困下终于开城。汉旗终插长安！桃园一梦，二十余年，至此圆满。——桃园之志·全卷终",
        loseText: "长安坚不可摧，粮尽而退。出师未捷……但故事仍可重写。",
        renown: 4,
      },
    ],
  },
  caocao: {
    name: "乱世奸雄", role: "势力 · 魏", fac: "wei",
    desc: "宁教我负天下人——以曹孟德之谋，横扫群雄。",
    chapters: [
      {
        title: "第一回 · 官渡",
        narration: "袁绍十万众南下（袁军以吴色代），曹公兵不满三万。粮草为命脉——烧尽袁军粮营，则十万之众不战自溃！",
        seed: 5200, opts: { river: "v", forestBias: 0.02 },
        choices: [
          { id: "a", text: "奇袭乌巢", desc: "许攸来投：敌一座粮营开局即已半毁" },
          { id: "b", text: "坚壁相持", desc: "深沟高垒：己方多筑一座兵营" },
        ],
        objective: { type: "burncamps", desc: "焚毁袁军全部粮营" },
        setup(sim, ctx, choice) {
          addBuilding(sim, "wei", "camp", W * 0.16, H * 0.3);
          if (choice === "b") addBuilding(sim, "wei", "camp", W * 0.14, H * 0.7);
          makeArmy(sim, "wei", "inf", 6000, W * 0.22, H * 0.5, { general: "曹操" });
          makeArmy(sim, "wei", "cav", 3000, W * 0.18, H * 0.42, { general: "许褚", form: "feng" });
          makeArmy(sim, "wei", "arch", 2500, W * 0.24, H * 0.62, { general: "徐晃" });
          const c1 = addBuilding(sim, "wu", "camp", W * 0.86, H * 0.28, "乌巢");
          if (choice === "a") c1.hp = Math.round(c1.maxHp * 0.45);
          addBuilding(sim, "wu", "camp", W * 0.88, H * 0.72);
          makeArmy(sim, "wu", "inf", 11000, W * 0.76, H * 0.5, { general: "袁绍" });
          makeArmy(sim, "wu", "cav", 4000, W * 0.8, H * 0.36, { general: "颜良", form: "feng" });
        },
        winText: "乌巢火起，袁军十万一夜崩解。北方之主，易姓曹矣。",
        loseText: "粮营未破，相持粮尽，只得退兵许都。",
        renown: 3,
      },
      {
        title: "第二回 · 赤壁·华容道",
        narration: "赤壁火起，八十三万大军灰飞烟灭。败军之中，唯有护住曹公性命，方有卷土重来之日——率残部下「撤退」令，护送曹操自西侧华容小道杀出重围！",
        seed: 208, opts: { river: "wide-h", forestBias: 0.08 },
        allies: [pairKey("shu", "wu")],
        choices: [
          { id: "a", text: "走华容道", desc: "云长念旧：追兵少一路，然士气更低" },
          { id: "b", text: "强行突围", desc: "虎豹骑残部随行护驾" },
        ],
        objective: { type: "escort", desc: "护送曹操（下「撤退」令）自西侧边界突围" },
        protectGeneral: "曹操",
        setup(sim, ctx, choice) {
          // 首回"奇袭乌巢"得手的余威：曹军将士锐气未失
          const m = (choice === "a" ? 55 : 68) + (ctx.priorChoice === "a" ? 5 : 0);
          const cc = makeArmy(sim, "wei", "inf", 3500, W * 0.62, H * 0.28, { general: "曹操", morale: m });
          cc.retreatDir = -1; cc.lockRetreatDir = true;
          makeArmy(sim, "wei", "spear", 2000, W * 0.55, H * 0.2, { general: "张辽", form: "yuan", morale: m + 10 });
          if (choice === "b") makeArmy(sim, "wei", "cav", 1500, W * 0.66, H * 0.36, { general: "许褚", form: "feng", elite: true, morale: m + 10 });
          makeArmy(sim, "wu", "inf", 5000, W * 0.3, H * 0.34, { general: "周瑜" });
          makeArmy(sim, "wu", "cav", 2500, W * 0.2, H * 0.16, { general: "甘宁", form: "feng" });
          if (choice !== "a") makeArmy(sim, "shu", "cav", 2500, W * 0.36, H * 0.12, { general: "关羽", form: "feng" });
          makeArmy(sim, "shu", "inf", 3000, W * 0.14, H * 0.4, { general: "张飞" });
        },
        winText: "「若奉孝在，不使孤至此！」大笑三声，杀出重围。胜负未分，来日方长。",
        loseText: "曹公殒命乱军之中，天下大势就此改writ……重来，护住主公。",
        renown: 3,
      },
      {
        title: "第三回 · 汉中拒蜀",
        narration: "刘备倾国而来，争夺汉中。据关而守，耗其锐气——坚守一百息，蜀军粮尽自退！",
        seed: 3111, opts: { river: "none", mountainBias: 0.06, ridges: [{ colFrac: 0.44, gapFrac: 0.5, gapH: 6 }] },
        objective: { type: "survive", t: 100, desc: "坚守汉中一百息（守军存续即胜）" },
        choices: [
          { id: "a", text: "据关死守", desc: "全军圆阵坚守关前" },
          { id: "b", text: "以攻代守", desc: "留一支铁骑机动反击" },
        ],
        setup(sim, ctx, choice) {
          const px = W * 0.44, py = H * 0.5;
          addBuilding(sim, "wei", "pass", px, py, "阳平关");
          addBuilding(sim, "wei", "camp", W * 0.28, H * 0.36);
          const d1 = makeArmy(sim, "wei", "spear", 5000, px - 45, py, { general: "张郃", form: "yuan" });
          d1.order = "hold"; d1.anchor = { x: px - 45, y: py };
          const d2 = makeArmy(sim, "wei", "arch", 4500, px - 85, py, { general: "徐晃", form: "yan" });
          d2.order = "hold"; d2.anchor = { x: px - 85, y: py };
          if (choice === "b") makeArmy(sim, "wei", "cav", 2500, W * 0.24, H * 0.6, { general: "曹彰", form: "feng" });
          else {
            const d3 = makeArmy(sim, "wei", "inf", 2500, px - 60, py + 60, { general: "曹洪", form: "yuan" });
            d3.order = "hold"; d3.anchor = { x: px - 60, y: py + 60 };
          }
          makeArmy(sim, "shu", "inf", 7500, W * 0.8, H * 0.4, { general: "刘备" });
          makeArmy(sim, "shu", "cav", 3500, W * 0.86, H * 0.58, { general: "马超", form: "feng" });
          makeArmy(sim, "shu", "arch", 3500, W * 0.78, H * 0.68, { general: "黄忠", form: "yan" });
        },
        winText: "蜀军顿于坚关之下，锐气尽丧，粮尽而退。汉中虽失于史册，今日却固若金汤。",
        loseText: "关破军溃，汉中易主。「鸡肋」之叹，一语成谶。",
        renown: 3,
      },
      {
        title: "终回 · 天下归一",
        narration: "洛阳城下，蜀吴联军齐至，欲毕其功于一役。挡住这最后一浪，九州便再无敌手——横槊赋诗之日，就在今朝！",
        seed: 2077, opts: { river: "none", forestBias: 0.03, clearRects: [{ xf: 0.39, yf: 0.34, w: 18, h: 16 }] },
        allies: [pairKey("shu", "wu")],
        choices: [
          { id: "a", text: "御驾亲征", desc: "曹操亲临：全军开局一级战阵" },
          { id: "b", text: "深壁固城", desc: "洛阳外再筑一营，守军更众" },
        ],
        objective: { type: "annihilate", desc: "歼灭来犯的蜀吴联军" },
        setup(sim, ctx, choice) {
          const lv = choice === "a" ? 1 : 0;
          const cx = W / 2, cy = H / 2;
          addBuilding(sim, "wei", "city", cx, cy, "洛阳");
          if (choice === "b") addBuilding(sim, "wei", "camp", cx + 90, cy + 60);
          const g1 = makeArmy(sim, "wei", "inf", 7000, cx, cy + 62, { general: "曹操", form: "yuan", level: lv });
          g1.order = "hold"; g1.anchor = { x: cx, y: cy + 62 };
          const g2 = makeArmy(sim, "wei", "arch", 4500, cx, cy - 66, { general: "张辽", form: "yan", level: lv });
          g2.order = "hold"; g2.anchor = { x: cx, y: cy - 66 };
          makeArmy(sim, "wei", "cav", 3500, cx + 120, cy, { general: "许褚", form: "feng", level: lv });
          makeArmy(sim, "shu", "inf", 6000, 110, H * 0.5, { general: "刘备" });
          makeArmy(sim, "shu", "cav", 3000, 90, H * 0.72, { general: "张飞", form: "feng" });
          makeArmy(sim, "wu", "inf", 6000, W - 110, H * 0.5, { general: "孙权" });
          makeArmy(sim, "wu", "arch", 3500, W - 80, H * 0.3, { general: "陆逊", form: "yan" });
        },
        winText: (c) => c === "a"
          ? "曹公亲临阵前，全军士气如虹，联军尽没于洛阳城下。九州一统，山河改色。——乱世奸雄·全卷终"
          : "深壁高垒，敌师顿于坚城之下，终至溃灭。联军尽没于洛阳城下。九州一统，山河改色。——乱世奸雄·全卷终",
        loseText: "洛阳城破，霸业成空。青梅煮酒之约，终究未能兑现。",
        renown: 4,
      },
    ],
  },
  zhaoyun: {
    name: "常山赵子龙", role: "人物 · 赵云", fac: "shu",
    desc: "单枪匹马入万军——以一支五百人的白袍精骑，演武圣枪。",
    chapters: [
      {
        title: "第一回 · 长坂坡救主",
        narration: "乱军之中，幼主何在！子龙单骑往还，七进七出。以五百白袍精骑在曹军汪洋中血战八十息——枪在，人在。（精锐部队：主将近千血、全员悍勇）",
        seed: 8202, opts: { river: "none", forestBias: 0.06 },
        objective: { type: "survive", t: 80, desc: "于曹军重围中血战八十息，赵云不可阵亡" },
        protectGeneral: "赵云",
        setup(sim) {
          makeArmy(sim, "shu", "cav", 500, W * 0.5, H * 0.5, { general: "赵云", form: "feng", elite: true, morale: 100 });
          makeArmy(sim, "wei", "inf", 4500, W * 0.2, H * 0.35, { general: "曹仁" });
          makeArmy(sim, "wei", "inf", 4500, W * 0.78, H * 0.6, { general: "夏侯惇" });
          makeArmy(sim, "wei", "cav", 2500, W * 0.5, H * 0.12, { general: "曹纯", form: "feng" });
        },
        winText: "怀抱后主，直透重围。曹操于景山之巅叹曰：真虎将也！",
        loseText: "白袍染尽征尘……常山之名不可堕，再入重围！",
        renown: 3,
      },
      {
        title: "第二回 · 汉水空营",
        narration: "黄忠劫粮未归，子龙引数十骑出寨接应，正遇曹军大队。偃旗息鼓，大开营门——待敌半渡汉水掩杀之，此谓「一身是胆」。",
        seed: 8606, opts: { river: "v", forestBias: 0.03 },
        objective: { type: "annihilate", desc: "依营据守，歼灭渡水来犯的曹军" },
        protectGeneral: "赵云",
        choices: [
          { id: "a", text: "偃旗息鼓", desc: "空营诱敌：敌军轻进，士气−15" },
          { id: "b", text: "弓弩伏于寨后", desc: "多得一支两千人弓队" },
        ],
        setup(sim, ctx, choice) {
          addBuilding(sim, "shu", "camp", W * 0.78, H * 0.5);
          const zy = makeArmy(sim, "shu", "cav", 800, W * 0.7, H * 0.5, { general: "赵云", form: "feng", elite: true });
          zy.order = "hold"; zy.anchor = { x: W * 0.7, y: H * 0.5 };
          if (choice === "b") {
            const gw = makeArmy(sim, "shu", "arch", 2000, W * 0.82, H * 0.42, { general: "黄忠", form: "yan" });
            gw.order = "hold"; gw.anchor = { x: W * 0.82, y: H * 0.42 };
          }
          const em = choice === "a" ? 75 : 90;
          makeArmy(sim, "wei", "inf", 6000, W * 0.16, H * 0.42, { general: "曹操", morale: em });
          makeArmy(sim, "wei", "cav", 2500, W * 0.14, H * 0.62, { general: "张郃", form: "feng", morale: em });
        },
        winText: "曹军自相践踏，坠汉水者不计其数。「子龙一身都是胆也！」",
        loseText: "营寨失守，汉水尽赤。重整旗鼓，再摆空营。",
        renown: 3,
      },
      {
        title: "终回 · 凤鸣山",
        narration: "年登七十，犹为先锋。韩德父子五将齐出，欺我年迈——枪出如龙，教他知道常山赵子龙尚在！",
        seed: 8707, opts: { river: "none", mountainBias: 0.08 },
        objective: { type: "slay", general: "韩德", desc: "阵斩魏将韩德" },
        protectGeneral: "赵云",
        setup(sim) {
          makeArmy(sim, "shu", "cav", 1000, W * 0.24, H * 0.5, { general: "赵云", form: "feng", elite: true, morale: 100 });
          makeArmy(sim, "shu", "inf", 3000, W * 0.14, H * 0.62, { general: "邓芝" });
          makeArmy(sim, "wei", "inf", 5000, W * 0.7, H * 0.44, { general: "韩德" });
          makeArmy(sim, "wei", "cav", 2500, W * 0.78, H * 0.62, { general: "韩瑛", form: "feng" });
          addBuilding(sim, "wei", "camp", W * 0.88, H * 0.5);
        },
        winText: "力斩五将，威风不减当阳。——常山赵子龙·全卷终",
        loseText: "英雄亦有失手时。挺枪再战！",
        renown: 4,
      },
    ],
  },
  guanyu: {
    name: "武圣春秋", role: "人物 · 关羽", fac: "shu",
    desc: "青龙偃月，义薄云天——过五关、淹七军、走麦城，且看能否改写结局。",
    chapters: [
      {
        title: "第一回 · 千里走单骑",
        narration: "挂印封金，护二嫂寻兄。东岭关守将不放行——那便过关斩将！率本部护卫杀透关隘，下「撤退」令自东侧边界扬长而去。",
        seed: 8801, opts: { river: "none", mountainBias: 0.05, ridges: [{ colFrac: 0.55, gapFrac: 0.5, gapH: 6 }] },
        objective: { type: "escort", desc: "护送关羽（下「撤退」令）自东侧边界过关而去" },
        protectGeneral: "关羽",
        setup(sim) {
          const gy = makeArmy(sim, "shu", "cav", 800, W * 0.2, H * 0.5, { general: "关羽", form: "feng", elite: true, morale: 100 });
          gy.retreatDir = 1; gy.lockRetreatDir = true;
          addBuilding(sim, "wei", "pass", W * 0.55, H * 0.5, "东岭关");
          const d1 = makeArmy(sim, "wei", "spear", 2500, W * 0.5, H * 0.5, { general: "孔秀", form: "yuan" });
          d1.order = "hold"; d1.anchor = { x: W * 0.5, y: H * 0.5 };
          makeArmy(sim, "wei", "inf", 2500, W * 0.72, H * 0.4, { general: "韩福" });
        },
        winText: "五关既过，六将授首。「云长真义士也！」曹公遥望，长叹不追。",
        loseText: "关前折戟……青龙刀岂能停在这里？再来。",
        renown: 3,
      },
      {
        title: "第二回 · 水淹七军",
        narration: "襄樊之役，秋雨连绵，汉水暴涨。于禁七军屯于低洼——决堤放水，趁其半没于水泽而击之！（敌军已陷水泽，防御大减）",
        seed: 8902, opts: { river: "wide-h", forestBias: 0.02 },
        objective: { type: "annihilate", desc: "歼灭困于水泽的于禁七军" },
        protectGeneral: "关羽",
        choices: [
          { id: "a", text: "乘船掩杀", desc: "全军直取水中之敌" },
          { id: "b", text: "先射庞德", desc: "多得一支三千人弓队压阵" },
        ],
        setup(sim, ctx, choice) {
          makeArmy(sim, "shu", "inf", 5000, W * 0.4, H * 0.82, { general: "关羽", elite: true });
          makeArmy(sim, "shu", "cav", 2000, W * 0.56, H * 0.86, { general: "关平", form: "feng" });
          if (choice === "b") makeArmy(sim, "shu", "arch", 3000, W * 0.48, H * 0.9, { general: "周仓", form: "yan" });
          // 七军半没水中：直接摆在江面与北岸水线
          makeArmy(sim, "wei", "inf", 7000, W * 0.44, H * 0.5, { general: "于禁", morale: 70 });
          makeArmy(sim, "wei", "spear", 4000, W * 0.6, H * 0.44, { general: "庞德", morale: 80 });
          makeArmy(sim, "wei", "arch", 3000, W * 0.36, H * 0.3, { general: "曹仁" });
        },
        winText: "禽于禁，斩庞德，威震华夏。曹操几欲迁都以避其锋！",
        loseText: "水势未成，强攻受挫。待秋雨，再决堤。",
        renown: 3,
      },
      {
        title: "终回 · 麦城改命",
        narration: "吕蒙白衣渡江，荆州已失，四面楚歌。史册上，武圣陨落于此——但今日，率残部死守麦城一百二十息，等来上庸援军，改写这段悲歌！",
        seed: 9003, opts: { river: "none", forestBias: 0.07 },
        objective: { type: "survive", t: 120, desc: "死守麦城一百二十息，关羽不可有失" },
        protectGeneral: "关羽",
        choices: [
          { id: "a", text: "据城死守", desc: "麦城小城相依，圈内可休养" },
          { id: "b", text: "隘口设伏", desc: "周仓弓队伏于林间" },
        ],
        setup(sim, ctx, choice) {
          addBuilding(sim, "shu", "city", W * 0.5, H * 0.5, "麦城");
          const gy = makeArmy(sim, "shu", "inf", 2000, W * 0.5, H * 0.56, { general: "关羽", form: "yuan", elite: true });
          gy.order = "hold"; gy.anchor = { x: W * 0.5, y: H * 0.56 };
          const gp = makeArmy(sim, "shu", "spear", 1500, W * 0.5, H * 0.4, { general: "关平", form: "yuan" });
          gp.order = "hold"; gp.anchor = { x: W * 0.5, y: H * 0.4 };
          if (choice === "b") {
            const zc = makeArmy(sim, "shu", "arch", 1500, W * 0.42, H * 0.62, { general: "周仓", form: "yan" });
            zc.order = "hold"; zc.anchor = { x: W * 0.42, y: H * 0.62 };
          }
          makeArmy(sim, "wu", "inf", 5500, W * 0.14, H * 0.44, { general: "吕蒙" });
          makeArmy(sim, "wu", "cav", 2500, W * 0.2, H * 0.7, { general: "潘璋", form: "feng" });
          makeArmy(sim, "wei", "inf", 4000, W * 0.84, H * 0.4, { general: "徐晃" });
        },
        winText: (c) => c === "a"
          ? "麦城虽小，据守相依，伤兵得以喘息。援旗终现于山口！武圣不死，青史改书。——武圣春秋·全卷终"
          : "周仓弓队伏于林间，敌军来袭屡遭重创。援旗终现于山口！武圣不死，青史改书。——武圣春秋·全卷终",
        loseText: "麦城月冷，将星陨落……不。这一次，再守久一点。",
        renown: 5,
      },
    ],
  },
  sunce: {
    name: "江东孙策", role: "势力 · 吴", fac: "wu",
    desc: "以传国玉玺换兵三千，小霸王横扫江东，为三分天下立下东南一足。",
    chapters: [
      {
        title: "第一回 · 江东起兵",
        narration: "孙策以传国玉玺质于袁术，借兵三千，欲渡江平定江东。刘繇据守曲阿，兵多将广——初渡大江，正是立威之战。",
        seed: 9101, opts: { river: "wide-h", forestBias: 0.04 },
        choices: [
          { id: "a", text: "轻骑猛进", desc: "全军锋矢列阵，攻+18% 防-12%" },
          { id: "b", text: "广纳贤才", desc: "多得一支两千人乡勇相助" },
        ],
        objective: { type: "annihilate", desc: "歼灭刘繇军（借魏色代）" },
        setup(sim, ctx, choice) {
          const form = choice === "a" ? "feng" : "fang";
          makeArmy(sim, "wu", "inf", 3000, W * 0.18, H * 0.5, { general: "孙策", form });
          makeArmy(sim, "wu", "cav", 1500, W * 0.16, H * 0.34, { general: "程普", form: "feng" });
          makeArmy(sim, "wu", "arch", 1500, W * 0.16, H * 0.66, { general: "黄盖", form: "yan" });
          if (choice === "b") makeArmy(sim, "wu", "inf", 2000, W * 0.24, H * 0.5, { general: "乡勇" });
          makeArmy(sim, "wei", "inf", 5000, W * 0.78, H * 0.42, { general: "刘繇" });
          makeArmy(sim, "wei", "inf", 3000, W * 0.8, H * 0.62, { general: "樊能" });
        },
        winText: "刘繇势蹙，弃城而走。孙郎年少，江东已闻其名。",
        loseText: "初战不利，然基业未失。整军再渡，江东仍在望。",
        renown: 2,
      },
      {
        title: "第二回 · 神亭酣战",
        narration: "神亭岭下，孙策单骑遭遇太史慈，酣战竟日不分胜负。刘繇势蹙，此战过后，江东再无劲敌——只是这位猛将，是杀是留？",
        seed: 9202, opts: { river: "none", mountainBias: 0.05 },
        choices: [
          { id: "a", text: "以诚相待", desc: "力克之后以礼相待，太史慈率部来投" },
          { id: "b", text: "力克强敌", desc: "本部将士死战，全军开局一级战阵" },
        ],
        objective: { type: "annihilate", desc: "力克刘繇残部于神亭岭下" },
        setup(sim, ctx, choice) {
          const lv = choice === "b" ? 1 : 0;
          makeArmy(sim, "wu", "cav", 1000, W * 0.22, H * 0.5, { general: "孙策", form: "feng", elite: true, level: lv });
          makeArmy(sim, "wu", "inf", 2500, W * 0.16, H * 0.64, { general: "程普", level: lv });
          makeArmy(sim, "wei", "cav", 2000, W * 0.7, H * 0.42, { general: "太史慈", form: "feng" });
          makeArmy(sim, "wei", "inf", 3500, W * 0.76, H * 0.62, { general: "刘繇" });
        },
        winText: (c) => c === "a"
          ? "神亭岭上，酣战竟日。孙策以诚相待，太史慈慨然纳降：「愿从明主，以死相报！」"
          : "神亭岭上，酣战竟日。孙策力克强敌，三军士气大振，刘繇残部望风而溃！",
        loseText: "神亭鏖战，未能竟功。收拾残部，再图后举。",
        renown: 3,
      },
      {
        title: "第三回 · 赤壁鏖兵",
        narration: "曹操挥师南下，号称八十三万众，兵临赤壁。周瑜都督三军，联刘拒曹——这一次，让江东的一把火，烧透北军连营。",
        seed: 208, opts: { river: "wide-h", forestBias: 0.05 },
        allies: [pairKey("wu", "shu")],
        choices: [
          { id: "a", text: "苦肉计", desc: "黄盖诈降：魏军开局士气−25" },
          { id: "b", text: "连环巧计", desc: "庞统献策，敌军连营已半毁" },
        ],
        objective: { type: "burncamps", desc: "焚毁曹军连营（水寨以魏营代）" },
        setup(sim, ctx, choice) {
          const em = choice === "a" ? 65 : 90;
          makeArmy(sim, "wei", "inf", 12000, W * 0.42, H * 0.2, { general: "曹操", morale: em });
          makeArmy(sim, "wei", "cav", 4500, W * 0.6, H * 0.16, { general: "张郃", form: "feng", morale: em });
          makeArmy(sim, "wei", "arch", 4500, W * 0.28, H * 0.14, { general: "许褚", morale: em });
          const c1 = addBuilding(sim, "wei", "camp", W * 0.5, H * 0.1);
          const c2 = addBuilding(sim, "wei", "camp", W * 0.36, H * 0.08);
          if (choice === "b") { c1.hp = Math.round(c1.maxHp * 0.5); c2.hp = Math.round(c2.maxHp * 0.5); }
          const w1 = makeArmy(sim, "wu", "arch", 6500, W * 0.46, H * 0.78, { general: "周瑜", form: "yan" });
          w1.order = "hold"; w1.anchor = { x: W * 0.46, y: H * 0.78 };
          makeArmy(sim, "wu", "cav", 3000, W * 0.56, H * 0.82, { general: "甘宁", form: "feng" });
          const s1 = makeArmy(sim, "shu", "arch", 4000, W * 0.6, H * 0.76, { general: "诸葛亮", form: "yan" });
          s1.order = "hold"; s1.anchor = { x: W * 0.6, y: H * 0.76 };
          makeArmy(sim, "wu", "inf", 5000, W * 0.5, H * 0.9, { general: "黄盖" });
          // 若上一章"以诚相待"收服太史慈，此战他率部来援
          if (ctx.priorChoice === "a") {
            const tsc = makeArmy(sim, "wu", "cav", 2500, W * 0.38, H * 0.82, { general: "太史慈", form: "feng" });
            tsc.order = "hold"; tsc.anchor = { x: W * 0.38, y: H * 0.82 };
            log(sim, "太史慈感于知遇之恩，率部前来助阵！");
          }
        },
        winText: "火光冲天，连营尽毁。曹操仓皇北还，江东威名，自此传遍天下。",
        loseText: "火计未成，北军渡江在即。重整旗鼓，再候东风。",
        renown: 3,
      },
      {
        title: "第四回 · 合肥拒曹",
        narration: "孙权继位，曹操屡犯合肥。凭坚城、拒守待援——护住这份基业，待援兵抵达之日，便是曹军退兵之时。",
        seed: 9404, opts: { river: "none", mountainBias: 0.02, forestBias: 0.04 },
        choices: [
          { id: "a", text: "甘宁百骑劫营", desc: "开局奇袭敌营，敌军士气−20" },
          { id: "b", text: "凌统死战不退", desc: "全军士气+10，据城更稳" },
        ],
        objective: { type: "survive", t: 100, desc: "坚守合肥一百息（守军存续即胜）" },
        setup(sim, ctx, choice) {
          const em = choice === "a" ? 70 : 90;
          const morale = choice === "b" ? 100 : 90;
          addBuilding(sim, "wu", "city", W * 0.36, H * 0.5, "合肥");
          const g1 = makeArmy(sim, "wu", "spear", 5000, W * 0.36, H * 0.56, { general: "凌统", form: "yuan", morale });
          g1.order = "hold"; g1.anchor = { x: W * 0.36, y: H * 0.56 };
          const g2 = makeArmy(sim, "wu", "arch", 4000, W * 0.36, H * 0.42, { general: "甘宁", form: "yan", morale });
          g2.order = "hold"; g2.anchor = { x: W * 0.36, y: H * 0.42 };
          makeArmy(sim, "wei", "inf", 7000, W * 0.82, H * 0.44, { general: "张辽", morale: em });
          makeArmy(sim, "wei", "cav", 3000, W * 0.86, H * 0.6, { general: "乐进", form: "feng", morale: em });
        },
        winText: "曹军久攻不克，粮尽而退。合肥依旧，江东根基已固。",
        loseText: "合肥失守，然江东根本未动。再聚兵马，卷土重来。",
        renown: 4,
      },
      {
        title: "终回 · 江东鼎立",
        narration: "建业城下，曹军大举来犯——这是最后一战。挡住这一浪，三分天下的格局，便就此写定。",
        seed: 9505, opts: { river: "wide-h", forestBias: 0.03 },
        choices: [
          { id: "a", text: "水陆并进", desc: "楼船助阵：全军开局一级战阵" },
          { id: "b", text: "深沟高垒", desc: "另筑一营，粮草无忧" },
        ],
        objective: { type: "annihilate", desc: "尽歼来犯魏军，江东根基永固" },
        setup(sim, ctx, choice) {
          const lv = choice === "a" ? 1 : 0;
          addBuilding(sim, "wu", "city", W * 0.72, H * 0.5, "建业");
          if (choice === "b") addBuilding(sim, "wu", "camp", W * 0.8, H * 0.36);
          const g1 = makeArmy(sim, "wu", "inf", 7000, W * 0.72, H * 0.56, { general: "孙权", form: "yuan", level: lv });
          g1.order = "hold"; g1.anchor = { x: W * 0.72, y: H * 0.56 };
          const g2 = makeArmy(sim, "wu", "arch", 4500, W * 0.72, H * 0.42, { general: "陆逊", form: "yan", level: lv });
          g2.order = "hold"; g2.anchor = { x: W * 0.72, y: H * 0.42 };
          makeArmy(sim, "wu", "cav", 3500, W * 0.6, H * 0.5, { general: "周泰", form: "feng", level: lv });
          makeArmy(sim, "wei", "inf", 8000, 110, H * 0.4, { general: "曹操" });
          makeArmy(sim, "wei", "cav", 4000, 90, H * 0.65, { general: "张辽", form: "feng" });
        },
        winText: (c) => c === "a"
          ? "楼船蔽江，水陆并进，曹军望风而溃。三分天下，江东终成鼎足之势！——江东孙策·全卷终"
          : "深沟高垒，粮草无忧，曹军顿于坚城之下，终至溃退。三分天下，江东终成鼎足之势！——江东孙策·全卷终",
        loseText: "建业城下，一时受挫。然基业未失，来日方长——三分之势，仍待再定。",
        renown: 5,
      },
    ],
  },
};

// 故事扩展章节：用统一的章节模板补充战场内容，仍然支持选择、目标、声望和前章选择传递。
function makeStoryExpansionChapter(line, spec) {
  const playerFac = line.fac;
  const enemyFac = spec.enemy || (playerFac === "shu" ? "wei" : "shu");
  const playerGen = spec.playerGen || GENERALS[playerFac][0];
  const enemyGen = spec.enemyGen || GENERALS[enemyFac][0];
  const objective = spec.objective || { type: "annihilate", desc: `击溃${FACTIONS[enemyFac].name}军` };
  const px = playerFac === "wei" ? W * 0.22 : W * 0.76;
  const ex = playerFac === "wei" ? W * 0.78 : W * 0.22;
  return {
    title: spec.title,
    narration: spec.narration,
    seed: spec.seed,
    opts: spec.opts || { river: "none", forestBias: 0.04 },
    choices: [
      { id: "a", text: spec.choiceA || "稳扎稳打", desc: "主将部队开局士气+10，阵形更稳" },
      { id: "b", text: spec.choiceB || "奇兵出击", desc: "增派一支两千五百人的机动部队" },
    ],
    objective,
    protectGeneral: objective.type === "survive" || objective.type === "escort" ? playerGen : undefined,
    setup(sim, ctx, choice) {
      const morale = choice === "a" ? 100 : 90;
      const lv = choice === "a" ? 1 : 0;
      if (objective.type === "capture") addBuilding(sim, enemyFac, "city", ex, H * 0.5, objective.building || spec.city || "敌城");
      if (objective.type === "survive") addBuilding(sim, playerFac, "city", px, H * 0.5, spec.city || "我方城寨");
      const p = makeArmy(sim, playerFac, spec.playerType || "inf", spec.playerMen || 6500, px, H * 0.5, { general: playerGen, morale, level: lv, form: spec.playerForm || "fang" });
      if (objective.type === "survive") { p.order = "hold"; p.anchor = { x: px, y: H * 0.5 }; }
      makeArmy(sim, playerFac, spec.playerType2 || "cav", spec.playerMen2 || 2800, px + (playerFac === "wei" ? 35 : -35), H * 0.68, { general: spec.playerGen2 || GENERALS[playerFac][1], form: "feng", morale });
      if (choice === "b") makeArmy(sim, playerFac, "arch", 2500, px, H * 0.3, { general: spec.playerGen3 || GENERALS[playerFac][2], form: "yan" });
      const e = makeArmy(sim, enemyFac, spec.enemyType || "inf", spec.enemyMen || 8500, ex, H * 0.46, { general: enemyGen, morale: choice === "a" ? 86 : 92, form: spec.enemyForm || "fang" });
      if (objective.type === "capture") { e.order = "hold"; e.anchor = { x: ex, y: H * 0.46 }; }
      makeArmy(sim, enemyFac, spec.enemyType2 || "arch", spec.enemyMen2 || 4200, ex + (playerFac === "wei" ? -35 : 35), H * 0.66, { general: spec.enemyGen2 || GENERALS[enemyFac][1], form: "yan" });
      if (spec.allies) sim.allies = [...spec.allies];
      if (ctx.priorChoice === "a") log(sim, "📜 前章选择延续：军心因旧日声望而更加稳固");
    },
    winText: spec.winText || `${spec.title}告捷，${line.name}的旗帜又向天下更近一步。`,
    loseText: spec.loseText || `${spec.title}失利，但主公基业未毁，来日仍可再战。`,
    renown: spec.renown || 3,
  };
}
function extendStoryLine(key, specs) {
  const line = STORY[key];
  if (!line || line.chapters.length >= 10) return;
  const oldFinal = line.chapters[line.chapters.length - 1];
  oldFinal.title = oldFinal.title.replace(/^终回/, `第${line.chapters.length}回`);
  for (const spec of specs) line.chapters.push(makeStoryExpansionChapter(line, spec));
}
extendStoryLine("liubei", [
  { title: "第六回 · 夷陵雪恨", narration: "夷陵火起，陆逊连营。刘备收拢残兵，借山林反击，夺回失去的荆州门户。", seed: 10101, opts: { river: "wide-h", forestBias: 0.14 }, enemy: "wu", playerGen: "刘备", enemyGen: "陆逊", playerType: "inf", objective: { type: "annihilate", desc: "击溃陆逊率领的吴军" }, choiceA: "稳住阵脚", choiceB: "火线反攻" },
  { title: "第七回 · 南中安民", narration: "南中诸部再起，蜀汉不能只靠刀兵。平定叛乱、护住粮道，方能让益州真正成为北伐根基。", seed: 10202, opts: { river: "v", mountainBias: 0.16, forestBias: 0.16 }, playerGen: "诸葛亮", enemy: "wu", enemyGen: "甘宁", objective: { type: "survive", t: 100, desc: "守住南中城寨一百息" }, city: "南中城", choiceA: "安抚诸部", choiceB: "分兵巡山" },
  { title: "第八回 · 祁山粮道", narration: "北伐再出祁山，魏军断我粮道。姜维率轻骑护送辎重，必须在山口失守前击退追兵。", seed: 10303, opts: { river: "none", mountainBias: 0.14 }, playerGen: "姜维", enemy: "wei", enemyGen: "张郃", objective: { type: "survive", t: 110, desc: "护住祁山粮寨一百一十息" }, city: "祁山粮寨", choiceA: "车阵护粮", choiceB: "轻骑袭后" },
  { title: "第九回 · 陈仓夜袭", narration: "陈仓城高沟深，强攻必伤亡惨重。诸葛亮借夜色分兵，务求在援军抵达前夺下城门。", seed: 10404, opts: { river: "none", mountainBias: 0.1, ridges: [{ colFrac: 0.5, gapFrac: 0.45, gapH: 5 }] }, playerGen: "诸葛亮", enemy: "wei", enemyGen: "司马懿", objective: { type: "capture", building: "陈仓", desc: "围困并夺取陈仓城" }, choiceA: "正面牵制", choiceB: "夜袭城门" },
  { title: "终回 · 兴复汉室", narration: "汉中、陇右、长安，最后的汉旗只差一座城。诸葛亮、姜维与五虎旧部齐聚，今日定要让兴复汉室不再只是遗愿。", seed: 10505, opts: { river: "none", mountainBias: 0.07 }, playerGen: "刘备", enemy: "wei", enemyGen: "司马懿", objective: { type: "capture", building: "长安", desc: "攻克长安，完成兴复汉室" }, choiceA: "六军并进", choiceB: "子午奇谋", renown: 5, winText: "长安城头汉旗重张，桃园旧誓终于有了回应。——桃园之志·全卷终" },
]);
extendStoryLine("caocao", [
  { title: "第五回 · 辽东远征", narration: "北方未靖，公孙氏据辽东自重。曹公命司马懿远征海东，千里孤军不可失于粮道。", seed: 10606, opts: { river: "none", mountainBias: 0.12, forestBias: 0.1 }, playerGen: "司马懿", enemy: "shu", enemyGen: "魏延", objective: { type: "capture", building: "襄平", desc: "夺取襄平城" }, choiceA: "稳步推进", choiceB: "骑兵奔袭" },
  { title: "第六回 · 合肥破吴", narration: "孙权再举江东之众，合肥成为魏国南门。张辽请以精骑突击，守住淮南咽喉。", seed: 10707, opts: { river: "v", forestBias: 0.03 }, playerGen: "张辽", enemy: "wu", enemyGen: "孙权", objective: { type: "annihilate", desc: "击退合肥来犯吴军" }, choiceA: "逍遥津突击", choiceB: "据关反击" },
  { title: "第七回 · 汉中决战", narration: "蜀军再出汉中，曹真与张郃据险相持。此战若败，关中门户尽开。", seed: 10808, opts: { river: "none", mountainBias: 0.15 }, playerGen: "曹真", enemy: "shu", enemyGen: "姜维", objective: { type: "survive", t: 110, desc: "守住阳平关一百一十息" }, city: "阳平关", choiceA: "坚壁清野", choiceB: "主动出击" },
  { title: "第八回 · 五丈原", narration: "诸葛亮病势已深，蜀军仍压五丈原。司马懿不求速胜，只求拖住汉军锋芒。", seed: 10909, opts: { river: "none", mountainBias: 0.08 }, playerGen: "司马懿", enemy: "shu", enemyGen: "诸葛亮", objective: { type: "survive", t: 120, desc: "坚守营垒一百二十息" }, city: "五丈原营", choiceA: "闭门不战", choiceB: "夜袭蜀营" },
  { title: "第九回 · 蜀道争锋", narration: "邓艾取阴平，钟会出剑阁，蜀汉最后的险关正在崩裂。集中兵力，夺下成都外围。", seed: 11010, opts: { river: "none", mountainBias: 0.18, forestBias: 0.14 }, playerGen: "邓艾", enemy: "shu", enemyGen: "姜维", objective: { type: "capture", building: "成都", desc: "攻克成都外围城寨" }, choiceA: "剑阁正攻", choiceB: "阴平奇袭" },
  { title: "终回 · 九洲归魏", narration: "蜀汉已平，吴国孤悬江东。洛阳受禅之前，曹魏必须挡住最后的联军反扑，完成北方霸业。", seed: 11111, opts: { river: "wide-h", forestBias: 0.04 }, playerGen: "曹操", enemy: "wu", enemyGen: "周瑜", objective: { type: "annihilate", desc: "歼灭最后的吴军联军" }, choiceA: "水陆并进", choiceB: "深沟高垒", renown: 5, winText: "九州归于魏旗下，洛阳宫阙见证了乱世终章。——乱世奸雄·全卷终" },
]);
extendStoryLine("zhaoyun", [
  { title: "第四回 · 博望救援", narration: "刘备新野兵少，曹军大举南下。赵云引骑兵穿过博望坡，救出被围的友军。", seed: 11212, opts: { river: "none", forestBias: 0.1 }, playerGen: "赵云", enemy: "wei", enemyGen: "夏侯惇", objective: { type: "annihilate", desc: "击溃博望坡曹军" }, choiceA: "诱敌入林", choiceB: "白马突击" },
  { title: "第五回 · 长坂再会", narration: "长坂旧地，曹军铁骑再次围来。赵云护送百姓与幼主，不能让旧日悲剧重演。", seed: 11313, opts: { river: "none", forestBias: 0.08 }, playerGen: "赵云", enemy: "wei", enemyGen: "曹纯", objective: { type: "survive", t: 100, desc: "护住刘备军寨一百息" }, city: "长坂寨", choiceA: "护民结阵", choiceB: "骑阵冲锋" },
  { title: "第六回 · 汉中突围", narration: "汉中粮道被断，黄忠老将身陷重围。赵云必须在敌军合围前凿穿山口。", seed: 11414, opts: { river: "none", mountainBias: 0.16 }, playerGen: "赵云", enemy: "wei", enemyGen: "张郃", objective: { type: "escort", desc: "护送赵云军团自东侧边界突围" }, choiceA: "接应黄忠", choiceB: "轻装快进" },
  { title: "第七回 · 祁山护粮", narration: "蜀军大营缺粮，魏骑兵日夜袭扰。赵云以少量精骑护住粮车，等待援军到达。", seed: 11515, opts: { river: "none", mountainBias: 0.13 }, playerGen: "赵云", enemy: "wei", enemyGen: "张辽", objective: { type: "survive", t: 115, desc: "守住粮寨一百一十五息" }, city: "祁山粮寨", choiceA: "车阵守粮", choiceB: "主动追击" },
  { title: "第八回 · 天水破阵", narration: "天水姜维初显锋芒，赵云奉命试其胆略。两军相逢，既要破阵，也要留住这位少年英才。", seed: 11616, opts: { river: "v", mountainBias: 0.08 }, playerGen: "赵云", enemy: "wei", enemyGen: "姜维", objective: { type: "annihilate", desc: "击溃天水守军" }, choiceA: "以礼招降", choiceB: "全力破阵" },
  { title: "第九回 · 剑阁守关", narration: "钟会大军逼近剑阁，蜀汉危在旦夕。赵云虽老，仍愿替后辈守住最后一道门。", seed: 11717, opts: { river: "none", mountainBias: 0.2, ridges: [{ colFrac: 0.52, gapFrac: 0.5, gapH: 5 }] }, playerGen: "赵云", enemy: "wei", enemyGen: "钟会", objective: { type: "survive", t: 130, desc: "守住剑阁一百三十息" }, city: "剑阁", choiceA: "据险坚守", choiceB: "出关决战" },
  { title: "终回 · 常山无双", narration: "从长坂到剑阁，白袍走过半生烽火。最后一战，赵云要让天下记住：常山赵子龙，永远在阵前。", seed: 11818, opts: { river: "none", mountainBias: 0.1 }, playerGen: "赵云", enemy: "wei", enemyGen: "张辽", objective: { type: "slay", general: "张辽", desc: "阵斩张辽" }, choiceA: "单骑挑战", choiceB: "白袍齐射", renown: 5, winText: "枪锋所指，万军辟易。常山赵子龙之名，成为后世不灭的传奇。——常山赵子龙·全卷终" },
]);
extendStoryLine("guanyu", [
  { title: "第四回 · 徐州再起", narration: "曹公厚待，云长仍念旧主。徐州故地再逢曹军，青龙刀必须护住百姓与旧部。", seed: 11919, opts: { river: "v", forestBias: 0.04 }, playerGen: "关羽", enemy: "wei", enemyGen: "夏侯惇", objective: { type: "escort", desc: "护送关羽自东侧边界突围" }, choiceA: "护民慢行", choiceB: "轻骑先走" },
  { title: "第五回 · 官渡护粮", narration: "袁曹决战之际，关羽暂居曹营。乌巢粮道告急，选择义与功，皆在一念之间。", seed: 12020, opts: { river: "v", forestBias: 0.06 }, playerGen: "关羽", enemy: "wu", enemyGen: "颜良", objective: { type: "annihilate", desc: "击溃截粮敌军" }, choiceA: "直取颜良", choiceB: "护住粮寨" },
  { title: "第六回 · 长坂护民", narration: "荆州百姓南迁，曹军追兵已近。关羽率水陆两军护住渡口，给百姓争一线生机。", seed: 12121, opts: { river: "wide-h", forestBias: 0.08 }, playerGen: "关羽", enemy: "wei", enemyGen: "曹仁", objective: { type: "survive", t: 105, desc: "守住渡口一百零五息" }, city: "渡口营", choiceA: "结阵护渡", choiceB: "主动截击" },
  { title: "第七回 · 荆州拒吴", narration: "孙权遣吕蒙窥伺荆州，关羽必须在援军抵达前守住江陵。", seed: 12222, opts: { river: "wide-h", forestBias: 0.05 }, playerGen: "关羽", enemy: "wu", enemyGen: "吕蒙", objective: { type: "capture", building: "江陵", desc: "夺回江陵城" }, choiceA: "水陆夹击", choiceB: "夜袭北门" },
  { title: "第八回 · 襄阳水战", narration: "汉水暴涨，关羽借水势反攻襄阳。庞德铁骑横江，正是青龙与白马的决战。", seed: 12323, opts: { river: "wide-h", forestBias: 0.03 }, playerGen: "关羽", enemy: "wei", enemyGen: "庞德", objective: { type: "slay", general: "庞德", desc: "阵斩庞德" }, choiceA: "水淹七军", choiceB: "弓弩压阵" },
  { title: "第九回 · 益州会师", narration: "西川援军终于抵达，关羽与刘备军会师。击退最后一支魏军，荆益连成一片。", seed: 12424, opts: { river: "none", mountainBias: 0.12 }, playerGen: "关羽", enemy: "wei", enemyGen: "徐晃", objective: { type: "annihilate", desc: "击溃魏军援军" }, choiceA: "义军并进", choiceB: "关门设伏" },
  { title: "终回 · 武圣不死", narration: "麦城月冷，四面楚歌。但今日援旗已至，关羽不再走向败亡，而要以青龙刀斩开命运。", seed: 12525, opts: { river: "none", forestBias: 0.08 }, playerGen: "关羽", enemy: "wu", enemyGen: "吕蒙", objective: { type: "survive", t: 135, desc: "守住麦城一百三十五息，关羽不可阵亡" }, city: "麦城", choiceA: "据城死守", choiceB: "突围反击", renown: 5, winText: "援军破围，武圣不死。麦城之后，青龙刀仍为汉室照亮一条路。——武圣春秋·全卷终" },
]);
extendStoryLine("sunce", [
  { title: "第六回 · 江夏争夺", narration: "江夏扼守长江中游，曹军与荆州势力皆欲夺之。孙策必须抢在北军之前立足江夏。", seed: 12626, opts: { river: "wide-h", forestBias: 0.05 }, playerGen: "孙策", enemy: "wei", enemyGen: "曹仁", objective: { type: "capture", building: "江夏", desc: "夺取江夏城" }, choiceA: "水军先登", choiceB: "陆路突袭" },
  { title: "第七回 · 合肥突围", narration: "吴军深入淮南，张辽闭关反击。孙权与甘宁必须杀出合肥重围，保住江东精锐。", seed: 12727, opts: { river: "v", forestBias: 0.04 }, playerGen: "孙权", enemy: "wei", enemyGen: "张辽", objective: { type: "escort", desc: "护送孙权自东侧边界突围" }, choiceA: "甘宁断后", choiceB: "水路疾行" },
  { title: "第八回 · 荆州水战", narration: "吕蒙白衣渡江之前，吴军先要在荆州水面击退关羽前锋。江风与战鼓同起。", seed: 12828, opts: { river: "wide-h", forestBias: 0.04 }, playerGen: "吕蒙", enemy: "shu", enemyGen: "关羽", objective: { type: "annihilate", desc: "击溃荆州蜀军前锋" }, choiceA: "白衣渡江", choiceB: "火船突袭" },
  { title: "第九回 · 夷陵援军", narration: "陆逊火烧连营，刘备残军退守山口。吴军要追击到底，还是先稳住夷陵门户？", seed: 12929, opts: { river: "wide-h", forestBias: 0.14 }, playerGen: "陆逊", enemy: "shu", enemyGen: "刘备", objective: { type: "survive", t: 115, desc: "守住夷陵城寨一百一十五息" }, city: "夷陵城寨", choiceA: "火攻连营", choiceB: "稳守江口" },
  { title: "终回 · 江东万里", narration: "曹魏大军南下，晋吴终局将至。建业城头，孙权与陆逊要为江东写下最后的鼎立篇章。", seed: 13030, opts: { river: "wide-h", forestBias: 0.06 }, playerGen: "孙权", enemy: "wei", enemyGen: "司马懿", objective: { type: "annihilate", desc: "击退南下魏军，保住建业" }, choiceA: "水陆并进", choiceB: "深沟高垒", renown: 5, winText: "江东万里，楼船蔽江。虽世事终有更替，孙吴的旗帜已在长江上留下永恒回声。——江东孙策·全卷终" },
]);

/* ---------- 天下模式 (grand campaign) ---------- */
const GRAND_KEY = "sanguo:grand-v2";
const GRAND_LEGACY_KEY = "sanguo:grand-v1";
// 女将名录（可俘获、劝降、收留、求亲）
const FEMALE_BY_FAC = {
  wei: ["张春华", "王异", "蔡文姬"],
  shu: ["黄月英", "鲍三娘", "祝融夫人"],
  wu:  ["孙尚香", "大乔", "小乔"],
  lvbu: ["貂蝉"],
  yuan: ["甄宓"],
  dong: ["董白"],
  liubiao: ["蔡夫人"],
  kongrong: [],
  yuanshu: [],
  gongsun: [],
  mateng: [],
  zhanglu: ["张琪瑛"],
  liuzhang: [],
  taoqian: [],
  zhangxiu: ["邹氏"],
};
// 仅用于武将档案中的婚配展示与外交剧情；不影响战斗数值。
const HISTORICAL_MARRIAGES = {
  "张春华": "司马懿", "王异": "赵昂", "蔡文姬": "董祀",
  "黄月英": "诸葛亮", "鲍三娘": "关索", "祝融夫人": "孟获",
  "孙尚香": "刘备", "大乔": "孙策", "小乔": "周瑜",
  "貂蝉": "吕布", "甄宓": "曹丕",
};
// 名册扩充：保留核心历史人物，再用汉末常见姓氏与名字补足可管理武将。
// 这些补充人物使用稳定姓名，因而编号、存档和招募结果不会随刷新变化。
const EXTRA_SURNAMES = ["安", "白", "鲍", "毕", "曹", "常", "成", "崔", "邓", "杜", "范", "方", "冯", "傅", "高", "龚", "顾", "郭", "韩", "何", "洪", "胡", "贾", "蒋", "金", "康", "孔", "雷", "黎", "梁", "廖", "凌", "鲁", "罗", "马", "毛", "孟", "米", "潘", "彭", "蒲", "秦", "沈", "石", "史", "苏", "孙", "陶", "田", "童", "万", "韦", "卫", "魏", "温", "伍", "谢", "熊", "许", "严", "杨", "尹", "余", "俞", "袁", "岳", "曾", "翟", "章", "赵", "郑", "钟", "周", "朱"];
const EXTRA_MALE_GIVEN = ["安", "伯", "承", "成", "德", "定", "方", "刚", "广", "浩", "弘", "宏", "济", "建", "景", "君", "康", "朗", "烈", "明", "平", "鹏", "启", "仁", "荣", "胜", "盛", "泰", "威", "文", "武", "雄", "宣", "彦", "义", "勇", "远", "昭", "忠", "仲"];
const EXTRA_FEMALE_GIVEN = ["安宁", "宝珠", "采薇", "春兰", "芳华", "凤仪", "桂英", "含章", "荷香", "惠心", "静姝", "兰芝", "丽华", "明珠", "清妍", "秋月", "柔嘉", "淑慧", "素琴", "婉仪", "文姬", "香君", "晓月", "秀兰", "雅琴", "玉华", "昭容", "贞娘", "芷若", "紫烟"];
function makeStableExtraNames(count, used, given, salt) {
  const names = [];
  let cursor = 0;
  while (names.length < count) {
    const surname = EXTRA_SURNAMES[(cursor + salt) % EXTRA_SURNAMES.length];
    const givenName = given[Math.floor((cursor + salt) / EXTRA_SURNAMES.length) % given.length];
    const name = `${surname}${givenName}`;
    cursor++;
    if (used.has(name)) continue;
    used.add(name); names.push(name);
  }
  return names;
}
const seededFemaleNames = new Set(Object.values(FEMALE_BY_FAC).flat());
const seededMaleNames = new Set(Object.values(GENERALS).flat().filter(name => !seededFemaleNames.has(name)));
const EXTRA_MALE_NAMES = makeStableExtraNames(Math.max(0, 300 - seededMaleNames.size), new Set([...seededMaleNames, ...seededFemaleNames]), EXTRA_MALE_GIVEN, 3);
const EXTRA_FEMALE_NAMES = makeStableExtraNames(Math.max(0, 200 - seededFemaleNames.size), new Set([...seededMaleNames, ...seededFemaleNames, ...EXTRA_MALE_NAMES]), EXTRA_FEMALE_GIVEN, 17);
for (const [i, name] of EXTRA_MALE_NAMES.entries()) {
  const rosterFactions = Object.keys(GENERALS);
  const fac = rosterFactions[i % rosterFactions.length];
  GENERALS[fac].push(name);
}
for (const [i, name] of EXTRA_FEMALE_NAMES.entries()) {
  const rosterFactions = Object.keys(FEMALE_BY_FAC);
  const fac = rosterFactions[i % rosterFactions.length];
  FEMALE_BY_FAC[fac].push(name);
}
const GENERAL_ROSTER_COUNTS = { male: seededMaleNames.size + EXTRA_MALE_NAMES.length, female: seededFemaleNames.size + EXTRA_FEMALE_NAMES.length };
// 城池陷落后可收押的敌方家眷。她们不是战斗单位，而是有身份、来历和婚配剧情的俘虏。
const FAMILY_BY_FAC = {
  wei: [
    { name: "卞夫人", role: "夫人", desc: "魏室旧后，持家有度，素有安抚人心之名。" },
    { name: "甄宓", role: "夫人", desc: "甄氏女，姿容明慧，熟知河北士族与宫府礼制。" },
    { name: "清河公主", role: "宗室女", desc: "魏室宗女，性情端庄，是连接河北宗亲的名义纽带。" },
  ],
  shu: [
    { name: "甘夫人", role: "夫人", desc: "蜀汉旧室夫人，温厚仁慈，在军民间颇有声望。" },
    { name: "吴夫人", role: "夫人", desc: "蜀中宗室夫人，熟悉益州士族与诸郡民情。" },
    { name: "刘氏宗女", role: "宗室女", desc: "蜀室宗亲之女，知晓巴蜀旧族之间的姻亲关系。" },
  ],
  wu: [
    { name: "步练师", role: "夫人", desc: "江东名门夫人，温雅聪慧，深得江东士族敬重。" },
    { name: "徐夫人", role: "夫人", desc: "江东旧族夫人，能以家声安抚水乡诸郡。" },
    { name: "孙鲁班", role: "宗室女", desc: "吴室宗女，出身显贵，是江东宗亲关系的象征。" },
  ],
};
const HEIR_NAMES = ["承志", "嗣业", "继祖", "绍勋", "定邦", "安世"];
const GENERAL_AGES = {
  "刘备": 45, "关羽": 42, "张飞": 40, "赵云": 35, "诸葛亮": 27, "黄忠": 62, "马超": 25, "魏延": 34,
  "曹操": 45, "张辽": 30, "夏侯惇": 39, "司马懿": 30, "许褚": 28, "徐晃": 34, "张郃": 35, "曹仁": 38,
  "孙权": 27, "周瑜": 24, "吕蒙": 28, "陆逊": 23, "甘宁": 30, "太史慈": 35, "黄盖": 50, "程普": 52,
  "吕布": 32, "陈宫": 35, "高顺": 36, "貂蝉": 20, "袁绍": 40, "颜良": 38, "文丑": 36,
  "董卓": 52, "李儒": 38, "华雄": 35,
};
const GENERAL_COMBAT_STATS = {
  "吕布": [100, 36], "关羽": [97, 78], "张飞": [98, 43], "赵云": [96, 76], "马超": [97, 54], "黄忠": [94, 68], "典韦": [96, 35], "许褚": [95, 32],
  "曹操": [72, 94], "诸葛亮": [58, 100], "司马懿": [61, 96], "周瑜": [72, 96], "陆逊": [67, 94], "郭嘉": [45, 98], "贾诩": [52, 99], "法正": [48, 91],
  "刘备": [70, 88], "孙权": [66, 86], "张辽": [93, 78], "甘宁": [91, 58], "太史慈": [92, 67], "魏延": [88, 62], "姜维": [86, 88], "黄月英": [42, 94],
  "貂蝉": [38, 82], "孙尚香": [82, 72], "祝融夫人": [86, 48], "王异": [58, 84],
};
function isFemaleName(name) {
  return Object.values(FEMALE_BY_FAC).some(l => l.includes(name));
}
function generalAge(name) {
  if (GENERAL_AGES[name]) return GENERAL_AGES[name];
  return 25 + (([...name].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % 21));
}
function generalCombatStats(name, gender = null) {
  if (GENERAL_COMBAT_STATS[name]) {
    const [force, wisdom] = GENERAL_COMBAT_STATS[name];
    return { force, wisdom };
  }
  const seed = avatarHash(name);
  const female = gender === "f" || isFemaleName(name);
  return {
    force: 48 + (seed % 45) + (female ? -4 : 0),
    wisdom: 48 + (Math.floor(seed / 5) % 45) + (female ? 4 : 0),
  };
}
function generalProfile(name, genderOverride = null) {
  const gender = genderOverride || (isFemaleName(name) ? "f" : "m");
  const skills = generalSkills(name).map(s => s.name);
  if (!skills.length) skills.push(gender === "f" ? "内助安民" : "统军作战");
  const combat = generalCombatStats(name, gender);
  return {
    gender, age: generalAge(name), skills,
    force: combat.force, wisdom: combat.wisdom,
    charm: gender === "f" ? 65 + ([...name].reduce((sum, ch) => sum + ch.charCodeAt(0), 0) % 31) : null,
  };
}
function rollGen(name, opts = {}) {
  const profile = generalProfile(name, opts.gender);
  return {
    name,
    ...profile,
    age: opts.age ?? profile.age,
    charm: profile.gender === "f" ? (opts.charm ?? profile.charm) : null,
    lead: 55 + Math.floor(Math.random() * 40),  // 统率
    pol: 40 + Math.floor(Math.random() * 55),   // 政务
    loyalty: opts.loyalty ?? 78,
    city: null, tried: false,
  };
}
function rollFamily(family, originFac, capturedFrom) {
  const profile = generalProfile(family.name, "f");
  return {
    name: family.name, ...profile, gender: "f", type: "family", captureType: "family",
    role: family.role, desc: family.desc, originFac, capturedFrom,
    lead: 48 + Math.floor(Math.random() * 25),
    pol: 62 + Math.floor(Math.random() * 30),
    city: null, tried: false,
  };
}
// 攻城成功后的战果：收押一名敌方将领，并在城市陷落时收押一名敌方妻女。
// 只从尚未进入我方武将府/俘虏营的人物中选择，避免重复俘获。
function captureGrandSpoils(g, defFac, nodeId) {
  const occupied = new Set([
    ...(g.myGens || []).map(x => x.name),
    ...(g.captives || []).map(x => x.name),
    ...(g.harem || []).map(x => x.name),
  ]);
  const captured = { generals: [], families: [] };
  const genPool = [...(GENERALS[defFac] || []), ...(FEMALE_BY_FAC[defFac] || [])]
    .filter(name => !occupied.has(name));
  if (genPool.length) {
    const name = genPool[Math.floor(Math.random() * genPool.length)];
    g.captives.push({ ...rollGen(name, { loyalty: 42 }), type: "general", captureType: "general", threat: 58, originFac: defFac, capturedFrom: nodeId });
    occupied.add(name);
    captured.generals.push(name);
  }
  if (GRAND_NODES[nodeId]?.type !== "city") return captured;
  const familyPool = (FAMILY_BY_FAC[defFac] || []).filter(x => !occupied.has(x.name));
  if (familyPool.length) {
    const family = familyPool[Math.floor(Math.random() * familyPool.length)];
    g.captives.push({ ...rollFamily(family, defFac, nodeId), threat: 32 });
    captured.families.push(family);
  }
  return captured;
}
// 节点坐标为地图容器的百分比
const GRAND_NODES = {
  yecheng:  { name: "邺城",   type: "city", x: 60, y: 12, owner: "wei" },
  julu:     { name: "钜鹿",   type: "city", x: 74, y: 12, owner: "wei" },
  pingyuan: { name: "平原",   type: "city", x: 85, y: 24, owner: "wei" },
  guandu:   { name: "官渡",   type: "pass", x: 57, y: 25, owner: "wei" },
  xuchang:  { name: "许昌",   type: "city", x: 57, y: 38, owner: "wei" },
  luoyang:  { name: "洛阳",   type: "city", x: 46, y: 30, owner: "wei" },
  wuguan:   { name: "武关",   type: "pass", x: 25, y: 19, owner: "wei" },
  wan:      { name: "宛城",   type: "city", x: 38, y: 43, owner: "wei" },
  hulao:    { name: "虎牢关", type: "pass", x: 52, y: 33, owner: "wei" },
  changan:  { name: "长安",   type: "city", x: 31, y: 28, owner: "wei" },
  jieting:  { name: "街亭",   type: "pass", x: 20, y: 36, owner: "wei" },
  hanzhong: { name: "汉中",   type: "city", x: 26, y: 48, owner: "shu" },
  chengdu:  { name: "成都",   type: "city", x: 13, y: 62, owner: "shu" },
  jiangzhou:{ name: "江州",   type: "city", x: 25, y: 72, owner: "shu" },
  nanzhong: { name: "南中",   type: "city", x: 17, y: 84, owner: "shu" },
  yiling:   { name: "夷陵",   type: "pass", x: 36, y: 61, owner: "shu" },
  xiangyang:{ name: "襄阳",   type: "city", x: 45, y: 52, owner: "shu" },
  jiangling:{ name: "江陵",   type: "city", x: 45, y: 67, owner: "shu" },
  jiangxia: { name: "江夏",   type: "city", x: 57, y: 56, owner: "wu" },
  chibi:    { name: "赤壁",   type: "pass", x: 54, y: 71, owner: "wu" },
  chaisang: { name: "柴桑",   type: "city", x: 63, y: 74, owner: "wu" },
  hefei:    { name: "合肥关", type: "pass", x: 74, y: 49, owner: "wu" },
  xiapi:    { name: "下邳",   type: "city", x: 83, y: 39, owner: "wu" },
  shouchun: { name: "寿春",   type: "city", x: 68, y: 44, owner: "wu" },
  jianye:   { name: "建业",   type: "city", x: 79, y: 58, owner: "wu" },
  // 海东与夷洲：按历史区域小势力划分；朝鲜方向为高句丽/百济/新罗，倭地合为倭国
  liaodong: { name: "襄平",   type: "city", x: 91, y: 11, owner: "neutral", region: "辽东" },
  lelang:   { name: "乐浪",   type: "city", x: 92, y: 18, owner: "baekje", region: "朝鲜" },
  daifang:  { name: "带方",   type: "city", x: 93, y: 23, owner: "silla", region: "朝鲜" },
  goguryeo: { name: "国内城", type: "city", x: 89, y: 5, owner: "goguryeo", region: "朝鲜" },
  tsushima: { name: "对马",   type: "pass", x: 94, y: 38, owner: "wa", region: "倭地" },
  itokoku:  { name: "伊都国", type: "city", x: 96, y: 50, owner: "wa", region: "倭地" },
  yamatai:  { name: "邪马台", type: "city", x: 94, y: 63, owner: "wa", region: "倭地" },
  yizhou:   { name: "夷洲",   type: "city", x: 87, y: 87, owner: "yizhou", region: "夷洲" },
};
const GRAND_EDGES = [
  ["yecheng","julu"],["julu","pingyuan"],["pingyuan","guandu"],
  ["yecheng","guandu"],["guandu","xuchang"],["guandu","luoyang"],
  ["wuguan","changan"],["wuguan","luoyang"],["luoyang","wan"],
  ["wan","xuchang"],["wan","xiangyang"],["luoyang","hulao"],["hulao","xuchang"],["luoyang","changan"],
  ["changan","jieting"],["jieting","hanzhong"],["changan","hanzhong"],
  ["hanzhong","chengdu"],["chengdu","jiangzhou"],["jiangzhou","nanzhong"],["jiangzhou","jiangling"],
  ["hanzhong","xiangyang"],["xiangyang","luoyang"],["xiangyang","xuchang"],
  ["xiangyang","jiangling"],["xiangyang","jiangxia"],["yiling","jiangling"],["yiling","chengdu"],
  ["jiangling","chibi"],["jiangling","jiangxia"],["chibi","chaisang"],["chibi","jiangxia"],
  ["chaisang","jianye"],["jianye","xiapi"],["xiapi","hefei"],["hefei","jianye"],
  ["jianye","shouchun"],["shouchun","xiapi"],["shouchun","xuchang"],
  ["shouchun","guandu"],["shouchun","hefei"],["chaisang","jiangling"],
  ["pingyuan","liaodong"],["liaodong","goguryeo"],["liaodong","lelang"],["lelang","daifang"],
  ["daifang","goguryeo"],["daifang","tsushima"],["tsushima","itokoku"],
  ["itokoku","yamatai"],["tsushima","yamatai"],["jianye","yizhou"],
  ["chaisang","yizhou"],
];
const FOREIGN_NODE_OWNERS = {
  lelang: "baekje", daifang: "silla", goguryeo: "goguryeo",
  tsushima: "wa", itokoku: "wa", yamatai: "wa", yizhou: "yizhou",
};
const GRAND_PERIODS = [
  {
    id: "yellowTurbans", year: "184年", name: "黄巾起义", desc: "汉室将倾，群雄尚未成形，州郡与豪强各守一方。",
    playable: ["dong", "gongsun", "liubiao", "kongrong", "wei", "wu"],
    defaultOwner: "neutral", factionLabels: { dong: "董卓军", gongsun: "公孙瓒军", liubiao: "刘表集团", kongrong: "孔融集团", wei: "曹操集团", wu: "孙坚集团" },
    owners: {
      dong: ["luoyang", "changan", "hulao"],
      gongsun: ["liaodong", "pingyuan"],
      liubiao: ["xiangyang", "jiangling"],
      kongrong: ["yecheng"],
      wei: ["xuchang", "guandu", "wan"],
      wu: ["jianye", "chaisang", "jiangxia", "chibi", "hefei", "shouchun"],
    },
  },
  {
    id: "dongZhuo", year: "190年", name: "董卓乱京", desc: "关东联军四起，董卓据洛阳长安，诸侯争夺关中与河北。",
    playable: ["dong", "yuan", "gongsun", "liubiao", "kongrong", "taoqian", "wei", "wu"],
    defaultOwner: "neutral", factionLabels: { dong: "董卓军", yuan: "袁绍集团", gongsun: "公孙瓒军", liubiao: "刘表集团", kongrong: "孔融集团", taoqian: "陶谦集团", wei: "曹操集团", wu: "孙坚集团" },
    owners: {
      dong: ["luoyang", "changan", "hulao"],
      yuan: ["yecheng", "julu"],
      gongsun: ["pingyuan", "liaodong"],
      liubiao: ["xiangyang", "jiangling"],
      kongrong: ["jieting"],
      taoqian: ["xiapi"],
      wei: ["xuchang", "guandu", "wan"],
      wu: ["jianye", "chaisang", "jiangxia", "chibi", "shouchun", "hefei"],
    },
  },
  {
    id: "guandu", year: "200年", name: "官渡之战", desc: "袁曹决战在即，河北、河南与江东各自积蓄力量。",
    playable: ["wei", "yuan", "lvbu", "yuanshu", "liubiao", "gongsun", "mateng", "zhangxiu", "wu"],
    defaultOwner: "neutral", factionLabels: { wei: "曹操集团", yuan: "袁绍集团", lvbu: "吕布集团", yuanshu: "袁术集团", liubiao: "刘表集团", gongsun: "公孙瓒军", mateng: "马腾军", zhangxiu: "张绣军", wu: "孙权集团" },
    owners: {
      wei: ["xuchang", "guandu", "luoyang", "hulao", "changan", "wuguan"],
      yuan: ["yecheng", "julu", "pingyuan"],
      lvbu: ["xiapi"],
      yuanshu: ["shouchun", "hefei"],
      liubiao: ["xiangyang", "jiangling"],
      gongsun: ["liaodong"],
      mateng: ["jieting"],
      zhangxiu: ["wan"],
      wu: ["jianye", "chaisang", "jiangxia", "chibi"],
    },
  },
  {
    id: "chibi", year: "208年", name: "赤壁前夕", desc: "曹操南下荆州，孙刘联盟在长江沿线迎来决战。",
    playable: ["wei", "shu", "wu", "liubiao", "liuzhang", "zhanglu", "mateng", "zhangxiu"],
    defaultOwner: "neutral", factionLabels: { wei: "曹操集团", shu: "刘备集团", wu: "孙权集团", liubiao: "刘表集团", liuzhang: "刘璋集团", zhanglu: "张鲁集团", mateng: "马腾军", zhangxiu: "张绣军" },
    owners: {
      wei: ["yecheng", "guandu", "xuchang", "luoyang", "hulao", "changan"],
      shu: ["yiling"],
      wu: ["jianye", "chaisang", "jiangxia", "chibi", "hefei", "shouchun"],
      liubiao: ["xiangyang"],
      liuzhang: ["chengdu", "jiangzhou", "nanzhong"],
      zhanglu: ["hanzhong"],
      mateng: ["jieting"],
      zhangxiu: ["wan"],
    },
  },
  {
    id: "tripartite", year: "220年", name: "三国鼎立", desc: "魏蜀吴相继立国，天下进入长期对峙。",
    playable: ["wei", "shu", "wu"],
    defaultOwner: "base", factionLabels: { wei: "魏", shu: "蜀", wu: "吴" }, owners: null,
  },
  {
    id: "zhugeNorthern", year: "227年", name: "诸葛亮北伐", desc: "蜀汉出祁山，魏据关中与中原，吴在江淮牵制。",
    playable: ["wei", "shu", "wu"],
    defaultOwner: "neutral", factionLabels: { wei: "魏", shu: "蜀汉", wu: "吴" },
    owners: {
      wei: ["yecheng", "julu", "pingyuan", "guandu", "xuchang", "luoyang", "hulao", "wan", "changan", "wuguan", "xiangyang", "jiangxia", "hefei", "xiapi", "shouchun"],
      shu: ["jieting", "hanzhong", "chengdu", "jiangzhou", "nanzhong", "yiling", "jiangling"],
      wu: ["chibi", "chaisang", "jianye"],
    },
  },
  {
    id: "jiangwei", year: "255年", name: "姜维北伐", desc: "魏国国力渐盛，蜀汉凭险据守，吴蜀仍在淮南与荆州鏖战。",
    playable: ["wei", "shu", "wu"],
    defaultOwner: "neutral", factionLabels: { wei: "魏", shu: "蜀汉", wu: "吴" },
    owners: {
      wei: ["yecheng", "julu", "pingyuan", "guandu", "xuchang", "luoyang", "hulao", "wan", "changan", "wuguan", "xiangyang", "jiangxia", "hefei", "xiapi", "shouchun", "jiangling"],
      shu: ["jieting", "hanzhong", "chengdu", "jiangzhou", "nanzhong", "yiling"],
      wu: ["chibi", "chaisang", "jianye"],
    },
  },
  {
    id: "fallShu", year: "263年", name: "魏灭蜀前夕", desc: "钟会、邓艾伺机西进，蜀汉危急，吴国仍守江东。",
    playable: ["wei", "shu", "wu"],
    defaultOwner: "neutral", factionLabels: { wei: "曹魏", shu: "蜀汉", wu: "东吴" },
    owners: {
      wei: ["yecheng", "julu", "pingyuan", "guandu", "xuchang", "luoyang", "hulao", "wan", "changan", "wuguan", "xiangyang", "jiangxia", "hefei", "xiapi", "shouchun", "jiangling", "yiling"],
      shu: ["jieting", "hanzhong", "chengdu", "jiangzhou", "nanzhong"],
      wu: ["chibi", "chaisang", "jianye"],
    },
  },
  {
    id: "jinWu", year: "279年", name: "西晋灭吴前夕", desc: "蜀汉已亡，西晋整军南下，东吴据长江作最后抵抗。",
    playable: ["wei", "wu"], factionLabels: { wei: "西晋", shu: "蜀汉遗脉", wu: "东吴" },
    defaultOwner: "neutral", owners: {
      wei: ["yecheng", "julu", "pingyuan", "guandu", "xuchang", "luoyang", "hulao", "wan", "changan", "wuguan", "xiangyang", "jiangxia", "jiangling", "yiling", "hefei", "xiapi", "shouchun", "chengdu", "hanzhong", "jieting", "jiangzhou", "nanzhong"],
      wu: ["chibi", "chaisang", "jianye"],
    },
  },
];
function grandPeriodDef(id) {
  return GRAND_PERIODS.find(p => p.id === id) || GRAND_PERIODS.find(p => p.id === "tripartite");
}
function grandFacLabel(g, fac) {
  return g?.factionLabels?.[fac] || FACTIONS[fac]?.name || fac;
}
function grandAdj(id) {
  const out = [];
  for (const [a, b] of GRAND_EDGES) {
    if (a === id) out.push(b);
    if (b === id) out.push(a);
  }
  return out;
}
function grandHops(fromId, toId) {
  if (fromId === toId) return 0;
  const seen = new Set([fromId]);
  let frontier = [fromId], hops = 0;
  while (frontier.length) {
    hops++;
    const next = [];
    for (const id of frontier) {
      for (const adj of grandAdj(id)) {
        if (seen.has(adj)) continue;
        if (adj === toId) return hops;
        seen.add(adj); next.push(adj);
      }
    }
    frontier = next;
    if (hops > 20) break;
  }
  return 99;
}
function dispatchGrandAlliedSupport(g, fromIds, toId, perNode = 2000) {
  const sourceSet = new Set(Array.isArray(fromIds) ? fromIds : [fromIds]);
  let support = 0;
  const contributions = [];
  for (const [id, node] of Object.entries(g.nodes)) {
    if (sourceSet.has(id) || !grandAreAllied(g, node.owner, g.playerFac) || !grandAdj(id).includes(toId)) continue;
    const use = Math.min(perNode, Math.max(0, node.troops - 500));
    if (use < 500) continue;
    node.troops -= use;
    support += use;
    contributions.push({ id, troops: use });
  }
  return { total: support, contributions };
}
function newGrandState(playerFac, periodId = "tripartite") {
  const period = grandPeriodDef(periodId);
  const periodOwners = {};
  for (const [fac, ids] of Object.entries(period.owners || {})) for (const id of ids) periodOwners[id] = fac;
  const nodes = {};
  for (const [id, n] of Object.entries(GRAND_NODES)) {
    const foreignOwner = FOREIGN_NODE_OWNERS[id];
    const owner = periodOwners[id] || (period.defaultOwner === "base" ? n.owner : foreignOwner || period.defaultOwner);
    nodes[id] = {
      owner,
      troops: n.type === "city" ? (owner === "neutral" ? 5000 : 8000) : (owner === "neutral" ? 3500 : 5000),
      farm: 30, trade: 30, wall: n.type === "pass" ? 50 : 35, loyalty: 60,
      acted: false,
    };
  }
  const res = {};
  for (const f of GRAND_FACTION_KEYS) res[f] = { gold: 600, food: 800 };
  // 玩家开局武将府：本势力三员大将 + 一员女将
  const myGens = [...(GENERALS[playerFac] || []).slice(0, 3), ...(FEMALE_BY_FAC[playerFac] || []).slice(0, 1)].map(name => ({ ...rollGen(name), currentFac: playerFac }));
  return {
    v: 4, playerFac, period: period.id, factionLabels: period.factionLabels, turn: 1, nodes, res, over: null,
    myGens, captives: [], eliminatedGenerals: [], powTroops: 0, harem: [], heirs: [], transfers: [], releasedSuitors: [],
    actionLimits: { intimacy: 1, oneNight: 1, agent: 3 }, actionUsage: { turn: 1, intimacy: 0, oneNight: 0, agent: 0 },
    relations: createGrandRelations(), alliances: [],
  };
}
function migrateGrandState(raw) {
  if (!raw || typeof raw !== "object") return raw;
  const g = { ...raw, v: 4, nodes: { ...(raw.nodes || {}) } };
  const period = grandPeriodDef(raw.period || "tripartite");
  if (!g.period) g.period = period.id;
  if (!g.factionLabels) g.factionLabels = period.factionLabels;
  for (const [id, node] of Object.entries(GRAND_NODES)) {
    if (g.nodes[id]) continue;
    g.nodes[id] = {
      owner: node.owner,
      troops: node.type === "city" ? 8000 : 5000,
      farm: 30, trade: 30, wall: node.type === "pass" ? 50 : 35, loyalty: 60,
      acted: false,
    };
  }
  // 旧档可能把海东节点记为 neutral，或把朝鲜节点都记为 goguryeo；按节点恢复历史区域小势力。
  for (const [id, fac] of Object.entries(FOREIGN_NODE_OWNERS)) {
    if (g.nodes[id] && (g.nodes[id].owner === "neutral" || (id === "lelang" || id === "daifang") && g.nodes[id].owner === "goguryeo")) g.nodes[id].owner = fac;
  }
  if (!g.res) g.res = Object.fromEntries(GRAND_FACTION_KEYS.map(f => [f, { gold: 600, food: 800 }]));
  for (const f of GRAND_FACTION_KEYS) if (!g.res[f]) g.res[f] = { gold: 600, food: 800 };
  if (!g.transfers) g.transfers = [];
  if (!g.captives) g.captives = [];
  if (!g.harem) g.harem = [];
  if (!g.releasedSuitors) g.releasedSuitors = [];
  if (!g.heirs) g.heirs = [];
  g.actionLimits = { intimacy: 1, oneNight: 1, agent: 3, ...(g.actionLimits || {}) };
  g.actionUsage = { turn: g.turn, intimacy: 0, oneNight: 0, agent: 0, ...(g.actionUsage || {}) };
  if (g.actionUsage.turn !== g.turn) g.actionUsage = { turn: g.turn, intimacy: 0, oneNight: 0, agent: 0 };
  if (!g.eliminatedGenerals) g.eliminatedGenerals = [];
  g.relations = { ...createGrandRelations(), ...(g.relations || {}) };
  g.alliances = [...new Set((g.alliances || []).filter(key => {
    const [a, b] = key.split("|");
    return GRAND_FACTION_KEYS.includes(a) && GRAND_FACTION_KEYS.includes(b) && a !== b;
  }))];
  if (!g.myGens && g.playerFac) g.myGens = [...(GENERALS[g.playerFac] || []).slice(0, 3), ...(FEMALE_BY_FAC[g.playerFac] || []).slice(0, 1)].map(name => ({ ...rollGen(name), currentFac: g.playerFac }));
  for (const gen of g.myGens || []) {
    gen.currentFac = g.playerFac;
    const savedSkills = Array.isArray(gen.skills) ? [...gen.skills] : null;
    const profile = generalProfile(gen.name, gen.gender);
    Object.assign(gen, profile, { age: gen.age ?? profile.age, charm: profile.gender === "f" ? (gen.charm ?? profile.charm) : null });
    // 保留玩家录入或手动分配的技能，旧档没有技能时才使用默认技能。
    if (savedSkills) gen.skills = savedSkills;
    gen.loyalty = Math.max(0, Math.min(100, gen.loyalty ?? 78));
    gen.courtship = Math.max(0, Math.min(100, gen.courtship ?? 0));
    gen.lastCourtshipTurn = gen.lastCourtshipTurn ?? -1;
    gen.lastOneNightTurn = gen.lastOneNightTurn ?? -1;
  }
  for (const captive of g.captives) {
    const profile = generalProfile(captive.name, captive.gender || (captive.captureType === "family" ? "f" : null));
    Object.assign(captive, profile, { age: captive.age ?? profile.age, charm: profile.gender === "f" ? (captive.charm ?? profile.charm) : null });
    if (captive.captureType !== "family") captive.loyalty = Math.max(0, Math.min(100, captive.loyalty ?? 42));
    captive.threat = Math.max(0, Math.min(100, captive.threat ?? (captive.captureType === "family" ? 32 : 58)));
  }
  for (const spouse of g.harem) {
    const profile = generalProfile(spouse.name, spouse.gender || "f");
    Object.assign(spouse, profile, { age: spouse.age ?? profile.age, charm: profile.gender === "f" ? (spouse.charm ?? profile.charm) : null });
    spouse.favor = Math.max(0, Math.min(100, spouse.favor ?? 70));
    spouse.intimacyCount = spouse.intimacyCount ?? 0;
    spouse.lastIntimacyTurn = spouse.lastIntimacyTurn ?? -1;
  }
  return g;
}
function grandIncome(g, fac) {
  let gold = 0, food = 0;
  for (const [id, n] of Object.entries(g.nodes)) {
    if (n.owner !== fac) continue;
    gold += 40 + n.trade * 2;
    food += 40 + n.farm * 2;
    if (fac === g.playerFac && g.myGens) {
      const tj = g.myGens.find(x => x.city === id);
      if (tj) {
        const trust = 0.7 + (tj.loyalty || 0) / 333;
        const vigor = tj.age >= 65 ? 0.82 : tj.age >= 55 ? 0.93 : 1;
        gold += Math.round(tj.pol / 4 * trust * vigor); food += Math.round(tj.pol / 4 * trust * vigor);
      } // 太守理政，忠诚不足时政务效率下降
    }
  }
  return { gold, food };
}
function grandFactionSummary(g, fac) {
  const owned = Object.entries(g.nodes).filter(([, n]) => n.owner === fac);
  const loyalty = owned.length
    ? Math.round(owned.reduce((sum, [, n]) => sum + n.loyalty, 0) / owned.length)
    : 0;
  return {
    total: owned.length,
    cities: owned.filter(([id]) => GRAND_NODES[id].type === "city").length,
    passes: owned.filter(([id]) => GRAND_NODES[id].type === "pass").length,
    troops: owned.reduce((sum, [, n]) => sum + n.troops, 0),
    loyalty,
    gold: g.res[fac]?.gold || 0,
    food: g.res[fac]?.food || 0,
  };
}
function effWallOf(g, nodeId) {
  const n = g.nodes[nodeId];
  let w = n.wall;
  if (n.owner === g.playerFac && g.myGens) {
    const tj = g.myGens.find(x => x.city === nodeId);
    if (tj) {
      const vigor = tj.age >= 65 ? 0.82 : tj.age >= 55 ? 0.93 : 1;
      w += Math.round(tj.lead / 3 * (0.7 + (tj.loyalty || 0) / 333) * vigor);
    } // 良将守城，忠诚不足或年老时守备打折
  }
  return w;
}
function grandAutoResolve(atkTroops, defTroops, defWall) {
  const a = atkTroops * (0.8 + Math.random() * 0.4);
  const d = defTroops * (1 + defWall / 120) * (0.8 + Math.random() * 0.4);
  const atkWin = a > d;
  const ratio = atkWin ? d / a : a / d;
  const winnerLossFrac = 0.25 + ratio * 0.45;
  return {
    atkWin,
    atkLeft: atkWin ? Math.round(atkTroops * (1 - winnerLossFrac)) : Math.round(atkTroops * 0.15),
    defLeft: atkWin ? Math.round(defTroops * 0.1) : Math.round(defTroops * (1 - winnerLossFrac)),
  };
}
// 内政行动定义
const GOV_ACTIONS = {
  farm:   { name: "劝农", icon: "🌾", gold: 100, note: "农业+8（每回合产粮+16）" },
  trade:  { name: "通商", icon: "🪙", gold: 100, note: "商业+8（每回合产金+16）" },
  wall:   { name: "筑城", icon: "🧱", gold: 120, note: "城防+8（守城战城墙更坚、委任战更利守方）" },
  loyalty:{ name: "抚民", icon: "🏮", gold: 80,  note: "民心+8（民心≥80时募兵折半价）" },
};

/* ---------- save / load ---------- */
function serialize(sim, scenario, sizeKey) {
  return JSON.stringify({
    v: 5, scenario, sizeKey,
    terrainMeta: sim.terrainMeta,
    allies: sim.allies,
    started: sim.started, done: sim.done, battleTime: sim.battleTime || 0,
    historicalFired: sim.historical?.fired || [],
    log: sim.log,
    armies: sim.armies,
    buildings: sim.buildings,
    units: sim.units.filter(u => !u.dead).map(u => ({
      x: +u.x.toFixed(1), y: +u.y.toFixed(1), fac: u.fac, army: u.army, type: u.type,
      hp: +u.hp.toFixed(1), maxHp: u.maxHp, atk: +u.atk.toFixed(2),
      gen: u.gen, flee: u.flee, kills: u.kills, charge: u.charge,
    })),
  });
}
function deserialize(json) {
  const d = JSON.parse(json);
  if (d.sizeKey) setWorldSize(d.sizeKey);
  const terrain = genTerrain(d.terrainMeta.seed, d.terrainMeta.opts);
  const sim = {
    terrain, terrainMeta: d.terrainMeta,
    units: d.units.map(u => ({ ...u, dead: false, cd: Math.random() * 0.6 })),
    armies: d.armies, buildings: d.buildings,
    allies: d.allies || [],
    fx: [], arrows: [], log: d.log || [],
    running: false, started: d.started, done: d.done, battleTime: d.battleTime || 0,
    historical: SCENARIOS[d.scenario]?.history ? { ...SCENARIOS[d.scenario].history, fired: d.historicalFired || [] } : null,
  };
  for (const army of sim.armies) {
    const profile = generalProfile(army.general);
    army.generalForce = army.generalForce ?? profile.force;
    army.generalWisdom = army.generalWisdom ?? profile.wisdom;
  }
  UID = Math.max(1, ...sim.armies.map(a => a.id)) + 1;
  BID = Math.max(1, ...sim.buildings.map(b => b.id), 0) + 1;
  return { sim, scenario: d.scenario, sizeKey: d.sizeKey || "m" };
}

/* ---------- kbd label ---------- */
function Kbd({ k, dark }) {
  return (
    <span style={{
      fontSize: 9, opacity: 0.75, marginLeft: 4, padding: "0 3px",
      border: `1px solid ${dark ? "#00000055" : "#e8dcc155"}`, borderRadius: 2,
      fontFamily: "monospace", verticalAlign: "1px",
    }}>{k}</span>
  );
}

/* ---------- component ---------- */
export default function SanguoSandbox() {
  const canvasRef = useRef(null);
  const battleAreaRef = useRef(null);
  const grandMapRef = useRef(null);
  const fullscreenRestoreRef = useRef(false);
  const terrainCanvasRef = useRef(null);
  const corpseCanvasRef = useRef(null);
  const battle3DRef = useRef(null);
  const simRef = useRef(null);
  const rafRef = useRef(0);
  const selectedRef = useRef([]);
  const viewRef = useRef({ z: 1, ox: 0, oy: 0 });
  const dragRef = useRef(null);
  const selRectRef = useRef(null);
  const stateRef = useRef({});
  const settingsRef = useRef({ stains: true, showNames: true, showKeys: true, fxLite: false, longLog: false, autoResolve: true, terrainLegend: true, cheats: false, battleAI: true });
  const [tick, setTick] = useState(0);
  const [mode, setMode] = useState("sandbox"); // sandbox | campaignHome | campaignBattle | storyHome | storyIntro | storyBattle
  const [campaignProgress, setCampaignProgress] = useState(createDefaultCampaignProgress);
  const [campaignProfiles, setCampaignProfiles] = useState(createDefaultCampaignProfiles);
  const [campaignStage, setCampaignStage] = useState(null); // { fac, idx }
  const [campaignResult, setCampaignResult] = useState(null); // null | 'win' | 'lose' | 'complete'
  const [campaignRewardOptions, setCampaignRewardOptions] = useState([]);
  const [campaignRewardPending, setCampaignRewardPending] = useState(false);
  const [storyProgress, setStoryProgress] = useState({});   // { lineKey: nextChapterIdx }
  const [storyRenown, setStoryRenown] = useState({});       // { lineKey: renown }
  const [storyChoices, setStoryChoices] = useState({});     // { lineKey: { chapterIdx: choiceId } }，只在通关时写入
  const [storyStage, setStoryStage] = useState(null);       // { line, idx }
  const [storyChoice, setStoryChoice] = useState(null);
  const [storyResult, setStoryResult] = useState(null);     // null | 'win' | 'lose'
  const [storyDifficulty, setStoryDifficulty] = useState("normal"); // easy | normal | hard
  const [stats, setStats] = useState({ battles: 0, wins: 0, kills: 0, captures: 0, experience: 30 });
  const [dialog, setDialog] = useState(null); // {type:'captive'|'surrender', phase:'choose'|'done', ...}
  const [grand, setGrand] = useState(null);          // 天下模式全局状态
  const [grandPeriodChoice, setGrandPeriodChoice] = useState("tripartite");
  const [generalManagerOpen, setGeneralManagerOpen] = useState(false);
  const [generalManagerModalOpen, setGeneralManagerModalOpen] = useState(false);
  const [generalManagerPage, setGeneralManagerPage] = useState("generals");
  const [customGeneralOpen, setCustomGeneralOpen] = useState(false);
  const [customGeneralForm, setCustomGeneralForm] = useState({ name: "", gender: "m", age: 25, force: 60, wisdom: 60, lead: 60, pol: 60, loyalty: 75, charm: 80, skills: "统军作战" });
  const [generalManagerView, setGeneralManagerView] = useState("cards");
  const [generalManagerSort, setGeneralManagerSort] = useState({ key: "name", dir: 1 });
  const [generalManagerFilter, setGeneralManagerFilter] = useState({ text: "", fac: "all", gender: "all", status: "all" });
  const [skillTooltip, setSkillTooltip] = useState(null);
  const [skillManagerSearch, setSkillManagerSearch] = useState("");
  const [skillManagerCategory, setSkillManagerCategory] = useState("all");
  const [skillManagerSelected, setSkillManagerSelected] = useState(null);
  const [skillManagerTarget, setSkillManagerTarget] = useState("");
  const [grandSel, setGrandSel] = useState(null);    // 选中节点 id
  const [grandDiplomacyFocus, setGrandDiplomacyFocus] = useState(null); // 地图外交视图当前势力
  const [grandAttackSources, setGrandAttackSources] = useState([]); // 多城合击来源
  const [grandDeploy, setGrandDeploy] = useState(4000); // 出征兵力
  const [grandSupport, setGrandSupport] = useState(false); // 是否调用同阵援军
  const [grandMapZoom, setGrandMapZoom] = useState(1);
  const [grandPanelOpacity, setGrandPanelOpacity] = useState(0.92); // 大地图城池内政弹框透明度
  const [grandHudOpen, setGrandHudOpen] = useState(true); // 天下地图全屏信息浮窗
  const [grandHudOpacity, setGrandHudOpacity] = useState(0.88);
  const [grandSummaryOpen, setGrandSummaryOpen] = useState(true); // 天下势力总览
  const [grandMsg, setGrandMsg] = useState([]);      // 天下模式简报
  const [grandBattleCtx, setGrandBattleCtx] = useState(null); // 进行中的天下会战
  const [hudOpen, setHudOpen] = useState(true); // 战场浮动信息面板
  const [battleInfoOpen, setBattleInfoOpen] = useState(true); // 全屏左侧诸军/战报面板
  const [isFullscreen, setIsFullscreen] = useState(false); // 战场全屏
  const [grandMapFullscreen, setGrandMapFullscreen] = useState(false); // 天下地图全屏
  const [view3D, setView3D] = useState(false); // 2D Canvas / 3D 战场视图
  const view3DRef = useRef(false);
  const [hoverInfo, setHoverInfo] = useState(null); // {x,y,px,py,terrain}
  const [fac, setFac] = useState("shu");
  const [utype, setUtype] = useState("inf");
  const [placeMode, setPlaceMode] = useState("army");
  const [men, setMen] = useState(3000);
  const [speedIdx, setSpeedIdx] = useState(2);
  const [running, setRunning] = useState(false);
  const [scenario, setScenario] = useState("random");
  const [selected, setSelected] = useState([]);
  const [saveMsg, setSaveMsg] = useState("");
  const [sizeKey, setSizeKey] = useState("m");
  const [showSettings, setShowSettings] = useState(false);
  const [settings, setSettings] = useState({ stains: true, showNames: true, showKeys: true, fxLite: false, longLog: false, autoResolve: true, terrainLegend: true, cheats: false, battleAI: true });
  useEffect(() => { FX_LITE = !!settings.fxLite; LOG_LIMIT = settings.longLog ? 14 : 8; BATTLE_AI = settings.battleAI !== false; }, [settings.fxLite, settings.longLog, settings.battleAI]);
  useEffect(() => {
    const onFsChange = () => {
      setIsFullscreen(document.fullscreenElement === battleAreaRef.current);
      setGrandMapFullscreen(document.fullscreenElement === grandMapRef.current);
      if (!document.fullscreenElement && fullscreenRestoreRef.current) {
        fullscreenRestoreRef.current = false;
        setTimeout(() => {
          grandMapRef.current?.requestFullscreen?.().catch?.(() => {});
        }, 0);
      }
    };
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);
  const toggleFullscreen = useCallback(() => {
    if (!document.fullscreenElement) battleAreaRef.current?.requestFullscreen();
    else document.exitFullscreen();
  }, []);
  const toggleGrandMapFullscreen = useCallback(() => {
    if (!document.fullscreenElement) grandMapRef.current?.requestFullscreen();
    else document.exitFullscreen();
  }, []);
  useEffect(() => {
    const fs = document.fullscreenElement;
    if (mode !== "grand" && fs === grandMapRef.current) document.exitFullscreen?.();
    if (!(mode === "sandbox" || mode === "campaignBattle" || mode === "storyBattle" || mode === "grandBattle") && fs === battleAreaRef.current) document.exitFullscreen?.();
  }, [mode]);
  settingsRef.current = settings;
  view3DRef.current = view3D;

  const initSim = useCallback((scKey, context = {}) => {
    const sc = SCENARIOS[scKey].make(context);
    simRef.current = {
      terrain: sc.terrain, terrainMeta: sc.meta,
      units: [], armies: [], fx: [], arrows: [], buildings: [],
      allies: sc.allies ? [...sc.allies] : [],
      log: [], running: false, done: false, started: false, battleTime: 0,
      historical: sc.history ? { ...sc.history, fired: [] } : null,
    };
    simRef.current.campaignFac = context.fac || null;
    sc.setup(simRef.current, context);
    log(simRef.current, `🗺 ${SCENARIOS[scKey].name} — ${SCENARIOS[scKey].desc}`);
    repaintTerrain();
    viewRef.current = { z: 1, ox: 0, oy: 0 };
    setRunning(false);
    setSelected([]);
    selectedRef.current = [];
    setTick(t => t + 1);
  }, []);

  function repaintTerrain() {
    const tc = terrainCanvasRef.current || document.createElement("canvas");
    tc.width = W; tc.height = H;
    terrainCanvasRef.current = tc;
    paintTerrain(tc.getContext("2d"), simRef.current.terrain);
    const cc = corpseCanvasRef.current || document.createElement("canvas");
    cc.width = W; cc.height = H;
    corpseCanvasRef.current = cc;
    cc.getContext("2d").clearRect(0, 0, W, H);
  }

  // load settings once
  useEffect(() => {
    (async () => {
      try {
        const r = await storage.get(SETTINGS_KEY);
        if (r) {
          const s = JSON.parse(r.value);
          if (s.sizeKey) { setWorldSize(s.sizeKey); setSizeKey(s.sizeKey); }
          if (Number.isFinite(s.grandPanelOpacity)) setGrandPanelOpacity(Math.min(1, Math.max(0.35, s.grandPanelOpacity)));
          if (Number.isFinite(s.grandHudOpacity)) setGrandHudOpacity(Math.min(1, Math.max(0.35, s.grandHudOpacity)));
          setSettings(v => ({ ...v, ...s.settings }));
        }
      } catch { /* no saved settings */ }
      try {
        const current = await storage.get(CAMPAIGN_KEY);
        const legacy = current ? null : await storage.get(CAMPAIGN_LEGACY_KEY);
        const rc = current || legacy;
        if (rc) {
          const save = migrateCampaignSave(JSON.parse(rc.value));
          setCampaignProgress(save.progress);
          setCampaignProfiles(save.profiles);
          if (legacy) await storage.set(CAMPAIGN_KEY, JSON.stringify(save));
        }
      } catch { /* no saved campaign progress */ }
      try {
        const rs = await storage.get(STORY_KEY);
        if (rs) {
          const d = JSON.parse(rs.value);
          if (d.progress) setStoryProgress(d.progress);
          if (d.renown) setStoryRenown(d.renown);
          if (d.difficulty) setStoryDifficulty(d.difficulty);
          if (d.choices) setStoryChoices(d.choices);
        }
      } catch { /* no saved story progress */ }
      try {
        const rt = await storage.get("sanguo:stats-v1");
        if (rt) setStats(v => {
          const saved = JSON.parse(rt.value);
          return { ...v, ...saved, experience: saved.experience ?? 30 + (saved.wins || 0) * 10 };
        });
      } catch { /* no saved stats */ }
      try {
        const current = await storage.get(GRAND_KEY);
        const legacy = current ? null : await storage.get(GRAND_LEGACY_KEY);
        const rg = current || legacy;
        if (rg) {
          const g = migrateGrandState(JSON.parse(rg.value));
          // 旧档迁移
          if (!g.myGens) g.myGens = [...(GENERALS[g.playerFac] || []).slice(0, 3), ...(FEMALE_BY_FAC[g.playerFac] || []).slice(0, 1)].map(name => ({ ...rollGen(name), currentFac: g.playerFac }));
          if (!g.captives) g.captives = [];
          if (g.powTroops === undefined) g.powTroops = 0;
          if (!g.harem) g.harem = [];
          if (!g.heirs) g.heirs = [];
          if (!g.transfers) g.transfers = [];
          setGrand(g);
          setGrandPeriodChoice(g.period || "tripartite");
          if (legacy) await storage.set(GRAND_KEY, JSON.stringify(g));
        }
      } catch { /* no saved grand game */ }
      initSim("random");
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const persistSettings = useCallback(async (nextSettings, nextSize, panelOpacity = grandPanelOpacity, hudOpacity = grandHudOpacity) => {
    try {
      await storage.set(SETTINGS_KEY, JSON.stringify({ settings: nextSettings, sizeKey: nextSize, grandPanelOpacity: panelOpacity, grandHudOpacity: hudOpacity }));
    } catch { /* ignore */ }
  }, [grandPanelOpacity, grandHudOpacity]);

  const changeScenario = useCallback((k) => {
    setScenario(k);
    initSim(k);
  }, [initSim]);

  const changeSize = useCallback((k) => {
    setWorldSize(k);
    setSizeKey(k);
    persistSettings(settingsRef.current, k);
    initSim(stateRef.current.scenario);
  }, [initSim, persistSettings]);

  const persistCampaign = useCallback(async (progress, profiles) => {
    try { await storage.set(CAMPAIGN_KEY, JSON.stringify({ version: 2, progress, profiles })); } catch { /* ignore */ }
  }, []);

  const startCampaignStage = useCallback((facKey, idx, profileOverride = null) => {
    const stage = CAMPAIGN_ORDER[facKey][idx];
    if (!stage) return;
    setScenario(stage.key);
    initSim(stage.key, { fac: facKey });
    const profile = profileOverride || campaignProfiles[facKey] || createDefaultCampaignProfile();
    applyCampaignProfile(simRef.current, profile, facKey);
    simRef.current.campaign = { fac: facKey, idx, key: stage.key, resolved: false };
    log(simRef.current, `🚩 闯关：${FACTIONS[facKey].name}军 · ${stage.obj}`);
    setFac(facKey);
    setCampaignStage({ fac: facKey, idx });
    setCampaignResult(null);
    setCampaignRewardOptions([]);
    setCampaignRewardPending(false);
    setMode("campaignBattle");
  }, [campaignProfiles, initSim]);

  const chooseCampaignReward = useCallback((rewardId) => {
    const sim = simRef.current;
    if (!sim?.campaign || !campaignRewardPending) return;
    const { fac, idx } = sim.campaign;
    const current = campaignProfiles[fac] || createDefaultCampaignProfile();
    const updatedProfile = applyCampaignReward(current, rewardId);
    const updatedProfiles = { ...campaignProfiles, [fac]: updatedProfile };
    setCampaignProfiles(updatedProfiles);
    persistCampaign(campaignProgress, updatedProfiles);
    setCampaignRewardPending(false);
    setCampaignRewardOptions([]);
    if (idx + 1 < CAMPAIGN_ORDER[fac].length) {
      startCampaignStage(fac, idx + 1, updatedProfile);
    } else {
      setCampaignResult("complete");
    }
  }, [campaignProfiles, campaignProgress, campaignRewardPending, persistCampaign, startCampaignStage]);

  const restartCampaignStage = useCallback(() => {
    if (!campaignStage) return;
    startCampaignStage(campaignStage.fac, campaignStage.idx);
  }, [campaignStage, startCampaignStage]);

  const exitToCampaignHome = useCallback(() => {
    if (simRef.current) simRef.current.running = false;
    setRunning(false);
    setMode("campaignHome");
    setCampaignResult(null);
  }, []);

  /* ---------- story mode logic ---------- */
  const persistStory = useCallback(async (progress, renown, difficulty, choices) => {
    try { await storage.set(STORY_KEY, JSON.stringify({ progress, renown, difficulty, choices })); } catch { /* ignore */ }
  }, []);

  const persistStats = useCallback(async (st) => {
    try { await storage.set("sanguo:stats-v1", JSON.stringify(st)); } catch { /* ignore */ }
  }, []);

  const recordBattle = useCallback((sim, playerTeamId, team, win) => {
    const mine = sim.armies.filter(a => team[a.fac] === playerTeamId);
    const pk = mine.reduce((s, a) => s + a.kills, 0) * MEN_PER_DOT;
    const pc = mine.reduce((s, a) => s + ((a.capturedGens && a.capturedGens.length) || 0), 0);
    setStats(prev => {
      const next = {
        battles: prev.battles + 1,
        wins: prev.wins + (win ? 1 : 0),
        kills: prev.kills + pk,
        captures: prev.captures + pc,
        experience: (prev.experience ?? 30) + (win ? 10 : 3) + Math.floor(pk / 1000) + pc * 4,
      };
      persistStats(next);
      return next;
    });
  }, [persistStats]);

  // 锦囊妙计：闯关与故事战斗中可用的主动技
  const applyTactic = useCallback((kind) => {
    const sim = simRef.current;
    if (!sim || !sim.tactics || sim.tactics <= 0 || !sim.tacticsFac) return;
    const team = teamsOf(sim);
    const pT = team[sim.tacticsFac];
    sim.tactics--;
    if (kind === "drum") {
      for (const a of sim.armies) if (team[a.fac] === pT && !a.broken && a.alive > 0) {
        a.morale = Math.min(100, a.morale + 28);
        a.food = Math.min(100, a.food + 30);
      }
      log(sim, "🥁 锦囊·擂鼓助威：全军士气大振、粮秣补充！");
    } else if (kind === "heal") {
      for (const u of sim.units) if (!u.dead && !u.flee && team[u.fac] === pT)
        u.hp = Math.min(u.maxHp, u.hp + u.maxHp * 0.4);
      log(sim, "🧪 锦囊·金疮妙药：全军伤势大愈！");
    } else if (kind === "fear") {
      for (const a of sim.armies) if (team[a.fac] !== pT && !a.broken && !a.militia && a.alive > 0)
        a.morale = Math.max(5, a.morale - 16);
      log(sim, "🎐 锦囊·疑兵之计：敌军疑神疑鬼、军心浮动！");
    } else if (kind === "charge") {
      for (const u of sim.units) if (!u.dead && u.type === "cav" && team[u.fac] === pT) u.charge = 1;
      log(sim, "🐎 锦囊·奇兵突骑：己方铁骑蓄势待发，下次冲阵威力拉满！");
    } else if (kind === "burn") {
      const targets = sim.buildings.filter(b => b.hp > 0 && team[b.fac] !== pT);
      if (!targets.length) {
        sim.tactics++;
        log(sim, "🔥 锦囊·火矢奇袭：未寻得可烧之敌营，锦囊退回");
      } else {
        const b = targets[Math.floor(Math.random() * targets.length)];
        b.hp = Math.max(1, b.hp - b.maxHp * 0.35);
        log(sim, "🔥 锦囊·火矢奇袭：火矢齐发，敌营付之一炬！");
      }
    } else if (kind === "reinforce") {
      let cx = 0, cy = 0, n = 0;
      for (const a of sim.armies) if (team[a.fac] === pT && a.alive > 0 && !a.broken) { cx += a.anchor.x; cy += a.anchor.y; n++; }
      const spawnX = n ? cx / n + (Math.random() - 0.5) * 30 : W * 0.12;
      const spawnY = n ? cy / n + (Math.random() - 0.5) * 30 : H * 0.5;
      const ra = makeArmy(sim, sim.tacticsFac, "inf", 2000, spawnX, spawnY, { general: "援军", morale: 80 });
      ra.order = "hold"; ra.anchor = { x: spawnX, y: spawnY };
      log(sim, "🚩 锦囊·援军突至：一支生力军已抵达战场！");
    }
    setTick(t => t + 1);
  }, []);

  // 武将专属技：只对被选中的己方军团生效，拥有独立冷却，不消耗锦囊。
  const applyGeneralSkill = useCallback((armyId, skillId) => {
    const sim = simRef.current;
    if (!sim) return;
    const army = sim.armies.find(a => a.id === armyId);
    const skill = army ? generalSkills(army.general).find(s => s.id === skillId) : null;
    if (!army || !skill || army.alive <= 0 || army.genDead) return;
    const cooldown = army.skillCooldowns?.[skillId] || 0;
    if (cooldown > 0) return;
    const team = teamsOf(sim);
    const playerFac = sim.tacticsFac || stateRef.current.fac;
    if (team[army.fac] !== team[playerFac]) return;
    const myTeam = team[army.fac];
    const center = { x: army.cx ?? army.anchor.x, y: army.cy ?? army.anchor.y };
    const enemy = u => !u.dead && !u.flee && team[u.fac] !== myTeam;
    if (skillId === "assassinate") {
      const targets = sim.units.filter(u => enemy(u) && u.gen)
        .map(u => ({ u, d: (u.x - center.x) ** 2 + (u.y - center.y) ** 2 }))
        .sort((a, b) => a.d - b.d);
      const target = targets[0];
      if (!target || target.d > 240 * 240) {
        log(sim, `🗡 ${FACTIONS[army.fac].name}·${army.general} 寻不到可刺杀的敌方武将（需在240步内）`);
        return;
      }
      const targetArmy = sim.armies.find(a => a.id === target.u.army);
      const force = army.generalForce ?? generalCombatStats(army.general).force;
      const wisdom = army.generalWisdom ?? generalCombatStats(army.general).wisdom;
      target.u.hp = Math.max(1, target.u.hp - target.u.maxHp * (0.58 + force / 500));
      if (targetArmy) targetArmy.morale = Math.max(0, targetArmy.morale - (12 + wisdom / 8));
      log(sim, `🗡 武将技「${army.general}·${skill.name.replace(/^🗡 /, "")}」命中 ${FACTIONS[target.u.fac].name}·${targetArmy?.general || "敌将"}！重创敌将，敌军士气−18`);
    } else if (skillId === "wideAttack") {
      const radius2 = 115 * 115;
      const targets = sim.units.filter(u => enemy(u) && (u.x - center.x) ** 2 + (u.y - center.y) ** 2 <= radius2);
      if (!targets.length) {
        log(sim, `🔥 ${FACTIONS[army.fac].name}·${army.general} 施放${skill.name}，范围内没有敌军`);
        return;
      }
      const affectedArmies = new Set();
      for (const target of targets) {
        const wisdom = army.generalWisdom ?? generalCombatStats(army.general).wisdom;
        target.hp = Math.max(1, target.hp - target.maxHp * (target.gen ? 0.24 : 0.30) * (0.85 + wisdom / 300));
        affectedArmies.add(target.army);
      }
      for (const targetArmyId of affectedArmies) {
        const targetArmy = sim.armies.find(a => a.id === targetArmyId);
        if (targetArmy) targetArmy.morale = Math.max(0, targetArmy.morale - (8 + (army.generalWisdom ?? generalCombatStats(army.general).wisdom) / 10));
      }
      log(sim, `🔥 武将技「${army.general}·${skill.name.replace(/^🔥 /, "")}」覆盖${targets.length}支敌军单位，敌军士气−12`);
    }
    army.skillCooldowns = army.skillCooldowns || {};
    army.skillCooldowns[skillId] = 18;
    setTick(t => t + 1);
  }, []);

  /* ---------- 对话事件系统 ---------- */
  const resolveCaptiveChoice = useCallback((option) => {
    const sim = simRef.current;
    const d = dialog;
    if (!sim || !d || d.type !== "captive") return;
    const captor = sim.armies.find(a => a.id === d.captorArmyId);
    const captorName = captor ? `${FACTIONS[captor.fac].name}·${captor.general}` : "我军";
    const vF = FACTIONS[d.victimFac];
    let result = "";
    if (option === "persuade") {
      const p = Math.min(0.75, 0.4 + (captor ? captor.level * 0.08 : 0));
      if (Math.random() < p) {
        const men = 800 + (captor ? captor.level * 300 : 0);
        if (captor) {
          const na = makeArmy(sim, captor.fac, "inf", men,
            captor.anchor ? captor.anchor.x : W / 2,
            captor.anchor ? captor.anchor.y : H / 2,
            { general: `${d.victimGeneral}(降)`, silent: true, morale: 70 });
          na.order = "hold"; na.anchor = { ...(captor.anchor || { x: W / 2, y: H / 2 }) };
        }
        log(sim, `🕊 ${vF.name}·${d.victimGeneral} 归降，率${men}降卒效力！`);
        result = `${d.victimGeneral} 沉吟良久，掷剑于地：「良禽择木而栖——愿效犬马之劳！」\n\n【${d.victimGeneral} 率 ${men.toLocaleString()} 名降卒加入我军，据地列阵】`;
      } else {
        log(sim, `⚔ ${vF.name}·${d.victimGeneral} 宁死不降，囚于军中`);
        result = `${d.victimGeneral} 昂然不跪，厉声道：「要杀便杀，何必多言！」\n\n【劝降不成，暂囚于辎重营中】`;
      }
    } else if (option === "execute") {
      for (const a of sim.armies) if (a.fac === d.victimFac && !a.broken && a.alive > 0)
        a.morale = Math.max(5, a.morale - 8);
      log(sim, `⚔ ${d.victimGeneral} 枭首辕门，${vF.name}军夺气！`);
      result = `刀光落处，${d.victimGeneral} 授首。首级悬于辕门——\n\n【${vF.name}军诸部望之股栗，全军士气−8】`;
    } else if (option === "release") {
      const team = teamsOf(sim);
      const myT = captor ? team[captor.fac] : null;
      for (const a of sim.armies) if (myT && team[a.fac] === myT && !a.broken && a.alive > 0)
        a.morale = Math.min(100, a.morale + 10);
      log(sim, `🌿 ${captorName} 义释 ${d.victimGeneral}，三军感奋！`);
      result = `解其缚，赠鞍马：「两军相争，各为其主。先生请回。」${d.victimGeneral} 回望三次，长揖而去。\n\n【义名远播，我军全军士气+10${mode === "storyBattle" ? "，声望+1" : ""}】`;
      if (mode === "storyBattle" && sim.story) {
        const lk = sim.story.lineKey;
        setStoryProgress(prev => {
          setStoryRenown(pr => {
            const nextR = { ...pr, [lk]: (pr[lk] || 0) + 1 };
            persistStory(prev, nextR, storyDifficulty, storyChoices);
            return nextR;
          });
          return prev;
        });
      }
    }
    setDialog({ ...d, phase: "done", resultText: result });
    setTick(t => t + 1);
  }, [dialog, mode, persistStory, storyDifficulty, storyChoices]);

  const openSurrenderDialog = useCallback((armyId) => {
    const sim = simRef.current;
    const a = sim && sim.armies.find(x => x.id === armyId);
    if (!a) return;
    sim.running = false;
    setRunning(false);
    setDialog({ type: "surrender", phase: "choose", armyId });
  }, []);

  const resolveSurrenderChoice = useCallback((option) => {
    const sim = simRef.current;
    const d = dialog;
    if (!sim || !d || d.type !== "surrender") return;
    const a = sim.armies.find(x => x.id === d.armyId);
    if (!a) { setDialog(null); return; }
    if (option === "cancel") { setDialog(null); return; }
    a.persuadeTried = true;
    const frac = a.total > 0 ? a.alive / a.total : 1;
    let p = (1 - a.morale / 100) * 0.85;
    if (a.genDead) p += 0.25;
    if (frac < 0.5) p += 0.2;
    if (a.starving) p += 0.15;
    p = Math.max(0.05, Math.min(0.85, p));
    const targetFac = stateRef.current.fac;
    let result;
    if (Math.random() < p) {
      defectArmy(sim, a, targetFac);
      result = `阵前使者往还三次。${a.general} 环顾残军，掷盔于地：「罢了！传令——倒戈！」\n\n【${a.general} 所部 ${(a.alive * MEN_PER_DOT).toLocaleString()} 人临阵倒戈，归入${FACTIONS[targetFac].name}军，据地固守】`;
    } else {
      a.morale = Math.min(100, a.morale + 10);
      log(sim, `⚔ ${FACTIONS[a.fac].name}·${a.general} 斩使拒降，同仇敌忾！`);
      result = `${a.general} 拔剑斩案：「再言降者，如同此案！」\n\n【劝降见拒，敌军同仇敌忾，士气+10】`;
    }
    setDialog({ ...d, phase: "done", resultText: result });
    setTick(t => t + 1);
  }, [dialog]);

  const closeDialog = useCallback(() => {
    setDialog(null);
  }, []);

  /* ---------- 天下模式逻辑 ---------- */
  const persistGrand = useCallback(async (g) => {
    try {
      if (g) await storage.set(GRAND_KEY, JSON.stringify(g));
      else await storage.delete(GRAND_KEY);
    } catch { /* ignore */ }
  }, []);

  const gMsg = useCallback((m) => {
    setGrandMsg(prev => [m, ...prev].slice(0, 8));
  }, []);

  const registerCustomGeneral = useCallback(() => {
    if (!grand) return;
    const name = String(customGeneralForm.name || "").trim();
    if (!name) { gMsg("请输入武将姓名"); return; }
    const knownNames = new Set([
      ...Object.values(GENERALS).flat(),
      ...Object.values(FEMALE_BY_FAC).flat(),
      ...(grand.myGens || []).map(g => g.name),
      ...(grand.captives || []).map(g => g.name),
      ...(grand.harem || []).map(g => g.name),
    ]);
    if (knownNames.has(name)) { gMsg(`武将「${name}」已在名册中，不能重复录入`); return; }
    const numberInRange = (value, min, max) => Math.max(min, Math.min(max, Number(value) || min));
    const skills = String(customGeneralForm.skills || "").split(/[，,、]/).map(s => s.trim()).filter(Boolean).slice(0, 6);
    const g = JSON.parse(JSON.stringify(grand));
    g.myGens = g.myGens || [];
    g.myGens.push({
      name,
      gender: customGeneralForm.gender === "f" ? "f" : "m",
      age: numberInRange(customGeneralForm.age, 1, 100),
      force: numberInRange(customGeneralForm.force, 1, 100),
      wisdom: numberInRange(customGeneralForm.wisdom, 1, 100),
      lead: numberInRange(customGeneralForm.lead, 1, 100),
      pol: numberInRange(customGeneralForm.pol, 1, 100),
      loyalty: numberInRange(customGeneralForm.loyalty, 0, 100),
      charm: customGeneralForm.gender === "f" ? numberInRange(customGeneralForm.charm, 1, 100) : null,
      skills: skills.length ? skills : [customGeneralForm.gender === "f" ? "内助安民" : "统军作战"],
      originFac: g.playerFac,
      city: null,
      tried: false,
      recruitedBy: "custom",
    });
    setGrand(g);
    persistGrand(g);
    setCustomGeneralForm({ name: "", gender: "m", age: 25, force: 60, wisdom: 60, lead: 60, pol: 60, loyalty: 75, charm: 80, skills: "统军作战" });
    setCustomGeneralOpen(false);
    gMsg(`已录入武将「${name}」，归入${grandFacLabel(g, g.playerFac)}武将府`);
  }, [customGeneralForm, grand, gMsg, persistGrand]);

  const updateGeneralSkill = useCallback((generalName, skillName, action) => {
    if (!grand || !generalName || !skillName) return;
    const current = grand.myGens?.find(gen => gen.name === generalName);
    if (!current) { gMsg("只有己方武将可以手动管理技能"); return; }
    const g = JSON.parse(JSON.stringify(grand));
    const gen = g.myGens.find(item => item.name === generalName);
    const skills = Array.isArray(gen.skills) ? gen.skills : [];
    if (action === "add") {
      if (skills.includes(skillName)) { gMsg(`${generalName} 已拥有「${skillName}」`); return; }
      if (skills.length >= 8) { gMsg(`${generalName} 最多同时拥有 8 项技能`); return; }
      gen.skills = [...skills, skillName];
      gMsg(`已将「${skillName}」添加给${generalName}`);
    } else {
      gen.skills = skills.filter(skill => skill !== skillName);
      gMsg(`已从${generalName}身上删除「${skillName}」`);
    }
    setGrand(g);
    persistGrand(g);
  }, [grand, gMsg, persistGrand]);

  // 武将府密探行动：对尚未入府的名将消耗通用经验点进行招募、煽动、刺杀或色诱。
  const attemptUnseenGeneralAction = useCallback((name, action, row) => {
    if (!grand || !row || row.gen || row.captive || row.battleArmy || row.eliminated) return;
    const costs = { recruit: 12, incite: 8, assassinate: 15, seduce: 10, entice: 18 };
    const cost = costs[action];
    if (!cost || (stats.experience ?? 0) < cost) return;
    const currentUsage = grand.actionUsage?.turn === grand.turn ? grand.actionUsage : { turn: grand.turn, intimacy: 0, oneNight: 0, agent: 0 };
    const actionLimit = Math.max(1, Number(grand.actionLimits?.agent) || 3);
    if (currentUsage.agent >= actionLimit) {
      gMsg(`本回合密探行动次数已达上限（${actionLimit}次）`);
      return;
    }
    const target = row.profile;
    const operative = grand.myGens.find(g => g.gender !== target.gender) || grand.myGens[0];
    if (action === "seduce" && (!operative || operative.gender === target.gender)) return;
    if (action === "entice" && (target.gender !== "f" || !row.marriage?.startsWith("已婚") || !operative || operative.gender !== "m")) return;
    const g = JSON.parse(JSON.stringify(grand));
    g.actionUsage = { ...currentUsage, agent: currentUsage.agent + 1 };
    g.eliminatedGenerals = g.eliminatedGenerals || [];
    const targetNodes = Object.values(g.nodes).filter(n => n.owner === row.facKey);
    let success = false;
    let message = "";
    if (action === "recruit") {
      success = Math.random() < Math.min(0.72, 0.34 + g.myGens.length * 0.025 + g.turn * 0.004);
      if (success) {
        const gen = { ...rollGen(name, { loyalty: 55, gender: target.gender }), originFac: row.facKey, currentFac: g.playerFac, recruitedBy: "recruit" };
        g.myGens.push(gen);
        message = `🕊 密使说服「${name}」弃暗投明，入武将府听用！`;
      } else message = `📜 招募「${name}」未成，对方暂不愿离开${FACTIONS[row.facKey]?.name || "故主"}。`;
    } else if (action === "incite") {
      success = Math.random() < Math.min(0.78, 0.48 + ((100 - (target.age || 30)) / 500));
      if (success) {
        for (const node of targetNodes) node.loyalty = Math.max(0, node.loyalty - 10);
        message = `🔥 「${name}」被密探煽动，${FACTIONS[row.facKey]?.name || "敌方"}所属城池民心−10！`;
      } else message = `🕯 煽动「${name}」失败，密探险些暴露。`;
    } else if (action === "assassinate") {
      const lead = operative?.lead || 55;
      success = Math.random() < Math.min(0.68, 0.24 + lead / 250);
      if (success) {
        if (!g.eliminatedGenerals.includes(name)) g.eliminatedGenerals.push(name);
        for (const node of targetNodes) {
          node.troops = Math.max(500, Math.round(node.troops * 0.84));
          node.wall = Math.max(0, node.wall - 8);
        }
        message = `🗡 密报传来：「${name}」遭刺杀，${FACTIONS[row.facKey]?.name || "敌方"}军心动摇！`;
      } else message = `🛡 刺杀「${name}」失败，目标提高戒备。`;
    } else if (action === "entice") {
      const charm = operative?.charm || 70;
      const loyalty = target.loyalty || 60;
      success = Math.random() < Math.min(0.62, 0.18 + charm / 300 + (100 - loyalty) / 350);
      if (success) {
        const gen = { ...rollGen(name, { loyalty: 52, gender: "f" }), originFac: row.facKey, currentFac: g.playerFac, recruitedBy: "entice" };
        g.myGens.push(gen);
        message = "🌙 " + operative.name + " 与已婚女将「" + name + "」建立私情，对方自愿离开旧主并入我方武将府。";
      } else message = "🕊 「" + name + "」拒绝了私情策反，已提高戒备。";
    } else if (action === "seduce") {
      const charm = operative?.charm || 70;
      success = Math.random() < Math.min(0.7, 0.25 + charm / 260 + (target.gender === "f" ? (target.charm || 70) / 700 : 0));
      if (success) {
        const gen = { ...rollGen(name, { loyalty: 50, gender: target.gender }), originFac: row.facKey, currentFac: g.playerFac, recruitedBy: "seduce" };
        g.myGens.push(gen);
        message = `💮 「${operative.name}」以情报与礼义周旋，成功让「${name}」转投我方！`;
      } else message = `🌹 色诱「${name}」未能得手，对方识破了这场美人计。`;
    }
    setStats(prev => ({ ...prev, experience: Math.max(0, (prev.experience ?? 0) - cost) }));
    persistStats({ ...stats, experience: Math.max(0, (stats.experience ?? 0) - cost) });
    persistGrand(g);
    setGrand(g);
    gMsg(`${message}（经验点−${cost}）`);
  }, [grand, gMsg, persistGrand, persistStats, stats]);

  const startGrand = useCallback((facKey, periodId = grandPeriodChoice) => {
    const g = newGrandState(facKey, periodId);
    setGrand(g);
    persistGrand(g);
    setGrandPeriodChoice(g.period);
    setGrandSel(null);
    setGrandDiplomacyFocus(null);
    setGrandAttackSources([]);
    setGrandMapZoom(1);
    setGrandSupport(false);
    setGrandMsg([`🌏 ${FACTIONS[facKey].name}主公上任 — 经营城池、扩军拓土，一统天下！`]);
  }, [grandPeriodChoice, persistGrand]);

  const grandDiplomacy = useCallback((targetFac, action) => {
    setGrand(prev => {
      if (!prev || !targetFac || targetFac === prev.playerFac || !GRAND_FACTION_KEYS.includes(targetFac)) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const key = grandRelationKey(g.playerFac, targetFac);
      const current = grandRelation(g, g.playerFac, targetFac);
      const cost = action === "gift" ? 100 : action === "alliance" ? 250 : 0;
      if (action === "gift") {
        if (g.res[g.playerFac].gold < cost) return prev;
        g.res[g.playerFac].gold -= cost;
        g.relations[key] = Math.min(100, current + 12);
        gMsg(`🎁 向${grandFacLabel(g, targetFac)}赠送金帛，友好度 ${current} → ${g.relations[key]}`);
      } else if (action === "alliance") {
        if (grandAreAllied(g, g.playerFac, targetFac)) return prev;
        if (current < 40) { gMsg(`📜 ${grandFacLabel(g, targetFac)}尚未信任我方，友好度至少达到40才可结盟`); return prev; }
        if (g.res[g.playerFac].gold < cost) return prev;
        g.res[g.playerFac].gold -= cost;
        g.alliances = [...new Set([...(g.alliances || []), key])];
        g.relations[key] = Math.min(100, current + 8);
        gMsg(`🤝 与${grandFacLabel(g, targetFac)}缔结盟约：相邻城池可互相出兵支援`);
      } else if (action === "break") {
        if (!grandAreAllied(g, g.playerFac, targetFac)) return prev;
        g.alliances = (g.alliances || []).filter(item => item !== key);
        g.relations[key] = Math.max(-100, current - 25);
        gMsg(`💔 与${grandFacLabel(g, targetFac)}的盟约破裂，友好度降至${g.relations[key]}`);
      } else return prev;
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const grandGovern = useCallback((nodeId, action) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const n = g.nodes[nodeId];
      const r = g.res[g.playerFac];
      const A = GOV_ACTIONS[action];
      if (!n || n.owner !== g.playerFac || n.acted || r.gold < A.gold) return prev;
      r.gold -= A.gold;
      const key = action === "farm" ? "farm" : action === "trade" ? "trade" : action === "wall" ? "wall" : "loyalty";
      n[key] = Math.min(100, n[key] + 8);
      n.acted = true;
      gMsg(`${A.icon} ${GRAND_NODES[nodeId].name}：${A.name}（${key === "farm" ? "农业" : key === "trade" ? "商业" : key === "wall" ? "城防" : "民心"} → ${n[key]}）`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const grandMuster = useCallback((nodeId) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const n = g.nodes[nodeId];
      const r = g.res[g.playerFac];
      if (!n || n.owner !== g.playerFac || n.acted) return prev;
      const costG = n.loyalty >= 80 ? 100 : 200;
      if (r.gold < costG || r.food < 200) return prev;
      r.gold -= costG; r.food -= 200;
      n.troops += 2000;
      n.acted = true;
      gMsg(`🪖 ${GRAND_NODES[nodeId].name} 募兵两千（今 ${n.troops.toLocaleString()} 人）${n.loyalty >= 80 ? " · 民心归附半价" : ""}`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  // 发起进攻：亲征(实时会战) 或 委任(自动裁决)
  // 调兵：己方城池之间转运兵力，按图上跳数计算在途回合
  const grandTransfer = useCallback((fromId, toId, troops) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const from = g.nodes[fromId], to = g.nodes[toId];
      if (!from || from.owner !== g.playerFac || !to || to.owner !== g.playerFac || fromId === toId) return prev;
      const use = Math.min(troops, from.troops - 500);
      if (use < 200) return prev;
      from.troops -= use;
      const hops = Math.max(1, grandHops(fromId, toId));
      g.transfers.push({ fromId, toId, troops: use, eta: g.turn + hops });
      gMsg(`🚚 「${GRAND_NODES[fromId].name}」调兵 ${use.toLocaleString()} 人赴「${GRAND_NODES[toId].name}」（约${hops}回合抵达）`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const grandAttack = useCallback((fromId, toId, troops, personally, withSupport = false) => {
    if (!grand) return;
    const g = JSON.parse(JSON.stringify(grand));
    const fromIds = Array.isArray(fromId) ? fromId : [fromId];
    const sources = fromIds.map(id => ({ id, node: g.nodes[id] }))
      .filter(({ node }) => node && node.owner === g.playerFac);
    const to = g.nodes[toId];
    if (!sources.length || !to || grandAreAllied(g, g.playerFac, to.owner)) return;
    const available = sources.reduce((sum, { node }) => sum + Math.max(0, node.troops - 500), 0);
    const mainUse = Math.min(troops, available);
    if (mainUse < 500) return;
    let remain = mainUse;
    const contributions = [];
    for (const source of sources) {
      const use = Math.min(remain, Math.max(0, source.node.troops - 500));
      if (use <= 0) continue;
      source.node.troops -= use;
      contributions.push({ id: source.id, troops: use });
      remain -= use;
      if (remain <= 0) break;
    }
    const supportInfo = withSupport ? dispatchGrandAlliedSupport(g, fromIds, toId) : { total: 0, contributions: [] };
    const support = supportInfo.total;
    const use = mainUse + support;
    const relationKey = grandRelationKey(g.playerFac, to.owner);
    g.relations[relationKey] = Math.max(-100, grandRelation(g, g.playerFac, to.owner) - 8);
    if (support) {
      const allyLabels = [...new Set(supportInfo.contributions.map(c => grandFacLabel(g, g.nodes[c.id].owner)))];
      gMsg(`🤝 已与${allyLabels.join("、")}商定共攻：${support.toLocaleString()} 人加入「${GRAND_NODES[toId].name}」战事`);
    }
    if (!personally) {
      const r = grandAutoResolve(use, to.troops, effWallOf(g, toId));
      const defFacPre = to.owner;
      if (r.atkWin) {
        to.owner = g.playerFac; to.troops = r.atkLeft; to.acted = true; to.loyalty = 40;
        gMsg(`⚔ 委任出征：攻克 ${GRAND_NODES[toId].name}！余部 ${r.atkLeft.toLocaleString()} 人驻守`);
        const spoils = captureGrandSpoils(g, defFacPre, toId);
        if (spoils.generals.length) {
          gMsg(`🔗 乱军中生擒敌将「${spoils.generals[0]}」，押入俘虏营`);
        }
        for (const family of spoils.families) {
          gMsg(`🏮 城破收押${family.role}「${family.name}」：${family.desc}`);
        }
        g.powTroops += Math.round(to.troops * 0.15) + 300;
      } else {
        const allContributions = [...contributions, ...supportInfo.contributions];
        let left = r.atkLeft;
        for (let i = 0; i < allContributions.length; i++) {
          const c = allContributions[i];
          const add = i === allContributions.length - 1 ? left : Math.round(r.atkLeft * c.troops / use);
          g.nodes[c.id].troops += add;
          left -= add;
        }
        to.troops = r.defLeft;
        gMsg(`☠ 委任出征：攻 ${GRAND_NODES[toId].name} 失利，残部 ${r.atkLeft.toLocaleString()} 人退回`);
      }
      checkGrandOver(g);
      setGrand(g); persistGrand(g);
      return;
    }
    // 亲征：进入实时会战
    setGrand(g); persistGrand(g);
    launchGrandBattle({ fromId: contributions[0].id, fromIds: contributions.map(c => c.id), toId, atkFac: g.playerFac, defFac: to.owner, atkTroops: use, defTroops: to.troops, wall: to.wall, role: "attack" });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grand, persistGrand, gMsg]);

  // 用现有战斗引擎打一场天下会战
  const spawnForce = useCallback((sim, facKey, men, cx, cy, hold) => {
    const mix = [["inf", 0.5], ["arch", 0.3], ["cav", 0.2]];
    const made = [];
    for (const [t, frac] of mix) {
      const m = Math.round(men * frac);
      if (m < 300) continue;
      const a = makeArmy(sim, facKey, t, m,
        cx + (Math.random() - 0.5) * 60,
        cy + (made.length - 1) * 70,
        { silent: true, form: t === "cav" ? "feng" : t === "arch" ? "yan" : "fang" });
      if (hold) { a.order = "hold"; a.anchor = { x: a.anchor.x, y: a.anchor.y }; }
      made.push(a);
    }
    return made;
  }, []);

  const launchGrandBattle = useCallback((ctx) => {
    const node = GRAND_NODES[ctx.toId];
    const seed = 7000 + Object.keys(GRAND_NODES).indexOf(ctx.toId) * 137;
    const opts = node.type === "pass"
      ? { river: "none", mountainBias: 0.06, ridges: [{ colFrac: 0.5, gapFrac: 0.5, gapH: 6 }] }
      : { river: "none", forestBias: 0.03, clearRects: [{ xf: 0.42, yf: 0.36, w: 16, h: 14 }] };
    const terrain = genTerrain(seed, opts);
    simRef.current = {
      terrain, terrainMeta: { seed, opts },
      units: [], armies: [], fx: [], arrows: [], buildings: [],
      allies: grand ? [...(grand.alliances || [])] : [], log: [], running: false, done: false, started: false, battleTime: 0,
    };
    const sim = simRef.current;
    const defX = W * 0.5, defY = H * 0.5;
    const b = addBuilding(sim, ctx.defFac, node.type === "pass" ? "pass" : "city", defX, defY, node.name);
    b.maxHp = b.hp = Math.round(b.maxHp * (1 + ctx.wall / 100));
    spawnForce(sim, ctx.defFac, ctx.defTroops, defX, defY + 80, true);
    const atkSide = ctx.role === "attack" ? "player" : "ai";
    spawnForce(sim, ctx.atkFac, ctx.atkTroops, W * 0.12, H * 0.5, false);
    sim.tactics = 3;
    sim.tacticsFac = grand ? grand.playerFac : ctx.atkFac;
    sim.grandCtx = { ...ctx, resolved: false, objective: node.type === "pass" ? "annihilate" : "capture" };
    log(sim, `🌏 天下会战：${FACTIONS[ctx.atkFac].name}军来攻「${node.name}」`);
    log(sim, node.type === "pass" ? "🎯 攻方须全歼守军；守军存续即守成" : "🎯 攻方围城易帜即克；守军存续即守成");
    repaintTerrain();
    viewRef.current = { z: 1, ox: 0, oy: 0 };
    setRunning(false);
    setSelected([]); selectedRef.current = [];
    setFac(grand ? grand.playerFac : ctx.atkFac);
    setGrandBattleCtx({ ...ctx });
    setMode("grandBattle");
    setTick(t => t + 1);
    void atkSide;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [grand, spawnForce]);

  function checkGrandOver(g) {
    const owners = new Set(Object.values(g.nodes).map(n => n.owner));
    if (owners.size === 1) g.over = [...owners][0];
    else if (![...owners].includes(g.playerFac)) g.over = "defeat";
  }

  // 天下会战结算
  useEffect(() => {
    const sim = simRef.current;
    if (!sim || !sim.grandCtx || sim.grandCtx.resolved || !sim.started || mode !== "grandBattle") return;
    const ctx = sim.grandCtx;
    const armyById = new Map(sim.armies.map(a => [a.id, a]));
    let atkAlive = 0, defAlive = 0;
    for (const u of sim.units) {
      if (u.dead || u.flee) continue;
      const a = armyById.get(u.army);
      if (a && a.order === "retreat") continue;
      if (u.fac === ctx.atkFac) atkAlive++; else if (u.fac === ctx.defFac) defAlive++;
    }
    const bld = sim.buildings.find(x => x.name === GRAND_NODES[ctx.toId].name);
    let atkWin = null;
    if (ctx.objective === "capture" && bld && bld.fac === ctx.atkFac) atkWin = true;
    else if (defAlive === 0) atkWin = true;
    else if (atkAlive === 0) atkWin = false;
    if (atkWin === null) return;
    ctx.resolved = true;
    sim.running = false;
    setRunning(false);
    // 结果回写天下
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const to = g.nodes[ctx.toId];
      const atkLeft = atkAlive * MEN_PER_DOT, defLeft = defAlive * MEN_PER_DOT;
      const playerIsAtk = ctx.atkFac === g.playerFac;
      // 俘虏入营：会战中我方所获战俘与被擒之将
      const myArmies = sim.armies.filter(a => a.fac === g.playerFac);
      const powGain = myArmies.reduce((sm, a) => sm + a.captives, 0) * MEN_PER_DOT;
      if (powGain > 0) { g.powTroops += powGain; gMsg(`⛓ 收押战俘 ${powGain.toLocaleString()} 人入俘虏营`); }
      for (const a of myArmies) {
        for (const nm0 of (a.capturedGens || [])) {
          const nm = nm0.replace(/\(降\)$/, "");
          if (g.myGens.some(x => x.name === nm) || g.captives.some(x => x.name === nm)) continue;
          g.captives.push(rollGen(nm, { loyalty: 42 }));
          gMsg(`🔗 ${isFemaleName(nm) ? "女将" : "敌将"}「${nm}」被押入俘虏营，可于武将府处置`);
        }
      }
      if (atkWin) {
        to.owner = ctx.atkFac; to.troops = Math.max(300, atkLeft); to.acted = true; to.loyalty = 40;
        gMsg(`🏆 「${GRAND_NODES[ctx.toId].name}」易主${FACTIONS[ctx.atkFac].name}！驻军 ${to.troops.toLocaleString()}`);
        const spoils = captureGrandSpoils(g, ctx.defFac, ctx.toId);
        if (spoils.generals.length) {
          gMsg(`🔗 城破生擒敌将「${spoils.generals[0]}」，押入俘虏营，可劝降或处置`);
        }
        for (const family of spoils.families) {
          gMsg(`🏮 收押${family.role}「${family.name}」：${family.desc} 可择礼求亲或礼送归乡`);
        }
      } else {
        to.troops = Math.max(300, defLeft);
        const sourceIds = ctx.fromIds || [ctx.fromId];
        const sourceNodes = sourceIds.map(id => g.nodes[id]).filter(n => n && n.owner === ctx.atkFac);
        let returned = Math.round(atkLeft * 0.6);
        for (let i = 0; i < sourceNodes.length; i++) {
          const add = i === sourceNodes.length - 1 ? returned : Math.round(returned / sourceNodes.length);
          sourceNodes[i].troops += add;
          returned -= add;
        }
        gMsg(`🛡 「${GRAND_NODES[ctx.toId].name}」守成！攻军败退`);
      }
      // 溃兵归队：跑出战场边缘的溃兵按比例延迟计入来源城池下回合兵力
      const strayAtk = Math.round((ctx.fledAtk || 0) * MEN_PER_DOT * 0.15);
      const strayDef = Math.round((ctx.fledDef || 0) * MEN_PER_DOT * 0.15);
      if (strayAtk > 0) {
        const sourceId = (ctx.fromIds || [ctx.fromId])[0];
        const source = g.nodes[sourceId];
        if (source && source.owner === ctx.atkFac) source.strayReturning = (source.strayReturning || 0) + strayAtk;
      }
      if (strayDef > 0 && to.owner === ctx.defFac) {
        to.strayReturning = (to.strayReturning || 0) + strayDef;
      }
      const team = teamsOf(sim);
      recordBattle(sim, team[g.playerFac], team, playerIsAtk ? !!atkWin : !atkWin);
      checkGrandOver(g);
      persistGrand(g);
      return g;
    });
    setGrandBattleCtx(c => c ? { ...c, done: true, atkWin } : c);
  }, [tick, mode, gMsg, persistGrand, recordBattle]);

  // 结束回合：收入、行动重置、AI 行动
  const grandEndTurn = useCallback(() => {
    setGrand(prev => {
      if (!prev || prev.over) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const pendingDefenses = [];
      // 溃兵归队：上次会战流散的兵力计入本回合驻军
      for (const [nid, n] of Object.entries(g.nodes)) {
        if (n.strayReturning) {
          n.troops += n.strayReturning;
          gMsg(`🚶 溃散士卒归队「${GRAND_NODES[nid].name}」（+${n.strayReturning.toLocaleString()}）`);
          n.strayReturning = 0;
        }
      }
      for (const f of GRAND_FACTION_KEYS) {
        const inc = grandIncome(g, f);
        g.res[f].gold += inc.gold;
        g.res[f].food += inc.food;
      }
      // 粮秣损耗
      for (const f of GRAND_FACTION_KEYS) {
        const troops = Object.values(g.nodes).filter(n => n.owner === f).reduce((s, n) => s + n.troops, 0);
        g.res[f].food = Math.max(0, g.res[f].food - Math.round(troops / 100));
      }
      for (const n of Object.values(g.nodes)) n.acted = false;
      // 在途调兵到达
      const arrived = g.transfers.filter(t => t.eta <= g.turn + 1);
      g.transfers = g.transfers.filter(t => t.eta > g.turn + 1);
      for (const t of arrived) {
        const dest = g.nodes[t.toId];
        if (dest.owner === g.playerFac) {
          dest.troops += t.troops;
          gMsg(`🚚 调兵抵达「${GRAND_NODES[t.toId].name}」（+${t.troops.toLocaleString()}）`);
        } else {
          const src = g.nodes[t.fromId];
          if (src && src.owner === g.playerFac) {
            src.troops += t.troops;
            gMsg(`🚚 「${GRAND_NODES[t.toId].name}」已易主，调兵折返「${GRAND_NODES[t.fromId].name}」`);
          } else {
            gMsg(`🚚 调往「${GRAND_NODES[t.toId].name}」的援军失去归处，散于途中`);
          }
        }
      }
      // 俘虏威胁：高威胁俘虏会扰乱看押城池，久不处置还有小概率趁乱逃脱。
      for (const captive of g.captives) {
        const threat = captive.threat ?? (captive.captureType === "family" ? 32 : 58);
        if (threat < 55) continue;
        const guardNode = g.nodes[captive.capturedFrom] && g.nodes[captive.capturedFrom].owner === g.playerFac
          ? g.nodes[captive.capturedFrom]
          : Object.values(g.nodes).find(n => n.owner === g.playerFac);
        if (guardNode) {
          const unrest = captive.captureType === "family" ? 1 : threat >= 80 ? 3 : 2;
          guardNode.loyalty = Math.max(0, guardNode.loyalty - unrest);
          gMsg(`⚠ 俘虏「${captive.name}」威胁升高，「${GRAND_NODES[captive.capturedFrom]?.name || "看押城"}」民心-${unrest}`);
        }
        if (threat >= 82 && Math.random() < threat / 650) {
          g.captives = g.captives.filter(x => x.name !== captive.name);
          gMsg(`🏃 俘虏「${captive.name}」趁看押混乱逃脱，敌方士气受鼓舞`);
        } else {
          captive.threat = Math.min(100, threat + 4);
        }
      }
      // 俘虏营与家眷
      for (const c of g.captives) c.tried = false;
      // 每四回合按战役时间推进一岁；年龄会轻微影响高龄武将的产出，但不会直接淘汰武将。
      if (g.turn % 4 === 0) {
        for (const gen of g.myGens) gen.age = (gen.age ?? generalAge(gen.name)) + 1;
        for (const spouse of g.harem) spouse.age = (spouse.age ?? generalAge(spouse.name)) + 1;
      }
      if (g.harem.length) {
        const charmScore = g.harem.reduce((sum, spouse) => sum + (spouse.charm ?? 70), 0);
        const moraleBonus = Math.min(4, Math.max(1, Math.floor(charmScore / 100)));
        for (const n of Object.values(g.nodes)) if (n.owner === g.playerFac)
          n.loyalty = Math.min(100, n.loyalty + moraleBonus); // 内助之贤
        for (const sp of g.harem) {
          if (!sp.child && (g.heirs.length + g.harem.filter(x => x.child).length) < 3 && Math.random() < 0.15) {
            sp.child = true;
            const nm = HEIR_NAMES[(g.heirs.length + g.harem.filter(x => x.child).length - 1 + HEIR_NAMES.length) % HEIR_NAMES.length];
            g.heirs.push({ name: nm, mother: sp.name, bornTurn: g.turn, mature: false });
            gMsg(`🍼 府中喜讯：${sp.name} 诞下麟儿，取名「${nm}」——八回合后束发听用`);
          }
        }
      }
      for (const h of g.heirs) {
        if (!h.mature && g.turn - h.bornTurn >= 8) {
          h.mature = true;
            const gen = rollGen(h.name);
            gen.lead = 65 + Math.floor(Math.random() * 30);
            gen.pol = 55 + Math.floor(Math.random() * 40);
            gen.gender = "m";
            gen.age = 16;
          g.myGens.push(gen);
          gMsg(`🎉 少主「${h.name}」束发成年（统率${gen.lead} 政务${gen.pol}），入武将府听用！`);
        }
      }
      // 被礼送归乡的成年女俘虏，若确实受到礼遇，可能在之后以自由身份主动回访。
      const returningSuitors = g.releasedSuitors.filter(x => x.returnTurn <= g.turn + 1);
      g.releasedSuitors = g.releasedSuitors.filter(x => x.returnTurn > g.turn + 1);
      for (const suitor of returningSuitors) {
        if (g.myGens.some(x => x.name === suitor.name) || g.captives.some(x => x.name === suitor.name)) continue;
        g.myGens.push({ ...suitor, currentFac: g.playerFac, originFac: suitor.originFac, loyalty: 58, city: null, tried: false, recruitedBy: "releasedCaptive" });
        gMsg(`🌸 女将「${suitor.name}」获释后主动回访，愿以自由身份入武将府；是否接受她的心意，由她本人决定`);
      }
      // AI 行动
      for (const f of GRAND_FACTION_KEYS) {
        if (f === g.playerFac) continue;
        const myNodes = Object.entries(g.nodes).filter(([, n]) => n.owner === f);
        if (!myNodes.length) continue;
        // 内政倾向：随机一城募兵或发展
        const [devId, devN] = myNodes[Math.floor(Math.random() * myNodes.length)];
        if (g.res[f].gold >= 200 && g.res[f].food >= 200 && Math.random() < 0.6) {
          g.res[f].gold -= 200; g.res[f].food -= 200; devN.troops += 2000;
        } else if (g.res[f].gold >= 100) {
          g.res[f].gold -= 100;
          devN.farm = Math.min(100, devN.farm + 8);
        }
        void devId;
        // 攻略倾向
        if (Math.random() < 0.4) {
          let best = null;
          for (const [id, n] of myNodes) {
            for (const adjId of grandAdj(id)) {
              const t = g.nodes[adjId];
              if (grandAreAllied(g, f, t.owner)) continue;
              if (n.troops < 3000) continue;
              if (!best || t.troops < best.t.troops) best = { fromId: id, toId: adjId, n, t };
            }
          }
          if (best) {
            const use = Math.round(best.n.troops * 0.7);
            if (best.t.owner === g.playerFac && !settingsRef.current.autoResolve) {
              // 玩家守城：入列待战（下回合前提示）
              pendingDefenses.push({ ...best, atkFac: f, use });
            } else {
              best.n.troops -= use;
              const r = grandAutoResolve(use, best.t.troops, effWallOf(g, best.toId));
              if (r.atkWin) {
                const oldOwner = best.t.owner;
                best.t.owner = f; best.t.troops = r.atkLeft; best.t.loyalty = 40;
                gMsg(`⚔ ${FACTIONS[f].name}军攻克${oldOwner === g.playerFac ? "我方" : ""}「${GRAND_NODES[best.toId].name}」！`);
              } else {
                best.n.troops += r.atkLeft;
                best.t.troops = r.defLeft;
                gMsg(`🛡 「${GRAND_NODES[best.toId].name}」击退${FACTIONS[f].name}军来犯`);
              }
            }
          }
        }
      }
      g.turn++;
      checkGrandOver(g);
      gMsg(`📯 第 ${g.turn} 回合 — 金${g.res[g.playerFac].gold} 粮${g.res[g.playerFac].food}`);
      persistGrand(g);
      // 玩家守城战：逐一亲征
      if (pendingDefenses.length) {
        const d = pendingDefenses[0];
        d.n.troops -= d.use;
        setTimeout(() => {
          launchGrandBattle({
            fromId: d.fromId, toId: d.toId,
            atkFac: d.atkFac, defFac: g.playerFac,
            atkTroops: d.use, defTroops: g.nodes[d.toId].troops,
            wall: effWallOf(g, d.toId), role: "defend",
          });
        }, 50);
      }
      return g;
    });
  }, [gMsg, persistGrand, launchGrandBattle]);

  const exitGrandBattle = useCallback(async () => {
    // 先退出浏览器原生全屏，再卸载战场容器，避免全屏目标卸载过程中留下黑屏。
    if (document.fullscreenElement === battleAreaRef.current) {
      try { await document.exitFullscreen(); } catch { /* 浏览器已退出时忽略 */ }
    }
    if (simRef.current) simRef.current.running = false;
    setRunning(false);
    setGrandBattleCtx(null);
    setMode("grand");
  }, []);

  /* ---------- 武将府：俘虏 / 家眷 / 太守 ---------- */
  const [grandDialog, setGrandDialog] = useState(null); // {captive, action, phase, resultText}

  const assignGen = useCallback((genName, cityId) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      for (const x of g.myGens) if (x.city === cityId) x.city = null; // 一城一太守
      const gen = g.myGens.find(x => x.name === genName);
      if (gen) gen.city = cityId || null;
      if (gen && cityId) gMsg(`🏛 委任 ${genName} 为「${GRAND_NODES[cityId].name}」太守（政务佐收入、统率固城防）`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const trainGen = useCallback((genName) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const gen = g.myGens.find(x => x.name === genName);
      const cost = 150;
      if (!gen || g.res[g.playerFac].gold < cost) return prev;
      g.res[g.playerFac].gold -= cost;
      const stat = Math.random() < 0.5 ? "lead" : "pol";
      const gain = 1 + Math.floor(Math.random() * 3);
      gen[stat] = Math.min(99, gen[stat] + gain);
      gMsg(`📚 ${genName} 潜心${stat === "lead" ? "练兵" : "研习"}，${stat === "lead" ? "统率" : "政务"}+${gain}（今${gen[stat]}）`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const rewardGen = useCallback((genName) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const gen = g.myGens.find(x => x.name === genName);
      const cost = 100;
      if (!gen || g.res[g.playerFac].gold < cost || gen.loyalty >= 100) return prev;
      g.res[g.playerFac].gold -= cost;
      const gain = Math.min(10, 100 - gen.loyalty);
      gen.loyalty += gain;
      gMsg(`🎁 赏赐 ${genName} 金${cost}，忠诚度+${gain}（今${gen.loyalty}）`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const dismissGen = useCallback((genName) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      g.myGens = g.myGens.filter(x => x.name !== genName);
      g.harem = g.harem.filter(x => x.name !== genName);
      gMsg(`🚶 ${genName} 挂印封金，飘然远去`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const arrangeMarriage = useCallback((maleName, femaleName) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      if (g.harem.some(x => x.name === femaleName)) return prev;
      const male = g.myGens.find(x => x.name === maleName);
      const female = g.myGens.find(x => x.name === femaleName);
      const femaleInfo = female || generalProfile(femaleName, "f");
      g.harem.push({ name: femaleName, gender: "f", age: femaleInfo.age, charm: femaleInfo.charm, skills: femaleInfo.skills, currentFac: g.playerFac, sinceTurn: g.turn, child: false, favor: 70, intimacyCount: 0, lastIntimacyTurn: -1 });
      const charmBonus = (femaleInfo.charm ?? 70) >= 85 ? 10 : 8;
      if (male) male.loyalty = Math.min(100, (male.loyalty ?? 78) + charmBonus);
      if (female) female.loyalty = Math.min(100, (female.loyalty ?? 78) + 8);
      gMsg(`💍 府中做媒：${maleName} 与 ${femaleName} 结为伉俪，${femaleInfo.charm >= 85 ? "高魅力使" : "双方"}忠诚度有所提升`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const haveIntimacy = useCallback((spouseName) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const usage = g.actionUsage?.turn === g.turn ? g.actionUsage : { turn: g.turn, intimacy: 0, oneNight: 0, agent: 0 };
      const limit = Math.max(1, Number(g.actionLimits?.intimacy) || 1);
      if (usage.intimacy >= limit) {
        gMsg(`本回合行房次数已达上限（${limit}次）`);
        return prev;
      }
      const spouse = g.harem.find(x => x.name === spouseName);
      const age = spouse?.age ?? generalAge(spouseName);
      if (!spouse || age < 18) {
        gMsg("行房仅对成年且双方自愿的家眷开放");
        return prev;
      }
      if (g.res[g.playerFac].gold < 30) {
        gMsg("金钱不足，无法置办寝居礼物（需30金）");
        return prev;
      }
      g.res[g.playerFac].gold -= 30;
      g.actionUsage = { ...usage, intimacy: usage.intimacy + 1 };
      spouse.lastIntimacyTurn = g.turn;
      spouse.intimacyCount = (spouse.intimacyCount || 0) + 1;
      spouse.favor = Math.min(100, (spouse.favor ?? 70) + 8);
      const eligibleHeirs = g.harem.filter(x => (x.age ?? generalAge(x.name)) >= 18 && !x.child);
      const canHaveChild = !spouse.child && eligibleHeirs.length <= 3 && g.heirs.length < 3;
      const intimacyBonus = Math.min(0.16, (spouse.intimacyCount || 0) * 0.025 + (spouse.favor || 70) / 1400);
      if (canHaveChild && Math.random() < Math.min(0.72, 0.38 + (spouse.charm ?? 70) / 850 + intimacyBonus)) {
        const nm = HEIR_NAMES[(g.heirs.length + g.turn) % HEIR_NAMES.length];
        g.heirs.push({ name: nm, mother: spouse.name, bornTurn: g.turn, mature: false });
        spouse.child = true;
        gMsg("🌙 与" + spouseName + "共度良宵，情意更笃；喜得子嗣「" + nm + "」");
      } else {
        gMsg("🌙 与" + spouseName + "共度良宵，双方情意与家眷安定有所提升");
      }
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const confessToFemale = useCallback((femaleName) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const female = g.myGens.find(x => x.name === femaleName && x.gender === "f");
      const age = female?.age ?? generalAge(femaleName);
      if (!female || age < 18 || g.harem.some(x => x.name === femaleName)) {
        gMsg("表白仅对未婚成年女将开放");
        return prev;
      }
      if (female.lastCourtshipTurn === g.turn) {
        gMsg(femaleName + " 本回合已经回应过你的心意");
        return prev;
      }
      female.lastCourtshipTurn = g.turn;
      const chance = 0.3 + (female.charm ?? 70) / 250 + (female.loyalty ?? 78) / 500;
      if (Math.random() < chance) {
        female.courtship = Math.min(100, (female.courtship ?? 0) + 55);
        female.loyalty = Math.min(100, (female.loyalty ?? 78) + 3);
        gMsg("💌 " + femaleName + " 接受了你的心意，二人情意渐深");
      } else {
        female.courtship = Math.max(0, (female.courtship ?? 0) - 10);
        gMsg("💌 " + femaleName + " 认为时机未到，请先尊重她的选择");
      }
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const haveOneNight = useCallback((femaleName) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const usage = g.actionUsage?.turn === g.turn ? g.actionUsage : { turn: g.turn, intimacy: 0, oneNight: 0, agent: 0 };
      const limit = Math.max(1, Number(g.actionLimits?.oneNight) || 1);
      if (usage.oneNight >= limit) {
        gMsg(`本回合一夜次数已达上限（${limit}次）`);
        return prev;
      }
      const female = g.myGens.find(x => x.name === femaleName && x.gender === "f");
      const age = female?.age ?? generalAge(femaleName);
      if (!female || age < 18 || g.harem.some(x => x.name === femaleName) || (female.courtship ?? 0) < 55) {
        gMsg("需要成年、未婚且已明确接受心意的女将");
        return prev;
      }
      if (g.res[g.playerFac].gold < 50) {
        gMsg("金钱不足，无法准备私下相伴所需的礼物（需50金）");
        return prev;
      }
      g.res[g.playerFac].gold -= 50;
      g.actionUsage = { ...usage, oneNight: usage.oneNight + 1 };
      female.lastOneNightTurn = g.turn;
      female.courtship = Math.min(100, (female.courtship ?? 0) + 10);
      female.loyalty = Math.min(100, (female.loyalty ?? 78) + 2);
      const canHaveChild = g.heirs.length < 3 && Math.random() < Math.min(0.62, 0.25 + (female.charm ?? 70) / 850 + (female.courtship ?? 0) / 1200);
      if (canHaveChild) {
        const nm = HEIR_NAMES[(g.heirs.length + g.turn + femaleName.length) % HEIR_NAMES.length];
        g.heirs.push({ name: nm, mother: femaleName, bornTurn: g.turn, mature: false });
        female.child = true;
        gMsg("🌙 你与" + femaleName + "在相互情愿下共度一夜；数月后传来喜讯，得子嗣「" + nm + "」");
      } else {
        gMsg("🌙 你与" + femaleName + "在相互情愿下共度一夜，关系更进一步");
      }
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  /* ---------- 作弊工具 ---------- */
  const cheatGrandGold = useCallback(() => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      g.res[g.playerFac].gold += 1000;
      g.res[g.playerFac].food += 1000;
      gMsg("🛠 作弊：金+1000 粮+1000");
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);
  const cheatGrandEmpowerCities = useCallback(() => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      let count = 0;
      for (const n of Object.values(g.nodes)) if (n.owner === g.playerFac) {
        n.troops = Math.max(n.troops || 0, 20000);
        n.farm = 100; n.trade = 100; n.wall = 100; n.loyalty = 100; n.acted = false;
        count++;
      }
      g.res[g.playerFac].gold = Math.max(g.res[g.playerFac].gold, 10000);
      g.res[g.playerFac].food = Math.max(g.res[g.playerFac].food, 10000);
      gMsg(`🛠 作弊：我方${count}座城池强化，驻军、城防、民心与资源拉满`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);
  const cheatGrandGenerals = useCallback(() => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      for (const gen of g.myGens || []) {
        gen.force = 100; gen.wisdom = 100; gen.lead = 100; gen.pol = 100; gen.loyalty = 100;
        if (gen.gender === "f") gen.charm = 100;
      }
      gMsg(`🛠 作弊：我方${(g.myGens || []).length}名武将全属性与忠诚度拉满`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);
  const cheatGrandDiplomacy = useCallback(() => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      g.relations = g.relations || {};
      g.alliances = [];
      for (let i = 0; i < GRAND_FACTION_KEYS.length; i++) {
        for (let j = i + 1; j < GRAND_FACTION_KEYS.length; j++) {
          const key = grandRelationKey(GRAND_FACTION_KEYS[i], GRAND_FACTION_KEYS[j]);
          g.relations[key] = 100;
          if (GRAND_FACTION_KEYS[i] === g.playerFac || GRAND_FACTION_KEYS[j] === g.playerFac) g.alliances.push(key);
        }
      }
      gMsg("🛠 作弊：与各方友好度提升至100，并与所有势力结盟");
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);
  const cheatGrandMatureHeirs = useCallback(() => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const known = new Set((g.myGens || []).map(gen => gen.name));
      for (const heir of g.heirs || []) {
        heir.mature = true;
        if (!known.has(heir.name)) {
          const gen = rollGen(heir.name, { age: 16, loyalty: 100, gender: "m" });
          gen.lead = 100; gen.pol = 100; gen.force = 100; gen.wisdom = 100;
          gen.originFac = g.playerFac;
          g.myGens.push(gen);
          known.add(heir.name);
        }
      }
      gMsg(`🛠 作弊：${(g.heirs || []).length}名子嗣立即成年并入武将府`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);
  const cheatGrandActionLimit = useCallback((key, amount = 5) => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const defaults = { intimacy: 1, oneNight: 1, agent: 3 };
      const current = Number(g.actionLimits?.[key]) || defaults[key];
      g.actionLimits = { ...defaults, ...(g.actionLimits || {}), [key]: Math.min(99, current + amount) };
      g.actionUsage = { turn: g.turn, intimacy: 0, oneNight: 0, agent: 0 };
      const labels = { intimacy: "行房", oneNight: "一夜", agent: "密探行动" };
      gMsg(`🛠 作弊：${labels[key]}本回合上限提升至${g.actionLimits[key]}次，并重置本回合次数`);
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);
  const cheatGrandUnlimitedActions = useCallback(() => {
    setGrand(prev => {
      if (!prev) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      g.actionLimits = { intimacy: 99, oneNight: 99, agent: 99 };
      g.actionUsage = { turn: g.turn, intimacy: 0, oneNight: 0, agent: 0 };
      gMsg("🛠 作弊：行房、一夜、密探行动本回合上限均设为99次");
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);
  const cheatGrandCapture = useCallback(() => {
    setGrand(prev => {
      if (!prev || !grandSel) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      const n = g.nodes[grandSel];
      if (n && n.owner !== g.playerFac) {
        n.owner = g.playerFac; n.troops = Math.max(n.troops, 2000); n.acted = false; n.loyalty = 50;
        gMsg(`🛠 作弊：「${GRAND_NODES[grandSel].name}」直接归附`);
        checkGrandOver(g);
        persistGrand(g);
      }
      return g;
    });
  }, [gMsg, persistGrand, grandSel]);
  const cheatFullHealMorale = useCallback(() => {
    const sim = simRef.current;
    if (!sim) return;
    for (const u of sim.units) if (!u.dead) u.hp = u.maxHp;
    for (const a of sim.armies) { a.morale = 100; a.food = 100; a.broken = false; }
    log(sim, "🛠 作弊：全军回满血、士气与粮草拉满");
    setTick(t => t + 1);
  }, []);
  const cheatForceResult = useCallback((win) => {
    const sim = simRef.current;
    if (!sim) return;
    if (sim.story) { sim.story.resolved = false; }
    const enemyFacs = new Set(sim.units.filter(u => !u.dead && u.fac !== stateRef.current.fac).map(u => u.fac));
    if (win) {
      for (const u of sim.units) if (!u.dead && enemyFacs.has(u.fac)) u.dead = true;
    } else {
      for (const u of sim.units) if (!u.dead && u.fac === stateRef.current.fac) u.dead = true;
    }
    sim.started = true;
    log(sim, `🛠 作弊：强制${win ? "胜利" : "失败"}`);
    setTick(t => t + 1);
  }, []);
  const cheatStoryRenown = useCallback(() => {
    if (!storyStage) return;
    const lk = storyStage.line;
    setStoryRenown(pr => {
      const next = { ...pr, [lk]: (pr[lk] || 0) + 10 };
      persistStory(storyProgress, next, storyDifficulty, storyChoices);
      return next;
    });
    setSaveMsg("🛠 声望+10"); setTimeout(() => setSaveMsg(""), 2000);
  }, [storyStage, storyProgress, storyDifficulty, storyChoices, persistStory]);
  const cheatExperience = useCallback(() => {
    setStats(prev => {
      const next = { ...prev, experience: (prev.experience ?? 0) + 100 };
      persistStats(next);
      return next;
    });
    setSaveMsg("🛠 通用经验+100"); setTimeout(() => setSaveMsg(""), 2000);
  }, [persistStats]);
  const cheatUnlockAll = useCallback(() => {
    const cp = {}; for (const f of CAMPAIGN_FACS) cp[f] = CAMPAIGN_ORDER[f].length;
    setCampaignProgress(cp); persistCampaign(cp, campaignProfiles);
    const sp = {}; for (const [k, l] of Object.entries(STORY)) sp[k] = l.chapters.length;
    setStoryProgress(sp); persistStory(sp, storyRenown, storyDifficulty, storyChoices);
    setSaveMsg("🛠 已解锁全部关卡与章节"); setTimeout(() => setSaveMsg(""), 2000);
  }, [campaignProfiles, persistCampaign, storyRenown, storyDifficulty, storyChoices, persistStory]);

  const powAction = useCallback((action) => {
    setGrand(prev => {
      if (!prev || prev.powTroops <= 0) return prev;
      const g = JSON.parse(JSON.stringify(prev));
      if (action === "enlist") {
        const cost = Math.ceil(g.powTroops / 1000) * 30;
        if (g.res[g.playerFac].gold < cost) return prev;
        g.res[g.playerFac].gold -= cost;
        let bestId = null;
        for (const [id, n] of Object.entries(g.nodes))
          if (n.owner === g.playerFac && (!bestId || n.troops > g.nodes[bestId].troops)) bestId = id;
        if (bestId) {
          g.nodes[bestId].troops += g.powTroops;
          gMsg(`⛓→🪖 ${g.powTroops.toLocaleString()} 名战俘编入「${GRAND_NODES[bestId].name}」行伍（费${cost}金）`);
          g.powTroops = 0;
        }
      } else {
        for (const n of Object.values(g.nodes)) if (n.owner === g.playerFac) n.loyalty = Math.min(100, n.loyalty + 3);
        gMsg(`⛓→🌿 遣散 ${g.powTroops.toLocaleString()} 名战俘归乡，仁名布于四海（诸城民心+3）`);
        g.powTroops = 0;
      }
      persistGrand(g);
      return g;
    });
  }, [gMsg, persistGrand]);

  const captiveAction = useCallback((name, action) => {
    const g0 = grand;
    if (!g0) return;
    const c = g0.captives.find(x => x.name === name);
    if (!c) return;
    // 对话式处置
    const open = (resultFn) => {
      const { text, apply } = resultFn();
      setGrandDialog({ captive: c, action, phase: "done", resultText: text });
      setGrand(prev => {
        const g = JSON.parse(JSON.stringify(prev));
        apply(g);
        checkGrandOver(g);
        persistGrand(g);
        return g;
      });
    };
    if (action === "specialInterrogate") {
      if (c.gender !== "f" || c.tried) return;
      open(() => {
        const success = Math.random() < Math.min(0.88, 0.58 + (c.pol || 60) / 420 + (c.charm || 70) / 700);
        const enemyNodes = Object.entries(g0.nodes).filter(([, node]) => node.owner === c.originFac);
        const intelNode = enemyNodes.length ? enemyNodes[Math.floor(Math.random() * enemyNodes.length)] : null;
        if (success) {
          return {
            text: `你命女官与礼官依身份、乡里和宗族关系耐心问讯，避免惊扰与羞辱。${c.name} 愿以旧日人脉交换平安，透露了更完整的粮道与守军消息。\n\n【特殊军情：敌方据点军力与民心同时受影响；她对我方戒备下降】`,
            apply: (g) => {
              const captive = g.captives.find(x => x.name === name);
              if (!captive) return;
              captive.loyalty = Math.max(0, (captive.loyalty ?? 42) - 5);
              captive.threat = Math.max(0, (captive.threat ?? 32) - 22);
              captive.courtship = Math.min(100, (captive.courtship ?? 0) + 15);
              captive.tried = true;
              const node = intelNode ? g.nodes[intelNode[0]] : Object.values(g.nodes).find(x => x.owner !== g.playerFac);
              if (node) {
                node.troops = Math.max(500, node.troops - 800);
                node.wall = Math.max(0, node.wall - 8);
                node.loyalty = Math.max(0, node.loyalty - 8);
                gMsg(`🌸 女官问讯获得完整军情：「${GRAND_NODES[intelNode?.[0]]?.name || "敌方据点"}」驻军−800、城防−8、民心−8`);
              } else gMsg(`🌸 女官问讯「${captive.name}」成功，威胁度降至${captive.threat}，更愿意接受礼遇`);
            },
          };
        }
        return {
          text: `${c.name} 谨守家门旧事，不愿透露军情；女官仍以礼送回，避免冲突扩大。\n\n【问讯未成：威胁度小幅上升】`,
          apply: (g) => {
            const captive = g.captives.find(x => x.name === name);
            if (!captive) return;
            captive.threat = Math.min(100, (captive.threat ?? 32) + 8);
            captive.tried = true;
            gMsg(`🌸 女官问讯「${captive.name}」未获军情，威胁度升至${captive.threat}`);
          },
        };
      });
    } else if (action === "interrogate") {
      if (c.tried) return;
      open(() => {
        const success = Math.random() < Math.min(0.82, 0.48 + (100 - (c.threat ?? 50)) / 220 + g0.turn * 0.004);
        const enemyNodes = Object.entries(g0.nodes).filter(([, node]) => node.owner === c.originFac);
        const intelNode = enemyNodes.length ? enemyNodes[Math.floor(Math.random() * enemyNodes.length)] : null;
        if (success) {
          return {
            text: `你命人依法审讯，反复核对口供与军中文书。${c.name} 最终吐露一处敌军布置，但对你方的强硬手段心生戒备。\n\n【获得军情：敌方一处据点防务受影响；忠诚度下降】`,
            apply: (g) => {
              const captive = g.captives.find(x => x.name === name);
              if (!captive) return;
              captive.loyalty = Math.max(0, (captive.loyalty ?? 42) - 12);
              captive.threat = Math.max(0, (captive.threat ?? 50) - 12);
              captive.tried = true;
              const node = intelNode ? g.nodes[intelNode[0]] : Object.values(g.nodes).find(x => x.owner !== g.playerFac);
              if (node) {
                node.troops = Math.max(500, node.troops - 500);
                node.loyalty = Math.max(0, node.loyalty - 4);
                gMsg(`🗺 审讯获得军情：「${GRAND_NODES[intelNode?.[0]]?.name || "敌方据点"}」驻军−500、民心−4`);
              } else gMsg(`📜 审讯「${captive.name}」获得敌方军情，威胁度降至${captive.threat}`);
            },
          };
        }
        return {
          text: `${c.name} 守口如瓶，审讯未能得到可靠军情；营中气氛紧张，必须加强看押。\n\n【审讯失败：威胁度上升】`,
          apply: (g) => {
            const captive = g.captives.find(x => x.name === name);
            if (!captive) return;
            captive.threat = Math.min(100, (captive.threat ?? 50) + 12);
            captive.tried = true;
            gMsg(`⚠ 审讯「${captive.name}」未获军情，威胁度升至${captive.threat}`);
          },
        };
      });
    } else if (action === "guard") {
      if (c.tried || g0.res[g0.playerFac].gold < 50) return;
      open(() => ({
        text: `你命亲卫加固营门、清点兵械，并安排轮班看守「${c.name}」。俘虏见戒备森严，反抗之意稍减。\n\n【威胁度下降，金−50】`,
        apply: (g) => {
          const captive = g.captives.find(x => x.name === name);
          if (!captive) return;
          g.res[g.playerFac].gold -= 50;
          captive.threat = Math.max(0, (captive.threat ?? 50) - 30);
          captive.tried = true;
          gMsg(`🛡 加强看押「${captive.name}」，威胁度降至${captive.threat}`);
        },
      }));
    } else if (action === "persuade") {
      if (c.tried) return;
      open(() => {
        const p = Math.min(0.7, Math.max(0.12, 0.25 + (100 - c.lead) / 200 + g0.turn * 0.005 + (55 - (c.threat ?? 50)) / 500));
        if (Math.random() < p) {
          return {
            text: `${c.name} 囚居多日，见军中秋毫无犯、主公以礼相待，长叹一声：「乱世择主而事——愿为帐下效力。」\n\n【${c.name}（统率${c.lead} 政务${c.pol}）入武将府听用】`,
            apply: (g) => {
              g.captives = g.captives.filter(x => x.name !== name);
              g.myGens.push({ ...c, loyalty: Math.max(55, c.loyalty || 42), city: null, tried: false, currentFac: g.playerFac });
              gMsg(`🕊 「${c.name}」归降，入武将府`);
            },
          };
        }
        return {
          text: `${c.name} ${c.gender === "f" ? "凛然" : "昂然"}道：「故主之恩未报，恕难从命。」\n\n【劝降不成，本回合不可再劝；来日方长】`,
          apply: (g) => { const x = g.captives.find(z => z.name === name); if (x) x.tried = true; },
        };
      });
    } else if (action === "release") {
      open(() => {
        const adultFemale = c.gender === "f" && (c.age ?? generalAge(c.name)) >= 18;
        const moved = adultFemale && Math.random() < Math.min(0.62, 0.18 + (c.charm ?? 70) / 300 + (c.courtship ?? 0) / 220 - (c.threat ?? 40) / 500);
        return {
          text: `解其缚，赠鞍马盘缠。${c.name} 深深一揖：「今日之义，${c.gender === "f" ? "妾身" : "某"}铭记于心。」\n\n${moved ? "她没有当场留下，只说若日后仍愿以礼相待，或许会再来相见。" : "她向你道谢后，带着随从踏上归途。"}\n\n【义名远播：诸城民心+4】`,
          apply: (g) => {
            g.captives = g.captives.filter(x => x.name !== name);
            for (const n of Object.values(g.nodes)) if (n.owner === g.playerFac) n.loyalty = Math.min(100, n.loyalty + 4);
            if (moved) {
              g.releasedSuitors.push({ ...c, returnTurn: g.turn + 1, threat: 0, tried: false, courtship: Math.max(60, c.courtship ?? 0) });
              gMsg(`🌿 义释「${c.name}」；她似乎被礼遇触动，日后可能以自由身份回访`);
            } else gMsg(`🌿 义释「${c.name}」，诸城民心+4`);
          },
        };
      });
    } else if (action === "ransom") {
      open(() => {
        const gold = c.lead * 8;
        return {
          text: `遣使议赎，敌营以${gold}金易还其将。押送出境之日，${c.name} 回望良久。\n\n【得金 ${gold}】`,
          apply: (g) => {
            g.captives = g.captives.filter(x => x.name !== name);
            g.res[g.playerFac].gold += gold;
            gMsg(`🪙 纳赎放还「${c.name}」，得金${gold}`);
          },
        };
      });
    } else if (action === "execute") {
      open(() => ({
        text: `军法无情。${c.name} 整衣冠，面北而立，从容就戮。\n\n【三军肃然，然仁者不忍：诸城民心−3】`,
        apply: (g) => {
          g.captives = g.captives.filter(x => x.name !== name);
          for (const n of Object.values(g.nodes)) if (n.owner === g.playerFac) n.loyalty = Math.max(0, n.loyalty - 3);
          gMsg(`⚔ 处决「${c.name}」，诸城民心−3`);
        },
      }));
    } else if (action === "marry") {
      if (c.gender !== "f" || c.tried) return;
      open(() => {
        const p = 0.35 + (g0.harem.length === 0 ? 0.1 : 0) + g0.turn * 0.005 + ((c.charm ?? 70) - 70) / 250 + (c.courtship ?? 0) / 500;
        if (Math.random() < Math.min(0.65, p)) {
          return {
            text: `${c.captureType === "family" ? `城破之后，你先约束军士，不许惊扰妇孺，并命人整备礼仪、归还私物。${c.name}（${c.role}）的来历是：${c.desc}` : "屏退左右，以礼相见"}\n\n你说道：「愿结秦晋之好，共谋大业——绝不相强，去留悉听尊便。」\n\n${c.name} 沉默良久，望着帐外烽烟，轻轻颔首：「……乱世飘零，得君以礼相待，妾愿托付。」\n\n【${c.name} 入府为眷。内助之贤：每回合诸城民心徐增，或有弄璋之喜】`,
            apply: (g) => {
              g.captives = g.captives.filter(x => x.name !== name);
              g.harem.push({ name: c.name, gender: c.gender, age: c.age, charm: c.charm, skills: c.skills, role: c.role, desc: c.desc, originFac: c.originFac, currentFac: g.playerFac, sinceTurn: g.turn, child: false, favor: 70, intimacyCount: 0, lastIntimacyTurn: -1 });
              gMsg(`💍 以礼成婚：「${c.name}」入府为眷，两家旧怨稍解`);
            },
          };
        }
        return {
          text: `${c.name} 敛衽正色：「国仇未雪，宁死不从。请以礼相待——或杀或放，悉听尊便。」\n\n${c.captureType === "family" ? "你没有强迫，只命人继续护送她的随从与私物。" : "言辞铮铮，反令人敬重三分。"}\n\n【求亲被拒。其志可敬，本回合不可再议；可择日劝降、索赎或礼送归乡】`,
          apply: (g) => { const x = g.captives.find(z => z.name === name); if (x) x.tried = true; },
        };
      });
    }
  }, [grand, gMsg, persistGrand]);

  const openStoryChapter = useCallback((lineKey, idx) => {
    if (simRef.current) simRef.current.running = false;
    setRunning(false);
    setStoryStage({ line: lineKey, idx });
    const ch = STORY[lineKey].chapters[idx];
    setStoryChoice(ch.choices ? ch.choices[0].id : null);
    setStoryResult(null);
    setMode("storyIntro");
  }, []);

  const startStoryBattle = useCallback((lineKey, idx, choiceId) => {
    const line = STORY[lineKey];
    const ch = line.chapters[idx];
    const terrain = genTerrain(ch.seed, ch.opts || {});
    simRef.current = {
      terrain, terrainMeta: { seed: ch.seed, opts: ch.opts || {} },
      units: [], armies: [], fx: [], arrows: [], buildings: [],
      allies: ch.allies ? [...ch.allies] : [],
      log: [], running: false, done: false, started: false, battleTime: 0,
    };
    const renown = storyRenown[lineKey] || 0;
    const priorChoice = idx > 0 ? (storyChoices[lineKey]?.[idx - 1] ?? null) : null;
    ch.setup(simRef.current, { renown, priorChoice }, choiceId);
    const simNow = simRef.current;
    const team0 = teamsOf(simNow);
    const pT0 = team0[line.fac];
    // 难度缩放
    if (storyDifficulty === "easy") {
      for (const a of simNow.armies) {
        if (team0[a.fac] === pT0) { a.morale = Math.min(100, a.morale + 10); continue; }
        const list = simNow.units.filter(u => u.army === a.id && !u.gen);
        const cut = new Set(list.slice(0, Math.floor(list.length * 0.3)));
        if (cut.size) {
          simNow.units = simNow.units.filter(u => !cut.has(u));
          a.total -= cut.size; a.alive -= cut.size;
        }
        a.morale = Math.max(45, a.morale - 12);
      }
      log(simNow, "☯ 简单难度：敌军三停去一，士气受挫");
    } else if (storyDifficulty === "hard") {
      for (const a of simNow.armies) {
        if (team0[a.fac] === pT0) continue;
        a.level = Math.min(5, a.level + 1);
        a.morale = 100;
      }
      log(simNow, "🔥 困难难度：敌军皆百战之师");
    }
    // 声望加成：己方军队士气与初始战阵
    for (const a of simNow.armies) {
      if (a.fac === line.fac) {
        a.morale = Math.min(100, a.morale + Math.min(12, renown));
        a.level = Math.max(a.level, Math.min(2, Math.floor(renown / 8)));
      }
    }
    // 主角光环
    if (ch.protectGeneral) {
      const pa = simNow.armies.find(a => a.fac === line.fac && a.general === ch.protectGeneral);
      if (pa) pa.protectRegen = true;
    }
    // 锦囊
    simNow.tactics = storyDifficulty === "easy" ? 4 : storyDifficulty === "hard" ? 2 : 3;
    simNow.tacticsFac = line.fac;
    simNow.story = {
      lineKey, idx, choiceId,
      objective: ch.objective,
      playerFac: line.fac,
      protectGeneral: ch.protectGeneral || null,
      resolved: false,
    };
    log(simRef.current, `📖 ${line.name}·${ch.title}`);
    log(simRef.current, `🎯 ${ch.objective.desc}`);
    repaintTerrain();
    viewRef.current = { z: 1, ox: 0, oy: 0 };
    setRunning(false);
    setSelected([]); selectedRef.current = [];
    setFac(line.fac);
    setStoryStage({ line: lineKey, idx, choiceId });
    setStoryResult(null);
    setMode("storyBattle");
    setTick(t => t + 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storyRenown, storyDifficulty, storyChoices]);

  const exitToStoryHome = useCallback(() => {
    if (simRef.current) simRef.current.running = false;
    setRunning(false);
    setMode("storyHome");
    setStoryResult(null);
  }, []);

  // main loop
  useEffect(() => {
    let last = performance.now();
    let uiAcc = 0;
    const loop = (now) => {
      const sim = simRef.current;
      const rawDt = Math.min(0.05, (now - last) / 1000);
      last = now;
      if (sim && corpseCanvasRef.current) {
        const speed = SPEEDS[stateRef.current.speedIdx ?? 2];
        const corpseCtx = corpseCanvasRef.current.getContext("2d");
        const simDt = rawDt * speed;
        const steps = Math.max(1, Math.ceil(speed));
        for (let i = 0; i < steps; i++) stepSim(sim, simDt / steps, corpseCtx);
        const ctx = canvasRef.current?.getContext("2d");
        if (ctx) {
          ctx.setTransform(1, 0, 0, 1, 0, 0);
          if (view3DRef.current) {
            // 3D 视图接管战场画面，但保留这张 Canvas 作为透明的输入层。
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
          } else {
            const { z, ox, oy } = viewRef.current;
            ctx.fillStyle = "#14100b";
            ctx.fillRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            ctx.setTransform(z, 0, 0, z, ox, oy);
            ctx.drawImage(terrainCanvasRef.current, 0, 0);
            if (settingsRef.current.stains) ctx.drawImage(corpseCanvasRef.current, 0, 0);
            drawBuildings(ctx, sim, now / 1000);
            drawUnits(ctx, sim, now / 1000, selectedRef.current, settingsRef.current, selRectRef.current);
            ctx.setTransform(1, 0, 0, 1, 0, 0);
          }
        }
        uiAcc += rawDt;
        if (uiAcc > 0.25) { uiAcc = 0; setTick(t => t + 1); }
      }
      rafRef.current = requestAnimationFrame(loop);
    };
    rafRef.current = requestAnimationFrame(loop);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  // detect campaign stage resolution once battle concludes
  useEffect(() => {
    const sim = simRef.current;
    if (!sim || !sim.campaign || sim.campaign.resolved || !sim.done) return;
    sim.campaign.resolved = true;
    const team = teamsOf(sim);
    const playerTeam = team[sim.campaign.fac];
    const aliveFacs = new Set();
    for (const u of sim.units) if (!u.dead && !u.flee) aliveFacs.add(u.fac);
    const win = [...aliveFacs].some(f => team[f] === playerTeam);
    setCampaignResult(win ? "win" : "lose");
    recordBattle(sim, playerTeam, team, win);
    if (win) {
      const fk = sim.campaign.fac, idx = sim.campaign.idx;
      setCampaignRewardOptions(getCampaignRewardOptions(fk, idx));
      setCampaignRewardPending(true);
      setCampaignProgress(prev => {
        const next = { ...prev, [fk]: Math.max(prev[fk] || 0, idx + 1) };
        persistCampaign(next, campaignProfiles);
        return next;
      });
    } else {
      setCampaignRewardOptions([]);
      setCampaignRewardPending(false);
    }
  }, [campaignProfiles, tick, persistCampaign, recordBattle]);

  // ---- story objective evaluation ----
  useEffect(() => {
    const sim = simRef.current;
    if (!sim || !sim.story || sim.story.resolved || !sim.started || mode !== "storyBattle") return;
    const st = sim.story;
    const o = st.objective;
    const team = teamsOf(sim);
    const pTeam = team[st.playerFac];
    const armyById = new Map(sim.armies.map(a => [a.id, a]));
    let playerAlive = false, enemyAlive = false;
    for (const u of sim.units) {
      if (u.dead || u.flee) continue;
      const a = armyById.get(u.army);
      const mine = team[u.fac] === pTeam;
      if (mine && a && a.order === "retreat") continue;
      if (mine) playerAlive = true; else enemyAlive = true;
    }
    const prot = st.protectGeneral
      ? sim.armies.find(a => a.fac === st.playerFac && a.general === st.protectGeneral)
      : null;
    let win = null;
    if (prot && prot.genDead) win = false; // 主角阵亡/被擒即败
    if (win === null) {
      switch (o.type) {
        case "annihilate": if (!enemyAlive) win = true; break;
        case "survive":
          if ((sim.battleTime || 0) >= o.t) win = playerAlive || (prot && !prot.genDead && prot.alive > 0);
          break;
        case "escort": if ((prot && prot.genEscaped) || !enemyAlive) win = true; break;
        case "slay": {
          const e = sim.armies.find(a => a.general === o.general && team[a.fac] !== pTeam);
          if (e && e.genDead) win = true;
          break;
        }
        case "capture": {
          const b = sim.buildings.find(x => x.name === o.building);
          if (b && team[b.fac] === pTeam) win = true;
          break;
        }
        case "burncamps": {
          const camps = sim.buildings.filter(x => x.type === "camp" && team[x.fac] !== pTeam);
          if (camps.length && camps.every(x => x.hp <= 0)) win = true;
          break;
        }
        default: break;
      }
    }
    if (win === null && !playerAlive && !(prot && prot.order === "retreat" && !prot.genDead && o.type === "escort")) {
      win = false; // 全军覆没/尽溃
    }
    if (win !== null) {
      st.resolved = true;
      sim.running = false;
      setRunning(false);
      setStoryResult(win ? "win" : "lose");
      const line = STORY[st.lineKey];
      const ch = line.chapters[st.idx];
      log(sim, win ? `🏆 ${ch.title} 目标达成！` : `☠ ${ch.title} 功败垂成`);
      recordBattle(sim, pTeam, team, win);
      if (win) {
        setStoryChoices(prevC => {
          const nextC = { ...prevC, [st.lineKey]: { ...(prevC[st.lineKey] || {}), [st.idx]: st.choiceId } };
          setStoryProgress(prev => {
            const nextP = { ...prev, [st.lineKey]: Math.max(prev[st.lineKey] || 0, st.idx + 1) };
            setStoryRenown(pr => {
              const nextR = { ...pr, [st.lineKey]: (pr[st.lineKey] || 0) + (ch.renown || 2) };
              persistStory(nextP, nextR, storyDifficulty, nextC);
              return nextR;
            });
            return nextP;
          });
          return nextC;
        });
      } else {
        // 败中取益：败战亦得一点声望，屡败屡战终能功成
        setStoryProgress(prev => {
          setStoryRenown(pr => {
            const nextR = { ...pr, [st.lineKey]: (pr[st.lineKey] || 0) + 1 };
            persistStory(prev, nextR, storyDifficulty, storyChoices);
            return nextR;
          });
          return prev;
        });
      }
    }
  }, [tick, mode, persistStory, recordBattle, storyDifficulty, storyChoices]);

  // ---- 对话事件泵：擒将等事件触发时暂停并弹出对话 ----
  useEffect(() => {
    const sim = simRef.current;
    if (!sim || dialog) return;
    if (sim.pendingDialogs && sim.pendingDialogs.length > 0) {
      const d = sim.pendingDialogs.shift();
      sim.running = false;
      setRunning(false);
      setDialog({ ...d, phase: "choose" });
    }
  }, [tick, dialog]);

  stateRef.current = { fac, utype, placeMode, men, speedIdx, running, scenario, selected, sizeKey, mode, generalManagerOpen, customGeneralOpen, grandSel, grandHudOpen };

  // 全局保险：若窗口失焦或原生菜单意外弹出，强制清空拖拽状态，避免卡死
  useEffect(() => {
    const clear = () => { dragRef.current = null; selRectRef.current = null; };
    window.addEventListener("blur", clear);
    window.addEventListener("contextmenu", (e) => {
      if (dragRef.current) e.preventDefault();
    }, true);
    return () => window.removeEventListener("blur", clear);
  }, []);

  const start = useCallback(() => {
    const sim = simRef.current;
    if (!sim) return;
    const team = teamsOf(sim);
    const teamsPresent = new Set(sim.armies.filter(a => a.alive > 0).map(a => team[a.fac]));
    if (teamsPresent.size < 2) {
      log(sim, "⚠ 至少需要两个敌对阵营方可开战（同盟不相攻）");
      setTick(t => t + 1);
      return;
    }
    sim.running = !sim.running;
    if (sim.running && !sim.started) {
      sim.started = true;
      log(sim, "🔥 战鼓擂动，两军交锋！");
    }
    setRunning(sim.running);
  }, []);

  const giveOrder = useCallback((armyIds, order) => {
    const sim = simRef.current;
    const names = [];
    for (const id of armyIds) {
      const a = sim.armies.find(x => x.id === id);
      if (!a || a.broken) continue;
      a.order = order;
      let cx = 0, cy = 0, n = 0;
      for (const u of sim.units) if (!u.dead && u.army === a.id) { cx += u.x; cy += u.y; n++; }
      if (n) a.anchor = { x: cx / n, y: cy / n };
      if (order === "retreat" && !a.lockRetreatDir) a.retreatDir = (n ? cx / n : W / 2) < W / 2 ? -1 : 1;
      names.push(`${FACTIONS[a.fac].name}·${a.general}`);
    }
    if (names.length) log(sim, `${ORDERS[order].icon} ${names.join("、")} 受令：${ORDERS[order].name}`);
    setTick(t => t + 1);
  }, []);

  const setForm = useCallback((armyIds, form) => {
    const sim = simRef.current;
    const names = [];
    for (const id of armyIds) {
      const a = sim.armies.find(x => x.id === id);
      if (!a || a.broken) continue;
      a.form = form;
      names.push(`${FACTIONS[a.fac].name}·${a.general}`);
    }
    if (names.length) log(sim, `⛛ ${names.join("、")} 变阵：${FORMS[form].name}`);
    setTick(t => t + 1);
  }, []);

  const setPursuitStance = useCallback((armyIds, stance) => {
    const sim = simRef.current;
    const names = [];
    for (const id of armyIds) {
      const a = sim.armies.find(x => x.id === id);
      if (!a || a.broken) continue;
      a.pursuitStance = stance;
      names.push(`${FACTIONS[a.fac].name}·${a.general}`);
    }
    if (names.length) log(sim, `${PURSUIT_STANCES[stance].icon} ${names.join("、")} 追击姿态：${PURSUIT_STANCES[stance].name}`);
    setTick(t => t + 1);
  }, []);

  const rallyArmy = useCallback((armyIds) => {
    const sim = simRef.current;
    for (const id of armyIds) {
      const a = sim.armies.find(z => z.id === id);
      if (!a || !a.broken || a.rallyCd > 0) continue;
      const nearFriendly = sim.buildings.some(b =>
        b.hp > 0 && isAllied(sim, a.fac, b.fac) &&
        (b.type === "city" || b.type === "pass") &&
        (b.x - a.anchor.x) ** 2 + (b.y - a.anchor.y) ** 2 <= B_TYPES[b.type].aura ** 2);
      const chance = Math.max(0.05, Math.min(0.75, 0.25 + a.level * 0.05 + (nearFriendly ? 0.2 : 0) + (a.alive / a.total > 0.5 ? 0.1 : 0) + (a.genDead ? -0.15 : 0)));
      a.rallyCd = 6;
      if (Math.random() < chance) {
        a.broken = false; a.manualMove = false; a.morale = 30; a.order = "hold";
        for (const u of sim.units) if (u.army === a.id && !u.dead) u.flee = false;
        log(sim, `🚩 ${FACTIONS[a.fac].name}·${a.general} 旗鼓重整，全军止溃！`);
      } else {
        log(sim, `🚩 ${FACTIONS[a.fac].name}·${a.general} 聚拢未果，军心仍散……`);
      }
    }
    setTick(t => t + 1);
  }, []);

  const toggleAlly = useCallback((f1, f2) => {
    const sim = simRef.current;
    const k = pairKey(f1, f2);
    if (sim.allies.includes(k)) {
      sim.allies = sim.allies.filter(x => x !== k);
      log(sim, `💔 ${FACTIONS[f1].name}${FACTIONS[f2].name}盟约破裂！`);
    } else {
      sim.allies.push(k);
      log(sim, `🤝 ${FACTIONS[f1].name}${FACTIONS[f2].name}歃血为盟！`);
    }
    setTick(t => t + 1);
  }, []);

  const doSave = useCallback(async () => {
    try {
      const s = stateRef.current;
      await storage.set(SAVE_KEY, serialize(simRef.current, s.scenario, s.sizeKey));
      setSaveMsg("💾 已存档");
    } catch { setSaveMsg("⚠ 存档失败"); }
    setTimeout(() => setSaveMsg(""), 2000);
  }, []);
  const doLoad = useCallback(async () => {
    try {
      const r = await storage.get(SAVE_KEY);
      const { sim, scenario: sc, sizeKey: sk } = deserialize(r.value);
      simRef.current = sim;
      setSizeKey(sk);
      repaintTerrain();
      log(sim, "📜 存档已读取（尸痕不复，江山依旧）");
      setScenario(sc);
      viewRef.current = { z: 1, ox: 0, oy: 0 };
      setRunning(false);
      setSelected([]); selectedRef.current = [];
      setSaveMsg("📜 已读档");
      setTick(t => t + 1);
    } catch { setSaveMsg("⚠ 无存档或读档失败"); }
    setTimeout(() => setSaveMsg(""), 2000);
  }, []);

  const clearSave = useCallback(async () => {
    try { await storage.delete(SAVE_KEY); setSaveMsg("🗑 存档已清除"); }
    catch { setSaveMsg("⚠ 无存档"); }
    setTimeout(() => setSaveMsg(""), 2000);
  }, []);

  /* ---------- pointer: click place / drag select / shift-drag or right-drag pan / wheel zoom ---------- */
  const toWorld = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * W;
    const py = ((e.clientY - rect.top) / rect.height) * H;
    if (view3D && battle3DRef.current?.screenToWorld) {
      const p = battle3DRef.current.screenToWorld(e.clientX, e.clientY);
      return { x: p.x, y: p.y, px, py };
    }
    const { z, ox, oy } = viewRef.current;
    return { x: (px - ox) / z, y: (py - oy) / z, px, py };
  };
  const clampView = () => {
    const v = viewRef.current;
    v.z = Math.max(1, Math.min(5, v.z));
    v.ox = Math.min(0, Math.max(W - W * v.z, v.ox));
    v.oy = Math.min(0, Math.max(H - H * v.z, v.oy));
  };
  const onWheel = (e) => {
    e.preventDefault();
    // 3D 视图：滚轮推拉相机视距，不走 2D 平移缩放。
    if (view3DRef.current && battle3DRef.current?.zoomBy) {
      battle3DRef.current.zoomBy(e.deltaY);
      return;
    }
    const { px, py } = toWorld(e);
    const v = viewRef.current;
    const f = e.deltaY < 0 ? 1.15 : 1 / 1.15;
    const nz = Math.max(1, Math.min(5, v.z * f));
    v.ox = px - ((px - v.ox) / v.z) * nz;
    v.oy = py - ((py - v.oy) / v.z) * nz;
    v.z = nz;
    clampView();
  };
  // React 的 wheel 事件在部分浏览器/版本中可能被视为 passive，导致
  // preventDefault() 无法阻止页面滚动。地图缩放必须使用明确的非 passive
  // 原生监听器，避免滚轮同时把外层页面带着上下移动。
  const wheelHandlerRef = useRef(onWheel);
  wheelHandlerRef.current = onWheel;
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return undefined;
    const handleWheel = (e) => wheelHandlerRef.current(e);
    canvas.addEventListener("wheel", handleWheel, { passive: false });
    return () => canvas.removeEventListener("wheel", handleWheel);
  }, []);
  // 按小方阵把 list 中的军队散开落点，避免全部叠在同一点导致局部单位密度暴增、拖垮帧率
  const spreadAnchors = (list, baseX, baseY, spacing) => {
    const cols = Math.ceil(Math.sqrt(list.length));
    const rows = Math.ceil(list.length / cols);
    list.forEach((a, i) => {
      const ox = list.length > 1 ? ((i % cols) - (cols - 1) / 2) * spacing : 0;
      const oy = list.length > 1 ? (Math.floor(i / cols) - (rows - 1) / 2) * spacing : 0;
      a.order = "hold";
      a.moveQueue = []; a.patrol = false; a.patrolRoute = null;
      a.anchor = { x: baseX + ox, y: baseY + oy };
    });
  };
  const giveMove = useCallback((armyIds, x, y, opts = {}) => {
    const sim = simRef.current;
    if (!sim) return;
    const target = opts.target || null;
    const targets = armyIds
      .map(id => sim.armies.find(z => z.id === id))
      .filter(a => a && (!a.broken || a.alive > 0));
    if (!targets.length) return;
    const nameOf = a => `${FACTIONS[a.fac].name}·${a.general}`;

    // Shift+右键：续入路径点，不改变当前军令，仅对已在"坚守"中的军队生效
    if (opts.queue) {
      const holders = targets.filter(a => a.order === "hold");
      if (!holders.length) return;
      const cols = Math.ceil(Math.sqrt(holders.length));
      const rows = Math.ceil(holders.length / cols);
      const spacing = 90;
      holders.forEach((a, i) => {
        const ox = holders.length > 1 ? ((i % cols) - (cols - 1) / 2) * spacing : 0;
        const oy = holders.length > 1 ? (Math.floor(i / cols) - (rows - 1) / 2) * spacing : 0;
        a.moveQueue.push({ x: x + ox, y: y + oy });
      });
      log(sim, `🚩 ${holders.length}军续入路径点`);
      sim.fx.push({ x, y, t: 0.35, type: 2 });
      setTick(t => t + 1);
      return;
    }

    const broken = targets.filter(a => a.broken);
    const regular = targets.filter(a => !a.broken);
    for (const a of broken) {
      a.manualMove = true;
      a.order = "hold";
      a.moveQueue = [];
      a.patrol = false;
      a.patrolRoute = null;
      a.anchor = { x, y };
      for (const u of sim.units) if (u.army === a.id && !u.dead) u.flee = false;
    }
    if (broken.length) {
      log(sim, `🏳 ${broken.slice(0, 3).map(nameOf).join("、")}${broken.length > 3 ? "等" : ""} 溃军改道，前往指定地点`);
    }

    const attackers = [], garrison = [], grounders = [];
    for (const a of regular) {
      if (target && target.fac != null && !isAllied(sim, a.fac, target.fac)) attackers.push(a);
      else if (target && target.kind === "building"
        && (target.building.type === "city" || target.building.type === "pass")
        && isAllied(sim, a.fac, target.building.fac)) garrison.push(a);
      else grounders.push(a);
    }

    if (attackers.length) {
      for (const a of attackers) {
        a.order = "attack"; a.moveQueue = []; a.patrol = false; a.patrolRoute = null;
        a.anchor = { x, y };
      }
      log(sim, `⚔ ${attackers.slice(0, 3).map(nameOf).join("、")}${attackers.length > 3 ? "等" : ""} 转为进攻`);
    }
    if (garrison.length) {
      const b = target.building;
      spreadAnchors(garrison, b.x, b.y, Math.min(70, B_TYPES[b.type].aura * 0.6));
      log(sim, `🏯 ${garrison.slice(0, 3).map(nameOf).join("、")}${garrison.length > 3 ? "等" : ""} 进驻协防「${b.name}」`);
    }
    if (grounders.length) {
      spreadAnchors(grounders, x, y, 90);
      log(sim, `🚩 ${grounders.slice(0, 3).map(nameOf).join("、")}${grounders.length > 3 ? "等" : ""} 移军新阵地`);
    }
    sim.fx.push({ x, y, t: 0.35, type: 2 });
    setTick(t => t + 1);
  }, []);

  const onPointerDown = (e) => {
    if (e.button === 2) e.preventDefault(); // 立即拦下右键菜单，避免原生菜单弹出卡住整局
    if (e.button === 1) e.preventDefault(); // 拦下中键的原生自动滚动图标
    const { x, y } = toWorld(e);
    // 左键拖拽 = 框选军队（默认）；Shift/Alt+左键拖拽或中键拖拽 = 平移地图；右键 = 移军令
    // 放大后（z>1）左键在空地上拖拽直接平移，省得每次都要按 Shift/Alt
    const placeRequested = e.ctrlKey || e.metaKey;
    let mode;
    if (e.button === 2) mode = "move";
    else if (e.button === 1 || e.shiftKey || e.altKey) mode = "pan";
    else if (e.button === 0 && !placeRequested && viewRef.current.z > 1 && simRef.current &&
      resolveMoveTarget(simRef.current, x, y).kind === "ground") mode = "pan";
    else mode = "select";
    // 3D 视图里中键/Shift/Alt+左键平移地图；右键拖拽仍负责旋转镜头。
    if (view3DRef.current && mode === "pan") mode = "pan3d";
    try { e.currentTarget.setPointerCapture(e.pointerId); } catch { /* not all browsers need this */ }
    dragRef.current = {
      sx: e.clientX, sy: e.clientY,
      lastX: e.clientX, lastY: e.clientY,
      ox: viewRef.current.ox, oy: viewRef.current.oy,
      wx: x, wy: y, button: e.button,
      moved: false, mode, placeRequested,
    };
    if (canvasRef.current) {
      if (["pan", "pan3d"].includes(mode)) canvasRef.current.style.cursor = "grabbing";
      else if (simRef.current && stateRef.current.selected.length) canvasRef.current.style.cursor = cursorForHover(simRef.current, stateRef.current, x, y, placeRequested);
      else canvasRef.current.style.cursor = mode === "select" ? "crosshair" : "";
    }
  };
  // 光标随情境切换：有选中军队时预览右键落点意图（进攻/进驻/移动），否则预览左键意图（选中/放置）
  const setCursor = (v) => { if (canvasRef.current) canvasRef.current.style.cursor = v; };
  const cursorForHover = (sim, s, x, y, placeRequested = false) => {
    const target = resolveMoveTarget(sim, x, y);
    if (s.mode === "sandbox" && s.placeMode && placeRequested) return "copy";
    if (s.selected.length > 0) {
      const first = sim.armies.find(a => a.id === s.selected[0]);
      const targetArmy = target.kind === "unit" ? sim.armies.find(a => a.id === target.armyId) : null;
      if (targetArmy && !isAllied(sim, first?.fac, targetArmy.fac)) return SWORD_CURSOR;
      if (first && target.fac != null && !isAllied(sim, first.fac, target.fac)) return "crosshair";
      if (target.kind === "building" && (target.building.type === "city" || target.building.type === "pass")) return "cell";
      return "move";
    }
    if (target.kind === "unit") return "pointer";
    return "default";
  };
  const onPointerMove = (e) => {
    const sim = simRef.current;
    const s = stateRef.current;
    if (sim && !dragRef.current) {
      const { x, y } = toWorld(e);
      if (x >= 0 && x <= W && y >= 0 && y <= H) {
        const rect = canvasRef.current.getBoundingClientRect();
        setHoverInfo({ dx: e.clientX - rect.left, dy: e.clientY - rect.top, t: terrainAt(sim.terrain, x, y) });
        setCursor(cursorForHover(sim, s, x, y, e.ctrlKey || e.metaKey));
      } else { setHoverInfo(null); setCursor("default"); }
    }
    const d = dragRef.current;
    if (!d) return;
    const rect = canvasRef.current.getBoundingClientRect();
    const scale = W / rect.width;
    const dx = (e.clientX - d.sx) * scale, dy = (e.clientY - d.sy) * scale;
    if (Math.abs(dx) + Math.abs(dy) > 6) d.moved = true;
    if (view3DRef.current && d.mode === "pan3d") {
      const px = e.clientX - d.lastX, py = e.clientY - d.lastY;
      d.lastX = e.clientX; d.lastY = e.clientY;
      battle3DRef.current?.panBy?.(px, py);
      setCursor("grabbing");
      return;
    }
    // 3D 相机轨道：右键拖拽绕战场旋转/俯仰。
    if (view3DRef.current && (d.mode === "orbit" || (d.mode === "move" && d.moved))) {
      const ox = e.clientX - d.lastX, oy = e.clientY - d.lastY;
      d.lastX = e.clientX; d.lastY = e.clientY;
      if (d.mode === "move") d.orbited = true; // 右键已变为拖拽转镜头，pointerUp 不再当作移军
      battle3DRef.current?.orbitBy?.(ox, oy);
      setCursor("grabbing");
      return;
    }
    if (d.mode === "move") {
      // 右键移军拖拽：不因抖动而平移镜头，实时预览落点意图
      if (sim) { const { x, y } = toWorld(e); setCursor(cursorForHover(sim, s, x, y, e.ctrlKey || e.metaKey)); }
      return;
    }
    setCursor(["pan", "pan3d"].includes(d.mode) ? "grabbing" : "crosshair");
    if (!d.moved) return;
    if (d.mode === "pan") {
      viewRef.current.ox = d.ox + dx;
      viewRef.current.oy = d.oy + dy;
      clampView();
    } else {
      const { x, y } = toWorld(e);
      selRectRef.current = { x0: d.wx, y0: d.wy, x1: x, y1: y };
    }
  };
  const onPointerUp = (e) => {
    const d = dragRef.current;
    dragRef.current = null;
    const rectSel = selRectRef.current;
    selRectRef.current = null;
    try { e.currentTarget.releasePointerCapture(e.pointerId); } catch { /* ignore */ }
    if (!d) return;
    const sim = simRef.current;
    if (!sim) return;
    // 右键：向该点移军（有选中军队时）— 不因手抖而作废
    // 右键命中敌军=转为进攻；命中己方城/关=进驻协防；Shift+右键=续入路径点
    if (d.mode === "move") {
      if (d.orbited) return; // 右键拖拽已用于转镜头，不发移军令
      const { x, y } = toWorld(e);
      if (x >= 0 && x <= W && y >= 0 && y <= H && stateRef.current.selected.length > 0) {
        const target = resolveMoveTarget(sim, x, y);
        giveMove(stateRef.current.selected, x, y, { target, queue: e.shiftKey });
      }
      return;
    }
    if (d.mode === "orbit" || d.mode === "pan3d") return; // 3D 镜头/地图拖拽，不触发选中/放置
    if (d.moved && d.mode === "pan") return;
    if (d.moved && d.mode === "select" && rectSel) {
      // 框选：只允许选中当前玩家阵营（含同盟）的军队
      const x0 = Math.min(rectSel.x0, rectSel.x1), x1 = Math.max(rectSel.x0, rectSel.x1);
      const y0 = Math.min(rectSel.y0, rectSel.y1), y1 = Math.max(rectSel.y0, rectSel.y1);
      // 战斗模式把玩家势力记录在 tacticsFac；沙盘模式没有固定玩家势力，
      // 使用当前左侧势力选择（stateRef.current.fac）作为玩家阵营。
      const playerFac = sim.tacticsFac || stateRef.current.fac;
      const playerTeam = playerFac ? teamsOf(sim)[playerFac] : null;
      const ids = new Set();
      for (const u of sim.units) {
        if (u.dead) continue;
        if (u.x >= x0 && u.x <= x1 && u.y >= y0 && u.y <= y1) ids.add(u.army);
      }
      const arr = [...ids].filter(id => {
        const a = sim.armies.find(z => z.id === id);
        return a && a.alive > 0 && playerTeam != null && teamsOf(sim)[a.fac] === playerTeam;
      });
      setSelected(arr);
      selectedRef.current = arr;
      if (arr.length) {
        const names = arr.map(id => sim.armies.find(a => a.id === id))
          .map(a => `${FACTIONS[a.fac].name}·${a.general}`);
        log(sim, `🔶 框选 ${arr.length} 军：${names.slice(0, 4).join("、")}${arr.length > 4 ? "…" : ""}`);
      }
      setTick(t => t + 1);
      return;
    }
    // 单击：若点在军队附近则智能选中该军；否则放置（故事模式禁用自由布置）
    const { x, y } = toWorld(e);
    if (x < 0 || x > W || y < 0 || y > H) return;
    const s = stateRef.current;
    // 就近选军（12px 内）
    let nearArmy = null, nd = 12 * 12;
    for (const u of sim.units) {
      if (u.dead) continue;
      const dd = (u.x - x) ** 2 + (u.y - y) ** 2;
      if (dd < nd) { nd = dd; nearArmy = u.army; }
    }
    if (nearArmy != null) {
      const a = sim.armies.find(z => z.id === nearArmy);
      if (a && a.alive > 0) {
        const isSel = s.selected.includes(nearArmy);
        const v = isSel ? s.selected.filter(z => z !== nearArmy) : [...s.selected, nearArmy];
        setSelected(v); selectedRef.current = v;
        setTick(t => t + 1);
        return;
      }
    }
    if (s.mode === "storyBattle" || s.mode === "grandBattle") return;
    // 沙盘普通点击只用于选中；按住 Ctrl/Cmd 点击空地才执行布置，避免误加队伍。
    if (!d.placeRequested) return;
    if (s.placeMode === "army") makeArmy(sim, s.fac, s.utype, s.men, x, y);
    else addBuilding(sim, s.fac, s.placeMode, x, y);
    setTick(t => t + 1);
  };
  // Shift+双击：全队（含所有同盟势力）军队一键全选，不限于屏幕可见范围
  const onCanvasDoubleClick = (e) => {
    if (!e.shiftKey) return;
    const sim = simRef.current;
    if (!sim) return;
    const { x, y } = toWorld(e);
    if (x < 0 || x > W || y < 0 || y > H) return;
    let nearArmy = null, nd = 12 * 12;
    for (const u of sim.units) {
      if (u.dead) continue;
      const dd = (u.x - x) ** 2 + (u.y - y) ** 2;
      if (dd < nd) { nd = dd; nearArmy = u.army; }
    }
    if (nearArmy == null) return;
    const clicked = sim.armies.find(z => z.id === nearArmy);
    if (!clicked) return;
    const myTeam = teamsOf(sim)[clicked.fac];
    const arr = sim.armies.filter(a => !a.broken && teamsOf(sim)[a.fac] === myTeam).map(a => a.id);
    setSelected(arr); selectedRef.current = arr;
    if (arr.length) {
      const names = arr.map(id => sim.armies.find(a => a.id === id)).map(a => `${FACTIONS[a.fac].name}·${a.general}`);
      log(sim, `🔷 全队选中 ${arr.length} 军：${names.slice(0, 4).join("、")}${arr.length > 4 ? "…" : ""}`);
    }
    setTick(t => t + 1);
  };

  /* ---------- hotkeys ---------- */
  useEffect(() => {
    const onKey = (e) => {
      if ((e.target.tagName === "INPUT" || e.target.tagName === "TEXTAREA") && e.key.toLowerCase() !== "escape") return;
      const s = stateRef.current;
      const k = e.key.toLowerCase();
      if (e.code === "Space") {
        e.preventDefault();
        if (["sandbox", "campaignBattle", "storyBattle", "grandBattle"].includes(s.mode)) start();
        return;
      }
      if (k === "1") setFac("wei");
      else if (k === "2") setFac("shu");
      else if (k === "3") setFac("wu");
      else if (k === "q") { setUtype("inf"); setPlaceMode("army"); }
      else if (k === "w") { setUtype("spear"); setPlaceMode("army"); }
      else if (k === "e") { setUtype("arch"); setPlaceMode("army"); }
      else if (k === "r") { setUtype("cav"); setPlaceMode("army"); }
      else if (k === "c") setPlaceMode("city");
      else if (k === "v") setPlaceMode("camp");
      else if (k === "g" && s.mode === "grand") setGrandHudOpen(v => !v);
      else if (k === "g") setPlaceMode("pass");
      else if (k === "z") setSpeedIdx(i => Math.max(0, i - 1));
      else if (k === "x") setSpeedIdx(i => Math.min(SPEEDS.length - 1, i + 1));
      else if (k === "s") { if (s.mode === "sandbox") doSave(); }
      else if (k === "l") { if (s.mode === "sandbox") doLoad(); }
      else if (k === "f") {
        if (["sandbox", "campaignBattle", "storyBattle", "grandBattle"].includes(s.mode)) toggleFullscreen();
        else if (s.mode === "grand") toggleGrandMapFullscreen();
      }
      else if (k === "escape") {
        const hasGrandFloat = s.mode === "grand" && (s.generalManagerOpen || s.customGeneralOpen || s.grandSel || s.grandHudOpen);
        if (hasGrandFloat && document.fullscreenElement === grandMapRef.current) fullscreenRestoreRef.current = true;
        if (s.mode === "grand") {
          // Esc 统一关闭可退出浮层；需要玩家作出选择的战役/故事结果不跳过。
          setGrandSel(null);
          setGrandAttackSources([]);
        }
        setSelected([]); selectedRef.current = [];
        setShowSettings(false);
        setGeneralManagerOpen(false);
        setGeneralManagerModalOpen(false);
        setCustomGeneralOpen(false);
        setGrandDialog(null);
        setGrandHudOpen(false);
        setHudOpen(false);
        setBattleInfoOpen(false);
      }
      else if (e.shiftKey && s.selected.length && /^Digit[1-4]$/.test(e.code)) {
        // Shift+1..4：按钮顺序对应方阵/锋矢/圆阵/雁行
        const formKey = Object.keys(FORMS)[Number(e.code.slice(5)) - 1];
        setForm(s.selected, formKey);
      } else if (k === "p" && s.selected.length) {
        // 巡逻开关：把当前锚点+已排路径点定格为循环路线
        const sim = simRef.current;
        if (sim) {
          let turnedOn = null, n = 0;
          for (const id of s.selected) {
            const a = sim.armies.find(z => z.id === id);
            if (!a || a.broken || a.order !== "hold") continue;
            a.patrol = !a.patrol;
            a.patrolRoute = a.patrol ? [{ ...a.anchor }, ...a.moveQueue.map(p => ({ ...p }))] : null;
            if (turnedOn === null) turnedOn = a.patrol;
            n++;
          }
          if (n) {
            log(sim, `🔁 ${n}军巡逻已${turnedOn ? "开启" : "关闭"}`);
            setTick(t => t + 1);
          }
        }
      } else if (s.selected.length) {
        if (k === "a") giveOrder(s.selected, "attack");
        else if (k === "h") giveOrder(s.selected, "hold");
        else if (k === "y") giveOrder(s.selected, "feint");
        else if (k === "t") giveOrder(s.selected, "retreat");
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [start, doSave, doLoad, giveOrder, setForm, toggleFullscreen, toggleGrandMapFullscreen]);

  const sim = simRef.current;
  const armies = sim ? sim.armies.filter(a => !a.militia || a.alive > 0) : [];
  const logs = sim ? sim.log : [];
  const selArmies = sim ? selected.map(id => sim.armies.find(a => a.id === id)).filter(a => a && !a.broken) : [];
  const brokenSelArmies = sim ? selected.map(id => sim.armies.find(a => a.id === id)).filter(a => a && a.broken && a.alive > 0) : [];
  const selectedSkillArmy = selArmies.find(a => generalSkills(a.general).length > 0 && !a.genDead);
  const allies = sim ? sim.allies : [];
  const showKeys = settings.showKeys;
  const campaignStageDef = campaignStage ? CAMPAIGN_ORDER[campaignStage.fac][campaignStage.idx] : null;
  const inBattle = mode === "sandbox" || mode === "campaignBattle" || mode === "storyBattle" || mode === "grandBattle";
  const facChoices = mode === "campaignBattle" && campaignStage ? [campaignStage.fac]
    : mode === "storyBattle" && storyStage ? [STORY[storyStage.line].fac]
    : mode === "grandBattle" && grand ? [grand.playerFac]
    : FAC_KEYS;
  const storyLine = storyStage ? STORY[storyStage.line] : null;
  const storyChapterDef = storyStage ? storyLine.chapters[storyStage.idx] : null;
  const storyObjLive = (() => {
    if (mode !== "storyBattle" || !sim || !sim.story) return null;
    const o = sim.story.objective;
    if (o.type === "survive") return `尚需坚守 ${Math.max(0, Math.ceil(o.t - (sim.battleTime || 0)))} 息`;
    return null;
  })();

  const btn = {
    background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e",
    padding: "5px 11px", cursor: "pointer", fontFamily: "inherit",
    fontSize: 14, borderRadius: 3,
  };
  const smallBtn = { ...btn, padding: "3px 8px", fontSize: 12 };
  return (
    <div style={{
      minHeight: "100vh", background: "#1c1712", color: "#e8dcc1",
      fontFamily: "'Songti SC','Noto Serif SC',serif", padding: 12,
      display: "flex", flexDirection: "column", alignItems: "center", gap: 8,
    }}>
      <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", justifyContent: "center" }}>
        <h1 style={{ margin: 0, fontSize: 26, letterSpacing: 8, color: "#d4b24c" }}>三国·沙盘</h1>
        <div style={{ display: "flex", gap: 4 }}>
          <button onClick={() => {
              if (simRef.current) simRef.current.running = false;
              setRunning(false);
              setMode("sandbox");
            }} style={{
            ...smallBtn, fontWeight: mode === "sandbox" ? 700 : 400,
            background: mode === "sandbox" ? "#5a4a28" : "#2a231a",
            borderColor: mode === "sandbox" ? "#d4b24c" : "#6b5a3e",
            color: mode === "sandbox" ? "#f0e2b6" : "#e8dcc1",
          }}>🗺 沙盘模式</button>
          <button onClick={() => {
              if (simRef.current) simRef.current.running = false;
              setRunning(false);
              if (mode !== "campaignBattle") setMode("campaignHome");
            }} style={{
            ...smallBtn, fontWeight: (mode === "campaignHome" || mode === "campaignBattle") ? 700 : 400,
            background: (mode === "campaignHome" || mode === "campaignBattle") ? "#5a4a28" : "#2a231a",
            borderColor: (mode === "campaignHome" || mode === "campaignBattle") ? "#d4b24c" : "#6b5a3e",
            color: (mode === "campaignHome" || mode === "campaignBattle") ? "#f0e2b6" : "#e8dcc1",
          }}>🚩 闯关模式</button>
          <button onClick={() => {
              if (simRef.current) simRef.current.running = false;
              setRunning(false);
              if (!mode.startsWith("story")) setMode("storyHome");
            }} style={{
            ...smallBtn, fontWeight: mode.startsWith("story") ? 700 : 400,
            background: mode.startsWith("story") ? "#5a4a28" : "#2a231a",
            borderColor: mode.startsWith("story") ? "#d4b24c" : "#6b5a3e",
            color: mode.startsWith("story") ? "#f0e2b6" : "#e8dcc1",
          }}>📖 故事模式</button>
          <button onClick={() => {
              if (simRef.current) simRef.current.running = false;
              setRunning(false);
              if (mode !== "grandBattle") setMode("grand");
            }} style={{
            ...smallBtn, fontWeight: (mode === "grand" || mode === "grandBattle") ? 700 : 400,
            background: (mode === "grand" || mode === "grandBattle") ? "#5a4a28" : "#2a231a",
            borderColor: (mode === "grand" || mode === "grandBattle") ? "#d4b24c" : "#6b5a3e",
            color: (mode === "grand" || mode === "grandBattle") ? "#f0e2b6" : "#e8dcc1",
          }}>🌏 天下</button>
        </div>
        {mode === "sandbox" && (
          <span style={{ fontSize: 11, opacity: 0.55 }}>
            点兵选军 · 拖拽框选（含溃军） · Shift+双击=全队选中 · Shift/Alt+拖拽或中键=平移 · 右键移军(点敌=进攻/点己方城关=进驻/溃军改道) · Shift+右键=续点路线 · P=巡逻 · 滚轮缩放 · Ctrl/Cmd+点击空地布置
          </span>
        )}
        {(grand || inBattle) && (
          <button onClick={() => {
            setGeneralManagerModalOpen(true);
            setGeneralManagerOpen(true);
          }} style={{ ...smallBtn, borderColor: generalManagerOpen ? "#d4b24c" : "#6b5a3e" }}>
            ⚔ 武将管理
          </button>
        )}
        <button onClick={() => setShowSettings(v => !v)} style={{ ...smallBtn, fontSize: 15, padding: "3px 10px" }}>⚙</button>
      </div>

      {/* settings panel */}
      {showSettings && (
        <div style={{
          display: "flex", flexWrap: "wrap", gap: 10, alignItems: "center", justifyContent: "center",
          background: "#262016", border: "1px solid #d4b24c66", borderRadius: 4, padding: "8px 14px",
        }}>
          <span style={{ fontSize: 13, color: "#d4b24c" }}>⚙ 设置</span>
          <span style={{ fontSize: 12 }}>图幅：</span>
          {Object.entries(SIZES).map(([k, s]) => (
            <button key={k} onClick={() => changeSize(k)} style={{
              ...smallBtn,
              background: sizeKey === k ? "#d4b24c" : "#2a231a",
              color: sizeKey === k ? "#1c1712" : "#e8dcc1",
            }}>{s.name} {s.w}×{s.h}</button>
          ))}
          <span style={{ width: 1, height: 18, background: "#6b5a3e" }} />
          {[
            ["stains", "血渍痕迹"],
            ["showNames", "将旗姓名"],
            ["showKeys", "快捷键标注"],
            ["fxLite", "特效精简(高兵力更流畅)"],
            ["battleAI", "🧠 敌军战场AI"],
            ["longLog", "加长战报(14条)"],
            ["autoResolve", "天下模式:他国战事自动裁决"],
            ["terrainLegend", "地形图例与光标提示"],
            ["cheats", "🛠 作弊工具"],
          ].map(([key, label]) => (
            <button key={key}
              onClick={() => {
                const next = { ...settings, [key]: !settings[key] };
                setSettings(next);
                persistSettings(next, sizeKey);
              }}
              style={{
                ...smallBtn,
                background: settings[key] ? "#5a4a28" : "#2a231a",
                borderColor: settings[key] ? "#d4b24c" : "#6b5a3e",
              }}>{settings[key] ? "✓" : "✗"} {label}</button>
          ))}
          <span style={{ width: 1, height: 18, background: "#6b5a3e" }} />
          <button onClick={clearSave} style={{ ...smallBtn, color: "#c98" }}>🗑 清除存档</button>
          <span style={{ fontSize: 11, opacity: 0.5 }}>（改图幅会重开当前场景）</span>
          {settings.cheats && (
            <div style={{
              width: "100%", marginTop: 6, paddingTop: 8, borderTop: "1px dashed #6b5a3e",
              display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center",
            }}>
              <span style={{ fontSize: 12, color: "#c07a6c" }}>🛠 作弊：</span>
              {mode === "grand" && grand && (
                <>
                  <button onClick={cheatGrandGold} style={smallBtn}>金粮+1000</button>
                  <button onClick={cheatGrandEmpowerCities} style={smallBtn}>我方城池强化</button>
                  <button onClick={cheatGrandGenerals} style={smallBtn}>我方武将满值</button>
                  <button onClick={cheatGrandDiplomacy} style={smallBtn}>外交全满/全结盟</button>
                  <button onClick={cheatGrandMatureHeirs} style={smallBtn}>子嗣立即成年</button>
                  <button onClick={() => cheatGrandActionLimit("intimacy")} style={smallBtn}>行房上限+5</button>
                  <button onClick={() => cheatGrandActionLimit("oneNight")} style={smallBtn}>一夜上限+5</button>
                  <button onClick={() => cheatGrandActionLimit("agent")} style={smallBtn}>行动上限+5</button>
                  <button onClick={cheatGrandUnlimitedActions} style={smallBtn}>三项上限99</button>
                  <button onClick={cheatGrandCapture} disabled={!grandSel} style={{ ...smallBtn, opacity: grandSel ? 1 : 0.4 }}>选中城直接归附</button>
                  <span style={{ fontSize: 11, opacity: 0.7 }}>本回合：行房 {grand.actionUsage?.turn === grand.turn ? grand.actionUsage.intimacy : 0}/{grand.actionLimits?.intimacy ?? 1} · 一夜 {grand.actionUsage?.turn === grand.turn ? grand.actionUsage.oneNight : 0}/{grand.actionLimits?.oneNight ?? 1} · 行动 {grand.actionUsage?.turn === grand.turn ? grand.actionUsage.agent : 0}/{grand.actionLimits?.agent ?? 3}</span>
                </>
              )}
              {(mode === "sandbox" || mode === "campaignBattle" || mode === "storyBattle" || mode === "grandBattle") && sim && (
                <>
                  <button onClick={cheatFullHealMorale} style={smallBtn}>我方满血满气满粮</button>
                  <button onClick={() => cheatForceResult(true)} style={smallBtn}>强制胜利</button>
                  <button onClick={() => cheatForceResult(false)} style={smallBtn}>强制失败</button>
                </>
              )}
              {mode.startsWith("story") && storyStage && (
                <button onClick={cheatStoryRenown} style={smallBtn}>声望+10</button>
              )}
              <button onClick={cheatExperience} style={smallBtn}>经验+100</button>
              <button onClick={cheatUnlockAll} style={smallBtn}>解锁全部关卡/章节</button>
            </div>
          )}
        </div>
      )}

      {/* ===== 武将管理 ===== */}
      {generalManagerModalOpen && generalManagerOpen && (grand || inBattle) && (
        <div onClick={() => setGeneralManagerOpen(false)} style={{
          position: "fixed", inset: 0, zIndex: 120, background: "rgba(10,8,5,0.68)",
          display: "flex", alignItems: "center", justifyContent: "center", padding: 14,
        }}>
          <div onClick={e => e.stopPropagation()} style={{
            width: "min(96vw, 980px)", maxHeight: "86vh", overflowY: "auto",
            background: "#211a12", border: "1px solid #c9a227", borderRadius: 6,
            boxShadow: "0 18px 70px rgba(0,0,0,0.7)", padding: "14px 16px",
          }}>
            {(() => {
              const own = grand?.myGens || [];
              const captives = grand?.captives || [];
              const harem = grand?.harem || [];
              const battleGens = (sim?.armies || []).filter(a => !a.militia && a.alive > 0 && a.general && a.general !== "援军");
              const knownNames = [...new Set([
                ...Object.keys(GENERALS).flatMap(f => [...(GENERALS[f] || []), ...(FEMALE_BY_FAC[f] || [])]),
                ...own.map(g => g.name), ...captives.map(g => g.name), ...harem.map(g => g.name), ...battleGens.map(a => a.general),
              ])];
              const findOwn = name => own.find(g => g.name === name);
              const findCaptive = name => captives.find(g => g.name === name);
              const findBattle = name => battleGens.find(a => a.general === name);
              const findFac = name => Object.keys(GENERALS).find(f => (GENERALS[f] || []).includes(name) || (FEMALE_BY_FAC[f] || []).includes(name));
              const rosterNames = [...new Set([
                ...Object.keys(GENERALS).flatMap(f => [...(GENERALS[f] || []), ...(FEMALE_BY_FAC[f] || [])]),
                ...Object.values(FAMILY_BY_FAC).flatMap(list => list.map(x => x.name)),
              ])];
              const generalId = name => {
                const idx = rosterNames.indexOf(name);
                return idx >= 0 ? `G-${String(idx + 1).padStart(3, "0")}` : `G-${String(rosterNames.length + knownNames.indexOf(name) + 1).padStart(3, "0")}`;
              };
              const getRow = name => {
                const gen = findOwn(name);
                const captive = findCaptive(name);
                const battleArmy = findBattle(name);
                const spouse = harem.find(h => h.name === name);
                const profile = gen || captive || spouse || generalProfile(name, captive?.gender || spouse?.gender);
                // currentFac 表示当前效忠阵营；originFac 仅保留武将原属势力，用于历史追溯。
                const facKey = gen?.currentFac || (gen ? grand?.playerFac : null) || spouse?.currentFac || captive?.originFac || findFac(name) || "neutral";
                const eliminated = !!grand?.eliminatedGenerals?.includes(name);
                const status = eliminated ? "已除名" : spouse ? "府中家眷" : gen ? "麾下" : captive ? (captive.captureType === "family" ? "俘虏家眷" : "俘虏") : battleArmy ? "当前战场" : "未登场";
                const marriedBy = harem.find(h => h.marriedTo === name);
                const historicalHusband = HISTORICAL_MARRIAGES[name];
                const marriage = spouse ? "已婚·" + (spouse.marriedTo || "主公") : marriedBy ? "已婚·" + marriedBy.name : historicalHusband ? "已婚·" + historicalHusband : "未婚";
                return { id: generalId(name), name, gen, captive, battleArmy, spouse, profile, facKey, status, marriage, eliminated };
              };
              const filteredNames = knownNames.filter(name => {
                const row = getRow(name);
                const query = generalManagerFilter.text.trim().toLowerCase();
                const matchesText = !query || `${row.id} ${row.name} ${(row.profile.skills || []).join(" ")}`.toLowerCase().includes(query);
                const matchesFac = generalManagerFilter.fac === "all" || row.facKey === generalManagerFilter.fac;
                const matchesGender = generalManagerFilter.gender === "all" || row.profile.gender === generalManagerFilter.gender;
                const matchesStatus = generalManagerFilter.status === "all" || row.status === generalManagerFilter.status;
                return matchesText && matchesFac && matchesGender && matchesStatus;
              });
              const sortedNames = [...filteredNames].sort((a, b) => {
                const av = getRow(a), bv = getRow(b);
                const value = row => {
                  if (generalManagerSort.key === "name") return row.name;
                  if (generalManagerSort.key === "gender") return row.profile.gender === "f" ? 1 : 0;
                  if (generalManagerSort.key === "status") return row.status;
                  if (generalManagerSort.key === "charm") return row.profile.charm ?? -1;
                  if (generalManagerSort.key === "age") return row.profile.age ?? 0;
                  if (generalManagerSort.key === "lead") return row.gen?.lead ?? -1;
                  if (generalManagerSort.key === "pol") return row.gen?.pol ?? -1;
                  if (generalManagerSort.key === "force") return row.profile.force ?? 0;
                  if (generalManagerSort.key === "wisdom") return row.profile.wisdom ?? 0;
                  if (generalManagerSort.key === "loyalty") return row.gen?.loyalty ?? row.captive?.loyalty ?? -1;
                  if (generalManagerSort.key === "marriage") return row.marriage;
                  return row.name;
                };
                const left = value(av), right = value(bv);
                return (typeof left === "string" ? left.localeCompare(right, "zh-CN") : left - right) * generalManagerSort.dir;
              });
              const sortHeader = (key, label) => (
                <button onClick={() => setGeneralManagerSort(s => ({ key, dir: s.key === key ? -s.dir : 1 }))} style={{ background: "transparent", color: "#d7c7a4", border: 0, cursor: "pointer", font: "inherit", padding: "3px 4px", whiteSpace: "nowrap" }}>
                  {label}{generalManagerSort.key === key ? (generalManagerSort.dir > 0 ? " ↑" : " ↓") : ""}
                </button>
              );
              const builtInSkillNames = new Set(["统军作战", "内助安民", ...Object.values(GENERAL_SKILLS).flat().map(skill => skill.name), ...ADDITIONAL_SKILLS.map(skill => skill.name)]);
              const customSkillNames = [...new Set(knownNames.flatMap(name => (getRow(name).profile.skills || []).filter(skill => !builtInSkillNames.has(skill))))];
              const skillCatalog = [
                { id: "command", name: "⚔ 统军作战", category: "军略", contribution: "统率越高，所属军团的指挥与战斗稳定性越强。", note: "普通武将基础技能。", owners: knownNames.filter(name => !isFemaleName(name) && generalSkills(name).length === 0) },
                { id: "domestic", name: "🏮 内助安民", category: "家眷", contribution: "女将婚配后可提升配偶忠诚度；魅力越高，家眷带来的民心收益越明显。", note: "女武将默认技能。", owners: knownNames.filter(name => isFemaleName(name) && generalSkills(name).length === 0) },
                ...Object.values(GENERAL_SKILLS).flatMap(list => list).filter((skill, i, all) => all.findIndex(x => x.id === skill.id) === i).map(skill => ({
                  id: skill.id, name: skill.name, category: skill.id === "assassinate" ? "武勇" : "谋略", contribution: skill.note,
                  note: "战斗中选中该武将所在军团后释放，冷却18秒。", owners: knownNames.filter(name => generalSkills(name).some(x => x.id === skill.id)),
                })),
                ...ADDITIONAL_SKILLS.map(skill => ({ ...skill, owners: knownNames.filter(name => (getRow(name).profile.skills || []).includes(skill.name)) })),
                ...customSkillNames.map(skillName => ({
                  id: `custom:${skillName}`, name: `✦ ${skillName}`, category: "自定义",
                  contribution: "该技能由玩家录入武将时自定义，当前作为武将档案特性展示。",
                  note: "自定义技能暂不绑定额外战斗按钮。",
                  owners: knownNames.filter(name => (getRow(name).profile.skills || []).includes(skillName)),
                })),
              ];
              const filteredSkills = skillCatalog.filter(skill => {
                const query = skillManagerSearch.trim().toLowerCase();
                return (!query || `${skill.name} ${skill.category} ${skill.contribution}`.toLowerCase().includes(query))
                  && (skillManagerCategory === "all" || skill.category === skillManagerCategory);
              });
              const selectedSkillInfo = skillCatalog.find(skill => skill.id === skillManagerSelected) || null;
              return (
                <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 10, marginBottom: 5 }}>
                    <div style={{ fontSize: 17, color: "#d4b24c", letterSpacing: 3 }}>⚔ 武将管理 <span style={{ fontSize: 12, letterSpacing: 0, color: "#e8c34a" }}>经验点：{stats.experience ?? 0}</span> <span style={{ fontSize: 11, letterSpacing: 0, color: "#b9aa8f" }}>名册 {GENERAL_ROSTER_COUNTS.male + GENERAL_ROSTER_COUNTS.female}人（男{GENERAL_ROSTER_COUNTS.male} / 女{GENERAL_ROSTER_COUNTS.female}）</span></div>
                    <div style={{ display: "flex", gap: 4, alignItems: "center" }}>
                      <button onClick={() => setGeneralManagerPage("generals")} style={{ ...smallBtn, padding: "3px 8px", borderColor: generalManagerPage === "generals" ? "#d4b24c" : "#6b5a3e" }}>武将名册</button>
                      <button onClick={() => grand && setCustomGeneralOpen(true)} disabled={!grand} style={{ ...smallBtn, padding: "3px 8px", borderColor: grand ? "#79a86b" : "#6b5a3e", opacity: grand ? 1 : 0.5 }}>＋录入武将</button>
                      <button onClick={() => setGeneralManagerPage("skills")} style={{ ...smallBtn, padding: "3px 8px", borderColor: generalManagerPage === "skills" ? "#d4b24c" : "#6b5a3e" }}>技能管理</button>
                      <button onClick={() => setGeneralManagerOpen(false)} style={{ ...smallBtn, fontSize: 16 }}>×</button>
                    </div>
                  </div>
                  {customGeneralOpen && grand && <div onClick={() => setCustomGeneralOpen(false)} style={{ position: "fixed", inset: 0, zIndex: 160, background: "rgba(8,6,4,0.72)", display: "flex", alignItems: "center", justifyContent: "center", padding: 14 }}>
                    <div onClick={e => e.stopPropagation()} style={{ width: "min(94vw, 570px)", maxHeight: "88vh", overflowY: "auto", background: "#211a12", border: "1px solid #79a86b", borderRadius: 6, boxShadow: "0 18px 70px rgba(0,0,0,0.75)", padding: "15px 17px" }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 10 }}><b style={{ color: "#d4b24c", fontSize: 16 }}>＋录入新武将</b><button onClick={() => setCustomGeneralOpen(false)} style={{ ...smallBtn, fontSize: 16 }}>×</button></div>
                      <div style={{ fontSize: 11.5, opacity: 0.72, lineHeight: 1.5, marginBottom: 10 }}>所有数值均由你自行设定，保存后立即加入当前势力的天下模式武将府。数值范围为 1～100，年龄为 1～100 岁。</div>
                      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 8 }}>
                        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5 }}>姓名<input value={customGeneralForm.name} onChange={e => setCustomGeneralForm(v => ({ ...v, name: e.target.value }))} placeholder="请输入姓名" style={{ ...smallBtn, cursor: "text", boxSizing: "border-box" }} /></label>
                        <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5 }}>性别<select value={customGeneralForm.gender} onChange={e => setCustomGeneralForm(v => ({ ...v, gender: e.target.value }))} style={{ ...smallBtn, cursor: "pointer" }}><option value="m">男</option><option value="f">女</option></select></label>
                        {[["age", "年龄", 1, 100], ["force", "武力", 1, 100], ["wisdom", "智慧", 1, 100], ["lead", "统率", 1, 100], ["pol", "政务", 1, 100], ["loyalty", "忠诚", 0, 100], ["charm", "魅力（女将）", 1, 100]].map(([key, label, min, max]) => <label key={key} style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5 }}>{label}<input type="number" min={min} max={max} value={customGeneralForm[key]} onChange={e => setCustomGeneralForm(v => ({ ...v, [key]: e.target.value }))} style={{ ...smallBtn, cursor: "text", boxSizing: "border-box" }} /></label>)}
                      </div>
                      <label style={{ display: "flex", flexDirection: "column", gap: 3, fontSize: 11.5, marginTop: 9 }}>技能（多个技能用逗号分隔）<input value={customGeneralForm.skills} onChange={e => setCustomGeneralForm(v => ({ ...v, skills: e.target.value }))} placeholder="例如：火攻、奇谋" style={{ ...smallBtn, cursor: "text", boxSizing: "border-box" }} /></label>
                      <div style={{ display: "flex", justifyContent: "flex-end", gap: 7, marginTop: 13 }}><button onClick={() => setCustomGeneralOpen(false)} style={smallBtn}>取消</button><button onClick={registerCustomGeneral} style={{ ...smallBtn, borderColor: "#79a86b", color: "#d8efbd" }}>确认录入</button></div>
                    </div>
                  </div>}
                  {generalManagerPage === "skills" ? <div style={{ marginTop: 8 }}>
                    <div style={{ fontSize: 12, opacity: 0.7, lineHeight: 1.5, marginBottom: 9 }}>技能管理汇总全部可用技能、战斗贡献、冷却规则和拥有武将。点击技能卡片可查看完整拥有者名单。</div>
                    <div style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", marginBottom: 10 }}>
                      <input value={skillManagerSearch} onChange={e => setSkillManagerSearch(e.target.value)} placeholder="搜索技能或贡献" style={{ ...smallBtn, width: 190, boxSizing: "border-box", cursor: "text" }} />
                      <select value={skillManagerCategory} onChange={e => setSkillManagerCategory(e.target.value)} style={{ ...smallBtn, cursor: "pointer" }}>
                        <option value="all">全部分类</option><option value="军略">军略</option><option value="武勇">武勇</option><option value="谋略">谋略</option><option value="家眷">家眷</option><option value="自定义">自定义</option>
                      </select>
                      <span style={{ fontSize: 11.5, opacity: 0.7 }}>共 {skillCatalog.length} 项，当前显示 {filteredSkills.length} 项</span>
                    </div>
                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))", gap: 8 }}>
                      {filteredSkills.map(skill => <button key={skill.id} onClick={() => setSkillManagerSelected(skill.id)} style={{ textAlign: "left", padding: "9px 10px", background: skill.id === skillManagerSelected ? "#5a4a28" : "#2a2118", color: "#e8dcc1", border: `1px solid ${skill.id === skillManagerSelected ? "#d4b24c" : "#6b5a3e"}`, borderRadius: 4, cursor: "pointer", fontFamily: "inherit" }}>
                        <div style={{ display: "flex", justifyContent: "space-between", gap: 7, alignItems: "baseline" }}><b style={{ color: "#ffe18a" }}>{skill.name}</b><span style={{ fontSize: 10.5, color: "#b9aa8f" }}>{skill.category}</span></div>
                        <div style={{ fontSize: 11.5, lineHeight: 1.45, marginTop: 5 }}>{skill.contribution}</div>
                        <div style={{ fontSize: 10.5, opacity: 0.65, marginTop: 5 }}>拥有者：{skill.owners.length} 名 · {skill.note}</div>
                      </button>)}
                    </div>
                    {selectedSkillInfo && <div style={{ marginTop: 10, padding: "9px 10px", border: "1px solid #d4b24c88", borderRadius: 4, background: "#332719" }}>
                      <div style={{ color: "#ffe18a", fontWeight: 700 }}>{selectedSkillInfo.name} · 拥有者名单</div>
                      <div style={{ fontSize: 11.5, opacity: 0.78, margin: "4px 0 7px" }}>{selectedSkillInfo.contribution} {selectedSkillInfo.note}</div>
                      <div style={{ display: "flex", gap: 5, flexWrap: "wrap", maxHeight: 150, overflowY: "auto" }}>{selectedSkillInfo.owners.map(name => {
                        const owner = getRow(name);
                        return <span key={name} style={{ display: "inline-flex", alignItems: "center", gap: 3, padding: "2px 5px", background: "#2a2118", border: "1px solid #574831", borderRadius: 3, fontSize: 10.5 }}><GeneralAvatar name={name} gender={owner.profile.gender} faction={owner.facKey} size={20} />{name}</span>;
                      })}</div>
                      {grand && <div style={{ marginTop: 9, paddingTop: 8, borderTop: "1px solid #574831" }}>
                        <div style={{ fontSize: 11.5, color: "#d7c7a4", marginBottom: 5 }}>手动分配给己方武将（最多 8 项技能）</div>
                        <div style={{ display: "flex", gap: 5, alignItems: "center", flexWrap: "wrap" }}>
                          <select value={skillManagerTarget} onChange={e => setSkillManagerTarget(e.target.value)} style={{ ...smallBtn, cursor: "pointer" }}>
                            <option value="">选择武将</option>
                            {own.map(gen => <option key={gen.name} value={gen.name}>{gen.name}（{(gen.skills || []).length}项）</option>)}
                          </select>
                          <button disabled={!skillManagerTarget} onClick={() => updateGeneralSkill(skillManagerTarget, selectedSkillInfo.name, "add")} style={{ ...smallBtn, padding: "3px 7px", borderColor: "#79a86b", color: "#d8efbd" }}>＋添加技能</button>
                          {skillManagerTarget && (own.find(gen => gen.name === skillManagerTarget)?.skills || []).includes(selectedSkillInfo.name) && <button onClick={() => updateGeneralSkill(skillManagerTarget, selectedSkillInfo.name, "remove")} style={{ ...smallBtn, padding: "3px 7px", borderColor: "#b66a5a", color: "#e8b0a5" }}>－删除技能</button>}
                        </div>
                        <div style={{ fontSize: 10.5, opacity: 0.62, marginTop: 5 }}>删除后不会影响武将基础属性；专属战斗技仍按原有战斗规则释放。</div>
                      </div>}
                    </div>}
                  </div> : <>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                    <div style={{ fontSize: 12, opacity: 0.68, lineHeight: 1.5 }}>
                    这里汇总已登场与名册中的武将。天下模式可直接管理麾下武将、俘虏和家眷；战斗中点击“战场”可快速选中对应军团。
                    </div>
                    <div style={{ display: "flex", gap: 4, flexShrink: 0 }}>
                      <button onClick={() => setGeneralManagerView("cards")} style={{ ...smallBtn, padding: "3px 8px", borderColor: generalManagerView === "cards" ? "#d4b24c" : "#6b5a3e" }}>▦ 卡片</button>
                      <button onClick={() => setGeneralManagerView("table")} style={{ ...smallBtn, padding: "3px 8px", borderColor: generalManagerView === "table" ? "#d4b24c" : "#6b5a3e" }}>☷ 表格</button>
                    </div>
                  </div>
                  <div style={{ display: "flex", alignItems: "center", gap: 5, flexWrap: "wrap", marginBottom: 9 }}>
                    <input value={generalManagerFilter.text} onChange={e => setGeneralManagerFilter(v => ({ ...v, text: e.target.value }))} placeholder="搜索编号、姓名或技能" style={{ ...smallBtn, width: 190, boxSizing: "border-box", cursor: "text" }} />
                    <select value={generalManagerFilter.fac} onChange={e => setGeneralManagerFilter(v => ({ ...v, fac: e.target.value }))} style={{ ...smallBtn, cursor: "pointer" }}>
                      <option value="all">全部阵营</option>
                      {Object.keys(FACTIONS).map(f => <option key={f} value={f}>{grand ? grandFacLabel(grand, f) : FACTIONS[f].name}</option>)}
                    </select>
                    <select value={generalManagerFilter.gender} onChange={e => setGeneralManagerFilter(v => ({ ...v, gender: e.target.value }))} style={{ ...smallBtn, cursor: "pointer" }}>
                      <option value="all">全部性别</option><option value="m">男将</option><option value="f">女将</option>
                    </select>
                    <select value={generalManagerFilter.status} onChange={e => setGeneralManagerFilter(v => ({ ...v, status: e.target.value }))} style={{ ...smallBtn, cursor: "pointer" }}>
                      <option value="all">全部状态</option><option value="麾下">麾下</option><option value="府中家眷">府中家眷</option><option value="俘虏">俘虏</option><option value="俘虏家眷">俘虏家眷</option><option value="当前战场">当前战场</option><option value="未登场">未登场</option><option value="已除名">已除名</option>
                    </select>
                    <span style={{ fontSize: 11.5, opacity: 0.72, marginLeft: 3 }}>当前 {sortedNames.length} / {knownNames.length} 名</span>
                    {(generalManagerFilter.text || generalManagerFilter.fac !== "all" || generalManagerFilter.gender !== "all" || generalManagerFilter.status !== "all") && <button onClick={() => setGeneralManagerFilter({ text: "", fac: "all", gender: "all", status: "all" })} style={{ ...smallBtn, padding: "3px 7px" }}>清除筛选</button>}
                  </div>
                  {generalManagerView === "cards" ? <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(285px, 1fr))", gap: 7 }}>
                    {sortedNames.map(name => {
                      const gen = findOwn(name);
                      const captive = findCaptive(name);
                      const battleArmy = findBattle(name);
                      const spouse = harem.find(h => h.name === name);
                      const profile = gen || captive || spouse || generalProfile(name, captive?.gender || spouse?.gender);
                      const facKey = gen?.currentFac || (gen ? grand?.playerFac : null) || spouse?.currentFac || captive?.originFac || findFac(name) || "neutral";
                      const color = FACTIONS[facKey]?.color || "#b9aa8f";
                      const status = spouse ? "府中家眷" : gen ? "麾下" : captive ? (captive.captureType === "family" ? "俘虏家眷" : "俘虏") : battleArmy ? "当前战场" : "未登场";
                      return (
                        <div key={name} style={{ border: `1px solid ${color}66`, borderRadius: 4, padding: "7px 8px", background: "#2a2118", minWidth: 0 }}>
                          <div style={{ display: "flex", alignItems: "center", gap: 7 }}>
                            <GeneralAvatar name={name} gender={profile.gender} faction={facKey} size={34} />
                            <div style={{ display: "flex", alignItems: "baseline", gap: 6, flexWrap: "wrap" }}>
                              <b style={{ color }}>{name}</b>
                              <span style={{ fontSize: 11, color: gen ? "#b9c875" : captive ? "#d99a90" : "#b8ac8e" }}>{status}</span>
                              {facKey !== "neutral" && <span style={{ fontSize: 10.5, opacity: 0.55 }}>{grand ? grandFacLabel(grand, facKey) : FACTIONS[facKey].name}</span>}
                            </div>
                          </div>
                          <div style={{ fontSize: 11.5, color: "#d7c7a4", marginTop: 3 }}>
                            {profile.gender === "f" ? "女" : "男"} · {profile.age}岁{profile.gender === "f" && ` · 魅力${profile.charm ?? 70}`}
                          </div>
                          <div title={generalSkillContribution(name, profile)} onMouseEnter={e => setSkillTooltip({ text: generalSkillContribution(name, profile), x: e.clientX + 14, y: e.clientY + 14 })} onMouseMove={e => setSkillTooltip(t => t ? { ...t, x: e.clientX + 14, y: e.clientY + 14 } : t)} onMouseLeave={() => setSkillTooltip(null)} style={{ fontSize: 11, opacity: 0.72, lineHeight: 1.35, marginTop: 2, cursor: "help" }}>技能：{(profile.skills || []).join("、") || "统军作战"}</div>
                          {gen && (
                            <div style={{ fontSize: 11.5, opacity: 0.75, marginTop: 3 }}>
                              武{gen.force ?? generalCombatStats(gen.name).force} · 智{gen.wisdom ?? generalCombatStats(gen.name).wisdom} · 统{gen.lead} · 政{gen.pol} · 忠{gen.loyalty ?? 78}{gen.city ? ` · 太守：${GRAND_NODES[gen.city]?.name || ""}` : " · 在府听用"}
                            </div>
                          )}
                          {captive?.desc && <div style={{ fontSize: 11, opacity: 0.62, lineHeight: 1.35, marginTop: 3 }}>{captive.role}：{captive.desc}</div>}
                          {battleArmy && <div style={{ fontSize: 11.5, opacity: 0.72, marginTop: 3 }}>兵力约{(battleArmy.alive * MEN_PER_DOT).toLocaleString()} · 士气{Math.round(battleArmy.morale)} · {battleArmy.order}</div>}
                          {gen && grand && (
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 5 }}>
                              <select value={gen.city || ""} onChange={e => assignGen(gen.name, e.target.value || null)} title="委任太守" style={{ background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e", borderRadius: 3, fontFamily: "inherit", fontSize: 11 }}>
                                <option value="">在府听用</option>
                                {Object.entries(grand.nodes).filter(([, n]) => n.owner === grand.playerFac).map(([id]) => <option key={id} value={id}>守{GRAND_NODES[id].name}</option>)}
                              </select>
                              <button onClick={() => trainGen(gen.name)} style={{ ...smallBtn, padding: "2px 6px", fontSize: 10.5 }} disabled={grand.res[grand.playerFac].gold < 150}>📚培养</button>
                              <button onClick={() => rewardGen(gen.name)} style={{ ...smallBtn, padding: "2px 6px", fontSize: 10.5, borderColor: "#c9a227" }} disabled={grand.res[grand.playerFac].gold < 100 || (gen.loyalty ?? 78) >= 100}>🎁赏赐</button>
                              <button onClick={() => dismissGen(gen.name)} style={{ ...smallBtn, padding: "2px 6px", fontSize: 10.5, color: "#c98" }}>放逐</button>
                            </div>
                          )}
                          {captive && grand && (
                            <div style={{ display: "flex", gap: 4, flexWrap: "wrap", marginTop: 5 }}>
                              {captive.captureType !== "family" && <button onClick={() => captiveAction(name, "persuade")} disabled={captive.tried} style={{ ...smallBtn, padding: "2px 6px", fontSize: 10.5 }}>🕊劝降</button>}
                              {captive.gender === "f" && <button onClick={() => captiveAction(name, "marry")} disabled={captive.tried} style={{ ...smallBtn, padding: "2px 6px", fontSize: 10.5, borderColor: "#d99a90" }}>💍婚配</button>}
                              <button onClick={() => captiveAction(name, "release")} style={{ ...smallBtn, padding: "2px 6px", fontSize: 10.5 }}>🌿释放</button>
                            </div>
                          )}
                          {battleArmy && <button onClick={() => { setSelected([battleArmy.id]); selectedRef.current = [battleArmy.id]; setGeneralManagerOpen(false); setTick(t => t + 1); }} style={{ ...smallBtn, padding: "2px 6px", fontSize: 10.5, marginTop: 5 }}>🎯选中战场军团</button>}
                        </div>
                      );
                    })}
                  </div> : <div style={{ overflowX: "auto", border: "1px solid #574831", borderRadius: 4 }}>
                    <table style={{ width: "100%", minWidth: 860, borderCollapse: "collapse", fontSize: 11.5 }}>
                      <thead><tr style={{ background: "#332719", color: "#d7c7a4", textAlign: "left" }}>
                        <th style={{ padding: "6px 5px", whiteSpace: "nowrap" }}>位置 / 共{sortedNames.length}</th>
                        <th style={{ padding: "6px 5px" }}>编号</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("name", "武将")}</th>
                        <th style={{ padding: "6px 5px" }}>阵营</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("gender", "性别")}</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("age", "年龄")}</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("charm", "魅力")}</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("force", "武力")}</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("wisdom", "智慧")}</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("lead", "统率")}</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("pol", "政务")}</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("loyalty", "忠诚")}</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("marriage", "婚配")}</th>
                        <th style={{ padding: "6px 5px" }}>{sortHeader("status", "状态")}</th>
                        <th style={{ padding: "6px 5px" }}>技能 / 操作</th>
                      </tr></thead>
                      <tbody>{sortedNames.map((name, position) => {
                        const row = getRow(name);
                        const { gen, captive, battleArmy, profile, facKey, status, eliminated } = row;
                        const color = FACTIONS[facKey]?.color || "#b9aa8f";
                        return <tr key={name} style={{ borderTop: "1px solid #493b29", background: "#2a2118" }}>
                          <td style={{ padding: "6px 5px", opacity: 0.68, whiteSpace: "nowrap" }}>#{position + 1}</td>
                          <td style={{ padding: "6px 5px", color: "#c9b477", fontFamily: "monospace", whiteSpace: "nowrap" }}>{row.id}</td>
                          <td style={{ padding: "5px", color, fontWeight: 700, whiteSpace: "nowrap" }}><span style={{ display: "inline-flex", alignItems: "center", gap: 5 }}><GeneralAvatar name={name} gender={profile.gender} faction={facKey} size={26} /><span>{name}</span></span></td>
                          <td style={{ padding: "6px 5px", color, whiteSpace: "nowrap" }}>{facKey !== "neutral" ? (grand ? grandFacLabel(grand, facKey) : FACTIONS[facKey].name) : FACTIONS.neutral.name}</td>
                          <td style={{ padding: "6px 5px", whiteSpace: "nowrap" }}>{profile.gender === "f" ? "女" : "男"}</td>
                          <td style={{ padding: "6px 5px" }}>{profile.age}岁</td>
                          <td style={{ padding: "6px 5px" }}>{profile.gender === "f" ? (profile.charm ?? 70) : "—"}</td>
                          <td style={{ padding: "6px 5px", color: "#e4b36a" }}>{profile.force}</td>
                          <td style={{ padding: "6px 5px", color: "#9fc4d4" }}>{profile.wisdom}</td>
                          <td style={{ padding: "6px 5px" }}>{gen?.lead ?? "—"}</td>
                          <td style={{ padding: "6px 5px" }}>{gen?.pol ?? "—"}</td>
                          <td style={{ padding: "6px 5px" }}>{gen?.loyalty ?? captive?.loyalty ?? "—"}</td>
                          <td style={{ padding: "6px 5px", color: row.marriage.startsWith("已婚") ? "#d99a90" : "#b8ac8e", whiteSpace: "nowrap" }}>{row.marriage}</td>
                          <td style={{ padding: "6px 5px", color: gen ? "#b9c875" : captive ? "#d99a90" : "#b8ac8e", whiteSpace: "nowrap" }}>{status}</td>
                          <td style={{ padding: "5px", minWidth: 250 }}><div title={generalSkillContribution(name, profile)} onMouseEnter={e => setSkillTooltip({ text: generalSkillContribution(name, profile), x: e.clientX + 14, y: e.clientY + 14 })} onMouseMove={e => setSkillTooltip(t => t ? { ...t, x: e.clientX + 14, y: e.clientY + 14 } : t)} onMouseLeave={() => setSkillTooltip(null)} style={{ opacity: 0.78, marginBottom: 3, cursor: "help" }}>{(profile.skills || []).join("、") || "统军作战"}</div><div style={{ display: "flex", gap: 3, flexWrap: "wrap" }}>
                            {gen && grand && <><button onClick={() => trainGen(gen.name)} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10 }} disabled={grand.res[grand.playerFac].gold < 150}>培养</button><button onClick={() => rewardGen(gen.name)} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10 }} disabled={grand.res[grand.playerFac].gold < 100 || (gen.loyalty ?? 78) >= 100}>赏赐</button><button onClick={() => dismissGen(gen.name)} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10, color: "#c98" }}>放逐</button></>}
                            {captive && grand && <>{captive.captureType !== "family" && <button onClick={() => captiveAction(name, "persuade")} disabled={captive.tried} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10 }}>劝降</button>}{captive.gender === "f" && <button onClick={() => captiveAction(name, "marry")} disabled={captive.tried} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10 }}>婚配</button>}<button onClick={() => captiveAction(name, "release")} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10 }}>释放</button></>}
                            {battleArmy && <button onClick={() => { setSelected([battleArmy.id]); selectedRef.current = [battleArmy.id]; setGeneralManagerOpen(false); setTick(t => t + 1); }} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10 }}>选中战场</button>}
                            {grand && !gen && !captive && !battleArmy && !eliminated && <><button onClick={() => attemptUnseenGeneralAction(name, "recruit", row)} disabled={(stats.experience ?? 0) < 12} title="消耗12经验点：尝试招募该武将" style={{ ...smallBtn, padding: "1px 5px", fontSize: 10 }}>招募·12</button><button onClick={() => attemptUnseenGeneralAction(name, "incite", row)} disabled={(stats.experience ?? 0) < 8} title="消耗8经验点：降低该势力城池民心" style={{ ...smallBtn, padding: "1px 5px", fontSize: 10 }}>煽动·8</button><button onClick={() => attemptUnseenGeneralAction(name, "assassinate", row)} disabled={(stats.experience ?? 0) < 15} title="消耗15经验点：尝试刺杀，成功后该武将除名" style={{ ...smallBtn, padding: "1px 5px", fontSize: 10, color: "#d99a90" }}>刺杀·15</button>{grand.myGens.some(g => g.gender !== profile.gender) && <button onClick={() => attemptUnseenGeneralAction(name, "seduce", row)} disabled={(stats.experience ?? 0) < 10} title="消耗10经验点：派遣异性武将施展美人计，成功后招募该武将" style={{ ...smallBtn, padding: "1px 5px", fontSize: 10, borderColor: "#d99a90" }}>色诱·10</button>}{profile.gender === "f" && row.marriage.startsWith("已婚") && <button onClick={() => attemptUnseenGeneralAction(name, "entice", row)} disabled={(stats.experience ?? 0) < 18} title="消耗18经验点：对成年已婚女性尝试私情策反，成功后招揽入府" style={{ ...smallBtn, padding: "1px 5px", fontSize: 10, borderColor: "#d99a90" }}>勾引·18</button>}</>}
                          </div></td>
                        </tr>;
                      })}</tbody>
                    </table>
                  </div>}
                  </>}
                  {skillTooltip && <div role="tooltip" style={{ position: "fixed", left: Math.min(skillTooltip.x, window.innerWidth - 360), top: Math.min(skillTooltip.y, window.innerHeight - 120), zIndex: 300, width: "min(340px, calc(100vw - 24px))", padding: "8px 10px", whiteSpace: "pre-line", pointerEvents: "none", background: "#17120d", color: "#f4e5c5", border: "1px solid #d4b24c", borderRadius: 4, boxShadow: "0 5px 18px rgba(0,0,0,0.7)", fontSize: 11.5, lineHeight: 1.55 }}>技能贡献：{skillTooltip.text}</div>}
                  </>);
              })()}
                </div>
        </div>
      )}

      {/* ===== 闯关首页 ===== */}
      {mode === "campaignHome" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "min(96vw, 900px)" }}>
          <div style={{ fontSize: 12, opacity: 0.65, textAlign: "center" }}>
            选择一路势力，按序攻略战役。通关即解锁下一关，进度自动保存于本机。战斗中有三枚🧧锦囊可解燃眉之急。
            <span style={{ display: "block", marginTop: 3, fontSize: 11.5, opacity: 0.85 }}>
              📜 战功簿：出战{stats.battles} 胜{stats.wins} · 歼敌{stats.kills.toLocaleString()} · 生擒名将{stats.captures}
            </span>
          </div>
          {CAMPAIGN_FACS.map(fk => {
            const F = FACTIONS[fk];
            const stages = CAMPAIGN_ORDER[fk];
            const progress = Math.min(campaignProgress[fk] || 0, stages.length);
            const done = progress >= stages.length;
            const profile = campaignProfiles[fk] || createDefaultCampaignProfile();
            return (
              <div key={fk} style={{
                border: `1px solid ${F.color}55`, borderRadius: 6, padding: "10px 14px",
                background: "#221c14",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: F.color }}>{F.name}军 · 出征路线</span>
                  <span style={{ fontSize: 12, opacity: 0.65 }}>
                    已通 {progress}/{stages.length} 关{done ? " · 🏆 全线告捷" : ""}
                  </span>
                  <span style={{ fontSize: 11.5, color: "#d4b24c", opacity: 0.9 }}>
                    战役档案：🏅{profile.medals} · ⭐{profile.veteran} · 🌾{profile.supply} · 🏅将校{profile.officer}
                  </span>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {stages.map((st, idx) => {
                    const unlocked = idx <= progress;
                    const cleared = idx < progress;
                    const scen = SCENARIOS[st.key];
                    return (
                      <button key={idx} disabled={!unlocked}
                        onClick={() => unlocked && startCampaignStage(fk, idx)}
                        title={st.obj}
                        style={{
                          minWidth: 128, maxWidth: 160, textAlign: "left", padding: "7px 10px",
                          borderRadius: 4, fontFamily: "inherit",
                          background: cleared ? "#33422a" : unlocked ? "#3a2f22" : "#1d1811",
                          border: `1px solid ${unlocked ? F.color : "#4a4030"}`,
                          color: unlocked ? "#e8dcc1" : "#665c48",
                          cursor: unlocked ? "pointer" : "not-allowed",
                        }}>
                        <div style={{ fontSize: 11, opacity: 0.75 }}>
                          第{idx + 1}关 {cleared ? "✓ 已克" : unlocked ? "" : "🔒 未解锁"}
                        </div>
                        <div style={{ fontSize: 14, fontWeight: 700, margin: "1px 0" }}>{scen.name}</div>
                        <div style={{ fontSize: 10.5, opacity: 0.75, lineHeight: 1.3 }}>{st.obj}</div>
                      </button>
                    );
        })}
      </div>
    </div>
  );
          })}
        </div>
      )}

      {/* ===== 故事模式首页 ===== */}
      {mode === "storyHome" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14, width: "min(96vw, 900px)" }}>
          <div style={{ fontSize: 12, opacity: 0.65, textAlign: "center" }}>
            择一人一世，亲历天下大事。章节间有抉择、有声望——声望愈高，麾下士气愈盛、开局战阵愈精。胜得声望、败亦取益，进度自动保存。
          </div>
          <div style={{ display: "flex", gap: 8, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
            <span style={{ fontSize: 12.5, color: "#d4b24c" }}>☯ 难度：</span>
            {[["easy", "简单", "敌军三停去一 · 锦囊×4"], ["normal", "普通", "史实兵力 · 锦囊×3"], ["hard", "困难", "敌皆百战之师 · 锦囊×2"]].map(([k, name, note]) => (
              <button key={k} title={note}
                onClick={() => {
                  setStoryDifficulty(k);
                  persistStory(storyProgress, storyRenown, k, storyChoices);
                }}
                style={{
                  ...smallBtn,
                  background: storyDifficulty === k ? "#d4b24c" : "#2a231a",
                  color: storyDifficulty === k ? "#1c1712" : "#e8dcc1",
                  borderColor: storyDifficulty === k ? "#d4b24c" : "#6b5a3e",
                  fontWeight: storyDifficulty === k ? 700 : 400,
                }}>{name}</button>
            ))}
            <span style={{ fontSize: 11.5, opacity: 0.6 }}>
              📜 战功簿：出战{stats.battles} 胜{stats.wins} · 歼敌{stats.kills.toLocaleString()} · 生擒名将{stats.captures}
            </span>
          </div>
          {Object.entries(STORY).map(([lk, line]) => {
            const F = FACTIONS[line.fac];
            const prog = Math.min(storyProgress[lk] || 0, line.chapters.length);
            const done = prog >= line.chapters.length;
            const rn = storyRenown[lk] || 0;
            return (
              <div key={lk} style={{
                border: `1px solid ${F.color}55`, borderRadius: 6, padding: "10px 14px",
                background: "#221c14",
              }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 10, marginBottom: 4, flexWrap: "wrap" }}>
                  <span style={{ fontSize: 16, fontWeight: 700, color: F.color }}>《{line.name}》</span>
                  <span style={{ fontSize: 11, padding: "1px 7px", border: `1px solid ${F.color}88`, borderRadius: 8, color: F.color }}>{line.role}</span>
                  <span style={{ fontSize: 12, opacity: 0.65 }}>
                    {prog}/{line.chapters.length} 章{done ? " · 🏆 全卷终" : ""} · 声望 {rn}
                  </span>
                </div>
                <div style={{ fontSize: 12, opacity: 0.7, marginBottom: 8 }}>{line.desc}</div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                  {line.chapters.map((ch, idx) => {
                    const unlocked = idx <= prog;
                    const cleared = idx < prog;
                    return (
                      <button key={idx} disabled={!unlocked}
                        onClick={() => unlocked && openStoryChapter(lk, idx)}
                        title={ch.objective.desc}
                        style={{
                          minWidth: 150, maxWidth: 190, textAlign: "left", padding: "7px 10px",
                          borderRadius: 4, fontFamily: "inherit",
                          background: cleared ? "#33422a" : unlocked ? "#3a2f22" : "#1d1811",
                          border: `1px solid ${unlocked ? F.color : "#4a4030"}`,
                          color: unlocked ? "#e8dcc1" : "#665c48",
                          cursor: unlocked ? "pointer" : "not-allowed",
                        }}>
                        <div style={{ fontSize: 13, fontWeight: 700 }}>
                          {ch.title} {cleared ? "✓" : unlocked ? "" : "🔒"}
                        </div>
                        <div style={{ fontSize: 10.5, opacity: 0.75, lineHeight: 1.3 }}>🎯 {ch.objective.desc}</div>
                      </button>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* ===== 天下模式 ===== */}
      {mode === "grand" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "min(96vw, 940px)" }}>
          {!grand ? (
            <div style={{ textAlign: "center", padding: "26px 0" }}>
              <div style={{ fontSize: 15, marginBottom: 6, color: "#d4b24c", letterSpacing: 3 }}>◈ 择主公，定天下</div>
              <div style={{ fontSize: 12, opacity: 0.65, marginBottom: 16 }}>
                {Object.keys(GRAND_NODES).length}座城池关隘星罗棋布，海东另有朝鲜、倭地与夷洲据点。经营内政、募兵积粮，回合制运筹；出征可亲临战场实时会战，也可委任裁决；相邻同阵营城池还可呼叫援军合击。占尽天下者胜。
              </div>
              <div style={{ textAlign: "left", marginBottom: 10 }}>
                <div style={{ color: "#d4b24c", fontSize: 13, marginBottom: 6 }}>一、选择历史时期</div>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 7 }}>
                  {GRAND_PERIODS.map(period => {
                    const active = period.id === grandPeriodChoice;
                    return (
                      <button key={period.id} onClick={() => setGrandPeriodChoice(period.id)} style={{
                        ...smallBtn, textAlign: "left", padding: "7px 9px", minHeight: 58,
                        background: active ? "#5a4a28" : "#2a231a", borderColor: active ? "#d4b24c" : "#6b5a3e",
                      }}>
                        <b style={{ color: active ? "#ffe18a" : "#e8dcc1" }}>{period.year} · {period.name}</b>
                        <span style={{ display: "block", marginTop: 3, fontSize: 10.5, opacity: 0.68, lineHeight: 1.35 }}>{period.desc}</span>
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ textAlign: "left" }}>
                <div style={{ color: "#d4b24c", fontSize: 13, marginBottom: 6 }}>二、选择该时期经营势力 · {grandPeriodDef(grandPeriodChoice).name}</div>
                <div style={{ display: "flex", gap: 10, justifyContent: "center", flexWrap: "wrap" }}>
                  {[...new Set([...(grandPeriodDef(grandPeriodChoice).playable || FAC_KEYS), ...FOREIGN_FACTION_KEYS])].map(k => (
                    <button key={k} onClick={() => startGrand(k, grandPeriodChoice)} style={{
                      ...btn, fontSize: 16, padding: "11px 25px", fontWeight: 700,
                      background: FACTIONS[k].color, borderColor: FACTIONS[k].color, color: "#f5efdf",
                    }}>
                      {grandPeriodDef(grandPeriodChoice).factionLabels[k] || FACTIONS[k].name}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            <>
              {/* 顶栏 */}
              <div style={{ display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "center" }}>
                <span style={{ fontSize: 14 }}>
                  📯 第 <b style={{ color: "#d4b24c" }}>{grand.turn}</b> 回合 ·{" "}
                  <b style={{ color: FACTIONS[grand.playerFac].color }}>{grandFacLabel(grand, grand.playerFac)}</b>
                </span>
                <span style={{ fontSize: 13 }}>🪙 金 {grand.res[grand.playerFac].gold}</span>
                <span style={{ fontSize: 13 }}>🌾 粮 {grand.res[grand.playerFac].food}</span>
                <span style={{ fontSize: 13, color: "#e8c34a" }}>✨ 经验 {stats.experience ?? 0}</span>
                <span style={{ fontSize: 12.5 }}>🏯 {Object.entries(grand.nodes).filter(([id, n]) => n.owner === grand.playerFac && GRAND_NODES[id]?.type === "city").length}城·{Object.entries(grand.nodes).filter(([id, n]) => n.owner === grand.playerFac && GRAND_NODES[id]?.type === "pass").length}关</span>
                <span style={{ fontSize: 12.5 }}>🪖 {Object.values(grand.nodes).filter(n => n.owner === grand.playerFac).reduce((sum, n) => sum + n.troops, 0).toLocaleString()}</span>
                <span style={{ fontSize: 12.5 }}>⚔ 战绩 {stats.wins ?? 0}/{stats.battles ?? 0}</span>
                <span style={{ fontSize: 12.5 }}>武将 {grand.myGens.length} · 俘虏 {grand.captives.length} · 家眷 {grand.harem.length}</span>
                <span style={{ fontSize: 12, opacity: 0.65 }}>
                  收入/回合：金+{grandIncome(grand, grand.playerFac).gold} 粮+{grandIncome(grand, grand.playerFac).food}
                </span>
                <button onClick={grandEndTurn} disabled={!!grand.over}
                  style={{ ...btn, background: "#2f4a2c", fontWeight: 700, opacity: grand.over ? 0.4 : 1 }}>▶ 结束回合</button>
                <button onClick={toggleGrandMapFullscreen} title="天下大地图全屏（F）" style={{ ...smallBtn, background: grandMapFullscreen ? "#5a4a28" : "#2a231a", borderColor: grandMapFullscreen ? "#d4b24c" : "#6b5a3e" }}>
                  {grandMapFullscreen ? "⛶ 退出地图全屏" : "⛶ 地图全屏"}{showKeys && <Kbd k="F" />}
                </button>
                <button onClick={() => setGrandHudOpen(v => !v)} title="展开/隐藏天下军情（G）" style={{ ...smallBtn, background: grandHudOpen ? "#5a4a28" : "#2a231a", borderColor: grandHudOpen ? "#d4b24c" : "#6b5a3e" }}>
                  {grandHudOpen ? "− 天下军情" : "＋ 天下军情"}{showKeys && <Kbd k="G" />}
                </button>
                <button onClick={() => { setGrand(null); persistGrand(null); setGrandSel(null); setGrandDiplomacyFocus(null); setGrandMsg([]); }}
                  style={{ ...smallBtn, color: "#c98" }}>🗑 重开天下</button>
              </div>
              {/* 势力总览 */}
              <div style={{ display: "flex", justifyContent: "center", marginTop: 2 }}>
                <button onClick={() => setGrandSummaryOpen(v => !v)} style={{ ...smallBtn, padding: "2px 8px", fontSize: 10.5 }}>
                  {grandSummaryOpen ? "− 隐藏势力图" : "＋ 展开势力图"}
                </button>
              </div>
              {grandSummaryOpen && <div style={{
                display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(190px, 1fr))", gap: 7,
                width: "100%",
              }}>
                {GRAND_SUMMARY_FACS.map(fk => {
                  const F = FACTIONS[fk];
                  const s = grandFactionSummary(grand, fk);
                  const isPlayer = fk === grand.playerFac;
                  return (
                    <div key={fk} style={{
                      minWidth: 0, padding: "7px 9px", borderRadius: 4,
                      border: `1px solid ${F.color}${isPlayer ? "cc" : "66"}`,
                      background: isPlayer ? `${F.color}22` : "rgba(34,28,20,0.72)",
                      boxShadow: isPlayer ? `0 0 0 1px ${F.color}33` : "none",
                      cursor: "pointer",
                    }} onClick={() => setGrandDiplomacyFocus(fk)} title="点击查看该势力与其他势力的地图外交关系">
                      <div style={{ display: "flex", justifyContent: "space-between", gap: 5, alignItems: "baseline", marginBottom: 3 }}>
                        <b style={{ color: F.color, fontSize: 13 }}>{grandFacLabel(grand, fk)}{fk === "neutral" ? "据点" : "势力"}</b>
                        {isPlayer && <span style={{ fontSize: 10.5, color: "#d4b24c" }}>我方</span>}
                      </div>
                      <div style={{ display: "flex", gap: 7, flexWrap: "wrap", fontSize: 11.5, lineHeight: 1.45 }}>
                        <span>🏯 {s.cities}城·{s.passes}关</span>
                        <span>🪖 {s.troops.toLocaleString()}</span>
                        {fk !== "neutral" && <><span>🪙 {s.gold}</span><span>🌾 {s.food}</span></>}
                        <span style={{ color: s.loyalty < 45 ? "#c07a6c" : "#b9c875" }}>🏮民心{s.loyalty}</span>
                      </div>
                    </div>
                  );
                })}
              </div>}
              {/* 外交关系 */}
              <div style={{ width: "100%", padding: "7px 9px", border: "1px solid #6b5a3e", borderRadius: 4, background: "rgba(34,28,20,0.72)" }}>
                <div style={{ display: "flex", alignItems: "baseline", gap: 8, flexWrap: "wrap", marginBottom: 5 }}>
                  <span style={{ color: "#d4b24c", fontSize: 13, letterSpacing: 2 }}>◈ 外交关系</span>
                  <span style={{ fontSize: 11, opacity: 0.68 }}>友好度 −100～100 · 达到40可提出结盟 · 盟友相邻城池可支援出兵</span>
                </div>
                <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                  {GRAND_FACTION_KEYS.filter(fk => fk !== grand.playerFac && Object.values(grand.nodes).some(n => n.owner === fk)).map(fk => {
                    const relation = grandRelation(grand, grand.playerFac, fk);
                    const allied = grandAreAllied(grand, grand.playerFac, fk);
                    const relationColor = relation >= 40 ? "#b9c875" : relation < 0 ? "#c07a6c" : "#d4b24c";
                    return <div key={fk} style={{ display: "flex", alignItems: "center", gap: 5, padding: "3px 5px", border: `1px solid ${FACTIONS[fk].color}66`, borderRadius: 3, fontSize: 11.5 }}>
                      <span style={{ color: FACTIONS[fk].color, fontWeight: 700 }}>{grandFacLabel(grand, fk)}</span>
                      <b style={{ color: relationColor, minWidth: 27, textAlign: "right" }}>{relation > 0 ? `+${relation}` : relation}</b>
                      <span style={{ color: allied ? "#b9c875" : "#b8ac8e", fontSize: 10 }}>{allied ? "盟友" : "未盟"}</span>
                      {!allied && <button onClick={() => grandDiplomacy(fk, "gift")} disabled={grand.res[grand.playerFac].gold < 100} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10 }}>赠礼·100金</button>}
                      {!allied && <button onClick={() => grandDiplomacy(fk, "alliance")} disabled={relation < 40 || grand.res[grand.playerFac].gold < 250} title={relation < 40 ? "友好度达到40后才可结盟" : "消耗250金缔结盟约"} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10, borderColor: relation >= 40 ? "#79a86b" : "#6b5a3e", opacity: relation >= 40 ? 1 : 0.5 }}>🤝 结盟·250金</button>}
                      {allied && <button onClick={() => grandDiplomacy(fk, "break")} style={{ ...smallBtn, padding: "1px 5px", fontSize: 10, color: "#e8b0a5" }}>解盟</button>}
                    </div>;
                  })}
                </div>
              </div>
              {grand.over && (
                <div style={{
                  textAlign: "center", fontSize: 18, fontWeight: 700, letterSpacing: 4, padding: "8px 0",
                  color: grand.over === grand.playerFac ? "#d4b24c" : "#c07a6c",
                }}>
                  {grand.over === grand.playerFac ? "🏆 九州归一，天下定于我手！" : grand.over === "defeat" ? "☠ 基业尽失，霸业成空……" : `🏳 天下已为${FACTIONS[grand.over] ? FACTIONS[grand.over].name : ""}所并`}
                </div>
              )}
              {/* 舆图 */}
              <div style={{ fontSize: 11.5, opacity: 0.7, textAlign: "center" }}>
                点击己方城池选择出征源，按住 Shift/Ctrl 再点其他己方城池可多选；随后点击相邻敌方城池进行合击。
              </div>
              {grandDiplomacyFocus && <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 8, flexWrap: "wrap", padding: "4px 8px", background: "#2a2118", border: "1px solid #d4b24c88", borderRadius: 4, fontSize: 11.5 }}>
                <span style={{ color: "#ffe18a" }}>🗺 外交视图：{grandFacLabel(grand, grandDiplomacyFocus)}势力</span>
                <span style={{ opacity: 0.72 }}>绿色实线=盟友 · 金线=友好 · 红线=敌对，线上数字为友好度</span>
                <button onClick={() => setGrandDiplomacyFocus(null)} style={{ ...smallBtn, padding: "2px 7px", fontSize: 10.5 }}>关闭外交视图</button>
              </div>}
              <div ref={grandMapRef} style={{
                position: grandMapFullscreen ? "fixed" : "relative",
                ...(grandMapFullscreen ? { inset: 0, zIndex: 9999, background: "#14100b", overflow: "hidden" } : { width: "100%" }),
                display: "flex", flexDirection: "column", gap: 8,
              }}>
              <div style={{
                position: "relative", width: grandMapFullscreen ? "100vw" : "100%", height: grandMapFullscreen ? "100vh" : "auto", aspectRatio: grandMapFullscreen ? "auto" : "8/5",
                background: "linear-gradient(135deg,#e7dcc1 0%,#dccfae 55%,#cfc09a 100%)",
                border: grandMapFullscreen ? "none" : "2px solid #6b5a3e", borderRadius: grandMapFullscreen ? 0 : 4, overflow: "hidden",
                overscrollBehavior: "none",
                zIndex: grandMapFullscreen ? 10000 : "auto",
              }} onWheelCapture={e => {
                e.preventDefault();
                setGrandMapZoom(z => Math.max(1, Math.min(2.4, z * (e.deltaY < 0 ? 1.12 : 1 / 1.12))));
              }}>
                {grandHudOpen && <div onClick={e => e.stopPropagation()} style={{
                  position: "absolute", top: grandMapFullscreen ? 10 : 6, left: grandMapFullscreen ? 10 : 6, zIndex: 20,
                  width: grandMapFullscreen ? "min(92vw, 720px)" : "min(94%, 720px)", maxHeight: grandMapFullscreen ? "58vh" : "46%", overflowY: "auto",
                  padding: "9px 11px", border: "1px solid #d4b24c99", borderRadius: 6,
                  background: "rgba(28,22,15," + grandHudOpacity + ")", color: "#e8dcc1",
                  boxShadow: "0 8px 28px rgba(20,14,8,0.45)", backdropFilter: "blur(3px)",
                }}>
                 <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", paddingRight: 26 }}>
                    <b style={{ color: "#ffe18a", fontSize: 13 }}>📯 天下军情 <span style={{ fontSize: 9, opacity: 0.7 }}>G</span></b>
                    <span>第 <b style={{ color: "#d4b24c" }}>{grand.turn}</b> 回合</span>
                    <span style={{ color: FACTIONS[grand.playerFac].color, fontWeight: 700 }}>{grandFacLabel(grand, grand.playerFac)}</span>
                    <span>🪙 {grand.res[grand.playerFac].gold}</span>
                    <span>🌾 {grand.res[grand.playerFac].food}</span>
                   <span style={{ opacity: 0.72, fontSize: 11 }}>收入 金+{grandIncome(grand, grand.playerFac).gold} / 粮+{grandIncome(grand, grand.playerFac).food}</span>
                 </div>
                  <div style={{ display: "flex", gap: 5, flexWrap: "wrap", marginTop: 7, paddingBottom: 6, borderBottom: "1px solid #6b5a3e88" }}>
                    <button onClick={grandEndTurn} disabled={!!grand.over} style={{ ...smallBtn, padding: "2px 7px", fontSize: 10.5, background: "#2f4a2c" }}>▶ 结束回合</button>
                    <button onClick={() => setGrandDiplomacyFocus(grand.playerFac)} style={{ ...smallBtn, padding: "2px 7px", fontSize: 10.5, borderColor: "#d4b24c" }}>◈ 外交关系</button>
                    <button onClick={() => { setGeneralManagerModalOpen(false); setGeneralManagerOpen(v => !v); }} style={{ ...smallBtn, padding: "2px 7px", fontSize: 10.5, borderColor: generalManagerOpen ? "#d4b24c" : "#79a86b", background: generalManagerOpen ? "#5a4a28" : "#2a231a" }}>⚔ {generalManagerOpen ? "− 武将府" : "＋ 武将府"}</button>
                    <button onClick={() => setGrandSummaryOpen(v => !v)} style={{ ...smallBtn, padding: "2px 7px", fontSize: 10.5 }}>{grandSummaryOpen ? "− 隐藏势力图" : "＋ 势力图"}</button>
                  </div>
                 {grandSummaryOpen && <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(145px, 1fr))", gap: 5, marginTop: 7 }}>
                    {GRAND_SUMMARY_FACS.map(fk => {
                      const F = FACTIONS[fk];
                      const summary = grandFactionSummary(grand, fk);
                      return <div key={"hud-" + fk} style={{ padding: "4px 6px", border: "1px solid " + F.color + "77", borderRadius: 3, background: F.color + "18", fontSize: 10.5 }}>
                        <b style={{ color: F.color }}>{grandFacLabel(grand, fk)}</b>
                        <span style={{ display: "block", marginTop: 2, opacity: 0.86 }}>🏯{summary.cities}城·{summary.passes}关 / 🪖{summary.troops.toLocaleString()}</span>
                      </div>;
                    })}
                  </div>}
                  <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginTop: 7, fontSize: 10.5 }}>
                    <span style={{ color: "#d4b24c", fontWeight: 700 }}>◈ 外交关系</span>
                   {GRAND_FACTION_KEYS.filter(fk => fk !== grand.playerFac && Object.values(grand.nodes).some(n => n.owner === fk)).map(fk => {
                     const relation = grandRelation(grand, grand.playerFac, fk);
                     const allied = grandAreAllied(grand, grand.playerFac, fk);
                      return <span key={"hud-rel-" + fk} style={{ display: "inline-flex", alignItems: "center", gap: 3, color: FACTIONS[fk].color }}>{grandFacLabel(grand, fk)} {relation > 0 ? "+" + relation : relation}{allied ? "·盟" : ""}{!allied && <button onClick={() => grandDiplomacy(fk, "gift")} disabled={grand.res[grand.playerFac].gold < 100} style={{ ...smallBtn, padding: "1px 4px", fontSize: 9 }}>赠礼</button>}{!allied && <button onClick={() => grandDiplomacy(fk, "alliance")} disabled={relation < 40 || grand.res[grand.playerFac].gold < 250} title={relation < 40 ? "友好度达到40后才可结盟" : "消耗250金缔结盟约"} style={{ ...smallBtn, padding: "1px 4px", fontSize: 9, opacity: relation >= 40 ? 1 : 0.5 }}>结盟</button>}{allied && <button onClick={() => grandDiplomacy(fk, "break")} style={{ ...smallBtn, padding: "1px 4px", fontSize: 9, color: "#e8b0a5" }}>解盟</button>}</span>;
                   })}
                   <label style={{ display: "inline-flex", alignItems: "center", gap: 4, marginLeft: "auto", opacity: 0.82 }}>
                      透明度
                      <input type="range" min="35" max="100" step="5" value={Math.round(grandHudOpacity * 100)} onChange={e => {
                        const value = Number(e.target.value) / 100;
                        setGrandHudOpacity(value);
                        persistSettings(settingsRef.current, sizeKey, grandPanelOpacity, value);
                      }} style={{ width: 82, accentColor: "#d4b24c" }} />
                      {Math.round(grandHudOpacity * 100)}%
                    </label>
                  </div>
                  {grandSel && grand.nodes[grandSel]?.owner === grand.playerFac && (() => {
                    const city = grand.nodes[grandSel];
                    const cityDef = GRAND_NODES[grandSel];
                    const resource = grand.res[grand.playerFac];
                    const hostileNeighbors = grandAdj(grandSel).filter(id => !grandAreAllied(grand, grand.nodes[id].owner, grand.playerFac));
                    const alliedSupportCandidates = Object.entries(grand.nodes)
                      .filter(([id, node]) => grandAreAllied(grand, node.owner, grand.playerFac) && id !== grandSel && node.troops > 1000 && grandAdj(id).some(targetId => hostileNeighbors.includes(targetId)));
                    const attackUse = Math.min(grandDeploy, Math.max(1000, city.troops - 500));
                    return (
                      <div style={{ marginTop: 8, paddingTop: 7, borderTop: "1px solid #d4b24c66" }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 5 }}>
                          <b style={{ color: FACTIONS[grand.playerFac].color }}>🏙 {cityDef.name} · 内政</b>
                          <span style={{ fontSize: 10.5, opacity: 0.82 }}>🪖{city.troops.toLocaleString()} / 🌾{city.farm} / 🪙{city.trade} / 🧱{city.wall} / 🏮{city.loyalty}</span>
                          <button onClick={() => { setGrandSel(null); setGrandAttackSources([]); }} style={{ ...smallBtn, marginLeft: "auto", padding: "1px 6px", fontSize: 10 }}>× 关闭</button>
                        </div>
                        <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ color: "#d4b24c", fontSize: 10.5 }}>◈ 内政</span>
                          {Object.entries(GOV_ACTIONS).map(([key, action]) => (
                            <button key={"hud-gov-" + key} title={action.note} disabled={city.acted || resource.gold < action.gold}
                              onClick={() => grandGovern(grandSel, key)}
                              style={{ ...smallBtn, padding: "2px 5px", fontSize: 9.5, opacity: city.acted || resource.gold < action.gold ? 0.4 : 1 }}>
                              {action.icon}{action.name}
                            </button>
                          ))}
                          <button title="募兵两千：消耗金粮，民心达到80时半价" disabled={city.acted || resource.gold < (city.loyalty >= 80 ? 100 : 200) || resource.food < 200}
                            onClick={() => grandMuster(grandSel)}
                            style={{ ...smallBtn, padding: "2px 5px", fontSize: 9.5, borderColor: "#7a9a4f", opacity: city.acted || resource.gold < (city.loyalty >= 80 ? 100 : 200) || resource.food < 200 ? 0.4 : 1 }}>
                            🪖 募兵
                          </button>
                        </div>
                        {hostileNeighbors.length > 0 && <div style={{ display: "flex", gap: 5, flexWrap: "wrap", alignItems: "center", marginTop: 5 }}>
                          <span style={{ color: "#d4b24c", fontSize: 10.5 }}>◈ 出征</span>
                          <input type="range" min={1000} max={Math.max(1000, city.troops - 500)} step={500}
                            value={attackUse} onChange={e => setGrandDeploy(+e.target.value)} style={{ width: 92, accentColor: "#d4b24c" }} />
                          <span style={{ color: "#d4b24c", fontSize: 10.5 }}>{attackUse.toLocaleString()}人</span>
                          {alliedSupportCandidates.length > 0 && <button onClick={() => setGrandSupport(v => !v)} title="与相邻盟友商议，共同攻打所选敌城；盟友每城最多出兵2000人" style={{ ...smallBtn, padding: "2px 5px", fontSize: 9.5, borderColor: grandSupport ? "#d4b24c" : "#6b5a3e", background: grandSupport ? "#5a4a28" : "#2a231a" }}>
                            {grandSupport ? "🤝 已商定共攻" : "🤝 商讨盟友共攻"}
                          </button>}
                          {hostileNeighbors.map(id => <span key={"hud-attack-" + id} style={{ display: "inline-flex", gap: 2 }}>
                            <button disabled={city.troops < 1500 || !!grand.over} onClick={() => grandAttack(grandSel, id, attackUse, true, grandSupport)} style={{ ...smallBtn, padding: "2px 5px", fontSize: 9.5, background: "#5a2320", borderColor: FACTIONS[grand.nodes[id].owner].color }}>
                              ⚔ 亲征{GRAND_NODES[id].name}
                            </button>
                            <button disabled={city.troops < 1500 || !!grand.over} onClick={() => grandAttack(grandSel, id, attackUse, false, grandSupport)} style={{ ...smallBtn, padding: "2px 5px", fontSize: 9.5 }}>
                              委任
                            </button>
                          </span>)}
                        </div>}
                      </div>
                    );
                  })()}
                  {generalManagerOpen && <GrandHudGenerals
                    grand={grand} smallBtn={smallBtn} grandNodes={GRAND_NODES} generalCombatStats={generalCombatStats}
                    onClose={() => setGeneralManagerOpen(false)} trainGen={trainGen} rewardGen={rewardGen} dismissGen={dismissGen}
                    confessToFemale={confessToFemale} haveOneNight={haveOneNight} haveIntimacy={haveIntimacy}
                  />}
                  <button onClick={() => setGrandHudOpen(false)} title="隐藏天下军情浮窗" style={{ position: "absolute", top: 6, right: 7, ...smallBtn, padding: "1px 6px", fontSize: 10 }}>−</button>
                </div>}
                {!grandHudOpen && <button onClick={() => setGrandHudOpen(true)} title="展开天下军情浮窗" style={{ position: "absolute", top: grandMapFullscreen ? 10 : 6, left: grandMapFullscreen ? 10 : 6, zIndex: 20, ...smallBtn, background: "rgba(28,22,15,0.88)", borderColor: "#d4b24c", color: "#ffe18a" }}>＋ 天下军情{showKeys && <Kbd k="G" dark />}</button>}
                {grandMapFullscreen && <button onClick={toggleGrandMapFullscreen} style={{ position: "absolute", top: 10, right: 10, zIndex: 20, ...smallBtn, background: "rgba(33,26,18,0.88)", borderColor: "#d4b24c", color: "#ffe18a" }}>
                  ⛶ 退出地图全屏
                </button>}
                <div style={{
                  position: "absolute", inset: 0,
                  transform: `scale(${grandMapZoom})`, transformOrigin: "center center",
                  transition: "transform 80ms ease-out",
                }}>
                {/* 历史地形底图：以水墨线条表现黄河、长江与主要山系，不参与交互 */}
                <svg aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none", opacity: 0.72 }} viewBox="0 0 100 100" preserveAspectRatio="none">
                  <defs>
                    <linearGradient id="grandRiver" x1="0" y1="0" x2="1" y2="0">
                      <stop offset="0" stopColor="#7893a0" stopOpacity="0.38" />
                      <stop offset="0.5" stopColor="#5d7f91" stopOpacity="0.62" />
                      <stop offset="1" stopColor="#7893a0" stopOpacity="0.38" />
                    </linearGradient>
                    <pattern id="grandHill" width="8" height="6" patternUnits="userSpaceOnUse">
                      <path d="M0 5 Q2 1 4 5 Q6 1 8 5" fill="none" stroke="#806f50" strokeWidth="0.55" opacity="0.28" />
                    </pattern>
                    <filter id="grandPaper" x="-10%" y="-10%" width="120%" height="120%">
                      <feTurbulence type="fractalNoise" baseFrequency="0.045" numOctaves="3" seed="27" result="noise" />
                      <feColorMatrix in="noise" type="saturate" values="0" result="mono" />
                      <feComponentTransfer in="mono">
                        <feFuncA type="table" tableValues="0 0.16" />
                      </feComponentTransfer>
                    </filter>
                    <filter id="grandRelief" x="-20%" y="-20%" width="140%" height="140%">
                      <feGaussianBlur stdDeviation="1.1" />
                    </filter>
                  </defs>
                  {/* 纸张颗粒与地貌色带 */}
                  <rect x="0" y="0" width="100" height="100" fill="#a89873" filter="url(#grandPaper)" opacity="0.8" />
                  <path d="M5 35 Q18 29 31 35 T56 38 Q70 34 95 42 L95 61 Q76 55 60 59 T28 57 Q14 61 5 55 Z" fill="#a8b58b" opacity="0.16" />
                  <path d="M33 43 Q46 38 59 45 T82 47 L79 64 Q63 62 51 66 T31 60 Z" fill="#c8b47c" opacity="0.17" />
                  {/* 山地阴影块 */}
                  <path d="M8 25 Q16 13 28 20 T43 30 Q35 38 25 41 T10 37 Z" fill="url(#grandHill)" opacity="0.9" />
                  <path d="M8 50 Q17 43 27 47 T42 57 Q32 67 21 72 T7 67 Z" fill="url(#grandHill)" opacity="0.7" />
                  <path d="M28 73 Q42 65 57 74 T77 84 Q60 94 43 91 T27 84 Z" fill="url(#grandHill)" opacity="0.62" />
                  {/* 山脊与等高线 */}
                  <g fill="none" stroke="#756447" strokeLinecap="round" opacity="0.28">
                    <path d="M8 27 Q17 18 27 23 T42 31" strokeWidth="1.1" />
                    <path d="M10 31 Q19 23 28 27 T39 34" strokeWidth="0.65" />
                    <path d="M12 35 Q20 29 29 31 T36 37" strokeWidth="0.45" />
                    <path d="M10 54 Q19 48 28 52 T40 58" strokeWidth="1" />
                    <path d="M12 59 Q20 54 29 57 T37 62" strokeWidth="0.6" />
                    <path d="M31 77 Q43 70 55 77 T74 84" strokeWidth="1" />
                    <path d="M34 82 Q45 76 56 82 T70 87" strokeWidth="0.6" />
                    <path d="M38 87 Q47 82 57 87 T66 90" strokeWidth="0.45" />
                  </g>
                  {/* 西部雪岭、秦岭与江南山脉的山峰轮廓 */}
                  <g fill="#77674b" opacity="0.22" filter="url(#grandRelief)">
                    <path d="M5 23 L10 16 L14 22 L19 12 L24 21 L29 15 L34 25 L40 20 L45 29 L5 29 Z" />
                    <path d="M8 48 L14 42 L18 48 L24 40 L29 49 L35 44 L42 55 L8 55 Z" />
                    <path d="M29 76 L35 68 L40 75 L46 67 L51 75 L57 70 L63 79 L70 73 L78 84 L29 84 Z" />
                  </g>
                  <path d="M57 7 Q63 14 61 27 T65 43" fill="none" stroke="#806f50" strokeWidth="2.2" opacity="0.24" />
                  <path d="M59 7 Q65 15 64 27 T68 43" fill="none" stroke="#806f50" strokeWidth="0.8" opacity="0.28" />
                  {/* 黄河 */}
                  <path d="M14 18 Q25 12 35 17 Q43 21 51 15 Q61 9 70 16 Q79 23 91 28" fill="none" stroke="url(#grandRiver)" strokeWidth="2.1" />
                  <path d="M14 18 Q25 12 35 17 Q43 21 51 15 Q61 9 70 16 Q79 23 91 28" fill="none" stroke="#f1ead1" strokeWidth="0.45" opacity="0.65" />
                  {/* 长江 */}
                  <path d="M7 65 Q19 61 29 66 Q38 70 47 66 Q56 63 63 70 Q73 78 91 73" fill="none" stroke="url(#grandRiver)" strokeWidth="2.5" />
                  <path d="M7 65 Q19 61 29 66 Q38 70 47 66 Q56 63 63 70 Q73 78 91 73" fill="none" stroke="#f1ead1" strokeWidth="0.5" opacity="0.68" />
                  {/* 汉水与湘江 */}
                  <path d="M42 38 Q44 46 48 53 Q51 59 55 66" fill="none" stroke="#6d8d98" strokeWidth="1.1" opacity="0.45" />
                  <path d="M35 54 Q41 59 47 66" fill="none" stroke="#6d8d98" strokeWidth="0.9" opacity="0.4" />
                  <path d="M23 18 Q29 24 34 27 Q39 30 43 36" fill="none" stroke="#6d8d98" strokeWidth="0.75" opacity="0.36" />
                  <path d="M50 16 Q54 24 54 31 Q53 38 49 44" fill="none" stroke="#6d8d98" strokeWidth="0.65" opacity="0.32" />
                  {/* 海东地形：朝鲜半岛、倭地与夷洲 */}
                  <path d="M88 3 Q94 5 96 12 L94 22 Q91 29 89 31 Q87 22 88 14 Z" fill="#899776" opacity="0.34" stroke="#6f765b" strokeWidth="0.55" />
                  <path d="M95 31 Q99 38 97 45 Q99 52 96 58 Q98 65 95 70" fill="none" stroke="#727b60" strokeWidth="2.1" opacity="0.34" />
                  <path d="M84 82 Q88 80 90 85 L88 93 Q84 95 83 89 Z" fill="#899776" opacity="0.38" stroke="#6f765b" strokeWidth="0.55" />
                  {/* 地形名称 */}
                  <g fill="#66583f" fontFamily="serif" fontSize="2.6" letterSpacing="0.7" opacity="0.6">
                    <text x="72" y="14">黄河</text>
                    <text x="76" y="76">长江</text>
                    <text x="31" y="39">秦岭</text>
                    <text x="62" y="28" transform="rotate(72 62 28)">太行山</text>
                    <text x="13" y="76">巴蜀山地</text>
                    <text x="57" y="88">江南丘陵</text>
                    <text x="89" y="3">朝鲜</text>
                    <text x="95" y="35" transform="rotate(72 95 35)">倭地</text>
                    <text x="84" y="80">夷洲</text>
                  </g>
                </svg>
                {/* 领地色带：以节点为中心的低透明度地貌染色，让城池与地图融为一体 */}
                <svg aria-hidden="true" style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }} viewBox="0 0 100 100" preserveAspectRatio="none">
                  {Object.entries(GRAND_NODES).map(([id, nd]) => (
                    <circle key={`territory-${id}`} cx={nd.x} cy={nd.y} r={nd.type === "city" ? 10 : 7}
                      fill={FACTIONS[grand.nodes[id].owner].color} opacity="0.09" />
                  ))}
                </svg>
                <svg style={{ position: "absolute", inset: 0, width: "100%", height: "100%", pointerEvents: "none" }}>
                  {/* 异阵营相邻节点之间的动态疆界 */}
                  {GRAND_EDGES.filter(([a, b]) => grand.nodes[a].owner !== grand.nodes[b].owner).map(([a, b], i) => (
                    <line key={`border-${i}`}
                      x1={`${GRAND_NODES[a].x}%`} y1={`${GRAND_NODES[a].y}%`}
                      x2={`${GRAND_NODES[b].x}%`} y2={`${GRAND_NODES[b].y}%`}
                      stroke="#6f3029" strokeWidth="3.4" strokeDasharray="1.5 1.2" opacity="0.78" />
                  ))}
                  {GRAND_EDGES.map(([a, b], i) => (
                    <line key={i}
                      x1={`${GRAND_NODES[a].x}%`} y1={`${GRAND_NODES[a].y}%`}
                      x2={`${GRAND_NODES[b].x}%`} y2={`${GRAND_NODES[b].y}%`}
                      stroke="#6b5a3e" strokeWidth="1.4" strokeDasharray="5 4" opacity="0.55" />
                  ))}
                </svg>
                <div style={{
                  position: "absolute", top: 5, left: 7, zIndex: 1, pointerEvents: "none",
                  padding: "2px 6px", borderRadius: 3, background: "rgba(231,220,193,0.72)",
                  color: "#6f3029", fontSize: 10.5,
                }}>━━ 疆界 · ┄┄ 道路</div>
                {Object.entries(GRAND_NODES).map(([id, nd]) => {
                  const st = grand.nodes[id];
                  const F = FACTIONS[st.owner];
                  const isSel = grandSel === id || grandAttackSources.includes(id);
                  const isMine = st.owner === grand.playerFac;
                  const canReach = st.owner !== grand.playerFac && grandAttackSources.some(sourceId => grandAdj(sourceId).includes(id));
                  return (
                    <button key={id}
                      onClick={e => {
                        const multi = e.shiftKey || e.ctrlKey || e.metaKey;
                        if (st.owner === grand.playerFac && multi) {
                          setGrandAttackSources(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
                          setGrandSel(id);
                          setGrandDiplomacyFocus(grand.playerFac);
                        } else {
                          setGrandSel(id);
                          if (st.owner === grand.playerFac) { setGrandAttackSources([id]); setGrandDiplomacyFocus(grand.playerFac); }
                          else if (!grandAttackSources.length) setGrandAttackSources([]);
                        }
                      }}
                      title={`${nd.name}${nd.region ? `（${nd.region}）` : ""} · ${F.name} · 兵${st.troops.toLocaleString()} · 农${st.farm} 商${st.trade} 防${st.wall} 民${st.loyalty}`}
                      style={{
                        position: "absolute", left: `${nd.x}%`, top: `${nd.y}%`, zIndex: 2,
                        transform: "translate(-50%,-50%)",
                        padding: 0, border: "none", background: "transparent", color: "#2a2118",
                        cursor: "pointer", fontFamily: "inherit", lineHeight: 1, textAlign: "center",
                      }}>
                      <span style={{
                        display: "inline-flex", width: nd.type === "city" ? 18 : 16, height: nd.type === "city" ? 18 : 16,
                        alignItems: "center", justifyContent: "center", color: "#f5efdf", fontSize: nd.type === "city" ? 10 : 9,
                        background: F.color, border: isSel ? "2px solid #ffdf70" : canReach ? "2px dashed #ffdf70" : isMine ? "2px solid #fff1a8" : "1.5px solid #2a2118",
                        borderRadius: nd.type === "city" ? "50%" : 3, transform: nd.type === "pass" ? "rotate(45deg)" : "none",
                        boxShadow: isSel ? "0 0 12px #ffdf70" : isMine ? "0 0 8px #ffe58a88" : "0 1px 4px rgba(42,33,24,0.45)",
                      }}><span style={{ transform: nd.type === "pass" ? "rotate(-45deg)" : "none" }}>{nd.type === "pass" ? "关" : "城"}</span></span>
                      <span style={{ display: "block", marginTop: 2, padding: "1px 3px", borderRadius: 2, background: "rgba(238,229,204,0.88)", border: `1px solid ${F.color}88`, whiteSpace: "nowrap", fontSize: nd.type === "city" ? 10.5 : 9.5, fontWeight: 700, textShadow: "0 1px #f5efdf" }}>
                        <span style={{ display: "inline-block", marginRight: 3, padding: "0 3px", borderRadius: 2, background: F.color, color: "#fff8e5", fontSize: 8.5, lineHeight: 1.25, textShadow: "none", verticalAlign: 1 }}>{isMine ? "我方" : F.name}</span>{nd.name}
                        <em style={{ display: "block", fontStyle: "normal", fontSize: 8.5, fontWeight: 400, color: F.color }}>
                          {(st.troops / 1000).toFixed(1)}k{st.acted ? " ·已动" : ""}
                        </em>
                      </span>
                    </button>
                  );
                })}
                </div>
                <div style={{
                  position: "absolute", right: 7, bottom: 6, zIndex: 3, pointerEvents: "none",
                  padding: "2px 6px", borderRadius: 3, background: "rgba(231,220,193,0.72)",
                  color: "#66583f", fontSize: 10.5,
                }}>滚轮缩放 {Math.round(grandMapZoom * 100)}%</div>
              </div>
              {/* 选中节点面板 */}
              {grandSel && grand.nodes[grandSel] && (() => {
                const st = grand.nodes[grandSel];
                const nd = GRAND_NODES[grandSel];
                const mine = st.owner === grand.playerFac;
                const r = grand.res[grand.playerFac];
                const neighbors = grandAdj(grandSel);
                const alliedNeighbors = Object.entries(grand.nodes)
                  .filter(([id, node]) => grandAreAllied(grand, node.owner, grand.playerFac) && id !== grandSel && node.troops > 1000 && grandAdj(id).some(targetId => neighbors.includes(targetId) && !grandAreAllied(grand, grand.nodes[targetId].owner, grand.playerFac)));
                const selectedSources = grandAttackSources
                  .map(id => ({ id, node: grand.nodes[id] }))
                  .filter(x => x.node && x.node.owner === grand.playerFac);
                const targetSources = selectedSources.filter(x => grandAdj(x.id).includes(grandSel));
                const multiAvailable = targetSources.reduce((sum, x) => sum + Math.max(0, x.node.troops - 500), 0);
                const multiUse = Math.min(grandDeploy, Math.max(1000, multiAvailable));
                const multiSupportCandidates = Object.entries(grand.nodes)
                  .filter(([id, node]) => grandAreAllied(grand, node.owner, grand.playerFac) && !grandAttackSources.includes(id) && node.troops > 1000 && grandAdj(id).includes(grandSel));
                return (!mine || !grandHudOpen) ? (
                  <>
                  {mine && <div onClick={() => { setGrandSel(null); setGrandAttackSources([]); }} aria-label="关闭城池面板"
                    style={{ position: "fixed", inset: 0, zIndex: 49, background: "rgba(8,6,4,0.22)" }} />}
                  <div onClick={e => mine && e.stopPropagation()} style={{
                      border: `1px solid ${FACTIONS[st.owner].color}77`, borderRadius: 5,
                      background: `rgba(34,28,20,${grandPanelOpacity})`, padding: "10px 14px",
                      display: "flex", flexDirection: "column", gap: 8,
                      ...(mine ? {
                        position: "fixed", left: "50%", top: "50%", transform: "translate(-50%, -50%)",
                        width: "min(92vw, 900px)", maxHeight: "76vh", overflowY: "auto", zIndex: 50,
                        boxShadow: "0 14px 50px rgba(20,14,8,0.72)",
                      } : {}),
                    }}>
                    {mine && <button onClick={() => { setGrandSel(null); setGrandAttackSources([]); }} title="关闭城池面板"
                      style={{
                        position: "absolute", top: 7, right: 9, zIndex: 1,
                        background: "transparent", color: "#b8ac8e", border: "1px solid #6b5a3e",
                        borderRadius: 3, padding: "1px 7px", cursor: "pointer", fontFamily: "inherit",
                      }}>×</button>}
                    <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "baseline" }}>
                      <span style={{ fontSize: 15, fontWeight: 700, color: FACTIONS[st.owner].color }}>
                        {nd.type === "pass" ? "🏯 " : "🏙 "}{nd.name}
                      </span>
                      <span style={{ fontSize: 12.5 }}>🪖 兵 {st.troops.toLocaleString()}</span>
                      <span style={{ fontSize: 12.5 }}>🌾 农 {st.farm}</span>
                      <span style={{ fontSize: 12.5 }}>🪙 商 {st.trade}</span>
                      <span style={{ fontSize: 12.5 }}>🧱 防 {st.wall}</span>
                      <span style={{ fontSize: 12.5 }}>🏮 民 {st.loyalty}</span>
                      {st.acted && <span style={{ fontSize: 11.5, color: "#c9a227" }}>（本回合已行动）</span>}
                      {mine && (
                        <label style={{ display: "inline-flex", gap: 5, alignItems: "center", marginLeft: "auto", fontSize: 11.5, color: "#c9a227" }}>
                          弹框透明度 {Math.round(grandPanelOpacity * 100)}%
                          <input type="range" min="35" max="100" step="5" value={Math.round(grandPanelOpacity * 100)}
                            onChange={e => {
                              const value = +e.target.value / 100;
                              setGrandPanelOpacity(value);
                              persistSettings(settingsRef.current, sizeKey, value);
                            }}
                            style={{ width: 105, accentColor: "#d4b24c" }} />
                        </label>
                      )}
                    </div>
                    {mine ? (
                      <>
                        {/* 内政 */}
                        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                          <span style={{ fontSize: 12.5, color: "#d4b24c", letterSpacing: 2 }}>◈ 内政</span>
                          {Object.entries(GOV_ACTIONS).map(([k, A]) => (
                            <button key={k} title={A.note}
                              disabled={st.acted || r.gold < A.gold}
                              onClick={() => grandGovern(grandSel, k)}
                              style={{
                                ...smallBtn,
                                opacity: (st.acted || r.gold < A.gold) ? 0.4 : 1,
                                cursor: (st.acted || r.gold < A.gold) ? "not-allowed" : "pointer",
                              }}>{A.icon}{A.name} {A.gold}金</button>
                          ))}
                          <button
                            title={`募兵两千：${st.loyalty >= 80 ? "100" : "200"}金+200粮（民心≥80半价）`}
                            disabled={st.acted || r.gold < (st.loyalty >= 80 ? 100 : 200) || r.food < 200}
                            onClick={() => grandMuster(grandSel)}
                            style={{
                              ...smallBtn, borderColor: "#7a9a4f",
                              opacity: (st.acted || r.gold < (st.loyalty >= 80 ? 100 : 200) || r.food < 200) ? 0.4 : 1,
                            }}>🪖 募兵 {st.loyalty >= 80 ? 100 : 200}金+200粮</button>
                        </div>
                        {/* 调兵 */}
                        {Object.entries(grand.nodes).some(([id, n]) => n.owner === grand.playerFac && id !== grandSel) && (
                          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ fontSize: 12.5, color: "#d4b24c", letterSpacing: 2 }}>◈ 调兵</span>
                            <select id="grand-transfer-dest" defaultValue=""
                              style={{
                                background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e",
                                borderRadius: 3, fontFamily: "inherit", fontSize: 12,
                              }}>
                              <option value="" disabled>目的地…</option>
                              {Object.entries(grand.nodes).filter(([id, n]) => n.owner === grand.playerFac && id !== grandSel)
                                .map(([id]) => (
                                  <option key={id} value={id}>
                                    {GRAND_NODES[id].name}（约{grandHops(grandSel, id)}回合）
                                  </option>
                                ))}
                            </select>
                            <button
                              disabled={st.troops < 700}
                              onClick={() => {
                                const sel = document.getElementById("grand-transfer-dest");
                                if (sel && sel.value) grandTransfer(grandSel, sel.value, Math.min(grandDeploy, st.troops - 500));
                              }}
                              style={{ ...smallBtn, opacity: st.troops < 700 ? 0.4 : 1 }}>🚚 调派 {Math.min(grandDeploy, Math.max(0, st.troops - 500)).toLocaleString()}人</button>
                          </div>
                        )}
                        {/* 出征 */}
                        {neighbors.some(n => !grandAreAllied(grand, grand.nodes[n].owner, grand.playerFac)) && (
                          <div style={{ display: "flex", gap: 7, flexWrap: "wrap", alignItems: "center" }}>
                            <span style={{ fontSize: 12.5, color: "#d4b24c", letterSpacing: 2 }}>◈ 出征</span>
                            <input type="range" min={1000} max={Math.max(1000, st.troops - 500)} step={500}
                              value={Math.min(grandDeploy, Math.max(1000, st.troops - 500))}
                              onChange={e => setGrandDeploy(+e.target.value)}
                              style={{ width: 110, accentColor: "#d4b24c" }} />
                            <span style={{ fontSize: 12.5, color: "#d4b24c", minWidth: 52 }}>
                              {Math.min(grandDeploy, Math.max(1000, st.troops - 500)).toLocaleString()}人
                            </span>
                            {alliedNeighbors.length > 0 && (
                              <button
                                title="与相邻盟友商议共攻敌城；盟友每城最多出兵2000人，支援兵力与本次出征一起结算"
                                onClick={() => setGrandSupport(v => !v)}
                                style={{ ...smallBtn, borderColor: grandSupport ? "#d4b24c" : "#6b5a3e", background: grandSupport ? "#5a4a28" : "#2a231a" }}>
                                {grandSupport ? "🤝 已商定共攻" : "🤝 商讨盟友共攻"}
                              </button>
                            )}
                            {neighbors.filter(n => !grandAreAllied(grand, grand.nodes[n].owner, grand.playerFac)).map(n => (
                              <span key={n} style={{ display: "inline-flex", gap: 3 }}>
                                <button
                                  title={`亲征「${GRAND_NODES[n].name}」：进入实时会战`}
                                  disabled={st.troops < 1500 || !!grand.over}
                                  onClick={() => grandAttack(grandSel, n, Math.min(grandDeploy, st.troops - 500), true, grandSupport)}
                                  style={{ ...smallBtn, background: "#5a2320", borderColor: FACTIONS[grand.nodes[n].owner].color }}>
                                  ⚔ 亲征{GRAND_NODES[n].name}
                                </button>
                                <button
                                  title="委任裁决：不打实时战，按兵力与城防自动判定"
                                  disabled={st.troops < 1500 || !!grand.over}
                                  onClick={() => grandAttack(grandSel, n, Math.min(grandDeploy, st.troops - 500), false, grandSupport)}
                                  style={{ ...smallBtn }}>委任</button>
                              </span>
                            ))}
                          </div>
                        )}
                      </>
                    ) : targetSources.length > 0 ? (
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        <span style={{ fontSize: 12.5, color: "#d4b24c", letterSpacing: 2 }}>◈ 多城合击</span>
                        <span style={{ fontSize: 12, color: "#e8dcc1" }}>
                          {targetSources.map(x => GRAND_NODES[x.id].name).join("、")} · 可出兵{multiAvailable.toLocaleString()}人
                        </span>
                        <input type="range" min={1000} max={Math.max(1000, multiAvailable)} step={500}
                          value={multiUse} onChange={e => setGrandDeploy(+e.target.value)}
                          style={{ width: 110, accentColor: "#d4b24c" }} />
                        <span style={{ fontSize: 12.5, color: "#d4b24c" }}>{multiUse.toLocaleString()}人</span>
                        {multiSupportCandidates.length > 0 && (
                          <button
                            title="与相邻盟友商议共攻目标城池，盟友每城最多出兵2000人"
                            onClick={() => setGrandSupport(v => !v)}
                            style={{ ...smallBtn, borderColor: grandSupport ? "#d4b24c" : "#6b5a3e", background: grandSupport ? "#5a4a28" : "#2a231a" }}>
                            {grandSupport ? "🤝 已商定共攻" : "🤝 商讨盟友共攻"}
                          </button>
                        )}
                        <button
                          title="多座己方城池共同出兵，分别保留500人"
                          onClick={() => grandAttack(targetSources.map(x => x.id), grandSel, multiUse, true, grandSupport)}
                          disabled={multiAvailable < 1500 || !!grand.over}
                          style={{ ...smallBtn, background: "#5a2320", borderColor: "#d4b24c" }}>
                          ⚔ 多城亲征
                        </button>
                        <button
                          title="多座己方城池共同出兵并委任裁决"
                          onClick={() => grandAttack(targetSources.map(x => x.id), grandSel, multiUse, false, grandSupport)}
                          disabled={multiAvailable < 1500 || !!grand.over}
                          style={smallBtn}>多城委任</button>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, opacity: 0.7 }}>
                        {FACTIONS[st.owner].name}方{nd.type === "pass" ? "关隘" : "城池"}。
                    自相邻我方城池方可出征攻取（虚线金框表示当前选中城可达）。
                      </div>
                    )}
                  </div>
                  </>
                ) : null;
              })()}
              </div>
              {/* 武将府 */}
              <div style={{
                border: "1px solid #6b5a3e", borderRadius: 5, background: "#221c14",
                padding: "10px 14px", display: "flex", flexDirection: "column", gap: 8,
              }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                  <div style={{ fontSize: 13, color: "#d4b24c", letterSpacing: 3 }}>◈ 武将府</div>
                  <button onClick={() => {
                    setGeneralManagerPage("generals");
                    setGeneralManagerView("table");
                    setGeneralManagerFilter(v => ({ ...v, status: "麾下" }));
                    setGeneralManagerModalOpen(true);
                    setGeneralManagerOpen(true);
                  }} style={{ ...smallBtn, padding: "3px 8px", borderColor: "#d4b24c" }}>☷ 表格管理</button>
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {grand.myGens.map(gn => (
                    <span key={gn.name} style={{
                      display: "inline-flex", gap: 6, alignItems: "center", fontSize: 12.5,
                      background: "#2a231a", border: "1px solid #6b5a3e", borderRadius: 3, padding: "4px 8px",
                    }}>
                      <b style={{ color: gn.gender === "f" ? "#d99a90" : "#e8dcc1" }}>
                        {gn.gender === "f" ? "❀" : ""}{gn.name}
                      </b>
                      <span style={{ opacity: 0.7 }}>武{gn.force ?? generalCombatStats(gn.name).force} 智{gn.wisdom ?? generalCombatStats(gn.name).wisdom} 统{gn.lead} 政{gn.pol} <span style={{ color: gn.loyalty < 50 ? "#d98767" : "#b9c875" }}>忠{gn.loyalty ?? 78}</span></span>
                      <select value={gn.city || ""}
                        onChange={e => assignGen(gn.name, e.target.value || null)}
                        style={{
                          background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e",
                          borderRadius: 3, fontFamily: "inherit", fontSize: 11.5,
                        }}>
                        <option value="">在府听用</option>
                        {Object.entries(grand.nodes).filter(([, n]) => n.owner === grand.playerFac)
                          .map(([id]) => (
                            <option key={id} value={id}>守{GRAND_NODES[id].name}</option>
                          ))}
                      </select>
                      <button onClick={() => trainGen(gn.name)} title="150金：随机提升统率或政务1~3点"
                        disabled={grand.res[grand.playerFac].gold < 150}
                        style={{ ...smallBtn, padding: "2px 6px", fontSize: 11, opacity: grand.res[grand.playerFac].gold < 150 ? 0.4 : 1 }}>📚培养</button>
                      <button onClick={() => rewardGen(gn.name)} title="100金：忠诚度+10，最高100"
                        disabled={grand.res[grand.playerFac].gold < 100 || (gn.loyalty ?? 78) >= 100}
                        style={{ ...smallBtn, padding: "2px 6px", fontSize: 11, borderColor: "#c9a227", opacity: grand.res[grand.playerFac].gold < 100 || (gn.loyalty ?? 78) >= 100 ? 0.4 : 1 }}>🎁赏赐</button>
                      <button onClick={() => dismissGen(gn.name)} title="放逐：移出武将府"
                        style={{ ...smallBtn, padding: "2px 6px", fontSize: 11, color: "#c98" }}>🚶放逐</button>
                      {gn.gender === "f" && !grand.harem.some(h => h.name === gn.name) && (gn.age ?? generalAge(gn.name)) >= 18 && <>
                        <button onClick={() => confessToFemale(gn.name)} disabled={gn.lastCourtshipTurn === grand.turn} title="成年未婚女将专属；尊重本人意愿" style={{ ...smallBtn, padding: "2px 5px", fontSize: 10, borderColor: "#d99a90", opacity: gn.lastCourtshipTurn === grand.turn ? 0.4 : 1 }}>💌表白</button>
                        {(gn.courtship ?? 0) >= 55 && <button onClick={() => haveOneNight(gn.name)} disabled={(grand.actionUsage?.turn === grand.turn && grand.actionUsage.oneNight >= (grand.actionLimits?.oneNight ?? 1)) || grand.res[grand.playerFac].gold < 50} title="仅在对方明确接受心意后开放；非露骨、双方自愿" style={{ ...smallBtn, padding: "2px 5px", fontSize: 10, borderColor: "#d99a90", opacity: (grand.actionUsage?.turn === grand.turn && grand.actionUsage.oneNight >= (grand.actionLimits?.oneNight ?? 1)) || grand.res[grand.playerFac].gold < 50 ? 0.4 : 1 }}>🌙一夜</button>}
                      </>}
                    </span>
                  ))}
                  {grand.myGens.length === 0 && <span style={{ fontSize: 12, opacity: 0.5 }}>府中无将——劝降俘将或静待少主成年。</span>}
                </div>
                {/* 府内联姻 */}
                {(() => {
                  const males = grand.myGens.filter(x => x.gender === "m");
                  const females = grand.myGens.filter(x => x.gender === "f" && !grand.harem.some(h => h.name === x.name));
                  if (!males.length || !females.length) return null;
                  return (
                    <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap" }}>
                      <span style={{ fontSize: 12.5, color: "#d99a90", letterSpacing: 2 }}>◈ 府内联姻</span>
                      <select id="grand-marry-m" defaultValue={males[0].name} style={{
                        background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e", borderRadius: 3, fontFamily: "inherit", fontSize: 11.5,
                      }}>
                        {males.map(m => <option key={m.name} value={m.name}>{m.name}</option>)}
                      </select>
                      <span style={{ fontSize: 12 }}>与</span>
                      <select id="grand-marry-f" defaultValue={females[0].name} style={{
                        background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e", borderRadius: 3, fontFamily: "inherit", fontSize: 11.5,
                      }}>
                        {females.map(f => <option key={f.name} value={f.name}>{f.name}</option>)}
                      </select>
                      <button onClick={() => {
                        const m = document.getElementById("grand-marry-m").value;
                        const f = document.getElementById("grand-marry-f").value;
                        arrangeMarriage(m, f);
                      }} style={{ ...smallBtn, borderColor: "#d99a90" }}>💍 结为伉俪</button>
                    </div>
                    );
                })()}
                {/* 战俘 */}
                {(grand.powTroops > 0 || grand.captives.length > 0) && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                    <div style={{ fontSize: 12.5, color: "#c9a227", letterSpacing: 2 }}>⛓ 俘虏营</div>
                    <div style={{ fontSize: 11.5, opacity: 0.62, lineHeight: 1.5 }}>
                      城池陷落后，敌方将领与家眷可能被收押。武将可劝降，妻女可在尊重本人意愿的前提下以礼求亲，也可礼送归乡。
                    </div>
                    {grand.powTroops > 0 && (
                      <div style={{ display: "flex", gap: 7, alignItems: "center", flexWrap: "wrap", fontSize: 12.5 }}>
                        <span>战俘 {grand.powTroops.toLocaleString()} 人</span>
                        <button onClick={() => powAction("enlist")}
                          title={`编入我方兵力最盛之城（${Math.ceil(grand.powTroops / 1000) * 30}金安家费）`}
                          disabled={grand.res[grand.playerFac].gold < Math.ceil(grand.powTroops / 1000) * 30}
                          style={{ ...smallBtn, borderColor: "#7a9a4f" }}>🪖 编入行伍</button>
                        <button onClick={() => powAction("disband")} title="遣散归乡：诸城民心+3"
                          style={smallBtn}>🌿 遣散归乡</button>
                      </div>
                    )}
                    {grand.captives.map(c => (
                      <div key={c.name} style={{ display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", fontSize: 12.5 }}>
                        <b style={{ color: c.captureType === "family" ? "#d99a90" : c.gender === "f" ? "#d99a90" : "#e8dcc1", minWidth: 100 }}>
                          {c.captureType === "family" ? "🏮" : c.gender === "f" ? "❀" : "🔗"}{c.name}
                        </b>
                        <span style={{ opacity: 0.65 }}>{c.captureType === "family" ? `${c.role} · ${c.desc}` : `武将 · 统${c.lead} 政${c.pol}`} · <span style={{ color: (c.threat ?? 50) >= 80 ? "#d98767" : (c.threat ?? 50) >= 55 ? "#d4b24c" : "#b9c875" }}>威胁{c.threat ?? 50}</span>{c.tried ? " · 本回合已议" : ""}</span>
                        <button disabled={c.tried || grand.res[grand.playerFac].gold < 50} onClick={() => captiveAction(c.name, "guard")}
                          title="加强看押：消耗50金，威胁度−30；每回合一次" style={{ ...smallBtn, borderColor: "#d4b24c", opacity: c.tried || grand.res[grand.playerFac].gold < 50 ? 0.4 : 1 }}>🛡 加强看押·50</button>
                        <button disabled={c.tried} onClick={() => captiveAction(c.name, "interrogate")}
                          title="依法审讯（非血腥叙事）：每回合一次，可能获得敌方军情" style={{ ...smallBtn, borderColor: "#c07a6c", opacity: c.tried ? 0.4 : 1 }}>📜 审讯</button>
                        {c.gender === "f" && <button disabled={c.tried} onClick={() => captiveAction(c.name, "specialInterrogate")}
                          title="女官问讯：非胁迫、非露骨的身份与人脉问讯；每回合一次，可能获得更完整军情" style={{ ...smallBtn, borderColor: "#d99a90", opacity: c.tried ? 0.4 : 1 }}>🌸 女官问讯</button>}
                        {c.captureType !== "family" && (
                          <button disabled={c.tried} onClick={() => captiveAction(c.name, "persuade")}
                            title="劝其归降为将" style={{ ...smallBtn, opacity: c.tried ? 0.4 : 1 }}>🕊 劝降</button>
                        )}
                        {c.gender === "f" && (
                          <button disabled={c.tried} onClick={() => captiveAction(c.name, "marry")}
                            title={c.captureType === "family" ? "先安置家眷与私物，再以礼求亲；绝不相强" : "以礼求亲，绝不相强；成则入府为眷"}
                            style={{ ...smallBtn, borderColor: "#d99a90", opacity: c.tried ? 0.4 : 1 }}>💍 {c.captureType === "family" ? "以礼求亲" : "求亲"}</button>
                        )}
                        <button onClick={() => captiveAction(c.name, "release")} title={c.captureType === "family" ? "礼送归乡：诸城民心+4" : "义释：诸城民心+4"} style={smallBtn}>🌿 {c.captureType === "family" ? "礼送归乡" : "义释"}</button>
                        <button onClick={() => captiveAction(c.name, "ransom")} title={`索赎放还：得${c.lead * 8}金`} style={smallBtn}>🪙 索赎</button>
                        <button onClick={() => captiveAction(c.name, "execute")} title="军法处决：民心−3" style={{ ...smallBtn, color: "#c98" }}>⚔ 处决</button>
                      </div>
                    ))}
                  </div>
                )}
                {/* 家眷 */}
                {(grand.harem.length > 0 || grand.heirs.length > 0) && (
                  <div style={{ display: "flex", flexDirection: "column", gap: 4 }}>
                    <div style={{ fontSize: 12.5, color: "#d99a90", letterSpacing: 2 }}>💍 家眷</div>
                    <div style={{ display: "flex", gap: 10, flexWrap: "wrap", fontSize: 12.5 }}>
                      {grand.harem.map(sp => (
                        <span key={sp.name} style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
                          <span>❀ {sp.name}{sp.child ? "（育有一子）" : ""}</span>
                          <button
                            disabled={(sp.age ?? generalAge(sp.name)) < 18 || (grand.actionUsage?.turn === grand.turn && grand.actionUsage.intimacy >= (grand.actionLimits?.intimacy ?? 1)) || grand.res[grand.playerFac].gold < 30}
                            onClick={() => haveIntimacy(sp.name)}
                            title="成年且双方自愿；每回合一次，消耗30金，有概率获得子嗣"
                            style={{ ...smallBtn, padding: "1px 5px", fontSize: 10, borderColor: "#d99a90", opacity: (sp.age ?? generalAge(sp.name)) < 18 || (grand.actionUsage?.turn === grand.turn && grand.actionUsage.intimacy >= (grand.actionLimits?.intimacy ?? 1)) || grand.res[grand.playerFac].gold < 30 ? 0.4 : 1 }}>
                            🌙 行房
                          </button>
                        </span>
                      ))}
                      {grand.heirs.map(h => (
                        <span key={h.name} style={{ opacity: h.mature ? 1 : 0.7 }}>
                          👶 {h.name}（{h.mother}所出{h.mature ? " · 已成年" : ` · ${Math.max(0, 8 - (grand.turn - h.bornTurn))}回合后成年`}）
                        </span>
                      ))}
                    </div>
                    <div style={{ fontSize: 11, opacity: 0.5 }}>内助之贤：每回合诸城民心徐增；子嗣八回合成年后入武将府。</div>
                  </div>
                )}
              </div>
              {/* 简报 */}
              {grand.transfers && grand.transfers.length > 0 && (
                <div style={{ fontSize: 11.5, opacity: 0.75 }}>
                  🚚 在途：{grand.transfers.map((t, i) => (
                    <span key={i}>{i > 0 ? "、" : ""}{GRAND_NODES[t.fromId].name}→{GRAND_NODES[t.toId].name}
                      ({t.troops.toLocaleString()}人·{Math.max(0, t.eta - grand.turn)}回合后)</span>
                  ))}
                </div>
              )}
              <div>
                <div style={{ fontSize: 13, color: "#d4b24c", marginBottom: 3, letterSpacing: 3 }}>◈ 军情</div>
                {grandMsg.map((m, i) => (
                  <div key={i} style={{ fontSize: 12.5, opacity: 1 - i * 0.1, marginBottom: 2 }}>{m}</div>
                ))}
              </div>
              <div style={{ fontSize: 11, opacity: 0.45, textAlign: "center" }}>
                每城每回合限一次行动（内政或募兵）。农业产粮、商业产金、城防利守、民心≥80募兵半价；军队按百人每回合耗粮一。
                亲征进入实时会战（可用锦囊、劝降、全套军令），委任按实力自动裁决。他国互攻自动裁决；敌军来犯我城时，可在设置中关闭「自动裁决」以亲自督守城战。
              </div>
            </>
          )}
        </div>
      )}

      {/* ===== 故事章节引子 ===== */}
      {mode === "storyIntro" && storyLine && storyChapterDef && (
        <div style={{
          width: "min(96vw, 720px)", border: `1px solid ${FACTIONS[storyLine.fac].color}66`,
          borderRadius: 6, background: "#221c14", padding: "16px 20px",
          display: "flex", flexDirection: "column", gap: 12,
        }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap" }}>
            <span style={{ fontSize: 15, color: FACTIONS[storyLine.fac].color, fontWeight: 700 }}>《{storyLine.name}》</span>
            <span style={{ fontSize: 17, fontWeight: 700, color: "#d4b24c" }}>{storyChapterDef.title}</span>
          </div>
          <div style={{ fontSize: 13.5, lineHeight: 1.9, opacity: 0.9, textIndent: "2em" }}>
            {storyChapterDef.narration}
          </div>
          <div style={{ fontSize: 12.5, color: "#d4b24c" }}>
            🎯 战役目标：{storyChapterDef.objective.desc}
            <span style={{ marginLeft: 12, opacity: 0.8 }}>
              ☯ {storyDifficulty === "easy" ? "简单" : storyDifficulty === "hard" ? "困难" : "普通"}难度
              {" "}· 🧧 锦囊×{storyDifficulty === "easy" ? 4 : storyDifficulty === "hard" ? 2 : 3}
            </span>
          </div>
          {storyChapterDef.choices && (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              <div style={{ fontSize: 12.5, opacity: 0.7 }}>◈ 战前抉择</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {storyChapterDef.choices.map(c => (
                  <button key={c.id} onClick={() => setStoryChoice(c.id)}
                    style={{
                      flex: "1 1 240px", textAlign: "left", padding: "8px 12px", borderRadius: 4,
                      fontFamily: "inherit", cursor: "pointer",
                      background: storyChoice === c.id ? "#5a4a28" : "#2a231a",
                      border: `1px solid ${storyChoice === c.id ? "#d4b24c" : "#6b5a3e"}`,
                      color: "#e8dcc1",
                    }}>
                    <div style={{ fontSize: 14, fontWeight: 700 }}>{storyChoice === c.id ? "◉" : "○"} {c.text}</div>
                    <div style={{ fontSize: 11.5, opacity: 0.75 }}>{c.desc}</div>
                  </button>
                ))}
              </div>
            </div>
          )}
          <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 4 }}>
            <button onClick={() => startStoryBattle(storyStage.line, storyStage.idx, storyChoice)}
              style={{ ...btn, background: "#2f4a2c", fontWeight: 700, fontSize: 15, padding: "7px 22px" }}>
              ⚔ 出阵
            </button>
            <button onClick={exitToStoryHome} style={btn}>↩ 返回卷目</button>
          </div>
        </div>
      )}

      {/* ===== 沙盘场景选择行（仅沙盘模式） ===== */}
      {mode === "sandbox" && (
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", justifyContent: "center", alignItems: "center" }}>
          <span style={{ fontSize: 12.5, color: "#d4b24c" }}>沙盘：</span>
          <select value={scenario} onChange={e => changeScenario(e.target.value)} title={SCENARIOS[scenario].desc}
            style={{
              background: "#3a2f22", color: "#e8dcc1", border: "1px solid #d4b24c",
              borderRadius: 3, padding: "5px 9px", minWidth: 190, fontFamily: "inherit", fontSize: 13,
            }}>
            {Object.entries(SCENARIOS).map(([k, s]) => <option key={k} value={k}>{s.name} · {s.desc}</option>)}
          </select>
          <span style={{ fontSize: 11.5, opacity: 0.65, maxWidth: 260 }}>{SCENARIOS[scenario].desc}</span>
          {SCENARIOS[scenario].history && <div style={{ width: "min(94vw, 760px)", padding: "7px 10px", textAlign: "left", background: "#2a2118", border: "1px solid #806b3c", borderRadius: 4, lineHeight: 1.45 }}>
            <div style={{ display: "flex", gap: 8, alignItems: "baseline", flexWrap: "wrap" }}><b style={{ color: "#ffe18a", fontSize: 12.5 }}>📜 {SCENARIOS[scenario].history.title}</b><span style={{ fontSize: 10.5, color: "#d4b24c" }}>{SCENARIOS[scenario].history.year}</span><span style={{ fontSize: 10.5, opacity: 0.7 }}>{SCENARIOS[scenario].history.source}</span></div>
            <div style={{ fontSize: 11.5, opacity: 0.82, marginTop: 3 }}>{SCENARIOS[scenario].history.background}</div>
            <div style={{ fontSize: 11, color: "#c9b477", marginTop: 3 }}>结局参考：{SCENARIOS[scenario].history.outcome}</div>
            <div style={{ fontSize: 10.5, opacity: 0.62, marginTop: 3 }}>战斗开始后会按阶段显示历史/演义旁白；玩家仍可自由指挥并改写结局。</div>
          </div>}
          <span style={{ width: 1, height: 22, background: "#6b5a3e" }} />
          {[["wei","shu"],["wei","wu"],["shu","wu"]].map(([f1, f2]) => {
            const on = allies.includes(pairKey(f1, f2));
            return (
              <button key={f1 + f2} onClick={() => toggleAlly(f1, f2)}
                title={on ? "点击解盟" : "点击结盟"}
                style={{
                  ...smallBtn,
                  background: on ? "#5a4a28" : "#2a231a",
                  borderColor: on ? "#d4b24c" : "#6b5a3e",
                }}>
                {on ? "🤝" : "⚔"}
                <span style={{ color: FACTIONS[f1].color, fontWeight: 700 }}> {FACTIONS[f1].name}</span>
                <span style={{ color: FACTIONS[f2].color, fontWeight: 700 }}>{FACTIONS[f2].name}</span>
              </button>
            );
          })}
          <span style={{ width: 1, height: 22, background: "#6b5a3e" }} />
          <button onClick={doSave} style={smallBtn}>💾 存档{showKeys && <Kbd k="S" />}</button>
          <button onClick={doLoad} style={smallBtn}>📜 读档{showKeys && <Kbd k="L" />}</button>
          {saveMsg && <span style={{ fontSize: 12, color: "#d4b24c" }}>{saveMsg}</span>}
        </div>
      )}

      {/* ===== 闯关战役横幅 ===== */}
      {mode === "campaignBattle" && campaignStageDef && (
        <div style={{
          display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "center",
          background: "#26201660", border: "1px solid #d4b24c66", borderRadius: 4, padding: "6px 14px",
        }}>
          <span style={{ fontSize: 13 }}>
            🚩 <b style={{ color: FACTIONS[campaignStage.fac].color }}>{FACTIONS[campaignStage.fac].name}军战役</b>
            {" "}第{campaignStage.idx + 1}关 · {SCENARIOS[campaignStageDef.key].name}
          </span>
          <span style={{ fontSize: 12, opacity: 0.75 }}>{campaignStageDef.obj}</span>
          <button onClick={exitToCampaignHome} style={smallBtn}>↩ 返回关卡选择</button>
        </div>
      )}

      {/* ===== 故事战役横幅 ===== */}
      {mode === "storyBattle" && storyLine && storyChapterDef && (
        <div style={{
          display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "center",
          background: "#26201660", border: "1px solid #d4b24c66", borderRadius: 4, padding: "6px 14px",
        }}>
          <span style={{ fontSize: 13 }}>
            📖 <b style={{ color: FACTIONS[storyLine.fac].color }}>《{storyLine.name}》</b> {storyChapterDef.title}
          </span>
          <span style={{ fontSize: 12, color: "#d4b24c" }}>🎯 {storyChapterDef.objective.desc}</span>
          {storyObjLive && <span style={{ fontSize: 12.5, fontWeight: 700, color: "#e8c34a" }}>⏳ {storyObjLive}</span>}
          <button onClick={exitToStoryHome} style={smallBtn}>↩ 返回卷目</button>
        </div>
      )}

      {/* ===== 天下会战横幅 ===== */}
      {mode === "grandBattle" && grandBattleCtx && (
        <div style={{
          display: "flex", gap: 10, alignItems: "center", flexWrap: "wrap", justifyContent: "center",
          background: "#26201660", border: "1px solid #d4b24c66", borderRadius: 4, padding: "6px 14px",
        }}>
          <span style={{ fontSize: 13 }}>
            🌏 天下会战 ·{" "}
            <b style={{ color: FACTIONS[grandBattleCtx.atkFac].color }}>{FACTIONS[grandBattleCtx.atkFac].name}</b>
            {" "}攻{" "}
            <b style={{ color: FACTIONS[grandBattleCtx.defFac].color }}>{FACTIONS[grandBattleCtx.defFac].name}</b>
            {" "}之「{GRAND_NODES[grandBattleCtx.toId].name}」
            {grandBattleCtx.role === "defend" && <span style={{ color: "#e8c34a" }}>（我军守城！）</span>}
          </span>
          <span style={{ fontSize: 12, opacity: 0.75 }}>
            {GRAND_NODES[grandBattleCtx.toId].type === "pass" ? "🎯 攻方全歼守军则克" : "🎯 攻方围城易帜则克"} · 守军存续即守成
          </span>
        </div>
      )}

      {/* ===== 锦囊妙计 ===== */}
      {(mode === "storyBattle" || mode === "campaignBattle" || mode === "grandBattle") && sim && sim.tactics !== undefined && !campaignResult && !storyResult && !(grandBattleCtx && grandBattleCtx.done) && (
        <div style={{
          display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "center",
          background: "#26201640", border: "1px dashed #6b5a3e", borderRadius: 4, padding: "4px 12px",
        }}>
          <span style={{ fontSize: 12.5, color: "#e8c34a" }}>🧧 锦囊 ×{sim.tactics}</span>
          {[["drum", "🥁 擂鼓助威", "全军士气+28、粮秣+30"],
            ["heal", "🧪 金疮妙药", "全军回血40%"],
            ["fear", "🎐 疑兵之计", "敌全军士气−16"],
            ...(mode === "storyBattle" ? [
              ["charge", "🐎 奇兵突骑", "己方骑兵瞬间蓄满冲阵之力"],
              ["burn", "🔥 火矢奇袭", "焚毁敌方一座建筑（35%血量），无目标则退回锦囊"],
              ["reinforce", "🚩 援军突至", "就地征召一支2000人生力军"],
            ] : []),
          ].map(([k, name, note]) => (
            <button key={k} title={note} disabled={sim.tactics <= 0}
              onClick={() => applyTactic(k)}
              style={{
                ...smallBtn,
                opacity: sim.tactics > 0 ? 1 : 0.4,
                cursor: sim.tactics > 0 ? "pointer" : "not-allowed",
                borderColor: "#c9a227",
              }}>{name}</button>
          ))}
        </div>
      )}

      {/* ===== 武将技 ===== */}
      {inBattle && sim && selectedSkillArmy && (
        <div style={{
          display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap", justifyContent: "center",
          background: "rgba(72,48,18,0.72)", border: "1px solid #c9a22788", borderRadius: 4, padding: "5px 12px",
        }}>
          <span style={{ fontSize: 12.5, color: "#ffe18a" }}>⭐ 武将技 · {selectedSkillArmy.general}</span>
          <span style={{ fontSize: 11.5, opacity: 0.68 }}>选中武将所在军团后释放，冷却18秒</span>
          {generalSkills(selectedSkillArmy.general).map(skill => {
            const cd = selectedSkillArmy.skillCooldowns?.[skill.id] || 0;
            return (
              <button key={skill.id} title={skill.note} disabled={cd > 0 || selectedSkillArmy.genDead}
                onClick={() => applyGeneralSkill(selectedSkillArmy.id, skill.id)}
                style={{
                  ...smallBtn, borderColor: cd > 0 ? "#6b5a3e" : "#e8c34a",
                  opacity: cd > 0 ? 0.45 : 1,
                }}>{cd > 0 ? `⏳ ${Math.ceil(cd)}秒` : skill.name}</button>
            );
          })}
        </div>
      )}

      {/* 战场区域：全屏时以此 div 为 Fullscreen 目标，内部三块控制面板转为浮窗 */}
      <div ref={battleAreaRef} style={isFullscreen ? {
        position: "fixed", inset: 0, zIndex: 9999, background: "#14100b",
        display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden",
      } : { display: "contents" }}>
      {/* deploy controls */}
      {inBattle && (
      <div style={{ display: "flex", flexWrap: "wrap", gap: 7, alignItems: "center", justifyContent: "center",
        ...(isFullscreen ? {
          position: "absolute", top: 8, left: 8, zIndex: 10, maxWidth: "92vw", overflowX: "auto",
          background: "rgba(24,19,12,0.85)", border: "1px solid #6b5a3e", borderRadius: 4, padding: "6px 10px",
        } : {}),
      }}>
        {facChoices.map(k => {
          const f = FACTIONS[k];
          const on = fac === k;
          return (
            <button key={k} onClick={() => setFac(k)} style={{
              ...btn,
              background: on ? f.color : "#241d15",
              borderColor: f.color,
              color: on ? "#f5efdf" : f.color,
              fontWeight: 700,
            }}>{f.name}{showKeys && <Kbd k={FAC_HOTKEY[k]} dark={on} />}</button>
          );
        })}
        {mode !== "storyBattle" && mode !== "grandBattle" && <>
        <span style={{ width: 1, height: 22, background: "#6b5a3e" }} />
        {Object.entries(UNIT_TYPES).map(([k, t]) => {
          const on = placeMode === "army" && utype === k;
          return (
            <button key={k} title={t.note}
              onClick={() => { setUtype(k); setPlaceMode("army"); }}
              style={{
                ...btn, padding: "5px 9px",
                background: on ? "#d4b24c" : "#2a231a",
                color: on ? "#1c1712" : "#e8dcc1",
                borderColor: on ? "#d4b24c" : "#6b5a3e",
              }}>{t.glyph}{showKeys && <Kbd k={t.key} dark={on} />}</button>
          );
        })}
        {Object.entries(B_TYPES).map(([k, t]) => {
          const on = placeMode === k;
          return (
            <button key={k} title={t.note} onClick={() => setPlaceMode(k)} style={{
              ...btn, padding: "5px 9px",
              background: on ? "#d4b24c" : "#2a231a",
              color: on ? "#1c1712" : "#e8dcc1",
              borderColor: on ? "#d4b24c" : "#6b5a3e",
            }}>{t.name[0]}{showKeys && <Kbd k={t.key} dark={on} />}</button>
          );
        })}
        {placeMode === "army" && <>
          <input type="range" min={500} max={30000} step={500} value={men}
            onChange={e => setMen(+e.target.value)} style={{ width: 100, accentColor: "#d4b24c" }} />
          <span style={{ fontSize: 13, minWidth: 56, color: "#d4b24c" }}>{men.toLocaleString()}人</span>
        </>}
        </>}
        <button onClick={start} style={{ ...btn, background: running ? "#5a2320" : "#2f4a2c", fontWeight: 700 }}>
          {running ? "⏸ 暂停" : "⚔ 开战"}{showKeys && <Kbd k="␣" />}
        </button>
        <button onClick={() => setView3D(v => !v)} title="切换战场渲染视图" style={{ ...btn, background: view3D ? "#5a4a28" : "#2a231a", fontWeight: 700 }}>
          {view3D ? "🗺 2D" : "🏔 3D"}
        </button>
        {view3D && <>
          <button onClick={() => battle3DRef.current?.setPreset?.("top")} title="正俯视" style={btn}>🔭俯视</button>
          <button onClick={() => battle3DRef.current?.setPreset?.("se")} title="东南斜视" style={btn}>⛰斜视</button>
          <button onClick={() => battle3DRef.current?.setPreset?.("low")} title="低空掠视" style={btn}>🛩掠视</button>
          <span style={{ fontSize: 11, color: "#b8ac8e", alignSelf: "center" }}>中键拖动平移 · 右键旋转 · 滚轮缩放</span>
        </>}
        <button onClick={() => setSpeedIdx(i => (i + 1) % SPEEDS.length)} style={btn}>
          ⏩ {SPEEDS[speedIdx]}×{showKeys && <Kbd k="Z/X" />}
        </button>
        {mode !== "grandBattle" && <button onClick={() => {
          if (mode === "campaignBattle") restartCampaignStage();
          else if (mode === "storyBattle") startStoryBattle(storyStage.line, storyStage.idx, storyChoice);
          else initSim(scenario);
        }} style={btn}>↺ 重开</button>}
        <button onClick={toggleFullscreen} title="全屏战场" style={btn}>
          {isFullscreen ? "⛶ 退出全屏" : "⛶ 全屏"}{showKeys && <Kbd k="F" />}
        </button>
      </div>
      )}

      {inBattle && (
      <div style={{
        position: "relative",
        width: isFullscreen ? `min(96vw, calc(92vh * ${W / H}))` : `min(96vw, ${W}px)`,
        overscrollBehavior: "contain",
      }}>
        {view3D && sim && (
          <React.Suspense fallback={<div style={{ position: "absolute", inset: 0, display: "grid", placeItems: "center", background: "#17130e", color: "#d4b24c", zIndex: 1 }}>🏔 正在铺开三维山河…</div>}>
            <Battle3DView
              key={`${sim.terrainMeta?.seed || "terrain"}-${sizeKey}`}
              simRef={simRef}
              selectedIds={selected}
              apiRef={battle3DRef}
              width={W}
              height={H}
              tile={TILE}
              cols={COLS}
              rows={ROWS}
              terrainKey={sim.terrainMeta?.seed || scenario}
            />
          </React.Suspense>
        )}
        <canvas
          key={sizeKey}
          ref={canvasRef} width={W} height={H}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={() => { dragRef.current = null; selRectRef.current = null; }}
          onDoubleClick={onCanvasDoubleClick}
          onMouseLeave={() => {
            if (!dragRef.current) selRectRef.current = null;
            setHoverInfo(null);
            if (canvasRef.current) canvasRef.current.style.cursor = "default";
          }}
          onContextMenu={e => e.preventDefault()}
          style={{
            width: "100%", display: "block", cursor: "default",
            opacity: view3D ? 0 : 1, position: "relative", zIndex: 2,
            border: "2px solid #6b5a3e", borderRadius: 4,
            boxShadow: "0 6px 30px rgba(0,0,0,0.6)",
            touchAction: "none",
            overscrollBehavior: "none",
          }}
        />
        {mode === "campaignBattle" && campaignResult && (
          <div style={{
            position: "absolute", inset: 0, borderRadius: 4,
            background: "rgba(10,8,5,0.82)", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 14,
            zIndex: 8,
          }}>
            <div style={{
              fontSize: 26, fontWeight: 700, letterSpacing: 5,
              color: campaignResult === "win" || campaignResult === "complete" ? "#d4b24c" : "#c07a6c",
            }}>
              {campaignResult === "complete" ? "🏆 战役全线完成" : campaignResult === "win" ? "🏆 此关告捷" : "☠ 兵败此关"}
            </div>
            {campaignResult === "win" && campaignRewardPending && (
              <>
                <div style={{ fontSize: 13, color: "#e8dcc1", marginTop: -5 }}>选择一项战后成长，整备后进入下一关</div>
                <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center", maxWidth: 720 }}>
                  {campaignRewardOptions.map(option => (
                    <button key={option.id} onClick={() => chooseCampaignReward(option.id)} style={{
                      ...btn, width: 205, minHeight: 112, textAlign: "left", background: "#2f4a2c",
                      borderColor: "#d4b24c", padding: "10px 12px",
                    }}>
                      <div style={{ fontSize: 16, color: "#f1d66b", marginBottom: 5 }}>{option.icon} {option.title}</div>
                      <div style={{ fontSize: 12, lineHeight: 1.55, color: "#e8dcc1" }}>{option.desc}</div>
                    </button>
                  ))}
                </div>
              </>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
              {campaignResult !== "complete" && <button onClick={restartCampaignStage} style={btn}>↺ 重试本关</button>}
              <button onClick={exitToCampaignHome} style={btn}>↩ 返回关卡选择</button>
            </div>
          </div>
        )}
        {mode === "storyBattle" && storyResult && storyChapterDef && (
          <div style={{
            position: "absolute", inset: 0, borderRadius: 4,
            background: "rgba(10,8,5,0.86)", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 14, padding: "0 40px",
          }}>
            <div style={{
              fontSize: 24, fontWeight: 700, letterSpacing: 5,
              color: storyResult === "win" ? "#d4b24c" : "#c07a6c",
            }}>
              {storyResult === "win" ? "🏆 目标达成" : "☠ 功败垂成"}
            </div>
            <div style={{
              fontSize: 13.5, lineHeight: 1.9, opacity: 0.92, textAlign: "center", maxWidth: 560,
              color: "#e8dcc1",
            }}>
              {resolveStoryText(storyResult === "win" ? storyChapterDef.winText : storyChapterDef.loseText, storyStage?.choiceId)}
            </div>
            {storyResult === "win" && (
              <div style={{ fontSize: 12.5, color: "#e8c34a" }}>声望 +{storyChapterDef.renown || 2}</div>
            )}
            <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
              {storyResult === "win" && storyStage.idx + 1 < storyLine.chapters.length && (
                <button onClick={() => openStoryChapter(storyStage.line, storyStage.idx + 1)}
                  style={{ ...btn, background: "#2f4a2c", fontWeight: 700 }}>下一章 →</button>
              )}
              <button onClick={() => openStoryChapter(storyStage.line, storyStage.idx)} style={btn}>↺ 重演此章</button>
              <button onClick={exitToStoryHome} style={btn}>↩ 返回卷目</button>
            </div>
          </div>
        )}
        {/* ===== 地形图例 ===== */}
        {inBattle && settings.terrainLegend && (
          <div style={{
            position: "absolute", left: 8, bottom: 8, zIndex: 4,
            display: "flex", flexDirection: "column", gap: 3,
            background: "rgba(24,19,12,0.78)", border: "1px solid #6b5a3e",
            borderRadius: 4, padding: "6px 9px", fontSize: 10.5,
          }}>
            <div style={{
              color: "#d4b24c", fontWeight: 700, fontSize: 10.5,
              borderBottom: "1px solid #6b5a3e55", paddingBottom: 3, marginBottom: 1,
            }}>地形图例</div>
            {T_NAME.map((name, i) => {
              const active = hoverInfo && !dragRef.current && hoverInfo.t === i;
              return (
                <div key={i} style={{
                  display: "flex", alignItems: "center", gap: 5,
                  background: active ? "rgba(212,178,76,0.16)" : "transparent",
                  borderLeft: active ? "2px solid #d4b24c" : "2px solid transparent",
                  borderRadius: 2, padding: "0 2px 0 3px",
                }}>
                  <span style={{
                    width: 11, height: 11, borderRadius: 2, background: T_SWATCH[i],
                    border: "1px solid #2a2118", display: "inline-block", flexShrink: 0,
                  }} />
                  <span style={{ color: "#e8dcc1", minWidth: 34, fontWeight: active ? 700 : 400 }}>{T_ICON[i]}{name}</span>
                  <span style={{ color: "#b8ac8e", fontSize: 9.5 }}>{Math.round(T_SPEED[i] * 100)}%</span>
                </div>
              );
            })}
          </div>
        )}
        {/* ===== 光标处地形提示 ===== */}
        {inBattle && settings.terrainLegend && hoverInfo && !dragRef.current && (
          <div style={{
            position: "absolute", zIndex: 6, pointerEvents: "none",
            left: Math.min(hoverInfo.dx + 14, (canvasRef.current ? canvasRef.current.getBoundingClientRect().width : W) - 150),
            top: Math.max(4, hoverInfo.dy - 10),
            background: "rgba(24,19,12,0.9)", border: `1px solid ${T_SWATCH[hoverInfo.t]}`,
            borderRadius: 3, padding: "4px 8px", fontSize: 11, color: "#e8dcc1",
            whiteSpace: "nowrap",
          }}>
            <b>{T_ICON[hoverInfo.t]} {T_NAME[hoverInfo.t]}</b>
            <div style={{ fontSize: 10, opacity: 0.85 }}>{T_INFO[hoverInfo.t]}</div>
          </div>
        )}
        {/* ===== 浮动信息面板（全屏时也能看到诸军与战报） ===== */}
        {inBattle && (
          <>
            <button onClick={() => setHudOpen(v => !v)} title="显示/收起浮动军情"
              style={{
                position: "absolute", top: 8, right: 8, zIndex: 5,
                background: "rgba(38,32,22,0.85)", color: "#d4b24c",
                border: "1px solid #6b5a3e", borderRadius: 3, padding: "3px 9px",
                cursor: "pointer", fontFamily: "inherit", fontSize: 13,
              }}>{hudOpen ? "📋 ▸" : "📋 ◂"}</button>
            {hudOpen && (
              <div style={{
                position: "absolute", top: 40, right: 8, zIndex: 4,
                width: 230, maxHeight: "72%", overflowY: "auto",
                background: "rgba(24,19,12,0.82)", border: "1px solid #6b5a3e",
                borderRadius: 4, padding: "7px 9px", backdropFilter: "blur(2px)",
              }}>
                {armies.filter(a => a.alive > 0).slice(0, 10).map(a => {
                  const F = FACTIONS[a.fac];
                  const frac = Math.max(0, a.alive / a.total);
                  const isSel = selected.includes(a.id);
                  return (
                    <div key={a.id}
                      onClick={() => {
                        const v = isSel ? selected.filter(x => x !== a.id) : [...selected, a.id];
                        setSelected(v); selectedRef.current = v;
                      }}
                      style={{
                        display: "flex", alignItems: "center", gap: 5, marginBottom: 3,
                        fontSize: 11, cursor: "pointer",
                        outline: isSel ? "1px solid #d4b24c" : "none", borderRadius: 2, padding: "1px 3px",
                      }}>
                      <span style={{ color: F.color, fontWeight: 700, minWidth: 62, whiteSpace: "nowrap" }}>
                        {F.name}·{a.general}
                      </span>
                      <div style={{ flex: 1, height: 5, background: "#2a231a", borderRadius: 2, overflow: "hidden" }}>
                        <div style={{ width: `${frac * 100}%`, height: "100%", background: a.broken ? "#777" : F.color }} />
                      </div>
                      <span style={{ opacity: 0.8, minWidth: 34, textAlign: "right" }}>
                        {((a.alive * MEN_PER_DOT) / 1000).toFixed(1)}k
                      </span>
                    </div>
                  );
                })}
                <div style={{ borderTop: "1px solid #6b5a3e55", margin: "5px 0 4px" }} />
                {logs.slice(0, 5).map((l, i) => (
                  <div key={i} style={{ fontSize: 10.5, opacity: 0.95 - i * 0.15, marginBottom: 1.5, lineHeight: 1.4 }}>{l}</div>
                ))}
              </div>
            )}
          </>
        )}
        {/* ===== 文字对话事件 ===== */}
        {mode === "grandBattle" && grandBattleCtx && grandBattleCtx.done && (
          <div style={{
            position: "absolute", inset: 0, borderRadius: 4,
            background: "rgba(10,8,5,0.84)", display: "flex", flexDirection: "column",
            alignItems: "center", justifyContent: "center", gap: 14,
            zIndex: 8,
          }}>
            <div style={{
              fontSize: 25, fontWeight: 700, letterSpacing: 5,
              color: (grandBattleCtx.role === "attack" ? grandBattleCtx.atkWin : !grandBattleCtx.atkWin) ? "#d4b24c" : "#c07a6c",
            }}>
              {(grandBattleCtx.role === "attack" ? grandBattleCtx.atkWin : !grandBattleCtx.atkWin)
                ? (grandBattleCtx.role === "attack" ? `🏆 攻克「${GRAND_NODES[grandBattleCtx.toId].name}」` : `🛡 「${GRAND_NODES[grandBattleCtx.toId].name}」守成`)
                : (grandBattleCtx.role === "attack" ? "☠ 攻城失利，残部退回" : `☠ 「${GRAND_NODES[grandBattleCtx.toId].name}」失守……`)}
            </div>
            <button onClick={exitGrandBattle}
              style={{ ...btn, background: "#2f4a2c", fontWeight: 700, fontSize: 15, padding: "8px 26px" }}>
              🌏 回到舆图
            </button>
          </div>
        )}
        {dialog && (
          <div style={{
            position: "absolute", inset: 0, borderRadius: 4,
            background: "rgba(10,8,5,0.78)", display: "flex",
            alignItems: "center", justifyContent: "center", padding: 20,
          }}>
            <div style={{
              width: "min(92%, 540px)", background: "#e7dcc1", color: "#2a2118",
              border: "3px double #6b5a3e", borderRadius: 4, padding: "18px 24px",
              boxShadow: "0 10px 40px rgba(0,0,0,0.7)",
              fontFamily: "'Songti SC','Noto Serif SC',serif",
            }}>
              {dialog.type === "captive" && (() => {
                const captor = sim && sim.armies.find(a => a.id === dialog.captorArmyId);
                const vF = FACTIONS[dialog.victimFac];
                return (
                  <>
                    <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: 4, marginBottom: 10, color: "#7c2820" }}>
                      ◈ 辕门献俘
                    </div>
                    {dialog.phase === "choose" ? (
                      <>
                        <div style={{ fontSize: 14, lineHeight: 1.9, marginBottom: 14 }}>
                          刀斧手押来一员敌将——<b style={{ color: vF.color }}>{vF.name}·{dialog.victimGeneral}</b>，
                          甲胄凌乱而目光如炬，立于帐前，不发一言。
                          {captor && <span>帐上 {FACTIONS[captor.fac].name}·{captor.general} 按剑而问：如何处置？</span>}
                        </div>
                        <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                          <button onClick={() => resolveCaptiveChoice("persuade")} style={{
                            textAlign: "left", padding: "9px 14px", borderRadius: 3, cursor: "pointer",
                            background: "#f2ead6", border: "1px solid #6b5a3e", fontFamily: "inherit", fontSize: 13.5,
                          }}>
                            <b>「久闻将军威名，何不共图大业？」</b>—— 劝降
                            <span style={{ opacity: 0.65, fontSize: 12 }}>（成则得其人并率降卒来投；主将功勋愈高，成算愈大）</span>
                          </button>
                          <button onClick={() => resolveCaptiveChoice("execute")} style={{
                            textAlign: "left", padding: "9px 14px", borderRadius: 3, cursor: "pointer",
                            background: "#f2ead6", border: "1px solid #6b5a3e", fontFamily: "inherit", fontSize: 13.5,
                          }}>
                            <b>「推出辕门，斩！」</b>—— 枭首立威
                            <span style={{ opacity: 0.65, fontSize: 12 }}>（{vF.name}军全军夺气，士气−8）</span>
                          </button>
                          <button onClick={() => resolveCaptiveChoice("release")} style={{
                            textAlign: "left", padding: "9px 14px", borderRadius: 3, cursor: "pointer",
                            background: "#f2ead6", border: "1px solid #6b5a3e", fontFamily: "inherit", fontSize: 13.5,
                          }}>
                            <b>「解绑，赠鞍马，送将军出营。」</b>—— 义释
                            <span style={{ opacity: 0.65, fontSize: 12 }}>（义名远播：我军士气+10{mode === "storyBattle" ? "，声望+1" : ""}）</span>
                          </button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 14, lineHeight: 1.9, whiteSpace: "pre-line", marginBottom: 14 }}>
                          {dialog.resultText}
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <button onClick={closeDialog} style={{
                            padding: "7px 26px", borderRadius: 3, cursor: "pointer", fontSize: 14, fontWeight: 700,
                            background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e", fontFamily: "inherit",
                          }}>继续 →</button>
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
              {dialog.type === "surrender" && (() => {
                const ta = sim && sim.armies.find(a => a.id === dialog.armyId);
                if (!ta) return null;
                const tF = FACTIONS[ta.fac];
                return (
                  <>
                    <div style={{ fontSize: 17, fontWeight: 700, letterSpacing: 4, marginBottom: 10, color: "#2a5537" }}>
                      ◈ 阵前劝降
                    </div>
                    {dialog.phase === "choose" ? (
                      <>
                        <div style={{ fontSize: 14, lineHeight: 1.9, marginBottom: 6 }}>
                          遣使持节，直入 <b style={{ color: tF.color }}>{tF.name}·{ta.general}</b> 阵中陈说利害。
                        </div>
                        <div style={{ fontSize: 12.5, opacity: 0.75, marginBottom: 14 }}>
                          其军尚余 {(ta.alive * MEN_PER_DOT).toLocaleString()} 人 · 士气 {Math.round(ta.morale)}
                          {ta.genDead ? " · 主将有失" : ""}{ta.starving ? " · 粮草断绝" : ""}
                          {ta.total > 0 && ta.alive / ta.total < 0.5 ? " · 折损过半" : ""}
                          ——士气愈低、处境愈困，归降之心愈切。
                        </div>
                        <div style={{ display: "flex", gap: 10, justifyContent: "center" }}>
                          <button onClick={() => resolveSurrenderChoice("try")} style={{
                            padding: "8px 20px", borderRadius: 3, cursor: "pointer", fontSize: 14, fontWeight: 700,
                            background: "#2f4a2c", color: "#e8dcc1", border: "1px solid #6b5a3e", fontFamily: "inherit",
                          }}>「将军何苦为他人殉葬？」遣使</button>
                          <button onClick={() => resolveSurrenderChoice("cancel")} style={{
                            padding: "8px 20px", borderRadius: 3, cursor: "pointer", fontSize: 14,
                            background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e", fontFamily: "inherit",
                          }}>再等时机</button>
                        </div>
                      </>
                    ) : (
                      <>
                        <div style={{ fontSize: 14, lineHeight: 1.9, whiteSpace: "pre-line", marginBottom: 14 }}>
                          {dialog.resultText}
                        </div>
                        <div style={{ textAlign: "center" }}>
                          <button onClick={closeDialog} style={{
                            padding: "7px 26px", borderRadius: 3, cursor: "pointer", fontSize: 14, fontWeight: 700,
                            background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e", fontFamily: "inherit",
                          }}>继续 →</button>
                        </div>
                      </>
                    )}
                  </>
                );
              })()}
            </div>
          </div>
        )}
      </div>
      )}

      {/* group order bar */}
      {inBattle && selArmies.length > 0 && (
        <div style={{
          display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "center",
          background: "#26201660", border: "1px solid #6b5a3e", borderRadius: 4, padding: "6px 12px",
          ...(isFullscreen ? {
            position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", zIndex: 10,
            maxWidth: "92vw", background: "rgba(24,19,12,0.85)",
          } : {}),
        }}>
          <span style={{ fontSize: 13 }}>
            军令({selArmies.length}) →{" "}
            {selArmies.slice(0, 3).map((a, i) => (
              <b key={a.id} style={{ color: FACTIONS[a.fac].color }}>
                {i > 0 ? "、" : ""}{FACTIONS[a.fac].name}·{a.general}
              </b>
            ))}
            {selArmies.length > 3 && <span> 等{selArmies.length}军</span>}
          </span>
          {Object.entries(ORDERS).map(([k, o]) => {
            const allOn = selArmies.every(a => a.order === k);
            return (
              <button key={k} title={o.note}
                onClick={() => giveOrder(selected, k)}
                style={{
                  ...smallBtn,
                  background: allOn ? "#d4b24c" : "#2a231a",
                  color: allOn ? "#1c1712" : "#e8dcc1",
                  borderColor: allOn ? "#d4b24c" : "#6b5a3e",
                }}>{o.icon}{o.name}{showKeys && <Kbd k={o.key} dark={allOn} />}</button>
            );
          })}
          <span style={{ width: 1, height: 18, background: "#6b5a3e" }} />
          {Object.entries(FORMS).map(([k, f]) => {
            const allOn = selArmies.every(a => a.form === k);
            return (
              <button key={k} title={f.note}
                onClick={() => setForm(selected, k)}
                style={{
                  ...smallBtn,
                  background: allOn ? "#8a6d2f" : "#2a231a",
                  borderColor: allOn ? "#d4b24c" : "#6b5a3e",
                }}>{f.name}</button>
            );
          })}
          <span style={{ width: 1, height: 18, background: "#6b5a3e" }} />
          {Object.entries(PURSUIT_STANCES).map(([k, p]) => {
            const allOn = selArmies.every(a => a.pursuitStance === k);
            return (
              <button key={k} title={`追杀溃兵时：${p.name}（${k === "ruthless" ? "生擒率↓" : k === "merciful" ? "生擒率↑" : "现状不变"}）`}
                onClick={() => setPursuitStance(selected, k)}
                style={{
                  ...smallBtn,
                  background: allOn ? "#5a6f8a" : "#2a231a",
                  borderColor: allOn ? "#d4b24c" : "#6b5a3e",
                }}>{p.icon}{p.name}</button>
            );
          })}
          {selArmies.length === 1 && sim && !isAllied(sim, selArmies[0].fac, fac) && !selArmies[0].militia && (
            <>
              <span style={{ width: 1, height: 18, background: "#6b5a3e" }} />
              <button
                title={selArmies[0].persuadeTried ? "已遣使劝降过，敌将闭门不见" : "遣使阵前劝降：敌军士气愈低、折损愈重、主将有失或断粮，成算愈大"}
                disabled={!!selArmies[0].persuadeTried}
                onClick={() => openSurrenderDialog(selArmies[0].id)}
                style={{
                  ...smallBtn, borderColor: "#7a9a4f",
                  opacity: selArmies[0].persuadeTried ? 0.4 : 1,
                  cursor: selArmies[0].persuadeTried ? "not-allowed" : "pointer",
                }}>🕊 阵前劝降</button>
            </>
          )}
        </div>
      )}

      {inBattle && brokenSelArmies.length > 0 && (
        <div style={{
          display: "flex", gap: 6, alignItems: "center", flexWrap: "wrap", justifyContent: "center",
          background: "#26201660", border: "1px solid #6b5a3e", borderRadius: 4, padding: "6px 12px",
          ...(isFullscreen ? {
            position: "absolute", bottom: 8, left: "50%", transform: "translateX(-50%)", zIndex: 10,
            maxWidth: "92vw", background: "rgba(24,19,12,0.85)",
          } : {}),
        }}>
          <span style={{ fontSize: 13 }}>
            溃军({brokenSelArmies.length}) →{" "}
            {brokenSelArmies.slice(0, 3).map((a, i) => (
              <b key={a.id} style={{ color: FACTIONS[a.fac].color }}>
                {i > 0 ? "、" : ""}{FACTIONS[a.fac].name}·{a.general}
              </b>
            ))}
          </span>
          <button
            title="手动右键可为溃军指定地点：入己方城池重整，靠近友军合并，空地超过500人重编；此按钮为原地尝试重整"
            disabled={brokenSelArmies.every(a => a.rallyCd > 0)}
            onClick={() => rallyArmy(brokenSelArmies.map(a => a.id))}
            style={{
              ...smallBtn, borderColor: "#d4b24c",
              opacity: brokenSelArmies.every(a => a.rallyCd > 0) ? 0.4 : 1,
              cursor: brokenSelArmies.every(a => a.rallyCd > 0) ? "not-allowed" : "pointer",
            }}>
            🚩 旗鼓聚拢
            {brokenSelArmies.some(a => a.rallyCd > 0) && ` (冷却${Math.max(...brokenSelArmies.map(a => Math.ceil(a.rallyCd))).toFixed(0)}s)`}
          </button>
        </div>
      )}

      {inBattle && isFullscreen && !battleInfoOpen && (
        <button onClick={() => setBattleInfoOpen(true)} title="显示诸军与战报"
          style={{
            position: "absolute", top: 60, left: 8, zIndex: 10,
            background: "rgba(24,19,12,0.85)", color: "#d4b24c",
            border: "1px solid #6b5a3e", borderRadius: 4, padding: "5px 9px",
            cursor: "pointer", fontFamily: "inherit", fontSize: 12,
          }}>📜 显示军情</button>
      )}

      {inBattle && (!isFullscreen || battleInfoOpen) && (
      <div style={{
        display: "flex", flexWrap: "wrap", gap: 12, width: `min(96vw, ${Math.max(W, 960)}px)`,
        alignItems: "flex-start",
        ...(isFullscreen ? {
          position: "absolute", top: 60, left: 8, bottom: 64, width: 300, zIndex: 10,
          overflowY: "auto", background: "rgba(24,19,12,0.85)", flexDirection: "column", flexWrap: "nowrap",
          border: "1px solid #6b5a3e", borderRadius: 4, padding: "8px 10px",
        } : {}),
      }}>
        {isFullscreen && <button onClick={() => setBattleInfoOpen(false)} title="隐藏诸军与战报"
          style={{
            position: "absolute", top: 4, right: 4, zIndex: 1,
            background: "transparent", color: "#b8ac8e", border: "1px solid #6b5a3e",
            borderRadius: 3, padding: "1px 6px", cursor: "pointer", fontFamily: "inherit",
          }}>× 隐藏</button>}
        <div style={isFullscreen ? { flex: "1 1 auto", minWidth: 0 } : { flex: "1 1 470px", minWidth: 340 }}>
          <div style={{ fontSize: 13, color: "#d4b24c", marginBottom: 4, letterSpacing: 3 }}>
            ◈ 诸军 <span style={{ fontSize: 11, opacity: 0.6, letterSpacing: 0 }}>（点击选中 · 地图上框选可多选 · ✕ 移除）</span>
          </div>
          {armies.length === 0 && <div style={{ fontSize: 13, opacity: 0.5 }}>尚无军队。</div>}
          {armies.map(a => {
            const F = FACTIONS[a.fac];
            const frac = Math.max(0, a.alive / a.total);
            const isSel = selected.includes(a.id);
            return (
              <div key={a.id}
                onClick={() => {
                  const v = isSel ? selected.filter(x => x !== a.id) : [...selected, a.id];
                  setSelected(v); selectedRef.current = v;
                }}
                style={{
                  display: "flex", alignItems: "center", gap: 7, marginBottom: 3, fontSize: 12.5,
                  cursor: "pointer", padding: "3px 6px", borderRadius: 3,
                  background: isSel ? "#3a2f2280" : "transparent",
                  outline: isSel ? "1px solid #d4b24c" : "none",
                }}>
                <span style={{ color: F.color, fontWeight: 700, minWidth: 104 }}>
                  {F.name}·{a.general}<span style={{ opacity: 0.7, fontWeight: 400 }}>{UNIT_TYPES[a.utype].glyph}</span>
                  {a.level > 0 && <span style={{ color: "#d4b24c", fontSize: 10 }}>{"★".repeat(a.level)}</span>}
                  {a.genDead ? "☠" : ""}
                </span>
                <span style={{ fontSize: 10.5, opacity: 0.7, minWidth: 28 }}>
                  {a.broken ? "溃" : ORDERS[a.order].name}
                </span>
                <div style={{ flex: 1, minWidth: 60 }}>
                  <div style={{ height: 7, background: "#2a231a", borderRadius: 2, overflow: "hidden" }}>
                    <div style={{ width: `${frac * 100}%`, height: "100%", background: a.broken ? "#777" : F.color, transition: "width 0.3s" }} />
                  </div>
                  <div style={{ display: "flex", gap: 2, marginTop: 1.5 }}>
                    <div title={`士气 ${Math.round(a.morale)}`} style={{ flex: 1, height: 3.5, background: "#2a231a", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: `${a.morale}%`, height: "100%", background: a.morale > 50 ? "#c9a227" : "#b03a2e" }} />
                    </div>
                    <div title={`粮草 ${Math.round(a.food)}`} style={{ flex: 1, height: 3.5, background: "#2a231a", borderRadius: 2, overflow: "hidden" }}>
                      <div style={{ width: `${a.food}%`, height: "100%", background: a.food > 30 ? "#7a9a4f" : "#b03a2e" }} />
                    </div>
                  </div>
                </div>
                <span style={{ minWidth: 132, textAlign: "right", opacity: 0.85, fontSize: 11.5 }}>
                  {(a.alive * MEN_PER_DOT).toLocaleString()}/{(a.total * MEN_PER_DOT).toLocaleString()}
                  {a.withdrawn > 0 ? ` 撤${(a.withdrawn * MEN_PER_DOT).toLocaleString()}` : ""}
                  {a.captives > 0 ? ` 俘${(a.captives * MEN_PER_DOT).toLocaleString()}` : ""}
                  {" "}歼{(a.kills * MEN_PER_DOT).toLocaleString()}
                </span>
                <button title="移除此军"
                  onClick={(e) => {
                    e.stopPropagation();
                    removeArmy(simRef.current, a.id);
                    const v = selected.filter(x => x !== a.id);
                    setSelected(v); selectedRef.current = v;
                    setTick(t => t + 1);
                  }}
                  style={{ ...smallBtn, padding: "1px 6px", fontSize: 11, color: "#c98" }}>✕</button>
              </div>
            );
          })}
          {armies.some(a => a.capturedGens && a.capturedGens.length > 0) && (
            <div style={{ fontSize: 11.5, opacity: 0.7, marginTop: 4 }}>
              🔗 被擒之将：{armies.flatMap(a => (a.capturedGens || []).map(g => `${g}(为${FACTIONS[a.fac].name}·${a.general}所擒)`)).join("，")}
            </div>
          )}
        </div>
        <div style={isFullscreen ? { flex: "1 1 auto", minWidth: 0 } : { flex: "1 1 280px", minWidth: 250 }}>
          <div style={{ fontSize: 13, color: "#d4b24c", marginBottom: 4, letterSpacing: 3 }}>◈ 战报</div>
          {logs.map((l, i) => (
            <div key={i} style={{ fontSize: 13, opacity: 1 - i * 0.1, marginBottom: 2 }}>{l}</div>
          ))}
        </div>
      </div>
      )}
      </div>

      {inBattle && (
      <div style={{ fontSize: 11, opacity: 0.45, textAlign: "center", maxWidth: 820 }}>
        城池：圈内友军防御×1.5、伤兵休养回血、缓募守军（上限千人）、粮草补给；敌军入圈且众寡悬殊则围而易帜（城头进度环）。
        关隘：扼山岭谷口，圈内防御×1.4，破关则军心大损。兵营可速募而易毁——烧敌粮营者胜。
        老兵星徽分三级（暗金/金/亮金加大且身形微壮），将旗随晋阶加长镶星、三级以上金边环身。
        框选多军可齐令齐变阵。虎牢关一夫当关，街亭据山断粮，官渡烧营决胜。
      </div>
      )}

      {/* ===== 天下模式对话（俘将处置 / 求亲等） ===== */}
      {grandDialog && (
        <div style={{
          position: "fixed", inset: 0, zIndex: 60,
          background: "rgba(10,8,5,0.72)", display: "flex",
          alignItems: "center", justifyContent: "center", padding: 20,
        }} onClick={() => setGrandDialog(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            width: "min(92%, 520px)", background: "#e7dcc1", color: "#2a2118",
            border: "3px double #6b5a3e", borderRadius: 4, padding: "18px 24px",
            boxShadow: "0 10px 40px rgba(0,0,0,0.7)",
            fontFamily: "'Songti SC','Noto Serif SC',serif",
          }}>
            <div style={{ fontSize: 16, fontWeight: 700, letterSpacing: 4, marginBottom: 10, color: grandDialog.action === "marry" ? "#8a4a5a" : "#7c2820" }}>
              {grandDialog.action === "marry" ? "◈ 秦晋之议" : grandDialog.action === "persuade" ? "◈ 帐前劝降" : grandDialog.action === "release" ? "◈ 义释" : grandDialog.action === "ransom" ? "◈ 纳赎" : "◈ 处置俘将"}
            </div>
            <div style={{ fontSize: 14, lineHeight: 1.95, whiteSpace: "pre-line", marginBottom: 14 }}>
              {grandDialog.resultText}
            </div>
            <div style={{ textAlign: "center" }}>
              <button onClick={() => setGrandDialog(null)} style={{
                padding: "7px 26px", borderRadius: 3, cursor: "pointer", fontSize: 14, fontWeight: 700,
                background: "#3a2f22", color: "#e8dcc1", border: "1px solid #6b5a3e", fontFamily: "inherit",
              }}>继续 →</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
